import { TEXT_LAYOUT_PARAGRAPHS, textLayoutAtLeast } from '@lite-workspace/text-layout';
import type {
  RenderBox,
  RenderGroupBox,
  RenderImageBox,
  RenderObjectBox,
  RenderSmartObjectBox,
  RenderTextBox,
  RenderRect,
  RenderVectorBox,
  FontErrorKind,
} from '@lite-workspace/shared/canvas-engine/core';
import {
  isRosterCellRole,
  refitBakedTileRect,
  scalePaths,
  scaleVectorParams,
  TextTypeEnum,
  type VectorBoxParams,
} from '@lite-workspace/dto-fotoclass-templates';
import { resolveVerticalAlign } from '@lite-workspace/shared/render-model';
// Narrow entry, never the pure/index barrel: those re-export the spread-template fixtures (~1.35 MB) into every
// consumer's eager bundle just to reach one helper.
import {
  clipCompanionRenderIds,
  maskDataUrlFromRgba,
  planCompoundClipRebakes,
  type CompoundClipMaskUpdate,
  type CompoundClipRebakePlan,
} from './compound-clip-rebake';
import { CROP_ROTATE_CURSOR, CROP_ZOOM_CURSOR } from './crop-cursors';
import { imageDecodeVersion, type DecodeImageOptions } from './decode-image';
import { RESIZE_DIR_CURSORS } from './resize-cursors';
import {
  autoWidthRect,
  fixedHeightRect,
  residualScaleX,
  spaceAdvanceOf,
  textCloneFrame,
  type FrameFitProbe,
} from './text-auto-width';
import {
  OverlayTextEdit,
  editCssFontFamily,
  cssFontVariationSettings,
  type TextEditStyle,
  type ShapedCaretLayout,
  type ShapedCaretSource,
  type TextEditRules,
} from './overlay-text-edit';
import {
  applyColorToStyleRuns,
  remapParagraphRunsForTextChange,
  remapStyleRunsForTextChange,
  stripStyleRunCaps,
  stripStyleRunColors,
  stripStyleRunDecorations,
  stripStyleRunFaces,
  type StyleRunDecorationPatch,
} from '@lite-workspace/photobook-model';
import {
  caretSlotAt,
  caretIndexAt,
  caretIndexByLineStep,
  caretLineEdge,
  caretSelectionBands,
  type AreaCaretLayout,
} from './area-caret-layout';
import { pathShapedCaret, type PathCaretLayout } from './path-caret-layout';
import { engineCaretNeeded, engineShapedCaret, type EngineCaretGeometry, type EngineCaretLine } from './engine-caret-layout';
import { CARET_BASELINE_DESCENT_RATIO } from './caret-metrics';
import { boxCaseTheLayoutDraws, runCapsDivergeFromBox, type TextFrameFit } from './glyph-layout-backend';
import { wrapZonesCrossFrame } from './text-paragraphs';
import { effectBleed } from './effect-bleed';
import { applyTextScale, textScaleSnapshotOf, type TextScaleSnapshot } from './text-scale-snapshot';
import { configurePsdRenderWasmUrl, type PsdRenderWasmService } from './psd-render-wasm.service';
import {
  collectImageRefs,
  collectBinaryRefs,
  appendZoneShapes,
  applyLiveOverridesToBoxes,
  noteShownRendition,
  withoutSourceTiers,
  type LiveCropRect,
  type LiveTrimOverride,
  type SceneFrameStroke,
  type SpreadScene,
} from './spread-scene-adapter';
import { albumStrokeScopeOf } from './frame-stroke-eligibility';
import { buildRustScene } from './rust-scene-build';
import { planImageTiers, visibleImages, type TierEntry } from './progressive-image-tiers';
import {
  createCropStateFromRects,
  cropStateToImageRect,
  panState,
  scaleStateByRatio,
  zoomStateByPercentDelta,
  applyRotationWithAutoScale,
  clampAndMagnetRotation,
  MAX_ROTATION,
  WHEEL_ZOOM_SENSITIVITY,
  WHEEL_ZOOM_MAX_DELTA,
  WHEEL_ROTATION_SENSITIVITY,
  WHEEL_ROTATION_MAX_DELTA,
  PINCH_ZOOM_BOOST,
  scaleToPercent,
  type CropState,
  type CropImageRect,
} from './crop-gesture-math';
import {
  clampWindowToContent,
  isUntrimmed,
  beginTrim,
  composeSourceTransform,
  scaleContentToFrame,
  type TrimRect,
} from './decor-trim-math';
import {
  createPathEditSession,
  sessionD,
  refreshSessionSampler,
  rangeFractions,
  pointAtFraction,
  nearestFraction,
  moveKnot,
  moveKnotHandle,
  insertKnotAtFraction,
  removeKnot,
  shiftRange,
  type PathEditMode,
  type PathEditSession,
  type TextPathWork,
} from './overlay-path-edit';
import {
  buildDropHoverShape,
  SELECTION_STROKE_COLOR,
  SELECTION_STROKE_WIDTH,
  SELECTION_OVERFLOW_STROKE_COLOR,
  SELECTION_OVERFLOW_STROKE_WIDTH,
  ACCENT_COLOR,
  accentAlpha,
  PHOTO_SELECTION_COLOR_TOKEN,
  PHOTO_SELECTION_WIDTH,
  SNAP_GUIDE_TOKEN,
  HOVER_STROKE_COLOR,
  HOVER_STROKE_WIDTH,
  HOVER_FADE_MS,
  CHROME_BLEED_PX,
  bleedOffset,
  buildZoneShapes,
  editorSurfaceBox,
  pageClipInsets,
  type LayerBox,
  type ZonePrintSizes,
  type ZoneVisibility,
  type ZoneStyle,
} from './zone-shapes';
import {
  overlayPointToScene,
  indexDrawableRects,
  inkExpanded,
  liftSourceRect,
  lockedRectCovers,
  topmostRectAt,
  topmostTextAt,
  type SceneRect,
} from './overlay-hit';
import {
  type Camera,
  type ResolutionCaps,
  cameraRenderArgs,
  fitCameraToBox,
  resolutionForZoom,
  shouldSharpTile,
  spreadBufferSize,
} from './camera-transform';
import {
  anchorPositions,
  resizeRect,
  resizeRectWithMatrix,
  rotateAngle,
  groupBounds,
  rotatedAABB,
  groupResize,
  groupResizeBounds,
  groupRotate,
  groupRotateDelta,
  marqueeRect,
  rectsIntersect,
  collectLineStops,
  collectSnapStops,
  mergeSnapStops,
  selectNeighborStops,
  computeMoveSnap,
  computeResizeSnap,
  computeSizeMatchSnap,
  rotationGuideAngle,
  handleAtPoint,
  handleCursorAngle,
  scaleRectAroundAnchor,
  TEXT_SCALE_FONT_MIN_PX,
  TEXT_SCALE_FONT_MAX_PX,
  CORNER_ANCHORS,
  ANCHOR_SIGN,
  snapKindRank,
  SNAP_ARBITRATION_MARGIN_PX,
  SNAP_GAP_RADII_PX,
  type GroupMember,
  type GroupMemberUpdate,
  type HandleId,
  type MoveSnapHold,
  type Point,
  type ResizeAnchor,
  type ResizeSnapHold,
  type SizeGuide,
  type SnapAxis,
  type TransformRect,
  type SnapProfile,
  type SnapStops,
  type SnapGuide,
  type SnapNeighbor,
  type SnapNeighborKind,
} from './overlay-transform';
import {
  computeEqualGapSnap,
  computeGapChannelsByAxis,
  combineMoveSnap,
  DUAL_GUIDE_EPS_PX,
  type GapGuide,
  type GapChannelsByAxis,
  type GapSnapRadii,
  type GapSnapHold,
  type GapMaxByAxis,
  type SnapAxisOwner,
} from './overlay-snap-gaps';
import { evictedStubRefs } from './resident-stubs';
import type { LaidOutGlyph, MappedFace } from './text-scene-adapter';
import type { FontVariation } from './font-instances';
import type { FontRefOf } from './run-fonts';
import { tokenColor } from './token-color';
import {
  mul,
  shearMatrix,
  shearAboutPivot,
  reanchorAffine,
  composeBoxDrawTransform,
  rotationAboutPoint,
  isIdentityAffine,
  scaleAffineTranslation,
  textMatrixAnisotropy,
  textMatrixScale,
  type Affine,
} from './text-affine';

/** Host stage viewport (scale + offsets) the overlay needs; the retired Konva stage once provided it. */
export interface OverlayStage {
  readonly scale: number;
  readonly viewportOffset: { x: number; y: number };
  readonly chromeOffset: { x: number; y: number };
}

export interface RustImage {
  sourceUrl?: string;
  preservePngRgba?: boolean;
  width: number;
  height: number;
  data: Uint8Array;
  /** The source URL these bytes came from — lets the persistent WASM renderer re-upload a box when its photo changes. */
  version?: string;
  /** Photo identity behind the bytes (SceneImageRef.imageId) — survives box-id renames and rendition changes. */
  imageId?: string;
  /** Ladder rung of `version` (TierEntry.rungPx); absent = below the ladder (thumb, blur) or off it. */
  rungPx?: number;
}

export interface ResolvedFonts {
  fonts: Record<string, Uint8Array>;
  // These four only feed toSpreadScene in psd-render-lab; the overlay builds through build_scene and never reads them.
  layoutGlyphs?: (box: RenderTextBox) => LaidOutGlyph[];
  usWeightClass?: (box: RenderTextBox) => number;
  hasVariableWeight?: (box: RenderTextBox) => boolean;
  /** True when a font mapping swapped the family for this box — suppresses weight synthesis/variation. */
  familySubstituted?: (box: RenderTextBox) => boolean;
  /** The instance the font-mapping row behind this box's face names. */
  mappedFace?: (box: RenderTextBox) => MappedFace | undefined;
  /** The axis values the engine draws the box font at (pinnedOrDerivedVariation) — the inline-edit surface lays its
   *  CSS face out at the same axes, or its caret runs at the file's default instance. Absent: no variation known. */
  fontVariationOf?: (box: RenderTextBox) => FontVariation | undefined;
  /** Layout font key for a text box (matches a `fonts` map key) — injected into boxes so build_scene shapes them. */
  fontRefOf?: FontRefOf;
  /** First-line cap metrics (book px at box.fontSize) from the engine's own resolveCapHeightRatio — SSoT source the
   *  inline-edit caret overlay consumes so it tracks the rendered glyphs. Omitted → caret falls back to Canvas metrics. */
  caretMetrics?: (box: RenderTextBox) => { capHeightPx: number; ascenderDeltaPx: number } | undefined;
  /** The engine's index↔position caret grid (box canvas px) for layouts the browser cannot reproduce; `force` keeps
   *  a started session on it. Undefined when the textarea lays the box out the same way. */
  caretLayoutOf?: (box: RenderTextBox, force?: boolean) => AreaCaretLayout | undefined;
  /** The same grid for text on a path (box canvas px), from the placements its glyphs are drawn at. */
  pathCaretLayoutOf?: (box: RenderTextBox) => PathCaretLayout | undefined;
  /** The caret grid from the Rust layout build_scene draws the box by (warp and pinned baselines included). */
  engineCaretOf?: (box: RenderTextBox) => EngineCaretGeometry | undefined;
  /** Kind of font-resolution failure for a box (its intended font was missing/unavailable and got a fallback), else undefined. */
  fontErrorOf?: (box: RenderTextBox) => FontErrorKind | undefined;
  /** True when the box's wrapped text overflows its rect height — drives the warn-coloured selection frame. */
  overflowOf?: (box: RenderTextBox) => boolean;
  /** How far `text` would spill past this box's frame, by the renderer's own layout pass — the inline-edit clamp's
   *  SSoT, so the editor and the canvas cannot disagree about what fits. Omitted → the clamp stays off. */
  frameFitOf?: (box: RenderTextBox, text: string) => TextFrameFit;
  /** The TS layout twin's fit of `text`, without the engine's glyph-grid line count {@link frameFitOf} adds: a probe that
   *  scans many lengths (the quote fill) finds its edge on the twin and asks `frameFitOf` only around it. */
  twinFrameFitOf?: (box: RenderTextBox, text: string) => TextFrameFit;
  /** Unique non-space characters of `text` the box face maps to .notdef — the canvas draws them as empty squares. */
  missingGlyphsOf?: (box: RenderTextBox, text: string) => string[];
}

/** A font-error badge to draw over a box whose font failed to resolve — screen px in the overlay canvas' client space. */
export interface FontErrorBadge {
  id: string;
  kind: FontErrorKind;
  left: number;
  top: number;
  width: number;
  height: number;
}

/** A styled line for the editor chrome canvas (pixel grid, central guides, user guides). */
export interface ChromeLine {
  /** Endpoints in book/print px (the overlay scales them by stage.scale). */
  x1: number;
  y1: number;
  x2: number;
  y2: number;
  color: string;
  /** Stroke width in screen px. */
  width: number;
  /** Stroke opacity; omitted = fully opaque. */
  opacity?: number;
  /** Dash pattern in screen px; empty/undefined = solid. */
  dash?: number[];
}

/** A filled chrome rect (column/row grid stripe) in book/print px; the overlay scales it by stage.scale. */
export interface ChromeRect {
  x: number;
  y: number;
  width: number;
  height: number;
  /** Pre-composited fill (e.g. rgba with the grid opacity baked in). */
  fill: string;
}

/** A text badge on the chrome canvas (e.g. retouch photo numbers), anchored in book/print px, centred on x. */
export interface ChromeText {
  x: number;
  y: number;
  text: string;
  color: string;
  /** Font size in screen px (chrome canvas tracks screen px — unaffected by zoom). */
  fontPx: number;
  /** Rounded plate behind the text; omitted → bare text. */
  plateFill?: string;
}

export interface ResizeRotateUpdate {
  /** This leaf was transformed through an explicitly selected parent group. */
  parentTransform?: boolean;
  id: string;
  rect: [number, number, number, number];
  rotation: number;
  /** Re-covered photo placement (container-relative) for a resized image box; omitted for text/shape and for rotate. */
  imageRect?: { x: number; y: number; width: number; height: number; rotation: number };
  /** Crop-preserving re-fit of a trimmed decor's contentRect (box-local); omitted when untrimmed and for rotate. */
  contentRect?: TrimRect;
  /** The user dragged this box's own resize handle — the model sink freezes an auto-width text frame on it. */
  frameTakeover?: boolean;
  /** FKS-11514: proportional corner resize of a text box — the font multiplier (equals the frame multiplier,
   *  already clamped to the font-size range). The host must scale its model's font fields by it; absent → font unchanged. */
  fontScale?: number;
}

/**
 * A live layout that follows an in-progress resize: boxes outside the selection the host moves with it (a band of
 * cells that shares a row with the resized one). `candidates` is asked once at grab with the gesture's own box ids and
 * names every box that may move — they are lifted with the gesture; `reflow` is asked per preview frame with that
 * frame's updates (the shape `onResizeRotate` gets) and returns the candidates' updates for the frame. The commit stays
 * the host's own: `onResizeRotate` still carries only the gesture's boxes.
 */
export interface ResizeReflowProvider {
  candidates(boxIds: readonly string[]): readonly string[];
  reflow(updates: readonly ResizeRotateUpdate[]): readonly ResizeRotateUpdate[];
}

export interface MoveUpdate {
  parentTransform?: boolean;
  id: string;
  rect: [number, number, number, number];
  rotation: number;
  /** A trimmed decor's contentRect (box-local) translated by the same move/nudge delta; omitted when untrimmed. */
  contentRect?: TrimRect;
}

/**
 * What a hovered box says about the drag over it: takes it, refuses it — or (null) is no target at all.
 * 'forbid' and 'blocked' both read red; they differ only in what the HOST does with the drop itself —
 * 'forbid' is a soft refusal the drag cursor must kill, 'blocked' still lets the drop land so the host
 * can explain it. Painting them alike is the point: the frame under the photo says «нельзя» either way.
 */
export type DropHoverVerdict = 'allow' | 'forbid' | 'blocked';

export interface RustOverlayConfig {
  stage: OverlayStage;
  host: HTMLElement;
  wasm: PsdRenderWasmService;
  /** Served wasm asset path, e.g. '/psd_render_bg.wasm'. */
  wasmUrl: string;
  getRenderBoxes: () => RenderBox[];
  getViewport: () => { width: number; height: number };
  /**
   * Per-page rects (book px) for tiered snapping: each page contributes its edges and centre as strong
   * page-class stops with the measured capture/release radii. Absent or empty → the host keeps the
   * historical uniform 2-screen-px snap over the whole-viewport stops.
   */
  getSnapPages?: () => TransformRect[];
  /**
   * Extra page-class lines (book px) the host already draws and wants the magnet to land on — e.g. the centre
   * of each page's VISIBLE (trim) area, the stop that reads as «поля от корешка и от обреза равны». `vertical`
   * are x-coordinates, `horizontal` are y-coordinates; the guide spans the page like any other page stop.
   */
  getSnapLines?: () => { vertical: readonly number[]; horizontal: readonly number[] } | undefined;
  /** Пользовательский тумблер «Примагничивание»: `false` — ни один магнит не собирается вовсе. */
  getSnapEnabled?: () => boolean;
  /**
   * Host veto on a drawable as a snap anchor (background is always vetoed). The design editor drops
   * decor/clipart here: scenery should not attract frames and text being aligned.
   */
  isSnapTarget?: (boxType: string | undefined, id: string) => boolean;
  /**
   * Угол `object`-бокса держит пропорцию без Shift и без магнита (FKS-12037, ac14). Включает только тот
   * редактор, где это обещано пользователю: фотокнига и редактор дизайн-паков тянут угол по-прежнему свободно.
   */
  lockDecorAspect?: boolean;
  /**
   * Рамка с фотографией держит пропорцию на любой ручке, а Shift наоборот освобождает её: снимок — картинка,
   * свободное растяжение молча перекадрировало бы его. Включает только редактор альбома.
   */
  lockImageAspect?: boolean;
  /**
   * Хост отпускает замок `lockImageAspect` на БОКОВЫХ ручках этой рамки: двигается одна кромка, как у пустой.
   * Углы держат пропорцию по-прежнему. Редактор альбома так отвечает за любую рамку со свободной пропорцией —
   * верхнего уровня или одинокую в группе: «Свободная» в плашке обязана значить свободную форму.
   */
  freeEdgeAspect?: (id: string) => boolean;
  getZones: () => ZonePrintSizes | undefined;
  /** Per-zone visibility (the five editor toggles). Omitted → all visible. */
  getZoneVisibility?: () => ZoneVisibility | undefined;
  /** Which editor's zone geometry to mirror (designEditor splits the safe frame per page). */
  zoneStyle?: ZoneStyle;
  resolveFonts: (textBoxes: RenderTextBox[]) => Promise<ResolvedFonts>;
  decodeImage: (url: string, options?: DecodeImageOptions) => Promise<RustImage>;
  /** Called when a click hit-tests a drawable (id) or empty space (undefined); host owns selection state. */
  onSelect?: (id: string | undefined, multiSelect: boolean) => void;
  /** Called on a rubber-band marquee release with the ids it enclosed; host writes selection. */
  onMarqueeSelect?: (ids: string[]) => void;
  /** Right-click hit-tested a drawable (id) or empty space (undefined); host opens its context menu. Not preventDefault'd. */
  onContextMenu?: (id: string | undefined, event: MouseEvent) => void;
  /** Selection AABB in overlay-canvas-local screen px (null when empty) — host positions its floating selection toolbar. */
  onSelectionRect?: (rect: { left: number; top: number; width: number; height: number } | null) => void;
  /** A manipulation gesture (move / resize / rotate / skew / swap drag) started or ended. `onSelect` fired while
   *  this is still active is a drop's reselect, not a click — the host uses it to keep its floating toolbar down. */
  onGestureActiveChange?: (active: boolean) => void;
  /** Lift offset of a move gesture in screen px (`ids` = the lifted boxes), `null` at the drop — host badges pinned to
   *  those boxes translate with the lift instead of sitting on the committed rect until the model lands. */
  onDragOffset?: (offset: { ids: readonly string[]; x: number; y: number } | null) => void;
  /** Boxes whose font failed to resolve (rendered in a fallback) — the component draws a warning badge over each. */
  onFontError?: (badges: FontErrorBadge[]) => void;
  /** Called when the hovered drawable changes (id) or the pointer leaves content (undefined). */
  onHover?: (id: string | undefined) => void;
  /** Box types the pointer may target (hover/select/menu/marquee). Omitted → every drawable is targetable;
   *  an EMPTY list means nothing is (a read-only surface), never everything. */
  getTargetableTypes?: () => readonly string[] | undefined;
  /** Per-box targeting veto on top of the type list (e.g. a student may act only on own vignette card).
   *  Omitted → the type list alone decides. A vetoed box gets no hover, no pointer cursor, no selection. */
  canTargetBox?: (id: string) => boolean;
  /** Committed when a resize/rotate ends — one entry per box (single or group); host writes ONE undo entry.
   *  A resized image box also carries its re-covered `imageRect` so the photo stays Fill in the new frame. */
  onResizeRotate?: (updates: ResizeRotateUpdate[]) => void;
  /** Live layout of other boxes while a resize is dragged (`ResizeReflowProvider`); absent or null → only the
   *  gesture's own boxes move until release. Read at every grab, so the host may switch it per spread or mode. */
  resizeReflow?: () => ResizeReflowProvider | null | undefined;
  /** Committed when a move (translate) ends — one entry per box; host writes ONE undo entry.
   *  A trimmed decor also carries its translated `contentRect` so the crop moves with the frame as one unit. */
  onMove?: (updates: MoveUpdate[]) => void;
  /** Arrow-key nudge of the selection — a burst of keystrokes debounces into ONE commit; falls back to onMove. */
  onNudge?: (updates: MoveUpdate[]) => void;
  /** Ends a ⌘-drag skew (shear) — the box's full affine `[a,b,c,d,e,f]`; host writes ONE undo entry. */
  onTransform?: (id: string, matrix: [number, number, number, number, number, number]) => void;
  /** Double-click hit a text box — host enters inline edit (e.g. toggles its text-editing UI state). */
  onTextEditStart?: (id: string) => void;
  /** Живая каретка/выделение открытой сессии — чип цвета показывает букву перед курсором. */
  onTextEditSelectionChange?: (id: string, selection: { start: number; end: number }) => void;
  /** Inline edit ended (blur/Escape) — host clears its text-editing UI state. `selection` — the final
   *  textarea range (model-text indices): the panel paints it AFTER the session is gone (Photoshop-style). */
  onTextEditEnd?: (id: string, selection?: { start: number; end: number }) => void;
  /**
   * Inline edit committed the final text once on blur — host writes the box text + ONE undo entry. `rect` comes with
   * an auto-width block whose frame grew around the typed glyphs; it belongs to the same write, not a second one.
   */
  onTextEditCommit?: (
    id: string,
    text: string,
    rect?: RenderRect,
    styleRuns?: RenderTextBox['runs'],
    paragraphRuns?: RenderTextBox['paragraphRuns'],
  ) => void;
  /**
   * Silhouettes re-baked for textures clipped into text (FKS-12999): the source text changed since its clipper's mask
   * was drawn — typed, restyled, moved, undone. Derived data, not an edit: the host writes it without an undo step.
   * Omitted → the surface keeps the masks it was given: the album/photobook do carry compound-clip recipes (imported
   * textures clipped into text) but never re-bake them, so their clippers draw with the stored mask or the live clip.
   */
  onCompoundClipMasks?: (updates: readonly CompoundClipMaskUpdate[]) => void;
  /** Double-click hit a person-owned text — host opens the person flow; text edit never starts.
   *  `null` — тип сказал «поле персоны», но группы-владельца нет (рамка на обложке верхним уровнем):
   *  владельца знает только хост — у альбома он есть, у шаблона нет. */
  onPersonSelect?: (personId: string | null, boxId: string) => void;
  /** Click hit a box whose type is not targetable for this role — selection stays untouched; host may explain. */
  onUntargetableClick?: (boxId: string) => void;
  /** Double-click landed on empty page space (nothing targetable under the pointer, background included). */
  onEmptyDblClick?: () => void;
  /** Принадлежит ли текст персоне — вопрос ТИПА поля, а не того, лежит ли бокс в persona-группе:
   *  `true` — на месте не правится (значение кладёт подстановка), `false` — свободный текст альбома,
   *  `undefined` — хост не знает, решает группа. Не задан → решает только группа. */
  isPersonOwnedText?: (boxId: string) => boolean | undefined;
  /** Enables double-click inline image crop (pan/zoom/rotate the photo within its frame). Off → dblclick on images is inert. */
  enableInlineCrop?: boolean;
  /** Per-box crop veto on top of enableInlineCrop (e.g. a student may crop only own frames). Omitted → allowed. */
  canCropBox?: (id: string) => boolean;
  /** Enables double-click decor trim (edge-resize the window over fixed content) on object/background/smartObject boxes.
   *  Off → dblclick on decor is inert. */
  enableDecorTrim?: boolean;
  /** Dbl-click drill-into-group probe: returns true when it consumed the gesture, suppressing the leaf's crop/text-edit. */
  tryDrillIn?: (id: string, additive: boolean) => boolean;
  /** Press-drag on an UNSELECTED box moves these ids — what a click on `id` would select given `selected` (the
   *  host's group unit). Omitted → the grabbed box alone. */
  pressUnitOf?: (id: string, selected: readonly string[]) => readonly string[];
  /** Gates double-click inline text editing (capability-driven). Omitted → allowed. */
  canEditText?: (boxId: string) => boolean;
  /** Product rules for a single box (student quote: character cap, Enter saves, «Осталось N символов» chip).
   *  Omitted or `null` for the box → the inline session behaves exactly as it always did. */
  textEditRules?: (boxId: string) => TextEditRules | null;
  /** A single click on an editable, non-person text box enters inline edit directly (dblclick still works).
   *  Off → click only selects (select-then-edit). */
  enableClickTextEdit?: boolean;
  /** Rejects typed/pasted input that would push the laid-out text past the frame of an AREA (`block`) box, on
   *  either axis. Free (point) text is never clamped — its rect is the ink box the parser derived around the
   *  authored glyphs, not a container. Pre-existing overflow stays editable-shorter; additions are blocked. */
  enableTextClampToFrame?: boolean;
  /** Auto-width frames follow the typed text and ride the commit (design editor). A host without this cannot
   *  STORE a grown rect — its auto-width layers keep fixed-frame semantics AND their clamp, when it has one. */
  enableTextFrameFollow?: boolean;
  /** Corner drag of a single text box scales frame AND font size proportionally (aspect held, snap dropped,
   *  scale stops whole at the font-size range bounds); side handles keep frame-only resize. The commit carries
   *  `fontScale` — the host must multiply its model's font fields by it. Off → corner drag reshapes the frame
   *  only, as before.
   *  `'free'` narrows the same scaling to NON-`block` text (free) and widens it to the left/right side
   *  handles — such a box has no independent height, so it offers no top/bottom handle at all.
   *  `block` text (block/auto-width) under `'free'` keeps the plain frame resize, corners included. */
  enableTextCornerScale?: boolean | 'free';
  /** 'select' → no transform handles, no move/resize/rotate/nudge gestures; click-select, marquee, text edit and
   *  crop stay. 'place' → a click reports a book point via onPlace instead of selecting (tool placement). Omitted → 'full'. */
  getInteractionMode?: () => 'full' | 'select' | 'place';
  /** 'place' mode: a click reported a book-space point (undefined when the overlay isn't laid out) —
   *  host inserts an element there. */
  onPlace?: (point: { x: number; y: number } | undefined) => void;
  /** Arms the card-swap drag in 'select' mode: pressing a top-level group and dragging it onto another
   *  group emits onGroupSwap instead of a move (the host gates this by spread type + capability). */
  getCardSwapEnabled?: () => boolean;
  /** A card-swap drag dropped one top-level group onto another — host swaps them (one undo entry). */
  onGroupSwap?: (sourceId: string, targetId: string) => void;
  /** A card-swap drag walked the held card across N same-size cards and dropped: `[heldId, ...steppedOver]`,
   *  each taking the slot of the one before it (one undo entry). Omitted → only pairwise swaps commit. */
  onGroupCascade?: (ids: readonly string[]) => void;
  /** Arms the photo-swap drag in 'select' mode: pressing a filled image frame and dragging it onto another
   *  image frame emits onPhotoSwap instead of a move (the host gates this by spread type + capability). */
  getPhotoSwapEnabled?: () => boolean;
  /** A photo-swap drag dropped one image frame onto another — host trades their photos (one undo entry). */
  onPhotoSwap?: (sourceId: string, targetId: string) => void;
  /** The same drag released over the FREE AREA of the OTHER page (`side`: 0 left, 1 right) — the host moves the
   *  photo there (one undo entry). Silent on a release over the pasteboard, over its own page, or on an aborted
   *  gesture (blur/pointercancel): those are not drops. */
  onPhotoDragRelease?: (sourceId: string, target: { side: 0 | 1 }) => void;
  /** CSS box-shadow for the spread buffer (shows the page bounds over the host backdrop). Omitted → none. */
  spreadShadow?: string;
  /** CSS border-radius for the spread buffer (rounds the sheet corners). Omitted → square. */
  spreadRadius?: string;
  /** Presents the page as it prints: the bleed/wrap the cutter takes off is hidden, or ghosted outside the page
   *  while the outer technical zone is on. Omitted → the whole pre-trim sheet IS the page (design surfaces). */
  trimPage?: boolean;
  /** Text-on-path edit mode entered (mode) or left (null) — host syncs its panel toggle state. */
  onTextPathModeChange?: (id: string, mode: PathEditMode | null) => void;
  /** A path-edit gesture (anchor/handle/add/delete point) committed new curve geometry — host writes textPath.d, ONE undo. */
  onTextPathD?: (id: string, d: string) => void;
  /** A range gesture (bracket / whole-text drag) committed a new curve position — host writes pathRange*, ONE undo. */
  onTextPathRange?: (id: string, start: number, end: number) => void;
  /** Double-click entered inline crop on an image box — host may toggle its crop-mode UI state. */
  onCropStart?: (id: string) => void;
  /** Inline crop ended (commit/cancel/Escape) — host clears its crop-mode UI state. */
  onCropEnd?: (id: string) => void;
  /** Live inline-crop zoom, 0–100 across the allowed scale range — host renders it next to its +/- buttons.
   *  Fires on crop entry and on every scale change (wheel, edge handle, rotate auto-scale, reset, +/- step). */
  onCropZoomChange?: (id: string, percent: number) => void;
  /** The live inline-crop placement (same container-relative coords as onCropCommit), on every crop change and once
   *  with the final rect at commit — the host recomputes the print-quality badge from it. `onCropEnd` (commit and
   *  cancel alike) takes the live value down, so there is no null call. */
  onCropRectChange?: (id: string, imageRect: { x: number; y: number; width: number; height: number; rotation: number }) => void;
  /** Inline crop committed the new photo placement (container-relative coords) — host writes naturalAligns.image + ONE undo. */
  onCropCommit?: (id: string, imageRect: { x: number; y: number; width: number; height: number; rotation: number }) => void;
  /** Decor trim committed a new window/content-rect pair — host writes box.rect = container, box.contentRect = content
   *  (undefined clears a stored contentRect when the box ends up fully un-trimmed) + ONE undo entry. */
  onTrimCommit?: (id: string, update: { container: TrimRect; content: TrimRect | undefined }) => void;
  /** Editor chrome lines (grid/guides) in book px, redrawn on pan/zoom and via redrawChrome(). */
  getChromeLines?: () => ChromeLine[];
  /** Editor chrome fills (column/row grid stripes) in book px, drawn under the chrome lines. */
  getChromeRects?: () => ChromeRect[];
  /** Editor chrome text badges (e.g. retouch numbers) in book px, drawn over the chrome lines. */
  getChromeTexts?: () => ChromeText[];
  /** Ids of text boxes to outline for the "show text frames" toggle; the overlay frames them via their
   *  draw-transform (so rotated/flipped text is framed correctly), not the host's rect. Empty → toggle off. */
  getTextFrameBoxIds?: () => string[];
  /** Empty owner quote frames: a grey dashed outline drawn with or without the toggle; wins over its text frame. */
  getQuoteFrameBoxIds?: () => string[];
  /** Phone editor: text boxes marked as tappable — a dashed frame and a pencil chip each; the editing box is skipped. */
  getTextMarkBoxIds?: () => string[];
  /** Boxes to outline as invalid (e.g. a required field left empty), framed by the same draw-transform.
   *  The host resolves the colour from its own token — the canvas cannot read CSS vars.
   *  `emptyFrames` (a subset of `ids`) render a light wash over the frame plus a centred plus glyph
   *  INSTEAD of the error stroke: an empty PHOTO frame is an invitation to add a photo, not an alarm.
   *  Both colours arrive opaque (token-resolved); the wash/plus translucency is this overlay's own
   *  constant. Without `plus` the glyph falls back to the outline colour.
   *  `warnFrames` — a warning (not an error) outline in its own colour, same stroke. */
  getInvalidFrames?: () =>
    | {
        ids: readonly string[];
        color: string;
        emptyFrames?: { ids: readonly string[]; fill: string; plus?: string };
        warnFrames?: { ids: readonly string[]; color: string };
      }
    | undefined;
  /** Paint for boxes flagged `isPlaceholder` (a hint, not content). Applied at compose time, so the first typed
   *  glyph of an inline edit already renders in the slot's authored paint. Host-resolved (no CSS vars in canvas). */
  getPlaceholderPaint?: () => { color: string } | undefined;
  /** Album-wide frame outline in BOOK px — a frame with no stroke effect of its own adopts it at draw time.
   *  Width stays raw: the scene root carries it unscaled, exactly as print does. Absent for hosts with no album. */
  getFrameStroke?: () => SceneFrameStroke | undefined;
  /** Colour of a 'forbid' drop hover, resolved opaque from the host's token — absent for hosts that never forbid. */
  getDropForbidColor?: () => string;
  /** A full render() just composited its content onto the overlay canvas. Camera/zone/chrome repaints don't fire it.
   *  Carries the content stamp snapshotted when THAT render started, so a host waiting for the frame of its latest
   *  commit can tell it from the frame of a render that was already in flight when the commit landed. */
  onContentPainted?: (contentStamp: unknown) => void;
  /** Identity of the content the next render will draw — snapshotted at render start and echoed by onContentPainted. */
  getContentStamp?: () => unknown;
  /** Spread identity: unchanged by an in-place rebuild, new on a spread switch. Absent = every render is the same spread. */
  getContentId?: () => unknown;
  /** Every progressive tier of one render() is composited — the spread is complete, no gray placeholders remain.
   *  A render superseded by a newer one never fires it. */
  onContentSettled?: () => void;
  /** A render rejected before any content reached the canvas — no frame will come for it. The host drops its
   *  «first frame pending» state so page chrome stays usable; the canvas placeholder keeps waiting for a settle. */
  onRenderFailed?: () => void;
  /** A settle's rendition upgrades are downloading/decoding — the host may mark those frames with a quiet
   *  «loading sharper» hint. `boxIds` = the refs of every settle in flight (union across overlapping settles);
   *  empty, with `active` false, when the last one finished, was superseded, or the controller died. */
  onRenditionUpgradesActive?: (active: boolean, boxIds: readonly string[]) => void;
  /** Overlay render-resolution cap (full print-px would freeze the UI). */
  maxWidth?: number;
  /** Per-surface snap tuning (threshold, rotation tolerance, cut-line/fraction stops, size-match).
   *  Omitted → the historical defaults every editor ships with today. */
  snapProfile?: SnapProfile;
}

// Placeholder bytes for refs whose texture already lives in the wasm renderer — never uploaded; the entry
// still carries the texture's real width/height, which crop and cover-fit read.
const RESIDENT_IMAGE_STUB = new Uint8Array(0);

// «Поле персоны, но владелец неизвестен» — правку на месте запирает; наружу уходит как `null`,
// владельца решает хост. Пустой uuid, а не строка-слово: настоящим personId такой быть не может.
const PERSON_TEXT_WITHOUT_OWNER = '00000000-0000-0000-0000-000000000000';

// Screen px around a text frame a double-click still opens it from, so small type stays reachable at any zoom.
const DBLCLICK_TEXT_SLOP_PX = 6;

// rAF alone lands BEFORE the frame's paint; the trailing macrotask runs after the frame commits to screen.
const afterNextCommittedFrame = (): Promise<void> =>
  new Promise(resolve => {
    if (typeof requestAnimationFrame !== 'function') return void setTimeout(resolve, 0);
    requestAnimationFrame(() => setTimeout(resolve, 0));
  });

const DEFAULT_MAX_WIDTH = 1600;
// Zoom-in sharpening ceiling: the buffer may grow past the baseline cap up to this width so zoomed-in pixels stay crisp.
const SHARP_MAX_WIDTH = 3200;
// Beyond 2 the buffer cost outweighs the visible gain (matches cameraDpr).
const SHARP_MAX_DPR = 2;
// Черновик на время жеста: растр сцены линеен по числу пикселей буфера, и на этой ширине кадр укладывается
// в ~20 мс против ~120 мс полного разворота — жест идёт за ручкой, чёткость возвращает resharpen на отпускании.
const DRAFT_MAX_WIDTH = 1000;
// Страховка на выход из жеста: чёткий кадр обычно приносит коммит хоста тем же тиком, и второй рендер той же
// сцены стоил бы столько же, сколько первый. Ждём коммит — а если его не было (Esc, клик мимо, то же
// значение), кадр ставим сами. Задержка короче, чем читается «залипание», и длиннее любого коммита.
const DRAFT_RELEASE_SHARPEN_MS = 120;
// A repaint drops the sharp tile; this is how long after the last one we wait before painting a fresh tile, so a
// burst (progressive image tiers, a zone toggle, a resize) costs one tile render instead of one per repaint.
const SHARP_TILE_REARM_MS = 80;
// Quiet period before parked rendition lifts run: long enough that a live slider drag (a render per tick) keeps
// pushing the deadline and never swaps mid-gesture, short enough that the upgrade lands right after release.
const RENDITION_UPGRADE_QUIET_MS = 400;
/**
 * Верхняя граница ожидания байтов ОДНОЙ волны. Ни у `decodeImage`, ни у fetch'а под ним таймаута нет, а
 * `abort()` кэша картинок не отменяет уже летящий запрос — молчащий сокет (лифт, captive portal, сервер,
 * замолчавший на теле ответа) держал бы цикл волн внутри `await` без конца: кольцо горит, хвост разворота
 * не запрашивается никогда. По истечении волна считается сбойной ровно как отказ декода — кадр остаётся на
 * своих пикселях, ступень переспрашивается на следующей остановке камеры (ac2), остальные волны идут дальше.
 */
export const RENDITION_DECODE_TIMEOUT_MS = 25_000;
// Frames per rendition wave after the lead one: small enough that each wave lands in its own repaint on a thin
// link, big enough that a full spread does not trickle in one photo at a time.
const RENDITION_UPGRADE_CHUNK = 2;
const CHROME_MAX_DIM = 8192;
const SPREAD_PAPER = '#ffffff';
// Native transformer chrome — sizes are SCREEN px (constant at any zoom), positions are book px × stage.scale.
const HANDLE_HIT_PX = 11;
const ROTATE_HANDLE_DIST_PX = 26;
const CHROME_COLOR = '#7177D6';
const HANDLE_FILL = '#ffffff';
const CORNER_R_PX = 4;
const SIDE_PILL_LONG_PX = 12;
const SIDE_PILL_SHORT_PX = 5;
// Inline crop chrome (screen px) — blue frame + rule-of-thirds, white square handles, dimmed ghost.
const CROP_BORDER_COLOR = '#4A90D9';
const CROP_GRID_COLOR = 'rgba(255,255,255,0.6)';
const CROP_HANDLE_FILL = '#ffffff';
const CROP_HANDLE_R_PX = 5;
const CROP_GHOST_ALPHA = 0.3;
// Paper veil over the OTHER photos while one is cropped — they step back without changing.
const CROP_DIM_ALPHA = 0.75;
const CROP_RESET_R_PX = 11;
const CROP_RESET_PAD_PX = 14;
// Decor trim handles (screen px): L-brackets on the corners, bars along the edges — the classic crop affordance.
const TRIM_CORNER_ARM_PX = 18;
// Bracket body width — a touch under the edge bar's short side so the corners read lighter than the side handles.
const TRIM_CORNER_W_PX = 4.5;
// Arms shrink on a small frame so opposite corners can't meet and read as a solid border.
const TRIM_CORNER_MAX_EDGE_FRACTION = 0.3;
const TRIM_EDGE_BAR_LONG_PX = 22;
const TRIM_EDGE_BAR_SHORT_PX = 5;
// ONE dashed language for every frame the canvas draws: a long dash and a clearly visible gap. The frame paths
// are already built in screen px, so the rhythm is the same at any zoom. Every dashed box frame below is this array.
const FRAME_DASH = [8, 5];
const TRIM_FRAME_DASH = FRAME_DASH;
// Decor trim: window can't shrink below this on either axis (book px), matching the resize gesture's own floor.
const TRIM_MIN_SIZE_PX = 10;
// Trim's live content redraw debounces like live text edit — a per-move WASM repaint is too costly.
// Text-on-path edit chrome (screen px) — legacy-parity colors: orange range brackets, blue anchors.
const PATH_RANGE_COLOR = '#f5a623';
const PATH_EDIT_COLOR = '#2196f3';
const PATH_CURVE_SAMPLES = 200;
const PATH_ANCHOR_R_PX = 4;
const PATH_HANDLE_R_PX = 3;
const PATH_BRACKET_LEN_PX = 14;
const SMALL_BOX_PX = 48; // below this min screen edge, draw only the hovered handle — else handles cover the content
// Members of a multi-selection get a dashed frame; the solid outer frame is the transform chrome (drawHandles).
const SELECTION_MEMBER_DASH = FRAME_DASH;
// Card-swap drop target: a dashed frame flush inside the hovered card, matching the legacy Konva drag-swap
// highlight (stroke #7177D6, width 3, dash [8,4]). Fades in so the target reads as "the card will land here".
const SWAP_TARGET_STROKE_WIDTH = 1.5;
const SWAP_TARGET_DASH = FRAME_DASH;
// The dash rides on a solid white line, so its gaps read white and the frame holds on any photo.
const SWAP_TARGET_DASH_COLOR = ACCENT_COLOR;
const SWAP_TARGET_BASE_COLOR = '#ffffff';
const SWAP_TARGET_FADE_MS = 150;
// Photo swap: the traded photo travels into the held frame, leaving its own frame tinted as the drop slot.
const SWAP_PREVIEW_MS = 190;
const SWAP_SLOT_FILL = accentAlpha(0.22);
// Only reached when the page colour cannot be sampled at all (no buffer / fully transparent pixel).
const SWAP_SLOT_EMPTY_FALLBACK = '#ffffff';
// The held photo's own copy flies under the cursor for the whole swap drag, so a swap inside the spread reads
// exactly like a drop out of the photo panel: same travelling picture, same tinted frame under it.
const SWAP_GHOST_MAX_PX = 132;
const SWAP_GHOST_STYLE =
  'position:fixed;left:0;top:0;pointer-events:none;opacity:0.9;border-radius:var(--r-chip);overflow:hidden;' +
  'box-shadow:var(--shadow-drag-ghost);will-change:transform;';
// Above every app layer — the ghost belongs to the cursor, not to the spread, and must not slip under the chrome.
const SWAP_GHOST_Z_INDEX = '2147483647';
// A whole-cell move in the designer (s10-5): the held cell floats see-through, and a dimmed copy keeps its start
// slot, so the slot never reads as emptied before the drop.
const CARD_LIFT_OPACITY = '0.7';
const CARD_ORIGIN_OPACITY = '0.35';
// Card cascade: a hovered same-size card steps into the slot the held card just left, so the arrangement the
// drop will commit is on screen before the release. Legacy Konva ran this tween at 0.15 s — matched here.
const CASCADE_SHIFT_MS = 150;
// "Show text frames" toggle: a dashed outline per text box. Drawn from the drawable's draw-transform (below),
// so a 90°/flipped text (rotation lives in params.transform, not rect.rotation) is framed where it renders.
const TEXT_FRAME_COLOR = 'rgba(105, 110, 197, 0.9)';
const TEXT_FRAME_WIDTH = 1;
const TEXT_FRAME_DASH = FRAME_DASH;
const QUOTE_FRAME_WIDTH = 1.5;
// Phone text marks: a dashed frame 3px off the text box, a pencil chip on its right centre line.
const TEXT_MARK_WIDTH = 1.5;
const TEXT_MARK_DASH = [4.5, 3];
const TEXT_MARK_OUTSET_PX = 3;
const TEXT_MARK_CHIP_R = 7;
// CSS `right:-18px` on a 14px chip: its centre sits 11px past the frame edge.
const TEXT_MARK_CHIP_OFFSET_PX = 11;
const TEXT_MARK_ICON_PX = 8;
// lucide `pencil`, 24-unit viewBox, stroked.
const LUCIDE_PENCIL_D =
  'M21.174 6.812a1 1 0 0 0-3.986-3.987L3.842 16.174a2 2 0 0 0-.5.83l-1.321 4.352' +
  'a.5.5 0 0 0 .623.622l4.353-1.32a2 2 0 0 0 .83-.497z M15 5l4 4';
// An invalid slot must read as an error at a glance, so it is solid and thicker than the informational text frame.
const INVALID_FRAME_WIDTH = 2;
// Empty-photo-frame mark: the plus is SCREEN px (constant at any zoom) but never outgrows a small frame.
// Kept small and thin — it is an invitation, not an alarm; the outline already carries the error weight.
const EMPTY_FRAME_PLUS_PX = 16;
const EMPTY_FRAME_PLUS_WIDTH = 2;
const EMPTY_FRAME_PLUS_MAX_EDGE_FRACTION = 0.4;
// The host hands over opaque token colours; the mark's translucency is this overlay's own design
// constant, applied via globalAlpha — the wash must read light, the plus soft, in either theme.
// Рамка приходит со своей заливкой (у пустого слота она голубовато-серая), и на ней слабый смыв
// красного просто гасил цвет в нейтральный — читателю, которого сюда привело «Исправить», казалось,
// что подсветки нет вовсе. Смыв держим на том уровне, где поле однозначно читается КРАСНОВАТЫМ,
// оставаясь прозрачным: собственный тон рамки под ним всё ещё виден.
const EMPTY_FRAME_FILL_ALPHA = 0.4;
const EMPTY_FRAME_PLUS_ALPHA = 0.6;

// The 4 ordered corners of a scene rect, with the box's affine transform (or rotation about the centre) applied.
// Most chrome sits OUTSIDE the content — the path is pushed out by half the stroke so the box stays uncovered.
// A caller passing 0 keeps the stroke centred on the edge instead, to paint over the content/chrome seam.
/** «…» around a vignette quote: the print adds them and they take the frame's capacity like the text (fitVignetteQuote). */
const QUOTE_MARKS = /^«|»$/g;
const QUOTE_MARKS_LENGTH = 2;

/**
 * Characters a typed vignette series quote runs past its frame's own capacity (R10), counted as the fill prints it —
 * the body inside «…» plus the marks. Absent for every other box: they keep the frame-edge clamp.
 */
function quoteOverCapacity(box: RenderTextBox): ((text: string) => number) | undefined {
  const capacity = box.quoteCapacity;
  if (capacity === undefined || capacity <= 0) return undefined;
  return text => Math.max(0, text.trim().replace(QUOTE_MARKS, '').trim().length + QUOTE_MARKS_LENGTH - capacity);
}

function outsetRect(r: SceneRect, d: number): SceneRect {
  return { ...r, x: r.x - d, y: r.y - d, width: r.width + d * 2, height: r.height + d * 2 };
}

function quadCorners(r: SceneRect): Array<{ x: number; y: number }> {
  const u = [
    { x: r.x, y: r.y },
    { x: r.x + r.width, y: r.y },
    { x: r.x + r.width, y: r.y + r.height },
    { x: r.x, y: r.y + r.height },
  ];
  if (r.transform && r.transform.length === 6) {
    const [a, b, c, d, e, f] = r.transform;
    return u.map(p => ({ x: a * p.x + c * p.y + e, y: b * p.x + d * p.y + f }));
  }

  if (r.rotation) {
    const c = { x: r.x + r.width / 2, y: r.y + r.height / 2 };
    return u.map(p => rotatePointAround(p, c, r.rotation ?? 0));
  }

  return u;
}

// A closed quad stroked as ONE path carries the dash phase around the corners, so every side starts and ends
// mid-dash and the corners collect stubs — the ragged dash-dot look. Each side is stroked on its own with the
// pair stretched to a whole number of periods, so a side begins and ends with a full dash and the corners close.
function strokeDashedQuad(
  ctx: CanvasRenderingContext2D,
  corners: ReadonlyArray<{ x: number; y: number }>,
  dash: readonly number[],
): void {
  if (dash.length < 2) {
    ctx.beginPath();
    corners.forEach((p, i) => (i === 0 ? ctx.moveTo(p.x, p.y) : ctx.lineTo(p.x, p.y)));
    ctx.closePath();
    ctx.stroke();
    return;
  }

  const [d, g] = dash;
  for (let i = 0; i < corners.length; i++) {
    const a = corners[i];
    const b = corners[(i + 1) % corners.length];
    const len = Math.hypot(b.x - a.x, b.y - a.y);
    if (!(len > 0)) continue;
    // n dashes + (n - 1) gaps span the side exactly; scaling the pair keeps the authored dash:gap rhythm.
    const n = Math.max(1, Math.round((len + g) / (d + g)));
    const k = len / (n * d + (n - 1) * g);
    ctx.setLineDash([d * k, g * k]);
    ctx.beginPath();
    ctx.moveTo(a.x, a.y);
    ctx.lineTo(b.x, b.y);
    ctx.stroke();
  }

  ctx.setLineDash(dash as number[]);
}

// A drawable's affine maps the box's OWN corners, so moving the rect alone carries the frame by the affine's
// linear part instead of by the requested delta — a quarter-turned text nudged right would preview downward
// until the canonical repaint. Shifting the affine's translation by the difference restores the exact travel.
function nudgedAffine(transform: number[] | undefined, dx: number, dy: number): number[] | undefined {
  if (!transform || transform.length !== 6) return transform;
  const [a, b, c, d, e, f] = transform;
  return [a, b, c, d, e + dx - (a * dx + c * dy), f + dy - (b * dx + d * dy)];
}

const MARQUEE_FILL = 'rgba(113, 119, 214, 0.12)';
const MARQUEE_STROKE = CHROME_COLOR;
const MARQUEE_DASH = FRAME_DASH;
// Stronger than the selection marquee so the drop-target tint stays legible over dark full-bleed photos.
const DROP_ZONE_FILL = 'rgba(113, 119, 214, 0.22)';
// Below this pointer travel (screen px) a press on empty space is a click (deselect), not a marquee.
const MARQUEE_THRESHOLD_PX = 3;
const SNAP_GUIDE_DASH = [6, 4];
const MOVE_SNAP_THRESHOLD_PX = 2; // screen px; converted to book px via stage.scale at gesture start
// A burst of arrow-key nudges within this window collapses into one model write (one undo entry).
const NUDGE_DEBOUNCE_MS = 250;
const LIVE_TEXT_PAINT_DEBOUNCE_MS = 30; // per-keystroke WASM repaint is too costly
const NUDGE_STEP_PX = 1;
const NUDGE_STEP_COARSE_PX = 10;
const NUDGE_DIRECTIONS: Record<string, Point> = {
  ArrowLeft: { x: -1, y: 0 },
  ArrowRight: { x: 1, y: 0 },
  ArrowUp: { x: 0, y: -1 },
  ArrowDown: { x: 0, y: 1 },
};
const OVERLAY_STOP_EVENTS = ['pointerdown', 'mousedown', 'touchstart'] as const;
// Photoshop skew is a side/middle-handle gesture: top/bottom shear horizontally, left/right shear vertically.
const SIDE_HANDLES: ReadonlySet<ResizeAnchor> = new Set(['top-center', 'bottom-center', 'middle-left', 'middle-right']);

// The anchor opposite each handle — the fixed pivot for a resize (its position is the CSS transform-origin of the lift).
const OPPOSITE_ANCHOR: Record<ResizeAnchor, ResizeAnchor> = {
  'top-left': 'bottom-right',
  'top-center': 'bottom-center',
  'top-right': 'bottom-left',
  'middle-left': 'middle-right',
  'middle-right': 'middle-left',
  'bottom-left': 'top-right',
  'bottom-center': 'top-center',
  'bottom-right': 'top-left',
};

function isEditableEventTarget(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false;
  return target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.isContentEditable;
}

// A dialog or a full-screen clip owns the keyboard: an arrow pressed inside it never moves the frame hidden below.
function isModalLayerTarget(target: EventTarget | null): boolean {
  return target instanceof Element && target.closest('[aria-modal="true"], .cdk-overlay-container') !== null;
}

function findRenderBox(boxes: readonly RenderBox[], id: string): RenderBox | undefined {
  for (const b of boxes) {
    if (b.id === id) return b;
    if (b.type === 'group') {
      const found = findRenderBox((b as RenderGroupBox).children, id);
      if (found) return found;
    }
  }

  return undefined;
}

// Same search, but down VISIBLE boxes only — the mirror of the engine's emit_box, which returns on
// `visible == Some(false)` before it recurses (engine/psd-render/core/src/adapter/mod.rs). A hidden
// group hides its children without marking them, so «is in the model» and «is drawn» differ for them.
function findVisibleRenderBox(boxes: readonly RenderBox[], id: string): RenderBox | undefined {
  for (const b of boxes) {
    if (b.visible === false) continue;
    if (b.id === id) return b;
    if (b.type === 'group') {
      const found = findVisibleRenderBox((b as RenderGroupBox).children, id);
      if (found) return found;
    }
  }

  return undefined;
}

// Mirrors the Rust smart_object_drawable's chosen_is_source (engine/psd-render/core/src/adapter/mod.rs):
// no baked pixelData + a resolved live PSD source renders unclipped (source_transform bypasses the
// content_rect clip in raster.rs draw_object_at) — trim stays off it until that Rust path clips too (phase-2).
function isLiveSourceSmartObject(box: RenderBox): boolean {
  return box.type === 'smartObject' && !box.pixelData && !!box.useRuntimeSO && !!box.sourceUrl;
}

/** Decor trim is offered for object/background always, and for a smartObject only when it is NOT a live-source render. */
function canTrimDecorBox(box: RenderBox | undefined): box is RenderObjectBox | RenderSmartObjectBox {
  return (
    !!box && (box.type === 'object' || box.type === 'background' || (box.type === 'smartObject' && !isLiveSourceSmartObject(box)))
  );
}

/** The TOP-LEVEL group owning `id` (the group itself or any descendant), or undefined. */
function findTopLevelGroup(boxes: readonly RenderBox[], id: string): RenderGroupBox | undefined {
  for (const b of boxes) {
    if (b.type !== 'group') continue;
    const g = b as RenderGroupBox;
    if (g.id === id || findRenderBox(g.children, id)) return g;
  }

  return undefined;
}

// A person cell of a vignette: a roster role seated on a person (the design host's typed root carries a role, no person).
function isVignetteCell(group: RenderGroupBox): boolean {
  return isRosterCellRole(group.peopleType) && !!group.personId;
}

// The lift/drag layer works on leaf ids (groups are flattened out of the wasm scene).
function collectLeafIds(boxes: readonly RenderBox[], out: string[] = []): string[] {
  for (const b of boxes) {
    if (b.type === 'group') collectLeafIds((b as RenderGroupBox).children, out);
    else out.push(b.id);
  }

  return out;
}

// A selection may carry group-container ids (whole-group select); the drag layer only moves leaves, so expand them.
export function expandSelectionToLeafIds(ids: readonly string[], boxes: readonly RenderBox[]): string[] {
  const out: string[] = [];
  const seen = new Set<string>();
  for (const id of ids) {
    const box = findRenderBox(boxes, id);
    const leafIds = box?.type === 'group' ? collectLeafIds((box as RenderGroupBox).children) : [id];
    for (const lid of leafIds) {
      if (!seen.has(lid)) {
        seen.add(lid);
        out.push(lid);
      }
    }
  }

  return out;
}

// A point (book px) is inside a selection when it falls within the union of the members' AABBs — lets a group
// be grabbed by the empty gaps between its members, Figma-style, not only by hitting a member.
export function pointInsideBounds(
  point: { x: number; y: number },
  aabbs: readonly { x: number; y: number; width: number; height: number }[],
): boolean {
  let minX = Infinity;
  let minY = Infinity;
  let maxX = -Infinity;
  let maxY = -Infinity;
  for (const b of aabbs) {
    if (b.x < minX) minX = b.x;
    if (b.y < minY) minY = b.y;
    if (b.x + b.width > maxX) maxX = b.x + b.width;
    if (b.y + b.height > maxY) maxY = b.y + b.height;
  }

  return Number.isFinite(minX) && point.x >= minX && point.x <= maxX && point.y >= minY && point.y <= maxY;
}

/** The outermost non-PSD group above `id`: a PSD group is transparent to the click (ADR 0043), its layer is picked. */
function findClickGroup(boxes: readonly RenderBox[], id: string): RenderGroupBox | undefined {
  for (const b of boxes) {
    if (b.type !== 'group') continue;
    const g = b as RenderGroupBox;
    if (!findRenderBox(g.children, id)) continue;
    return g.psdGroup ? findClickGroup(g.children, id) : g;
  }

  return undefined;
}

/** personId of the group owning `id` (the group itself or any descendant), or undefined. */
function findPersonGroupId(boxes: readonly RenderBox[], id: string): string | undefined {
  for (const b of boxes) {
    if (b.type !== 'group') continue;
    const g = b as RenderGroupBox;
    if (g.id === id || findRenderBox(g.children, id)) {
      if (g.personId) return g.personId;
      const nested = findPersonGroupId(g.children, id);
      if (nested) return nested;
    }
  }

  return undefined;
}

// Group children carry coords relative to the group's origin; gesture/frame math runs in absolute book px.
function collectGroupOffsets(boxes: readonly RenderBox[], ox = 0, oy = 0, out = new Map<string, Point>()): Map<string, Point> {
  for (const b of boxes) {
    if (b.type !== 'group') continue;
    const g = b as RenderGroupBox;
    const gx = ox + g.rect.x;
    const gy = oy + g.rect.y;
    for (const child of g.children) out.set(child.id, { x: gx, y: gy });
    collectGroupOffsets(g.children, gx, gy, out);
  }

  return out;
}

// tiny-skia from_row order: x' = t0·x + t2·y + t4, y' = t1·x + t3·y + t5.
function applyAffinePoint(t: readonly number[], p: Point): Point {
  return { x: t[0] * p.x + t[2] * p.y + t[4], y: t[1] * p.x + t[3] * p.y + t[5] };
}

function rotatePointAround(p: Point, c: Point, deg: number): Point {
  if (!deg) return p;
  const a = (deg * Math.PI) / 180;
  const cos = Math.cos(a);
  const sin = Math.sin(a);
  const dx = p.x - c.x;
  const dy = p.y - c.y;
  return { x: c.x + dx * cos - dy * sin, y: c.y + dx * sin + dy * cos };
}

/** Screen-px positions of the 8 resize anchors + the rotate handle (a possibly-skewed quad). */
interface HandleLayout {
  anchors: Record<ResizeAnchor, Point>;
  rotate: Point;
}

/**
 * An armed pointer press whose intent (marquee on empty/floor vs. move a box body) is resolved by an async
 * hit-test, then activated once it travels past the drag threshold; a release without travel falls through to click.
 */
interface PressState {
  parentTransformIds?: ReadonlySet<string>;
  mode: 'pending' | 'marquee' | 'move' | 'card-swap' | 'photo-swap';
  startBook: Point;
  current: Point;
  startClient: Point;
  active: boolean;
  moveIds: string[];
  startRects: Map<string, { rect: TransformRect; rotation: number }>;
  reselectId?: string;
  // Shift-drag axis constraint, latched during the move because commitMove runs without the event.
  axisLock?: 'x' | 'y';
  snap?: {
    startAABB: TransformRect;
    stops: SnapStops;
    /** Page stops alone — tiered mode merges them with the per-frame neighbourhood instead of using `stops`. */
    pageStops: SnapStops;
    tiered: boolean;
    /** 1/viewScale at gesture start: screen-px radii → book px. */
    radiusScale: number;
    /** Capture/release radii for the equal-gap snap, book px. */
    gapRadii: GapSnapRadii;
    /** Widest equalized gap the snap will offer per axis, book px (25% of a page in tiered mode). */
    gapMax: GapMaxByAxis;
    neighbors: SnapNeighbor[];
    /** Class of the box being dragged — tiered hosts only; it picks which neighbour classes may anchor it. */
    draggedKind?: SnapNeighborKind;
    /** Established distances elsewhere in the layout — legacy hosts only; the tiered magnet measures the
     *  direct neighbour and nothing beyond it. */
    channels?: GapChannelsByAxis;
    /** Sticky per-axis hold state, mutated frame to frame by applyMoveSnap. */
    hold: MoveSnapHold;
    gapHold: GapSnapHold;
    /** Which snap kind owns each axis right now — the incumbent of the tiered arbitration. */
    snapOwner: { V?: SnapAxisOwner; H?: SnapAxisOwner };
  };
  /** Last pointer position in client px — where a verdict re-read on the force key alone hangs the cursor hint. */
  lastClient?: Point;
  /** Swap drag (card or photo): the dragged box and the swap candidate currently under the pointer. */
  swapSourceId?: string;
  swapTargetId?: string;
  /** Card-swap only: the live cascade preview — which cards have stepped aside and where they sit now. */
  cascade?: CascadeState;
}

/** One card lifted onto the cascade layer. Once lifted it stays painted there for the rest of the gesture:
 *  its home slot on the backdrop may have been tinted as the vacant slot, and only this bitmap covers it. */
interface CascadeCard {
  /** The card's own model slot (book px) — where it returns when the chain unwinds past it. */
  home: Point;
  /** The slot it is travelling to (book px). Hit-testing reads THIS, not the tween position: a card that has
   *  handed over its slot must stop answering for it immediately, or the pointer chases it and re-triggers. */
  slot: Point;
  /** Tween start (book px) — the position it actually occupied when the last shift began, so an interrupted
   *  shift resumes from where it is on screen instead of snapping back. */
  from: Point;
  start: number;
  width: number;
  height: number;
  /** Pixels blitted off the backdrop before the card's home was tinted; null in jsdom. */
  bitmap: HTMLCanvasElement | null;
}

/** The cascade chain: legacy's shift-chain + vacant-slot bookkeeping, driven by the pointer's path. */
interface CascadeState {
  /** Displaced cards, in the order the pointer picked them up. Re-entering one unwinds the tail from it. */
  chain: string[];
  /** Every card ever displaced this gesture — including unwound ones, which stay on the cascade layer. */
  cards: Map<string, CascadeCard>;
  /** The empty slot the held card would land in (book px): the source's home, or the last chain member's. */
  vacant: Point;
  /** The held card's own slot, so an unwind to an empty chain restores the vacant slot to it. */
  sourceHome: Point;
  /** The accent placeholder currently ON the backdrop (book px). Owned, not fire-and-forget: it has to be
   *  painted away the moment the vacant slot moves, or every crossing inside one shift leaves a purple gap. */
  tinted?: { x: number; y: number; width: number; height: number };
  raf: number;
}

/** ⌘-drag skew of a single text box: a shear composed onto the box's affine, committed once on release. */

interface SkewGesture {
  kind: 'skew';
  handle: ResizeAnchor;
  id: string;
  axis: 'horizontal' | 'vertical';
  rect: TransformRect;
  baseMatrix: Affine;
  sceneBase: SceneRect;
  startPointer: Point;
  /** Alt/Option: shear about the centre (both edges move) instead of anchoring the opposite edge. */
  fromCenter: boolean;
  current: { matrix: Affine; shx: number; shy: number };
}

function layoutFromCorners(tl: Point, tr: Point, br: Point, bl: Point, center: Point): HandleLayout {
  const mid = (a: Point, b: Point): Point => ({ x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 });
  const tc = mid(tl, tr);
  const anchors: Record<ResizeAnchor, Point> = {
    'top-left': tl,
    'top-right': tr,
    'bottom-right': br,
    'bottom-left': bl,
    'top-center': tc,
    'bottom-center': mid(bl, br),
    'middle-left': mid(tl, bl),
    'middle-right': mid(tr, br),
  };
  const ex = tr.x - tl.x;
  const ey = tr.y - tl.y;
  const len = Math.hypot(ex, ey) || 1;
  let nx = ey / len;
  let ny = -ex / len;
  // Flip the normal if it points toward the box centre, so the rotate handle always sits outside the top edge.
  if ((tc.x + nx - center.x) ** 2 + (tc.y + ny - center.y) ** 2 < (tc.x - center.x) ** 2 + (tc.y - center.y) ** 2) {
    nx = -nx;
    ny = -ny;
  }

  return { anchors, rotate: { x: tc.x + nx * ROTATE_HANDLE_DIST_PX, y: tc.y + ny * ROTATE_HANDLE_DIST_PX } };
}

// Midpoint of the edge OPPOSITE the dragged side handle — the anchored edge of a Photoshop skew.
function skewPivotScene(handle: ResizeAnchor, tl: Point, tr: Point, br: Point, bl: Point): Point {
  const mid = (a: Point, b: Point): Point => ({ x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 });
  switch (handle) {
    case 'top-center':
      return mid(bl, br);
    case 'bottom-center':
      return mid(tl, tr);
    case 'middle-left':
      return mid(tr, br);
    default:
      return mid(tl, bl);
  }
}

function svgCursor(svg: string, hotX: number, hotY: number, fallback: string): string {
  return `url("data:image/svg+xml,${encodeURIComponent(svg)}") ${hotX} ${hotY}, ${fallback}`;
}

const ROTATE_SVG =
  `<svg xmlns="http://www.w3.org/2000/svg" width="26" height="26" viewBox="0 0 26 26">` +
  `<g fill="none" stroke-linecap="round" stroke-linejoin="round">` +
  `<path d="M6 15a8 8 0 1 0 1.6-6.4" stroke="#fff" stroke-width="4.5"/><path d="M4 4v6h6" stroke="#fff" stroke-width="4.5"/>` +
  `<path d="M6 15a8 8 0 1 0 1.6-6.4" stroke="#000" stroke-width="2"/><path d="M4 4v6h6" stroke="#000" stroke-width="2"/>` +
  `</g></svg>`;

const SKEW_H_SVG =
  `<svg xmlns="http://www.w3.org/2000/svg" width="28" height="28" viewBox="0 0 28 28">` +
  `<g fill="none" stroke-linejoin="round" stroke-linecap="round">` +
  `<path d="M9 8h12l-2 12H7z M3 14h22M7 10l-4 4 4 4M21 10l4 4-4 4" stroke="#fff" stroke-width="4"/>` +
  `<path d="M9 8h12l-2 12H7z M3 14h22M7 10l-4 4 4 4M21 10l4 4-4 4" stroke="#000" stroke-width="1.6"/>` +
  `</g></svg>`;

const SKEW_V_SVG =
  `<svg xmlns="http://www.w3.org/2000/svg" width="28" height="28" viewBox="0 0 28 28">` +
  `<g fill="none" stroke-linejoin="round" stroke-linecap="round">` +
  `<path d="M8 9v10l12 2V7z M14 3v22M10 7l4-4 4 4M10 21l4 4 4-4" stroke="#fff" stroke-width="4"/>` +
  `<path d="M8 9v10l12 2V7z M14 3v22M10 7l4-4 4 4M10 21l4 4 4-4" stroke="#000" stroke-width="1.6"/>` +
  `</g></svg>`;

const ROTATE_CURSOR = svgCursor(ROTATE_SVG, 13, 13, 'grab');
const SKEW_CURSOR_H = svgCursor(SKEW_H_SVG, 14, 14, 'ew-resize');
const SKEW_CURSOR_V = svgCursor(SKEW_V_SVG, 14, 14, 'ns-resize');

// Tool-placement crosshair: white outline + dark core so it reads on any spread colour. Hotspot at its centre.
const CROSS_SVG =
  `<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">` +
  `<path d="M12.99 10V4H10.01V10H4V13H10.01V19H12.99V13H19V10H12.99Z" fill="#fff"/>` +
  `<path d="M12 11V5H11V11H5V12H11V18H12V12H18V11H12Z" fill="#232020"/>` +
  `</svg>`;
const PLACE_CURSOR = svgCursor(CROSS_SVG, 12, 12, 'crosshair');

// Resize cursor for an anchor whose outward direction is `angleDeg` in screen px
// (y-down). 180°-periodic, snapped to the nearest 45° so it tracks a rotated/skewed box like Photoshop.
function resizeCursorFor(angleDeg: number): string {
  const idx = Math.round((((angleDeg % 180) + 180) % 180) / 45) % 4;
  return RESIZE_DIR_CURSORS[idx];
}

// Capped so a 3× retina host doesn't blow the crop-ghost buffer (memory + per-frame copy); 1 in jsdom.
function cameraDpr(): number {
  const dpr = typeof window !== 'undefined' ? window.devicePixelRatio || 1 : 1;
  return Math.min(2, Math.max(1, dpr));
}

/**
 * The rendition scale a camera host asks for a spread shown at fit — the fit zoom × dpr, exactly what
 * `renditionScale` computes once that fit camera is set. A host warming a spread it has not shown yet passes
 * the same box, host size and padding its camera fits with, so the warmed rung is the one the canvas asks.
 */
export function fitRenditionScale(
  box: { x: number; y: number; width: number; height: number },
  hostCssW: number,
  hostCssH: number,
  padding: number | { x: number; y: number },
): number {
  return fitCameraToBox(box, hostCssW, hostCssH, padding).zoom * cameraDpr();
}

function handleCursorFor(anchors: Record<ResizeAnchor, Point>, handle: ResizeAnchor): string {
  return resizeCursorFor(handleCursorAngle(handle, anchors));
}

function collectTextBoxes(boxes: readonly RenderBox[]): RenderTextBox[] {
  const out: RenderTextBox[] = [];
  const walk = (b: RenderBox): void => {
    if (b.type === 'text') out.push(b as RenderTextBox);
    else if (b.type === 'group') for (const c of (b as RenderGroupBox).children) walk(c);
  };

  for (const b of boxes) walk(b);
  return out;
}

// Matching a group's own id lifts its whole subtree (photobook drags a vignette group); otherwise recurse into leaves.
function filterBoxes(boxes: readonly RenderBox[], keep: (id: string) => boolean): RenderBox[] {
  const out: RenderBox[] = [];
  for (const b of boxes) {
    if (keep(b.id)) {
      out.push(b);
    } else if (b.type === 'group') {
      const children = filterBoxes((b as RenderGroupBox).children, keep);
      if (children.length) out.push({ ...(b as RenderGroupBox), children });
    }
  }

  return out;
}

// The scene MINUS the lifted box(es): the main overlay keeps background + siblings so the lifted box reveals real content.
function excludeBoxes(boxes: readonly RenderBox[], exclude: ReadonlySet<string>): RenderBox[] {
  const out: RenderBox[] = [];
  for (const b of boxes) {
    if (exclude.has(b.id)) continue;
    if (b.type === 'group') {
      out.push({ ...(b as RenderGroupBox), children: excludeBoxes((b as RenderGroupBox).children, exclude) });
    } else {
      out.push(b);
    }
  }

  return out;
}

function sameRenderRect(a: RenderRect, b: RenderRect): boolean {
  return a.x === b.x && a.y === b.y && a.width === b.width && a.height === b.height && (a.rotation ?? 0) === (b.rotation ?? 0);
}

/**
 * Какие боксы жест переставил: сравнение идёт по МОДЕЛИ (книжные px), а не по сцене — у снимка входа и у
 * текущего тика разный масштаб буфера, и в сценных координатах неподвижная рамка выглядела бы сдвинутой.
 * `undefined` — сцена перестала быть той же (бокс появился, исчез, сменил тип или поехала группа): по такому
 * снимку кадр уже не собрать.
 */
function movedBoxIds(before: readonly RenderBox[], after: readonly RenderBox[]): string[] | undefined {
  if (before.length !== after.length) return undefined;
  const moved: string[] = [];
  for (let i = 0; i < before.length; i++) {
    const a = before[i];
    const b = after[i];
    if (a.id !== b.id || a.type !== b.type) return undefined;
    if (!sameRenderRect(a.rect, b.rect)) {
      moved.push(b.id);
      continue;
    }

    // Группу жест не двигает, а её листья в снимке рамок не индексируются отдельно — любое движение внутри
    // неё осталось бы незамеченным и висело бы на экране старым кадром.
    if (a.type === 'group' && movedBoxIds((a as RenderGroupBox).children, (b as RenderGroupBox).children)?.length !== 0) {
      return undefined;
    }
  }

  return moved;
}

// Top-left of `live` in the local frame of `entry` (book px); a grown frame keeps the rotation and turns about its centre.
function frameOriginShift(entry: RenderRect, live: RenderRect): { dx: number; dy: number } {
  const theta = ((entry.rotation ?? 0) * Math.PI) / 180;
  const cos = Math.cos(theta);
  const sin = Math.sin(theta);
  const cx = live.x + live.width / 2 - (entry.x + entry.width / 2);
  const cy = live.y + live.height / 2 - (entry.y + entry.height / 2);
  return {
    dx: cx * cos + cy * sin + (entry.width - live.width) / 2,
    dy: -cx * sin + cy * cos + (entry.height - live.height) / 2,
  };
}

/** The caret grid of a frame laid out at `(dx, dy)` local px from the session's frame, seated onto that frame. */
export function shiftShapedCaret(layout: ShapedCaretLayout, dx: number, dy: number): ShapedCaretLayout {
  if (dx === 0 && dy === 0) return layout;
  const point = (p: { x: number; y: number }): { x: number; y: number } => ({ x: p.x + dx, y: p.y + dy });
  return {
    slotAt: index => {
      const slot = layout.slotAt(index);
      return slot && { ...slot, x: slot.x + dx, top: slot.top + dy };
    },
    indexAt: (x, y) => layout.indexAt(x - dx, y - dy),
    indexByLineStep: (index, delta, desiredX) =>
      layout.indexByLineStep(index, delta, desiredX === undefined ? undefined : desiredX - dx),
    lineEdge: (index, edge) => layout.lineEdge(index, edge),
    bandsFor: (from, to) =>
      layout.bandsFor(from, to).map(b => ({ ...b, x: b.x + dx, top: b.top + dy, outline: b.outline?.map(point) })),
    hitArea: layout.hitArea?.map(poly => poly.map(point)),
  };
}

// The scene with one text box's content replaced by the in-progress inline-edit text (live preview, caret-only overlay).
function applyTextOverride(
  boxes: readonly RenderBox[],
  id: string,
  text: string,
  rect?: RenderRect,
  runs?: RenderTextBox['runs'],
  paragraphRuns?: RenderTextBox['paragraphRuns'],
): RenderBox[] {
  return boxes.map(b => {
    if (b.id === id && b.type === 'text') {
      const t = b as RenderTextBox;
      // An empty buffer keeps showing the hint; the first typed glyph is real content and drops the hint paint.
      if (text === '' && t.isPlaceholder) return t;
      // The grown frame rides along so the engine lays the live text out — and hit-tests it — inside the frame the
      // commit is about to store, instead of wrapping it into the frame the box was entered with.
      // `runs` — прогоны, инкрементально досчитанные сессией: буквы держат свои цвета, пока модель ждёт коммита.
      return {
        ...t,
        text,
        isPlaceholder: false,
        runs: runs && runs.length > 0 ? runs : undefined,
        // Абзацные прогоны сессии — так же: абзацы держат свои атрибуты, пока модель ждёт коммита.
        ...(paragraphRuns !== undefined ? { paragraphRuns: paragraphRuns.length > 0 ? paragraphRuns : undefined } : {}),
        ...(rect ? { rect } : {}),
      };
    }

    if (b.type === 'group') {
      return {
        ...(b as RenderGroupBox),
        children: applyTextOverride((b as RenderGroupBox).children, id, text, rect, runs, paragraphRuns),
      };
    }

    return b;
  });
}

/**
 * A hint must read as a hint, not as content: it keeps the slot's geometry and typography so the layout is honest,
 * but paints in the host's muted token with the slot's decorative treatment dropped — a hint wearing the design's
 * gradient, stroke or glow is indistinguishable from a real value.
 */
function applyPlaceholderPaint(boxes: readonly RenderBox[], color: string): RenderBox[] {
  return boxes.map(b => {
    if (b.type === 'group') {
      return { ...(b as RenderGroupBox), children: applyPlaceholderPaint((b as RenderGroupBox).children, color) };
    }

    if (b.type !== 'text' || !(b as RenderTextBox).isPlaceholder) return b;
    const t = b as RenderTextBox;
    return {
      ...t,
      color,
      fillFlag: true,
      fillGradient: undefined,
      strokeFlag: false,
      fauxBold: false,
      effects: undefined,
      // A style run whose colour differs from the box outranks it in both glyph backends, so an authored run
      // would repaint the hint as content. Only the colour is levelled — the rest of the run still sets metrics.
      runs: t.runs?.map(run => ({ ...run, color })),
    };
  });
}

/**
 * Снимок входа в жест хоста, из которого собирается КАЖДЫЙ его тик, минуя движок: растр разворота, каким
 * его застал захват, плюс два готовых слоя — сцена под подвижными рамками и то, что стоит над ними. Тик
 * тогда стоит один блит подложки, по одному drawImage на рамку и один блит накладки.
 */
interface DraftGestureSnapshot {
  /** Растр разворота на входе — источник пикселей рамок; их прямоугольники в НЁМ лежат в `rects`. */
  base: HTMLCanvasElement;
  /** Модель на входе: по ней тик считает, что именно жест переставил. */
  boxes: readonly RenderBox[];
  /** Сценные прямоугольники входа, в пикселях `base`. */
  rects: ReadonlyMap<string, SceneRect>;
  viewport: { width: number; height: number };
  /** Сцена ДО первой подвижной рамки, залитая бумагой — непрозрачная основа кадра. */
  under?: HTMLCanvasElement;
  /** Сцена ПОСЛЕ последней подвижной рамки, прозрачная; пусто — накладки нет. */
  over?: HTMLCanvasElement;
  /** Сплошной ряд кадров, который ведёт жест; пока не разобран — слои ещё не заказаны. */
  movers?: readonly string[];
  /** Слои уехали в рендер: второй тик не заказывает их снова. */
  arming: boolean;
  /** Кадр из снимка не собрать (рамки вперемешку с другим, поворот, отказ рендера) — до конца жеста рисуем движком. */
  blocked: boolean;
}

/** A baked tile lifted with its owner: the owner's and the tile's own group offsets to spread space. */
interface DragTile {
  owner: string;
  dx: number;
  dy: number;
  tileDx: number;
  tileDy: number;
}

interface SpreadRegion {
  ox: number;
  oy: number;
  w: number;
  h: number;
}

/** One decoded rendition lift, waiting for its wave's repaint. */
interface DecodedRendition {
  ref: string;
  image: RustImage;
}

// Lets repaint/lift/backdrop re-render from the last resolve without re-fetching fonts or re-decoding images.
interface OverlayCache {
  boxes: RenderBox[];
  viewport: { width: number; height: number };
  renderScale: number;
  fonts: Record<string, Uint8Array>;
  mappedFace?: (box: RenderTextBox) => MappedFace | undefined;
  fontVariationOf?: (box: RenderTextBox) => FontVariation | undefined;
  fontRefOf?: FontRefOf;
  caretMetrics?: (box: RenderTextBox) => { capHeightPx: number; ascenderDeltaPx: number } | undefined;
  caretLayoutOf?: (box: RenderTextBox, force?: boolean) => AreaCaretLayout | undefined;
  pathCaretLayoutOf?: (box: RenderTextBox) => PathCaretLayout | undefined;
  engineCaretOf?: (box: RenderTextBox) => EngineCaretGeometry | undefined;
  /** True when the box's wrapped text overflows its rect height. Re-runs glyph shaping — resolve lazily, selected ids only. */
  overflowOf?: (box: RenderTextBox) => boolean;
  /** Frame spill for a candidate string; the inline-edit clamp consults it per keystroke on the edited box only. */
  frameFitOf?: (box: RenderTextBox, text: string) => TextFrameFit;
  zones: ZonePrintSizes | undefined;
  images: Record<string, RustImage>;
}

/** Grab-time picture for a region-only settle: the lifted ids, their pre-gesture region, a fingerprint of the rest. */
interface DragSettleBase {
  ids: readonly string[];
  region: { ox: number; oy: number; w: number; h: number };
  rest: string;
}

// Anti-aliasing slack around the settle's dirty region, in spread px.
const SETTLE_MARGIN_PX = 2;

// Whole device pixels only: a canvas placed or sized at a fraction of a device pixel is resampled by the
// compositor, so its bitmap no longer maps 1:1 onto the screen — a crisp outline the engine painted as an equal
// band on every side comes out one device pixel wider on one side than on the other, whatever the engine did.
function snapToDevicePx(css: number): number {
  const dpr = typeof window !== 'undefined' ? window.devicePixelRatio || 1 : 1;
  return Math.round(css * dpr) / dpr;
}

// The spread buffer FLOORS its pixel size (spreadBufferSize) while the CSS box rounds it: at a zoom where the buffer
// is drawn 1:1 the two can differ by one device pixel, and that one pixel stretches the whole bitmap across the
// box — a crisp band comes out one row thinner on one side than on the other. Within a pixel, the box takes the
// buffer's exact size; a buffer capped well below the box is being scaled on purpose and keeps the box.
function fitBoxToBitmap(canvas: HTMLCanvasElement, box: LayerBox): LayerBox {
  const dpr = typeof window !== 'undefined' ? window.devicePixelRatio || 1 : 1;
  const fit = (css: number, px: number): number => (px > 0 && Math.abs(css * dpr - px) <= 1 ? px / dpr : css);
  return { ...box, width: fit(box.width, canvas.width), height: fit(box.height, canvas.height) };
}

function setLayerBox(element: HTMLElement, box: LayerBox): void {
  element.style.left = `${snapToDevicePx(box.left)}px`;
  element.style.top = `${snapToDevicePx(box.top)}px`;
  element.style.width = `${snapToDevicePx(box.width)}px`;
  element.style.height = `${snapToDevicePx(box.height)}px`;
}

/**
 * Drives the `?engine=rust` Rust overlay over a Konva editor: renders the spread/template via the
 * Rust engine into an overlay canvas (content + print technical zones) plus a
 * standalone WASM hit-test interaction layer (hover + click-select) plus an editor-chrome line
 * canvas (pixel grid / central guides / user guides). Editor-specific inputs come in via
 * `RustOverlayConfig` providers, so the photobook and design editors share one implementation (SSoT).
 */
export class RustOverlayController {
  private overlayCanvas?: HTMLCanvasElement;
  private chromeCanvas?: HTMLCanvasElement;
  private hoverCanvas?: HTMLCanvasElement;
  private selectionCanvas?: HTMLCanvasElement;
  private dragCanvas?: HTMLCanvasElement;
  /** Dimmed copy of the card-swap lift, parked on its start slot for the gesture (showCardLiftOrigin). */
  private liftOriginCanvas?: HTMLCanvasElement;
  /** Boxes currently lifted onto the drag canvas. The backdrop must keep leaving them out for the WHOLE gesture. */
  private dragLiftIds?: ReadonlySet<string>;
  /**
   * Рамки, обрезанные слоем, который сейчас двигают или тянут (`maskBaseBoxId` → этот слой). Сами они на месте,
   * но видимая часть у них меняется с каждым кадром жеста — их поднимают на слой жеста ПАССАЖИРАМИ и рисуют заново
   * по живому положению маски, иначе в основном полотне они висят обрезанными по СТАРОМУ месту до самого отпускания.
   */
  private dragPassengers: ReadonlySet<string> = new Set();
  /**
   * Перенос идёт растром, а не сдвигом компоновщика: у поднятых боксов есть пассажиры, ИЛИ сам поднятый бокс
   * обрезан стоящим слоем-маской. Обрезанная рамка — прямоугольник, видимая часть которого зависит от того, где он
   * лежит под маской; сдвинутая как картинка, она тащила бы за собой облако, вырезанное в точке нажатия.
   */
  private dragRastersLive = false;
  /** Прямоугольники двигаемых боксов на момент нажатия — растровый перенос считает их из `начало + сдвиг`. */
  private dragStartRects?: ReadonlyMap<string, RenderRect>;
  /** Baked effect tiles of the pressed boxes → owner id + owner's group offset; they ride the lift as passengers. */
  private dragTiles: ReadonlyMap<string, DragTile> = new Map();
  private dragMoveRaf?: number;
  /** Движение пришло, пока предыдущий кадр подъёма ещё рисовался — дорисовать после него. */
  private dragMoveDirty = false;
  // Cards that stepped aside during a card-swap drag. Its own layer because the two neighbouring ones can't carry
  // them: the drag canvas is translated wholesale by one CSS transform (they must NOT follow the cursor), and every
  // chrome layer paints ABOVE it (they must stay UNDER the held card). Mirrors the overlay's geometry, so it shares
  // the backdrop's buffer coordinates.
  private cascadeCanvas?: HTMLCanvasElement;
  // Невидимый буфер кадра — свой на каждый видимый слой: движок и блиты пишут ТОЛЬКО сюда, на экран готовый
  // кадр уезжает одним `presentFrame`. Один общий буфер пересоздавался бы на каждой паре разворот/плитка —
  // это разные размеры. Никогда не в DOM — см. `ensureFrameBuffer`.
  private readonly frameBuffers = new WeakMap<HTMLCanvasElement, HTMLCanvasElement>();
  // Off-DOM scratch buffers for the non-camera grab patch: the WASM raster lands here (bbox-sized), then gets
  // blitted onto the spread-sized dragCanvas/overlayCanvas at the right offset — never shown directly.
  private liftPatchCanvas?: HTMLCanvasElement;
  private backdropPatchCanvas?: HTMLCanvasElement;
  private handleCanvas?: HTMLCanvasElement;
  // trimPage: the paper plate (shadow + rounded corners) follows the surface on show — the trimmed page,
  // or the whole sheet while the technical zones are on.
  private pagePlateEl?: HTMLDivElement;
  // The canvas currently carrying pointer input. Konva mode = the spread overlay; camera mode = the full-host handle layer.
  private inputCanvas?: HTMLCanvasElement;
  // Crisp viewport tile (render_camera) shown over the spread buffer past the zoom cap; hidden during gestures/content renders.
  private sharpTileCanvas?: HTMLCanvasElement;
  // The camera the visible tile was rendered for: a live pan CSS-translates the tile by the delta against it.
  private sharpTileCamera?: Camera;
  // Одноразовый: следующий (и только следующий) paint() не гасит плитку — см. applyRenditionWave.
  private keepTileForUpgrade = false;
  // Active (selected/hovered) boxes drawn full-size above the clipped main overlay so their off-spread part shows.
  private overflowCanvas?: HTMLCanvasElement;
  private gesture?:
    | {
        kind: 'single';
        handle: HandleId;
        id: string;
        startRect: TransformRect;
        startRotation: number;
        center: Point;
        startPointer: Point;
        /** The box's draw affine (scene transform, book-space linear part) — the live frame re-anchors it per frame
         *  so the gesture frame matches the idle sceneHandleLayout quad (no jump at grab/release). */
        frameAffine?: Affine;
        /** Decor contentRect snapshotted at grab — scaledContentRect scales from this fixed start, never the live
         *  cache (previewResize mutates that cache every tick, which would compound the scale). Undefined when
         *  untrimmed/not decor. */
        startContentRect?: TrimRect;
        /** Photo placement snapshotted at grab — the same anti-compounding freeze as startContentRect
         *  (previewResize mutates the cached imageRect every tick). Undefined → the photo fills the frame. */
        startImageRect?: CropImageRect;
        /** Whether THIS tick holds the frame's aspect for a filled photo frame — the flag the commit reads
         *  to scale the crop instead of re-covering it. Mutated per tick, like `current`. */
        imageLock?: boolean;
        /** FKS-11514: corner drag of a text box scales frame and font together. Font metrics frozen at grab —
         *  the same anti-compounding freeze as startContentRect. Undefined → ordinary frame-only resize. */
        textScale?: TextScaleSnapshot;
        current: { rect: TransformRect; rotation: number };
      }
    | {
        kind: 'group';
        handle: HandleId;
        startGroup: TransformRect;
        groupCenter: Point;
        startPointer: Point;
        members: GroupMember[];
        /** Per-member decor contentRect snapshot at grab, keyed by member id — same anti-compounding freeze
         *  as the single case. */
        startContentRects: Map<string, TrimRect>;
        /** Grab-time fonts of the captions whose font follows the gesture; absent → every font stays. */
        captionScales?: Map<string, TextScaleSnapshot>;
        current: { frameRect: TransformRect; frameRotation: number; updates: GroupMemberUpdate[] };
      }
    | SkewGesture;

  private resizePreviewRaf?: number;
  /** The host's live layout armed for the current resize: the lifted candidates and their grab-time fonts and photo
   *  placements — each frame scales from these, never from the mutated cache (the anti-compounding rule). */
  private reflowLive?: {
    provider: ResizeReflowProvider;
    ids: readonly string[];
    texts: Map<string, TextScaleSnapshot>;
    images: Map<string, { width: number; image: CropImageRect }>;
  };

  private trimPreviewRaf?: number;
  // Guards against stacking async single-box lift renders while a live resize preview frame is still in flight.
  private previewInFlight = false;
  /** Vector params and frame at the first preview tick of a gesture, keyed by the gesture: the stroke takes the smaller
   *  axis, which does not compose tick by tick, so each tick scales the start once — as the commit does. */
  private previewVectorStarts = new WeakMap<object, Map<string, { rect: RenderRect; vector: VectorBoxParams }>>();

  private press?: PressState;
  private nudge?: {
    parentTransformIds?: ReadonlySet<string>;
    start: Map<string, { rect: TransformRect; rotation: number; transform?: number[] }>;
    dx: number;
    dy: number;
  };

  private nudgeTimer?: ReturnType<typeof setTimeout>;
  /** The host hid the canvas under another page: window key/wheel listeners outlive `inert`, so they check this. */
  private inputSuspended = false;
  private gestureMoved = false;
  private gestureActive = false;
  private suppressClick = false;
  private overlayBook = { w: 0, h: 0 };
  // Content buffer region: scene-px offset+dims for render_region, and book-px origin+size for CSS positioning.
  // Equals the spread (offset 0) outside camera mode; in camera mode it grows to enclose off-spread drawables.
  private contentRegionScene = { ox: 0, oy: 0, w: 0, h: 0 };
  // Page size in the SAME scale as contentRegionScene and the chrome buffers — resharpen() drops a new renderScale
  // into the live cache before the buffers rebuild, so a clip re-derived from the cache would mismatch what is drawn.
  private spreadSceneSize = { width: 0, height: 0 };
  private overlayRegionBook = { x: 0, y: 0, w: 0, h: 0 };
  // Whether a box in the active set really crosses the design edge. Measured inside regionForIds against the BARE
  // spread, because the region it returns always carries the chrome bleed — a spill smaller than that bleed is
  // invisible in the region rect and would read as no-spill.
  private overlayRegionSpills = false;
  // The region the drag canvas's PIXELS were rendered in. It outlives a gesture (the layer stands in for the overlay
  // until the settle render lands), so placing it from the live region would slide it sideways the moment that shrinks.
  private dragRegionBook?: { x: number; y: number; w: number; h: number };
  private generation = 0;
  private paintedContentStamp: unknown;
  private paintedContentId: unknown;
  // Set by the first content paint; a failed render may only lift the veil over a frame that exists.
  private contentOnScreen = false;
  // Spread whose frame is on screen — only content paints of renderNow() set it, cache repaints leave it.
  private screenContentId: unknown;
  // Sharp-tile staleness, kept separate from `generation` so a sharpen never supersedes an in-flight content render.
  private sharpTileToken = 0;
  private sharpRearmTimer?: ReturnType<typeof setTimeout>;
  private renderTimer?: ReturnType<typeof setTimeout>;
  private paintTimer?: ReturnType<typeof setTimeout>;
  private livePaintFrame?: number;
  private renderInFlight = false;
  private pendingRender = false;
  /** A zoom settled while a render owned the plan — re-plan the lifts as soon as that render lands. */
  private replanZoomAfterRender = false;
  private draftResolution = false;
  /** Смена разворота: первый кадр растрируется на черновом капе, чёткий приносит отложенный рендер. */
  private switchDraft = false;
  private draftSnapshot?: DraftGestureSnapshot;
  private dragSettlePending = false;
  // Taken at the grab, handed to the settle at the release: the settle rasters only the dirty region when it holds.
  private dragLiftBase?: DragSettleBase;
  private dragSettleBase?: DragSettleBase;
  // A drag lift render captures this at start; a settle/re-grab bumps it so the late render bails (stale origin).
  private dragRenderToken = 0;
  // Resize magnet frame, captured at gesture start: edge stops + the neighbourhood whose dimensions may be
  // matched, plus the per-axis hold that carries the incumbent between frames.
  private resizeSnap?: {
    stops: SnapStops;
    pageStops: SnapStops;
    tiered: boolean;
    radiusScale: number;
    neighbors: SnapNeighbor[];
    draggedKind?: SnapNeighborKind;
    hold: ResizeSnapHold;
  };

  private snapGuides: SnapGuide[] = [];
  private sizeGuides: SizeGuide[] = [];
  // Resolved theme tokens for the duration of one gesture — getComputedStyle is expensive to call on every raw
  // mousemove; cleared on every gesture-state transition (see setGestureActive) since the theme may change between gestures.
  private readonly gestureTokenCache = new Map<string, string>();
  // True only while dragging the current selection — then the selection frame trails the content; otherwise the
  // selection is dropped for the drag and a hover frame trails the grabbed box instead.
  private selectedIds: string[] = [];
  private parentTransformIds: ReadonlySet<string> = new Set<string>();
  private hoverId?: string;
  private hoverRaf = 0;
  private dropHovering = false;
  private dropHoverId?: string;
  private dropHoverVerdict?: DropHoverVerdict;
  private dropHoverRaf = 0;
  private dropZone: 'left' | 'right' | 'full' | null = null;
  private dropZoneStale = false;
  /** Bumped by every drop-hover paint — an in-flight blit checks it before it lands (see drawDropHover). */
  private dropHoverGen = 0;
  private swapTargetFadeRaf = 0;
  private hoverPaint: { id: string; rects: SceneRect[]; alpha: number } | null = null;
  private hoverFadeRaf = 0;
  /** The frame currently painted as an emptied slot on the backdrop, so it can be blitted back. */
  private previewedSlotId?: string;
  private slotCapture?: HTMLCanvasElement;
  // The page colour behind the lifted photo, sampled ONCE (from the source frame, at the only moment the backdrop
  // really shows it empty) and reused for the rest of the gesture: a later full paint() re-renders the lifted photo
  // into that frame, so re-sampling then would tint the slot with the photo instead of the page.
  private swapSlotFill?: string;
  /** The travelling copy of the held photo, live only while a photo-swap drag is on. */
  private swapGhostEl?: HTMLDivElement;
  private swapPreview: {
    bitmap: HTMLCanvasElement | null;
    from: { x: number; y: number; width: number; height: number };
    to: { x: number; y: number; width: number; height: number };
    start: number;
    raf: number;
  } | null = null;

  private lastDropPt?: { clientX: number; clientY: number; verdictOf: (id: string) => DropHoverVerdict | null };
  private lastCursorPtr?: Point;
  private hoveredHandle?: HandleId;
  private sceneJson?: string;
  private drawableRects = new Map<string, SceneRect>();
  // Boxes (by id) whose font failed to resolve this render — drives the warning badges; persists across cache repaints.
  private fontErrors = new Map<string, FontErrorKind>();
  private fontErrorsEmitted = false;
  private groupOffsets = new Map<string, Point>();
  private cache?: OverlayCache;

  private destroyed = false;
  /** The box being inline-edited — the engine keeps rendering it (the textarea is caret-only). */
  private editingBoxId?: string;
  /** In-progress text for the inline-edited box, re-rendered by the engine on each keystroke. */
  private liveEditText?: string;
  /** Frame the auto-width block has grown to for the text being typed; committed with it, dropped when the session ends. */
  private liveEditRect?: RenderRect;
  /** The edited box's residual glyph scale, measured once on entry — re-deriving it per keystroke costs two layouts. */
  private liveEditScaleX?: number;
  /** Advance пробела бокса, замеренный один раз на сеанс — хвостовые пробелы не платят раскладкой за нажатие. */
  private liveEditSpaceAdvance?: number;
  /** Ширина «голой» последней строки на серию хвостовых пробелов: меряется сырым fit, мемо каретки не трогает. */
  private liveEditBareWidth?: { text: string; width: number };
  /** One zero-width point layout per (box, text) — the caret ratio and the follow frame share it (~15 ms each on a long line). */
  private livePointFit?: { id: string; text: string; rectHeight: number; fit: TextFrameFit };
  /** The edited box's scene rect FROZEN at session entry. Mid-session drawableRects tracks the auto-width growth
   *  (renderEditLift keeps it current for the chrome), so a camera re-seat computed from the live rect would scale
   *  the caret surface by grown/entry — the re-seat must project the ENTRY frame through the current camera. */
  private editEntrySceneRect?: SceneRect;
  /** Text of the LAST commit until the settle render folds it into the cache — the click that committed re-enters
   *  immediately (click-to-edit), and a session seeded from the stale cache would resurrect the pre-commit string. */
  private pendingCommittedText?: {
    id: string;
    text: string;
    styleRuns?: RenderTextBox['runs'];
    paragraphRuns?: RenderTextBox['paragraphRuns'];
    rect?: RenderRect;
  };

  /** Прогоны живой сессии: пересчитываются ИНКРЕМЕНТАЛЬНО на каждый ввод (пред→след — одна непрерывная
   *  правка, как операция в Quill/ProseMirror). Диффовать модель→живой текст нельзя: две точки правок в
   *  одной сессии схлопываются одиночным диффом в одну «вставку» и границы цветов расползаются. */
  private liveEditRuns?: RenderTextBox['runs'];
  /** Абзацные прогоны живой сессии (textLayout 7) — ведутся тем же инкрементальным законом, что и {@link liveEditRuns}. */
  private liveEditParagraphRuns?: RenderTextBox['paragraphRuns'];
  private liveEditPrevText?: string;
  /** The two frame ids a photo-swap just traded, held for exactly the render that settles them (renderNow reads
   *  and clears it in the same pass) — planImageTiers' holdPixelRefs, so only THAT render skips their thumbHash
   *  blur. A cancelled/failed gesture between the commit and the render just leaves this unset: the render then
   *  falls back to painting the blur like any other replacement, the safe default. */
  private pendingSwapSettleRefs?: ReadonlySet<string>;
  /** Rendition lifts parked until the UI goes quiet (same photo, new resolution). Replaced WHOLE by every
   *  render — batch identity doubles as the staleness stamp for the async settle. */
  private pendingRenditionUpgrades?: TierEntry[];
  private renditionUpgradeTimer?: ReturnType<typeof setTimeout>;
  /** Refs of every settle whose decodes are in flight right now, one entry per settle. A rescheduled settle can
   *  start while an older one still awaits its waves, so the host is told the UNION — the last one out empties it. */
  /** Ключ — батч settle'а, значение — его ещё не закрытые ref'ы: устаревший батч чистится СРАЗУ, а не когда
   *  цикл волн доберётся до своего `finally` (он мог застрять внутри `await` на молчащем декоде). */
  private readonly renditionSettleRefs = new Map<readonly TierEntry[], string[]>();
  private textEdit?: OverlayTextEdit;
  // Inline-edit lift (drag-layer pattern): while typing, the box renders ALONE on editCanvas over a
  // backdrop overlay, so a keystroke costs one box + a bbox-sized region — not a full-spread raster.
  private editCanvas?: HTMLCanvasElement;
  private editRegionBook?: { x: number; y: number; w: number; h: number };
  private editLiftReady = false;
  private editSettlePending = false;
  private editTickRaf?: number;
  private editTickToken = 0;
  private editSessionToken = 0;
  private editTickInFlight = false;
  private editTickPending = false;

  // Style lift (same drag-layer pattern): while a shadow/stroke slider drags, the affected boxes render
  // ALONE on styleCanvas over a backdrop overlay — a tick costs a bbox-sized region, not a full spread.
  private styleCanvas?: HTMLCanvasElement;
  private styleRegionBook?: { x: number; y: number; w: number; h: number };
  private styleLiftIds: string[] = [];
  // Compound-clip masks (FKS-12999): the source key each clipper's current mask was drawn from, and a single flight.
  private readonly clipMaskKeys = new Map<string, string>();
  // The settled render a clipper was last baked on: a host that drops the result gets one more bake per settle,
  // never a loop inside the same frame.
  private readonly clipBakeTicks = new Map<string, number>();
  private clipSettleTick = 0;
  private clipRebakeRunning = false;
  private clipRebakeQueued = false;

  /**
   * A clipper is one bond with its base (FKS-12999): whatever ids a gesture or the host selects, the clippers of
   * those boxes ride along — and group ids still expand to leaves.
   */
  private withClipCompanions(ids: readonly string[]): string[] {
    const boxes = this.cache?.boxes ?? [];
    return expandSelectionToLeafIds([...ids, ...clipCompanionRenderIds(boxes, ids)], boxes);
  }

  private explicitParentLeaves(ids: readonly string[]): ReadonlySet<string> {
    const boxes = this.cache?.boxes ?? this.cfg.getRenderBoxes();
    const parents = ids.filter(id => findRenderBox(boxes, id)?.type === 'group');
    return new Set(expandSelectionToLeafIds(parents, boxes));
  }

  /** The base an OBJECT box is clipped to, when it carries a bake recipe. */
  private clipBaseOf(id: string): string | undefined {
    const box = findRenderBox(this.cache?.boxes ?? [], id);
    if (!box || box.type !== 'object') return undefined;

    return (box as RenderObjectBox).compoundClipBakeRecipe?.leafBoxIds[0];
  }

  /**
   * On the canvas a clipper is never a target of its own (FKS-12999 U4): a hit on it is a hit on its base — click,
   * shift-click, right click, press-drag and marquee alike. The one exception is the clipper the host picked alone
   * (layers panel ⌥-click): while it is the whole selection, gestures keep addressing it.
   */
  private clipTargetOf(id: string): string {
    const alone = this.selectedIds.length === 1 ? this.selectedIds[0] : undefined;
    if (alone !== undefined && (alone === id || this.liveClipBaseHolds(alone, id))) return alone;
    return this.clipBaseOf(id) ?? id;
  }

  // The engine never hits a decor clipped live to a group (hit_test.rs): it answers the group's leaf beneath instead.
  private liveClipBaseHolds(clipperId: string, hitId: string): boolean {
    const boxes = this.cache?.boxes ?? [];
    const clipper = findRenderBox(boxes, clipperId);
    const baseId = clipper?.type === 'object' ? (clipper as RenderObjectBox).maskBaseBoxId : undefined;
    const base = baseId ? findRenderBox(boxes, baseId) : undefined;
    return base?.type === 'group' && findRenderBox((base as RenderGroupBox).children, hitId) !== undefined;
  }

  /** The box being typed plus the clippers cut by it: both leave the backdrop while the session lasts. */
  private editLiftIds(id: string): Set<string> {
    return new Set([id, ...clipCompanionRenderIds(this.cache?.boxes ?? [], [id])]);
  }

  /** Whether the box, or a group holding it, is the base of a decor clipped live to it. */
  private inLiveClipRun(id: string): boolean {
    let list: readonly RenderBox[] = this.cache?.boxes ?? [];
    for (;;) {
      const node = list.find(b => b.id === id || (b.type === 'group' && findRenderBox((b as RenderGroupBox).children, id)));
      if (!node) return false;
      if (list.some(b => b.type === 'object' && (b as RenderObjectBox).maskBaseBoxId === node.id)) return true;
      if (node.id === id) return false;
      list = (node as RenderGroupBox).children;
    }
  }

  private styleLiftReady = false;
  private styleSettlePending = false;
  private styleTickRaf?: number;
  private styleTickToken = 0;
  private styleSessionToken = 0;
  private styleTickInFlight = false;
  private styleTickPending = false;

  /** The image box in inline-crop mode (pan/zoom/rotate the photo within its frame); undefined when not cropping. */
  private cropBoxId?: string;
  private cropState?: CropState;
  /** State captured on crop entry — restored by the reset button. */
  private cropInitial?: CropState;
  /** Raw (unmagnetised) Alt+wheel rotation of this crop session — lets the wheel walk out of the 0° magnet.
   *  Stale as soon as another actor moved the angle, so it reseeds from the state instead of being reset explicitly. */
  private cropWheelRotationRaw?: number;
  /** Active crop pointer gesture (corner=rotate, edge=scale, body=pan). */
  private cropGesture?:
    | { kind: 'pan'; startBook: Point; startOffsetX: number; startOffsetY: number }
    | { kind: 'scale'; centerBook: Point; startDist: number; startScale: number }
    | { kind: 'rotate'; centerBook: Point; startAngleRad: number; startRotation: number };

  /** Live photo placement during a crop gesture, fed to the scene build without a store round-trip. */
  private liveCropOverrides = new Map<string, LiveCropRect>();
  /** Decoded full photo for the dimmed ghost peeking outside the frame. */
  private cropGhostBitmap?: ImageBitmap;
  /** The committed placement kept on the ghost layer between a commit and the render that replaces it: the raster
   *  underneath still carries the crop as it was on ENTRY, so the layer is the only thing showing what the user
   *  just made. Owns its bitmap — released with the layer. */
  private cropSettleGhost?: { id: string; rect: CropImageRect; bitmap: ImageBitmap };
  /** Bumped on every crop entry — the box id alone can't tell a re-entered crop (replaced photo) from the
   *  previous session, whose in-flight source decode would otherwise install the old photo as the ghost. */
  private cropSessionToken = 0;
  /** Last percent handed to onCropZoomChange — a pan frame leaves the scale alone and must not re-notify the host. */
  private cropZoomEmitted?: number;

  /** The decor box (object/background/smartObject) in trim mode (edge-resize the window inside fixed
   *  content); undefined when not trimming. */
  private trimBoxId?: string;
  private trimState?: { window: TrimRect; content: TrimRect };
  /** Active trim pointer gesture — an edge/corner resize only; trim has no pan, rotate or zoom. */
  private trimGesture?: { handle: ResizeAnchor; startWindow: TrimRect; startPointer: Point };
  /** Live window/content override during a trim gesture, fed to the scene build without a store round-trip. */
  private liveTrimOverrides = new Map<string, LiveTrimOverride>();

  /** The text box in path-edit mode (curve anchors / position along the curve); undefined when inactive. */
  private pathEditBoxId?: string;
  private pathEditMode?: PathEditMode;
  /** Working copy of the box's textPath — gestures mutate it; each dragend commits to the host. */
  private pathEditTp?: TextPathWork;
  /** The exact model `d` we consider current — raw at entry, the emitted string after each commit. */
  private pathEditLiveD?: string;
  private pathEditSession?: PathEditSession;
  /** Selected anchor index (edit mode) — shows its control handles, target of Delete. */
  private pathEditSelected?: number;
  private pathEditGesture?:
    | { kind: 'bracket'; which: 'start' | 'end'; startTp: TextPathWork }
    | { kind: 'text'; startFrac: number; startTp: TextPathWork }
    | { kind: 'anchor'; index: number; startBook: Point; startKnots: PathEditSession['knots'] }
    | { kind: 'handle'; index: number; side: 'in' | 'out'; startKnots: PathEditSession['knots'] };

  // Camera mode positions the spread-sized overlay via CSS instead of borrowing the Konva stage viewport.
  private camera?: Camera;

  // A crashed render worker takes every resident texture with it, and the render it was running only logs.
  private readonly stopRendererLost?: () => void;

  constructor(private readonly cfg: RustOverlayConfig) {
    this.stopRendererLost = cfg.wasm.onRendererLost?.(() => void this.render());
  }

  /**
   * The host builds this config ONCE, but the album editor flips text-interaction mode
   * (Редактор ↔ Дизайнер) at runtime without remounting the canvas. A live click/resize reads these
   * flags off `this.cfg`, so they must be pushed in when the mode changes — otherwise the canvas keeps
   * the mode it mounted in (e.g. click-to-edit stays on after switching to Дизайнер).
   */
  setTextInteractionFlags(flags: {
    enableClickTextEdit?: boolean;
    enableTextClampToFrame?: boolean;
    enableTextFrameFollow?: boolean;
    enableTextCornerScale?: boolean | 'free';
  }): void {
    if (flags.enableClickTextEdit !== undefined) this.cfg.enableClickTextEdit = flags.enableClickTextEdit;
    if (flags.enableTextClampToFrame !== undefined) this.cfg.enableTextClampToFrame = flags.enableTextClampToFrame;
    if (flags.enableTextFrameFollow !== undefined) this.cfg.enableTextFrameFollow = flags.enableTextFrameFollow;
    if (flags.enableTextCornerScale !== undefined) this.cfg.enableTextCornerScale = flags.enableTextCornerScale;
  }

  /** Live crop right — the host's value changes per spread while this controller lives on. */
  setInlineCropEnabled(enabled: boolean): void {
    this.cfg.enableInlineCrop = enabled;
  }

  // undefined → revert to the Konva-stage viewport.
  setCamera(cam: Camera | undefined): void {
    const prev = this.camera;
    this.camera = cam;
    if (this.destroyed || !this.cache) return;
    // A no-change camera set (host fit re-emitting the same frame) must not repaint — rerouted through the
    // coalescer it would chain render→layout→fit→render forever.
    if (prev && cam && prev.originX === cam.originX && prev.originY === cam.originY && prev.zoom === cam.zoom) return;
    this.paintSuperseding();
  }

  /**
   * Pan/zoom during a gesture: update the camera and only CSS-reposition the (already-rendered) canvases — no WASM
   * re-render. The browser GPU-scales the existing buffer, like the Konva stage-scale path; the content render runs
   * only when the content itself changes. This is what keeps pan/zoom smooth on a heavy spread.
   */
  setCameraLive(cam: Camera): void {
    const tileCam = this.sharpTileCamera;
    this.camera = cam;
    if (this.destroyed || !this.cache) return;
    // Pan = translate, zoom = hide. A translate-only gesture keeps the tile's scale, so CSS can carry its pixels along
    // by the screen-px delta; dropping it there exposed the resolution-capped spread buffer underneath, which reads as
    // text going blurry the moment the camera moves. A zoom changes the scale CSS cannot fake — hide until the settle
    // re-renders. Edge gaps where the panned tile no longer covers the viewport show that same buffer: acceptable.
    const tile = this.sharpTileCanvas;
    if (tile && tileCam && tile.style.display !== 'none' && cam.zoom === tileCam.zoom) {
      const dx = (tileCam.originX - cam.originX) * cam.zoom;
      const dy = (tileCam.originY - cam.originY) * cam.zoom;
      tile.style.transform = `translate(${dx}px, ${dy}px)`;
    } else {
      this.hideSharpTile();
    }

    this.reposition();
  }

  getCamera(): Camera | undefined {
    return this.camera;
  }

  /** True while an inline crop gesture owns the wheel (so the host's camera wheel steps aside). */
  isCropping(): boolean {
    return this.cropBoxId !== undefined;
  }

  // book→CSS-px scale: the camera zoom in camera mode, else the Konva stage's fit×zoom scale.
  private get viewScale(): number {
    return (this.camera ? this.camera.zoom : this.cfg.stage.scale) || 1;
  }

  // The positioning triple the overlay canvases are laid out from. Camera mode maps origin→viewportOffset, no chrome.
  private get viewState(): { scale: number; viewportOffset: { x: number; y: number }; chromeOffset: { x: number; y: number } } {
    if (this.camera) {
      return {
        scale: this.camera.zoom || 1,
        viewportOffset: { x: this.camera.originX, y: this.camera.originY },
        chromeOffset: { x: 0, y: 0 },
      };
    }

    return this.cfg.stage;
  }

  // The canvas whose CSS cursor reflects the pointer (the input surface), so cursors show in camera mode too.
  private get cursorCanvas(): HTMLCanvasElement | undefined {
    return this.inputCanvas ?? this.overlayCanvas;
  }

  private get isPlaceMode(): boolean {
    return this.cfg.getInteractionMode?.() === 'place';
  }

  // Camera mode routes input through the full-host handle layer (so off-spread handles are grabbable); Konva keeps it
  // on the spread overlay (byte-identical). The non-input canvas goes pointer-inert so it never steals the off-spread press.
  private updateInputSurface(): void {
    const cameraMode = !!this.camera;
    const surface = cameraMode ? this.handleCanvas : this.overlayCanvas;
    if (surface && this.inputCanvas !== surface) {
      this.detachInput();
      this.attachInput(surface);
    }

    if (this.overlayCanvas) this.overlayCanvas.style.pointerEvents = cameraMode ? 'none' : 'auto';
    if (this.handleCanvas) this.handleCanvas.style.pointerEvents = cameraMode ? 'auto' : 'none';
  }

  private attachInput(canvas: HTMLCanvasElement): void {
    canvas.addEventListener('click', this.onClick);
    canvas.addEventListener('dblclick', this.onDblClick);
    canvas.addEventListener('contextmenu', this.onContextMenu);
    canvas.addEventListener('mousedown', this.onPointerDown);
    canvas.addEventListener('mousemove', this.onMouseMove);
    canvas.addEventListener('mouseleave', this.onMouseLeave);
    for (const ev of OVERLAY_STOP_EVENTS) canvas.addEventListener(ev, this.stopPropagation);
    // The gesture surface owns its touches outright — otherwise the browser races us with native scroll/zoom
    // and a half-claimed touch leaves the page unscrollable until reload.
    canvas.style.touchAction = 'none';
    this.inputCanvas = canvas;
  }

  private detachInput(): void {
    const canvas = this.inputCanvas;
    if (!canvas) return;
    canvas.removeEventListener('click', this.onClick);
    canvas.removeEventListener('dblclick', this.onDblClick);
    canvas.removeEventListener('contextmenu', this.onContextMenu);
    canvas.removeEventListener('mousedown', this.onPointerDown);
    canvas.removeEventListener('mousemove', this.onMouseMove);
    canvas.removeEventListener('mouseleave', this.onMouseLeave);
    for (const ev of OVERLAY_STOP_EVENTS) canvas.removeEventListener(ev, this.stopPropagation);
    canvas.style.touchAction = '';
    this.inputCanvas = undefined;
  }

  // A drag armed on mousedown can miss its window mouseup (touch pointercancel, release outside the window,
  // tab blur) — the armed listeners then swallow interaction until reload. pointerup/pointercancel/blur
  // guarantee every arm has a matching disarm; both release handlers are idempotent.
  private armWindowDrag(onMove: (event: MouseEvent) => void, onRelease: () => void): void {
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onRelease);
    window.addEventListener('pointerup', onRelease);
    window.addEventListener('pointercancel', onRelease);
    window.addEventListener('blur', onRelease);
  }

  private disarmWindowDrag(onMove: (event: MouseEvent) => void, onRelease: () => void): void {
    window.removeEventListener('mousemove', onMove);
    window.removeEventListener('mouseup', onRelease);
    window.removeEventListener('pointerup', onRelease);
    window.removeEventListener('pointercancel', onRelease);
    window.removeEventListener('blur', onRelease);
  }

  /** Host CSS size for the camera viewport — the full overlay area (not the spread). */
  private hostSize(): { width: number; height: number } {
    const el = this.cfg.host;
    return { width: el.clientWidth || el.offsetWidth || 0, height: el.clientHeight || el.offsetHeight || 0 };
  }

  private resolutionCaps(): ResolutionCaps {
    return { baseMaxWidth: this.cfg.maxWidth ?? DEFAULT_MAX_WIDTH, sharpMaxWidth: SHARP_MAX_WIDTH, maxDpr: SHARP_MAX_DPR };
  }

  // Buffer renderScale: zoom-aware in camera mode (crisp on zoom-in, capped), the plain maxWidth cap otherwise.
  private contentRenderScale(viewportWidth: number): number {
    const full = this.fullRenderScale(viewportWidth);
    if (!this.draftResolution && !this.switchDraft) return full;
    return Math.min(full, DRAFT_MAX_WIDTH / Math.max(1, viewportWidth));
  }

  /**
   * Rendition-selection scale — the resolution a photo is really DISPLAYED at (camera zoom × dpr), deliberately
   * uncapped by SHARP_MAX_WIDTH: that cap is the buffer's memory budget, not a statement about how large the photo
   * looks, and past it the sharp tile draws at full device resolution anyway. The ladder's top rung (1920) bounds
   * the pick instead. ONE source for every collectImageRefs/scene-build call site: a full render while zoomed in
   * (an edit, a text commit, a drag) must not re-pick a lower rung than what is on screen, or the deferred lane
   * would swap the sharp texture back down. Without a camera (non-camera hosts) the buffer scale still rules.
   */
  private renditionScale(renderScale: number): number {
    return this.camera ? this.camera.zoom * this.cameraDpr() : renderScale;
  }

  private fullRenderScale(viewportWidth: number): number {
    if (!this.camera) return Math.min(1, (this.cfg.maxWidth ?? DEFAULT_MAX_WIDTH) / viewportWidth);
    const dpr = typeof window !== 'undefined' ? window.devicePixelRatio || 1 : 1;
    return resolutionForZoom(this.camera.zoom, viewportWidth, dpr, this.resolutionCaps());
  }

  /**
   * Жест хоста ведёт кадр (драг ползунка «Масштаб»). Пока включено, разворот стоит на трёх подпорках сразу:
   * буфер сцены не шире чернового капа, текстуры движка садятся на ступень размеров, а сам тик собирается
   * блитами по снимку входа, вообще без растра. Выключение снимает все три и отдаёт чёткий кадр ОДНОМУ
   * рендеру — тому, что приносит коммит хоста тем же тиком.
   */
  setDraftResolution(on: boolean): void {
    if (this.destroyed || this.draftResolution === on) return;
    this.draftResolution = on;
    this.cfg.wasm.setDraftScaling?.(on);
    if (on) {
      // Снимок берём ДО applyRenderScale: он тут же перерисует разворот в черновом масштабе поверх оригинала.
      this.captureDraftSnapshot();
      this.applyRenderScale();
      return;
    }

    this.draftSnapshot = undefined;
    // Коммит хоста приходит тем же тиком и уже поставил свой рендер — флаг снят раньше него, значит именно он
    // приедет в полном разрешении, и платить за второй такой же растр незачем. Своего не ставим, чтобы не
    // отодвинуть чужой; ставим только когда коммита не случилось (Esc, клик мимо, то же самое значение).
    if (this.renderTimer === undefined) this.scheduleRender(DRAFT_RELEASE_SHARPEN_MS);
  }

  /**
   * Снимок входа в жест: копия растра разворота + прямоугольники, какими его застал захват. Синхронно и
   * до первой перерисовки — сразу за ним буфер садится на черновой кап и оригинала уже не будет.
   */
  private captureDraftSnapshot(): void {
    const overlay = this.overlayCanvas;
    const cache = this.cache;
    if (!overlay || !cache || overlay.width === 0 || overlay.height === 0) return;
    const base = document.createElement('canvas'); // никогда не в DOM — чистый источник пикселей
    // eslint-disable-next-line no-restricted-syntax -- холст вне DOM: стирать нечего
    base.width = overlay.width;
    // eslint-disable-next-line no-restricted-syntax -- холст вне DOM: стирать нечего
    base.height = overlay.height;
    const ctx = base.getContext('2d');
    if (!ctx) return; // headless/jsdom без canvas — жест просто идёт полным растром
    ctx.drawImage(overlay, 0, 0);
    this.draftSnapshot = {
      base,
      boxes: cache.boxes,
      rects: new Map(this.drawableRects),
      viewport: cache.viewport,
      arming: false,
      blocked: false,
    };
  }

  /**
   * Кадр тика жеста без движка: подложка, поверх неё каждая подвижная рамка одним `drawImage` из растра
   * входа, поверх — накладка. `false` — снимок не готов или сцена с ним разошлась, и тик рисует растром.
   * Рамка, сменившая пропорции, берёт из входного кадра центральный вырез под свои новые пропорции
   * (cover, как настоящий рефит на отпускании) — фото не растягивается ни на тике, ни на отпускании;
   * слегка плывёт только сам кроп, точный приезжает с рендером коммита.
   */
  private paintDraftFrame(canvas: HTMLCanvasElement, rects: ReadonlyMap<string, SceneRect>): boolean {
    const snap = this.draftSnapshot;
    const cache = this.cache;
    if (!snap || snap.blocked || !cache) return false;
    if (cache.viewport.width !== snap.viewport.width || cache.viewport.height !== snap.viewport.height) return false;
    const moved = movedBoxIds(snap.boxes, cache.boxes);
    if (!moved) return false;

    if (!snap.movers) {
      void this.armDraftLayers(moved);
      return false;
    }

    // Поехало что-то мимо ряда кадров — контент сменился не жестом, и снимок больше не описывает эту сцену.
    if (moved.some(id => !snap.movers?.includes(id))) return false;
    const under = snap.under;
    const ctx = under ? canvas.getContext('2d') : null;
    if (!under || !ctx) return false;

    // 'copy' одной операцией: пара clearRect+drawImage оставляет буфер прозрачным между ними, и
    // компоновщик волен показать именно этот зазор — он читается вспышкой бумаги.
    ctx.globalCompositeOperation = 'copy';
    ctx.drawImage(under, 0, 0, canvas.width, canvas.height);
    ctx.globalCompositeOperation = 'source-over';
    for (const id of snap.movers) {
      const src = snap.rects.get(id);
      const dst = rects.get(id);
      if (!src || !dst || src.width <= 0 || src.height <= 0 || dst.width <= 0 || dst.height <= 0) continue;
      // Центральный cover-вырез источника под пропорции новой рамки — блит не искажает фото.
      const dstAR = dst.width / dst.height;
      let sw = src.width;
      let sh = src.height;
      if (sw / sh > dstAR) sw = sh * dstAR;
      else sh = sw / dstAR;
      const sx = src.x + (src.width - sw) / 2;
      const sy = src.y + (src.height - sh) / 2;
      ctx.drawImage(snap.base, sx, sy, sw, sh, dst.x, dst.y, dst.width, dst.height);
    }

    if (snap.over) ctx.drawImage(snap.over, 0, 0, canvas.width, canvas.height);
    return true;
  }

  /**
   * Разбор снимка на слои по первому же тику жеста: подвижные — сплошной ряд КАДРОВ, всё до него уходит в
   * подложку, всё после — в накладку. Ряд берём шире сдвинувшихся: рамка, прижатая к вылету, на первом тике
   * не шелохнётся, а на следующем поедет, и тик за ней откатывался бы на полный растр.
   */
  private async armDraftLayers(moved: readonly string[]): Promise<void> {
    const snap = this.draftSnapshot;
    const cache = this.cache;
    if (!snap || snap.arming || snap.blocked || !cache || moved.length === 0) return;
    snap.arming = true;
    const boxes = cache.boxes;
    const ids = new Set(moved);
    let first = boxes.findIndex(b => ids.has(b.id));
    if (first < 0) {
      snap.blocked = true; // сдвинулось что-то не верхнего уровня — по такому снимку кадр не собрать
      return;
    }

    let last = first;
    for (let i = first; i < boxes.length; i++) if (ids.has(boxes[i].id)) last = i;
    while (first > 0 && boxes[first - 1].type === 'image') first--;
    while (last < boxes.length - 1 && boxes[last + 1].type === 'image') last++;
    const run = boxes.slice(first, last + 1);
    // Порядок сцены блитом не восстановить, если между кадрами что-то стоит (подпись между двумя фото ушла бы
    // под них) или если сдвинулось не только то, что лежит в ряду.
    if (!run.every(b => b.type === 'image') || moved.some(id => !run.some(b => b.id === id))) {
      snap.blocked = true;
      return;
    }

    // Блит прямоугольный и берёт ровно прямоугольник рамки: повёрнутая рамка тянется аффинным
    // преобразованием, эффект (тень, обводка) рисуется ЗА рамкой, а рамки без пикселей во входном растре
    // нет вовсе. Любой из трёх случаев — кадр соврал бы, поэтому жест идёт полным растром.
    if (run.some(b => !snap.rects.get(b.id) || snap.rects.get(b.id)?.transform || snap.rects.get(b.id)?.rotation || b.effects)) {
      snap.blocked = true;
      return;
    }

    const { width, height } = spreadBufferSize(cache.viewport, cache.renderScale);
    const layer = (): HTMLCanvasElement => {
      const canvas = document.createElement('canvas'); // никогда не в DOM
      // eslint-disable-next-line no-restricted-syntax -- холст вне DOM: стирать нечего
      canvas.width = width;
      // eslint-disable-next-line no-restricted-syntax -- холст вне DOM: стирать нечего
      canvas.height = height;
      return canvas;
    };

    try {
      const under = layer();
      await this.renderSliceTo(under, boxes.slice(0, first), cache, true);
      const overBoxes = boxes.slice(last + 1);
      const over = overBoxes.length > 0 ? layer() : undefined;
      if (over) await this.renderSliceTo(over, overBoxes, cache, false);
      if (this.draftSnapshot !== snap || this.destroyed) return; // жест кончился, пока слои рисовались
      snap.under = under;
      snap.over = over;
      snap.movers = run.map(b => b.id);
    } catch (error) {
      snap.blocked = true;
      console.error('[engine=rust] draft gesture layers failed', error);
    }
  }

  // Sharp-on-settle: viewport tile past the resolution cap, else re-render the spread buffer at the new scale. Camera mode only.
  resharpen(): void {
    if (this.destroyed || !this.cache || !this.camera) return;
    // Ahead of the fork, not inside it: both branches repaint only what is ALREADY decoded, and past the sharp cap
    // the tile branch returns before any of them — the whole zoom range that matters would never ask for a file.
    this.planZoomRenditionUpgrades();
    if (shouldSharpTile(this.camera.zoom, this.cache.viewport.width, this.cameraDpr(), this.resolutionCaps())) {
      void this.renderSharpTile();
      return;
    }

    this.hideSharpTile();
    this.applyRenderScale();
  }

  /** Zoom settled: re-pick every photo's rendition at the new display scale and park the lifts on the deferred lane
   *  (decode off-screen, ONE atomic repaint). A photo whose pick is already decoded — or that has no higher rung —
   *  yields nothing, so a second settle at the same zoom costs one plan and not a single request. A render in flight
   *  owns the plan already: it selects at this very scale and would be clobbered by a batch built off the old cache. */
  private planZoomRenditionUpgrades(): void {
    const cache = this.cache;
    if (!cache) return;
    // …but the render read the camera BEFORE this zoom, so the batch it parked is stale, and nobody comes back for
    // it: past the sharp cap `resharpen` returns in the tile branch, and under the baseline `applyRenderScale`
    // stops at its 1e-3 guard — neither produces the follow-up render that re-plans. Re-arm instead of giving up,
    // the way `settleRenditionUpgrades` pushes its own deadline out when it lands mid-render.
    if (this.renderInFlight) {
      this.replanZoomAfterRender = true;
      return;
    }

    this.replanZoomAfterRender = false;
    const refs = collectImageRefs(cache.boxes, this.renditionScale(cache.renderScale));
    this.scheduleRenditionUpgrades(planImageTiers(refs, cache.images).deferred);
  }

  // Пересчёт масштаба буфера под текущий кап + перерисовка из кэша, если он поменялся.
  private applyRenderScale(): void {
    if (!this.cache) return;
    const next = this.contentRenderScale(this.cache.viewport.width);
    if (Math.abs(next - this.cache.renderScale) < 1e-3) return;
    this.cache.renderScale = next;
    this.paintSuperseding();
  }

  /** Блочная перекраска при АКТИВНОЙ сессии: модель сняла run-цвета — live-прогоны снимают их тем же
   *  правилом, иначе холст до blur рисует старые цвета, а blur-коммит после правки текста вернёт их в модель
   *  (нетронутая сессия — cancel, раны она не пишет). */
  stripLiveTextRunColors(id: string): void {
    if (this.editingBoxId !== id) return;
    this.replaceLiveEditRuns(stripStyleRunColors(this.liveEditRuns));
  }

  /** Блочная смена веса, курсива или гарнитуры при АКТИВНОЙ сессии: модель сняла вес и курсив run'ов — live-прогоны
   *  снимают их тем же правилом, иначе холст до blur рисует старое лицо, а blur-коммит после правки текста вернёт
   *  его в модель (нетронутая сессия — cancel, раны она не пишет). */
  stripLiveTextRunFaces(id: string): void {
    if (this.editingBoxId !== id) return;
    this.replaceLiveEditRuns(stripStyleRunFaces(this.liveEditRuns));
  }

  /** Блочная смена регистра при АКТИВНОЙ сессии: модель сняла регистр run'ов — live-прогоны снимают его тем же
   *  правилом, иначе холст до blur рисует старый регистр, а blur-коммит после правки текста вернёт его в модель
   *  (нетронутая сессия — cancel, раны она не пишет). */
  stripLiveTextRunCaps(id: string): void {
    if (this.editingBoxId !== id) return;
    this.replaceLiveEditRuns(stripStyleRunCaps(this.liveEditRuns));
  }

  /** Блочное подчёркивание или зачёркивание при АКТИВНОЙ сессии: модель сняла этот флаг с run'ов — live-прогоны
   *  снимают его тем же правилом, иначе холст до blur рисует старый флаг, а blur-коммит после правки текста вернёт
   *  его в модель (нетронутая сессия — cancel, раны она не пишет). */
  stripLiveTextRunDecorations(id: string, patch: StyleRunDecorationPatch): void {
    if (this.editingBoxId !== id) return;
    this.replaceLiveEditRuns(stripStyleRunDecorations(this.liveEditRuns, patch));
  }

  private replaceLiveEditRuns(next: RenderTextBox['runs'] | undefined): void {
    if (!next) return;
    this.liveEditRuns = next;
    if (this.liveEditText === undefined) this.liveEditText = this.liveEditPrevText;
    if (this.editLiftReady) this.scheduleEditTick();
    else this.schedulePaint(0);
  }

  /** Панель покрасила [from, to) АКТИВНОЙ сессии: прогоны живого рендера догоняют модель тем же правилом —
   *  иначе бокс до конца сессии рисуется старыми цветами под вуалью выделения («цвета наложились»). */
  paintLiveTextRunRange(id: string, from: number, to: number, color: string, blockColor: string): void {
    if (this.editingBoxId !== id || this.liveEditPrevText === undefined) return;
    const next = applyColorToStyleRuns(this.liveEditRuns, this.liveEditPrevText.length, from, to, color, blockColor);
    if (!next) return;
    this.liveEditRuns = next as RenderTextBox['runs'];
    if (this.liveEditText === undefined) this.liveEditText = this.liveEditPrevText;
    if (this.editLiftReady) this.scheduleEditTick();
    else this.schedulePaint(0);
  }

  // Public renders go through the coalescer — a bare renderNow() leaves renderInFlight false, so a camera-fit
  // repaint during the initial render would supersede its generation and strand onContentSettled forever.
  render(): Promise<void> {
    return this.renderCoalesced();
  }

  private async renderNow(): Promise<void> {
    if (this.destroyed) return;
    this.dropEvictedStubs();
    configurePsdRenderWasmUrl(this.cfg.wasmUrl);
    const viewport = this.cfg.getViewport();
    if (viewport.width <= 0 || viewport.height <= 0) return;
    const boxes = this.renderBoxes();
    const gen = ++this.generation;
    // Snapshotted with the boxes: whatever the host commits while this render runs belongs to the NEXT frame.
    this.paintedContentStamp = this.cfg.getContentStamp?.();
    this.paintedContentId = this.cfg.getContentId?.();
    const renderScale = this.contentRenderScale(viewport.width);
    // Черновик смены живёт ровно один рендер: следующий (в том числе чёткий ниже) идёт в полном масштабе.
    const switchDraft = this.switchDraft;
    this.switchDraft = false;
    const zones = this.cfg.getZones();

    const textBoxes = collectTextBoxes(boxes);
    const {
      fonts,
      mappedFace,
      fontVariationOf,
      fontRefOf,
      caretMetrics,
      caretLayoutOf,
      pathCaretLayoutOf,
      engineCaretOf,
      fontErrorOf,
      overflowOf,
      frameFitOf,
    } = await this.cfg.resolveFonts(textBoxes);
    const fontErrors = new Map<string, FontErrorKind>();
    if (fontErrorOf)
      for (const tb of textBoxes) {
        const kind = fontErrorOf(tb);
        if (kind) fontErrors.set(tb.id, kind);
      }

    // The decode-map (ref→url) is path-independent: build_scene carries image_ref=box.id and the host resolves bytes here.
    const imageRefs = collectImageRefs(boxes, this.renditionScale(renderScale));

    // version === url ⇒ same photo: reuse the prior decode so a drop re-decodes only the replaced box, not the whole spread.
    // Renderer-resident refs need no bytes at all — the wasm renderer keeps textures for the session, and
    // prepareRenderer skips a version-matched upload, so a revisited spread's decode would be thrown away.
    const resident: Record<string, RustImage> = {};
    for (const r of imageRefs) {
      if (this.cache?.images[r.ref]) continue;
      // The placeholder keeps the texture's natural size: crop and cover-fit read it off the cache entry.
      const version = imageDecodeVersion(r.url, r.preservePngRgba);
      const size = this.cfg.wasm.residentImageSize?.(r.ref, version);
      if (size)
        resident[r.ref] = {
          ...size,
          data: RESIDENT_IMAGE_STUB,
          version,
          imageId: r.imageId,
          rungPx: r.rungPx,
          ...(r.preservePngRgba && { preservePngRgba: true, sourceUrl: r.url }),
        };
    }

    // A layout rebuild renames box ids, so the ref-keyed cache misses even though the PHOTOS are the same —
    // and the first repaint would flash placeholders where finished photos just stood. The decoded bytes are
    // ref-agnostic: re-home any prior decode under the new ref that asks for the same url, and when the rename
    // also changed the RENDITION (the template regrew the frame), re-home the same photo's old-rendition bytes —
    // planImageTiers keeps them visible and lifts the resolution in the deferred lane. A resident stub carries
    // no pixels; its old rendition re-decodes under the new ref before the first paint (carryOver, usually a
    // blob-cache hit) so the frame never regresses to blur/white.
    const rehomed: Record<string, RustImage> = {};
    const carryOver: TierEntry[] = [];
    const prior = this.cache?.images;
    if (prior) {
      const byUrl = new Map<string, RustImage>();
      const byImageId = new Map<string, RustImage>();
      for (const key in prior) {
        const img = prior[key];
        if (!img.version) continue;
        if (img.data.length > 0) byUrl.set(img.version, img);
        // Pixel-bearing entries win over stubs — pixels can move to the new ref outright.
        if (img.imageId && (img.data.length > 0 || !byImageId.has(img.imageId))) byImageId.set(img.imageId, img);
      }

      for (const r of imageRefs) {
        if (resident[r.ref] || prior[r.ref]) continue;
        const match =
          byUrl.get(imageDecodeVersion(r.url, r.preservePngRgba)) ?? (r.imageId ? byImageId.get(r.imageId) : undefined);
        if (!match || !!match.preservePngRgba !== !!r.preservePngRgba) continue;
        rehomed[r.ref] = match;
        // A stub's texture lives under the OLD ref only — the new ref always needs real bytes before first paint.
        if (match.data.length === 0 && match.version !== undefined) {
          carryOver.push({
            ref: r.ref,
            url: match.sourceUrl ?? match.version,
            imageId: r.imageId,
            rungPx: match.rungPx,
            ...(r.preservePngRgba && { preservePngRgba: true }),
          });
        }
      }
    }

    // A stub whose texture left the renderer during the awaits above (worker crash) holds no pixels: decode it again.
    this.dropEvictedStubs();
    const prevImages: Record<string, RustImage> = { ...resident, ...this.cache?.images };

    // One-shot: this render is either the swap's settle (consumes it) or an unrelated one (nothing was pending).
    // Either way the field must not survive past this pass — a later replacement on the same frame is not a swap.
    const holdPixelRefs = this.pendingSwapSettleRefs;
    this.pendingSwapSettleRefs = undefined;
    // A frame that stood EMPTY in the scene we last painted and now carries a photo: a placement (double-click
    // from the panel, a drag into a hole). Its box id is the one it already had, so the previous boxes still
    // hold it — a spread switch or a layout rebuild renames ids and never matches here. Such a frame owns no
    // pixels to fall back on, so the ladder would paint it three times; it takes the final tier alone instead.
    const prevBoxes = this.cache?.boxes;
    const freshPlacementRefs = prevBoxes
      ? new Set(
          imageRefs
            .filter(r => !prevImages[r.ref] && !rehomed[r.ref] && findRenderBox(prevBoxes, r.ref)?.type === 'image')
            .map(r => r.ref),
        )
      : undefined;
    const plan = planImageTiers(imageRefs, { ...rehomed, ...prevImages }, holdPixelRefs, freshPlacementRefs);
    // Re-homed stubs re-decode their old rendition BEFORE the first paint — the new ref has no texture yet.
    plan.tiers[0].push(...carryOver);
    // Rendition lifts wait out the interaction: every render pushes the deadline, so a live slider drag never
    // swaps a photo mid-gesture — the whole page upgrades at once after the UI goes quiet.
    this.scheduleRenditionUpgrades(plan.deferred);
    // Every box already had pixels OF ITS OWN — only their versions moved, so what is on screen IS this spread.
    // A re-homed ref does not count: its box is new, and the layout it belongs to still owes its first paint.
    // A freshly placed frame counts: its box IS on screen (empty), so a pre-photo paint only re-rasters what
    // the user already sees — and it would lift the host's «before» snapshot a wave before the photo lands.
    const spreadAlreadyOnScreen = imageRefs.every(r => !!prevImages[r.ref] || !!freshPlacementRefs?.has(r.ref));
    const decoded: Record<string, RustImage> = { ...(plan.cacheHits as Record<string, RustImage>) };

    // A wave that does not answer within RENDITION_DECODE_TIMEOUT_MS (a fetch held open forever) must not hold the
    // spread veil: the render paints what landed and settles. Only the refs still hanging skip the later waves — the
    // rest of the spread keeps climbing the ladder; the next render asks for the hung ones again.
    let decodeGaveUp = false;
    const stalledRefs = new Set<string>();
    let ownCache: OverlayCache | undefined;
    const decodeTier = async (entries: TierEntry[]): Promise<void> => {
      let waveGaveUp = false;
      const answered = new Set<string>();
      const wave = Promise.all(
        entries.map(async ({ ref, url, imageId, rungPx, preservePngRgba }) => {
          try {
            const sourceDecoded = preservePngRgba
              ? await this.cfg.decodeImage(url, { preservePngRgba: true })
              : await this.cfg.decodeImage(url);
            const image: RustImage = {
              ...sourceDecoded,
              version: imageDecodeVersion(url, preservePngRgba),
              imageId,
              rungPx,
              ...(preservePngRgba && { preservePngRgba: true, sourceUrl: url }),
            };
            noteShownRendition(imageId, rungPx);
            if (this.destroyed) return;
            if (waveGaveUp) {
              // Slow but alive host: the bytes came after the render settled without them. Nobody else will ask —
              // land them in the cache this render built (a newer render re-plans the ref itself) and repaint.
              // Generation is no guard here: every cache repaint since (hover, lift) has bumped it.
              const cache = this.cache;
              if (!cache || cache !== ownCache) return;
              const shown = decoded[ref];
              decoded[ref] = image;
              const visible = visibleImages(decoded, imageRefs);
              const next = { ...cache.images };
              let changed = false;
              for (const key in visible) {
                const current = next[key];
                // Fill a frame left empty, or lift the lower rung this render painted — never undo a later swap.
                if (current === visible[key] || (current && !(key === ref && current === shown))) continue;
                next[key] = visible[key];
                changed = true;
              }

              if (!changed) return;
              cache.images = next;
              this.paintSuperseding();
              return;
            }

            if (gen !== this.generation) return;
            decoded[ref] = image;
          } catch (error) {
            console.warn('[engine=rust] image decode failed', ref, error);
          } finally {
            answered.add(ref);
          }
        }),
      );
      let timer: ReturnType<typeof setTimeout> | undefined;
      const gaveUp = await Promise.race([
        wave.then(() => false),
        new Promise<boolean>(resolve => (timer = setTimeout(() => resolve(true), RENDITION_DECODE_TIMEOUT_MS))),
      ]);
      clearTimeout(timer);
      if (!gaveUp) return;
      waveGaveUp = true;
      decodeGaveUp = true;
      for (const { ref } of entries) if (!answered.has(ref)) stalledRefs.add(ref);
      console.warn(
        '[engine=rust] spread decode wave gave up waiting for bytes',
        entries.map(entry => entry.ref),
      );
    };

    // The spread paints immediately after the cheap placeholder wave; thumbs and full-size arrive as repaints.
    await decodeTier(plan.tiers[0]);
    if (gen !== this.generation || this.destroyed) return;

    this.fontErrors = fontErrors;
    this.cache = ownCache = {
      boxes,
      viewport,
      renderScale,
      fonts,
      mappedFace,
      fontVariationOf,
      fontRefOf,
      caretMetrics,
      caretLayoutOf,
      pathCaretLayoutOf,
      engineCaretOf,
      overflowOf,
      frameFitOf,
      zones,
      images: visibleImages(decoded, imageRefs),
    };
    // The rebuilt cache carries the host model — any commit it was lagging behind is now in the boxes.
    this.pendingCommittedText = undefined;
    this.groupOffsets = collectGroupOffsets(boxes);
    // A scene that no longer carries the dragged frame (spread switch, undo) must not keep the gesture alive:
    // its ids now belong to different content, and a later release would trade the wrong photos — or nothing.
    if (this.press?.mode === 'photo-swap' && this.press.swapSourceId && !findRenderBox(boxes, this.press.swapSourceId)) {
      this.endPress();
    }

    this.overlayBook = { w: viewport.width, h: viewport.height };
    this.overlayRegionBook = { x: 0, y: 0, w: viewport.width, h: viewport.height };
    this.overlayRegionSpills = false;
    // Re-clip to THIS spread's page before its first pixels land: painting first and repositioning after
    // shows one frame of the untrimmed sheet, which reads as a flash on every spread switch.
    this.updatePageChrome(this.sheetLayerBox());
    // The placeholder wave brought nothing new (a photo swap: both frames already hold pixels, so neither takes
    // the thumbHash rung) — painting here would only re-raster what is already on screen and drop the drag ghost
    // onto it, leaving the swap to land a whole frame later. Skip straight to the wave that carries the photos.
    const emptyFirstWave = spreadAlreadyOnScreen && plan.tiers[0].length === 0 && plan.tiers[1].length + plan.tiers[2].length > 0;
    if (!emptyFirstWave) await this.paint(gen, true);

    for (const tier of [plan.tiers[1], plan.tiers[2]]) {
      const live = tier.filter(({ ref }) => !stalledRefs.has(ref));
      if (live.length === 0) continue;
      await decodeTier(live);
      if (gen !== this.generation || this.destroyed || !this.cache) return;
      this.cache.images = visibleImages(decoded, imageRefs);
      await this.paint(gen, true);
    }

    // Every real rendition of a swapped ref failed (offline, 404) while its frame still shows the OLD pixels —
    // the model already holds the new photo. The thumbHash decodes with no network at all: paint it rather than
    // leave a sharp frame lying about its content. Its version stays the placeholder's own URL, so the next
    // render retries the real tiers.
    const rescues: TierEntry[] = plan.tiers[2]
      .filter(({ ref }) => !decoded[ref] && prevImages[ref])
      .map(({ ref }) => ({ ref, url: imageRefs.find(r => r.ref === ref)?.placeholderUrl ?? '' }))
      .filter(e => e.url !== '');
    if (rescues.length > 0 && !decodeGaveUp) {
      await decodeTier(rescues);
      if (gen !== this.generation || this.destroyed || !this.cache) return;
      this.cache.images = visibleImages(decoded, imageRefs);
      await this.paint(gen, true);
    }

    if (gen !== this.generation || this.destroyed) return;
    if (this.screenShowsHostSpread()) this.cfg.onContentSettled?.();
    this.clipSettleTick++;
    this.syncCompoundClipMasks();
    // Черновой кадр уже на экране — чёткий ставим тем же таймером, что и после жеста, если хост не поставил свой.
    if (switchDraft && this.renderTimer === undefined) this.scheduleRender(DRAFT_RELEASE_SHARPEN_MS);
  }

  // Debounced repaint from cache (no font/image re-resolve) — for live inline-text keystrokes.
  private schedulePaint(delayMs: number): void {
    if (this.destroyed) return;
    if (this.paintTimer !== undefined) clearTimeout(this.paintTimer);
    this.paintTimer = setTimeout(() => {
      this.paintTimer = undefined;
      this.paintSuperseding();
    }, delayMs);
  }

  // Per-frame repaint for a continuous gesture. schedulePaint is a TRAILING debounce, so a drag emitting moves
  // faster than its delay keeps resetting the timer and the content never repaints until the pointer stops — the
  // clip visibly trails the handle. This fires once per frame and never resets, so motion stays live.
  private scheduleLivePaint(): void {
    if (this.destroyed || this.livePaintFrame !== undefined) return;
    this.livePaintFrame = requestAnimationFrame(() => {
      this.livePaintFrame = undefined;
      this.paintSuperseding();
    });
  }

  /**
   * Смена разворота: полный растр буфера (не уже 1600 px даже на телефоне) держит поток на сотни мс, и
   * тап по плитке обзора читается как залипание. Первый кадр идёт на черновом капе — в разы меньше
   * пикселей, — а чёткий приносит тот же отложенный рендер, что возвращает резкость после жеста.
   */
  scheduleSwitchRender(): void {
    this.switchDraft = true;
    this.scheduleRender(0);
  }

  /** Debounced full re-render — coalesces rapid edits (drag, keypress, font change) into one render. */
  scheduleRender(delayMs = 150): void {
    if (this.destroyed) return;
    if (this.renderTimer !== undefined) clearTimeout(this.renderTimer);
    this.renderTimer = setTimeout(() => {
      this.renderTimer = undefined;
      void this.renderCoalesced();
    }, delayMs);
  }

  // A cache repaint that superseded an in-flight render() would strand onContentSettled (the stale generation
  // never fires it) — while a render runs, route such repaints through a fresh full render instead.
  private paintSuperseding(): void {
    if (this.renderInFlight) {
      this.scheduleRender(0);
      return;
    }

    void this.paint(++this.generation);
  }

  // Read live: the host may switch spreads mid-render, and a frame of the spread it left must not lift its veil.
  private screenShowsHostSpread(): boolean {
    return this.contentOnScreen && this.screenContentId === this.cfg.getContentId?.();
  }

  // A fire while a render is in flight runs one follow-up after it settles — never two racing decodes.
  private async renderCoalesced(): Promise<void> {
    if (this.renderInFlight) {
      this.pendingRender = true;
      return;
    }

    this.renderInFlight = true;
    try {
      // A zero viewport sends renderNow() away before any frame and without a throw — the host must hear it the same way.
      const viewport = this.cfg.getViewport();
      const noViewport = viewport.width <= 0 || viewport.height <= 0;
      await this.renderNow();
      if (
        noViewport &&
        !this.destroyed &&
        !this.screenShowsHostSpread() &&
        !this.pendingRender &&
        this.renderTimer === undefined
      ) {
        this.cfg.onRenderFailed?.();
      }
    } catch (error) {
      // A reject out of renderNow() (a font resolve that threw) used to escape into `void`/`.then()` callers and
      // skip onContentSettled: the veil stayed up for good. Over a frame of the spread the host shows, lift the
      // veil on it: the painted signal releases the in-place "applying" veil, the settle releases the spread veil.
      // Over another spread's frame, or before the first paint, the veil stays up, but the host hears the failure
      // so a page-wide veil waiting on that frame does not lock the whole editor — unless a follow-up render is
      // queued (the finally below or a pending timer), which may still bring that frame.
      const lift = !this.destroyed && this.screenShowsHostSpread();
      console.error(
        lift ? '[engine=rust] render failed, veil lifted on the previous frame' : '[engine=rust] render failed, veil kept up',
        error,
      );
      if (lift) {
        this.cfg.onContentPainted?.(this.paintedContentStamp);
        this.cfg.onContentSettled?.();
      } else if (!this.destroyed && !this.pendingRender && this.renderTimer === undefined) {
        this.cfg.onRenderFailed?.();
      }
    } finally {
      this.renderInFlight = false;
      if (this.pendingRender && !this.destroyed) {
        this.pendingRender = false;
        void this.renderCoalesced();
      } else if (this.replanZoomAfterRender && !this.destroyed) {
        // A zoom settled while this render held the plan — take it now, at the camera the user is looking at.
        // Skipped when a follow-up render is queued: that render plans at the same scale on its own.
        this.planZoomRenditionUpgrades();
      }
    }
  }

  /** Park this render's rendition lifts and (re)arm the quiet timer. Called on EVERY render — an empty batch
   *  clears leftovers whose refs the newer scene no longer wants. */
  private scheduleRenditionUpgrades(entries: TierEntry[]): void {
    if (this.renditionUpgradeTimer !== undefined) clearTimeout(this.renditionUpgradeTimer);
    this.renditionUpgradeTimer = undefined;
    this.pendingRenditionUpgrades = entries.length > 0 ? entries : undefined;
    // Метки старого плана снимаются ЗДЕСЬ, а не в `finally` его цикла волн: цикл может стоять внутри `await`
    // на декоде, который не ответит, — и тогда разворот, которого давно нет на экране, вечно числился
    // догружающимся. Волны такого батча всё равно отсеются на своём охраннике `pendingRenditionUpgrades !== batch`.
    let stale = false;
    for (const batch of [...this.renditionSettleRefs.keys()]) {
      if (batch === this.pendingRenditionUpgrades) continue;
      this.renditionSettleRefs.delete(batch);
      stale = true;
    }

    if (stale) this.reportRenditionUpgrades();
    if (!this.pendingRenditionUpgrades || this.destroyed) return;
    this.renditionUpgradeTimer = setTimeout(() => {
      this.renditionUpgradeTimer = undefined;
      void this.settleRenditionUpgrades();
    }, RENDITION_UPGRADE_QUIET_MS);
  }

  // The seamless half of a rendition change: the OLD bitmap stayed on screen through the interaction; here the
  // target renditions decode off-screen and swap in atomic repaints — no blur, no white frame. Photo by photo, in
  // «biggest on screen» order: the frame that fills most of the viewport takes the first repaint alone, the rest
  // follow in small waves. Inside ONE decode-all + repaint the per-photo order is invisible — everything lands at
  // once and the spread the user is staring at waits for photos off the edge of the screen.
  private async settleRenditionUpgrades(): Promise<void> {
    const batch = this.pendingRenditionUpgrades;
    if (!batch || this.destroyed) return;
    // The render in flight already made its ONE scheduling call — before its own decodes — so nobody is going to
    // re-plan this batch: dropping it here would strand the frame on its stale bitmap forever. Only the moment is
    // wrong, so push the deadline out by another quiet period (scheduleRenditionUpgrades keeps the batch identity
    // and clears the old timer, so no two timers stack and a destroyed controller never re-arms).
    if (this.renderInFlight) {
      this.scheduleRenditionUpgrades(batch);
      return;
    }

    const lead = batch.length > 1 ? this.mostVisibleEntry(batch) : undefined;
    const rest = this.rankByVisibleArea(batch)
      .map(ranked => ranked.entry)
      .filter(entry => entry !== lead);
    // The lead alone, then the rest in visibility order, RENDITION_UPGRADE_CHUNK frames per wave. Each wave is
    // ASKED FOR only after the previous one landed, and that sequencing is the whole point: on a thin link the
    // network queue is served in request order, so bytes for what fills the screen never sit behind bytes for a
    // frame past the edge of it, and a pan arrives at photos already sharp or first in line to be.
    const waves: TierEntry[][] = lead ? [[lead]] : [];
    for (let i = 0; i < rest.length; i += RENDITION_UPGRADE_CHUNK) {
      waves.push(rest.slice(i, i + RENDITION_UPGRADE_CHUNK));
    }

    const refs = batch.map(entry => entry.ref);
    this.renditionSettleRefs.set(batch, refs);
    this.reportRenditionUpgrades();
    try {
      for (let i = 0; i < waves.length; i++) {
        // A newer plan owns these refs now (a render mid-settle, destroy) — stop asking for bytes nobody wants.
        if (this.pendingRenditionUpgrades !== batch) return;
        const wave = waves[i];
        await this.applyRenditionWave(batch, this.decodeWaveWithinTimeout(wave), i === waves.length - 1);
        try {
          // Метку снимаем по ПИКСЕЛЯМ на экране, а не по подмене в кэше. На глубоком зуме резкость даёт только
          // плитка окна, и она приходит ещё через сотни мс после репейнта — лоудер, снятый раньше, оставлял бы
          // пользователя перед всё ещё мыльным кадром. Свой перевзвод плитку не задвоит: у renderSharpTile
          // сквозной token. Сорвавшийся рендер (репейнт бампнул generation) перевзводит себя сам — метки
          // снимаем в finally, иначе он завесил бы лоудер навсегда.
          if (this.tileMode()) await this.renderSharpTile();
        } finally {
          // This wave's frames are sharp NOW, so their marks go out with their own repaint: a spinner still turning
          // on a photo that finished long ago — because the rest of the spread has not — is the opposite of true.
          let dropped = false;
          for (const entry of wave) {
            const at = refs.indexOf(entry.ref);
            if (at >= 0) {
              refs.splice(at, 1);
              dropped = true;
            }
          }

          if (dropped) this.reportRenditionUpgrades();
        }
      }
    } finally {
      // Covers every exit — every wave done, a wave dropped as stale, destroy (which empties the set itself).
      if (this.renditionSettleRefs.delete(batch)) this.reportRenditionUpgrades();
    }
  }

  /** Tell the host WHICH frames are waiting on sharper bytes — the union of the settles in flight, so an older
   *  settle's frames keep their mark while a newer settle adds its own. Empty union = nothing is loading. */
  private reportRenditionUpgrades(): void {
    const boxIds = [...new Set([...this.renditionSettleRefs.values()].flat())];
    this.cfg.onRenditionUpgradesActive?.(boxIds.length > 0, boxIds);
  }

  /** The wave's decodes with `RENDITION_DECODE_TIMEOUT_MS` над ними: волна, которая не отвечает, не имеет
   *  права держать settle. Отдаёт «ничего не приехало» — ровно то же, что отдаёт отказавший декод, поэтому
   *  дальше по циклу разницы нет: кадр остаётся на своих пикселях, а хвост разворота идёт своим чередом. */
  private decodeWaveWithinTimeout(wave: readonly TierEntry[]): Promise<(DecodedRendition | undefined)[]> {
    const decoding = this.decodeRenditions(wave);
    return new Promise(resolve => {
      const timer = setTimeout(() => {
        console.warn(
          '[engine=rust] rendition upgrade wave gave up waiting for bytes',
          wave.map(entry => entry.ref),
        );
        resolve(wave.map(() => undefined));
        // Slow but alive host: bytes that come after all still lift the frame — nobody else will ask for them.
        void decoding.then(late => {
          if (!this.destroyed && this.cache && !this.renderInFlight) this.landRenditions(late);
        });
      }, RENDITION_DECODE_TIMEOUT_MS);
      void decoding.then(decoded => {
        clearTimeout(timer);
        resolve(decoded);
      });
    });
  }

  /** Off-screen decode of one wave's rungs. Started EAGERLY and awaited later — a rung whose decode fails
   *  lands nothing, so this never rejects. */
  private decodeRenditions(wave: readonly TierEntry[]): Promise<(DecodedRendition | undefined)[]> {
    return Promise.all(
      wave.map(async entry => {
        try {
          const decoded = entry.preservePngRgba
            ? await this.cfg.decodeImage(entry.url, { preservePngRgba: true })
            : await this.cfg.decodeImage(entry.url);
          const image: RustImage = {
            ...decoded,
            version: imageDecodeVersion(entry.url, entry.preservePngRgba),
            imageId: entry.imageId,
            rungPx: entry.rungPx,
            ...(entry.preservePngRgba && { preservePngRgba: true, sourceUrl: entry.url }),
          };
          noteShownRendition(entry.imageId, entry.rungPx);
          return { ref: entry.ref, image };
        } catch (error) {
          // The frame keeps its current pixels; the url retries whenever the next render re-plans the lift.
          console.warn('[engine=rust] rendition upgrade decode failed', entry.ref, error);
          return undefined;
        }
      }),
    );
  }

  /** ONE wave of a settle batch: await its decodes, then swap those refs in a single repaint. `batch` is the
   *  staleness stamp for the whole settle — only the LAST wave clears it, so an earlier wave's repaint cannot let a
   *  newer plan through unnoticed, and a genuinely newer plan drops every remaining wave at this one guard. */
  private async applyRenditionWave(
    batch: TierEntry[],
    decoding: Promise<(DecodedRendition | undefined)[]>,
    last: boolean,
  ): Promise<void> {
    const decoded = await decoding;
    // A render that began mid-decode owns the cache now — it re-plans any still-needed lift itself.
    if (this.destroyed || !this.cache || this.renderInFlight || this.pendingRenditionUpgrades !== batch) return;
    if (last) this.pendingRenditionUpgrades = undefined;
    this.landRenditions(decoded);
  }

  /** Swap decoded rungs into the live cache and repaint once — only refs still showing the same photo at no sharper a rung. */
  private landRenditions(decoded: readonly (DecodedRendition | undefined)[]): void {
    if (!this.cache) return;
    const nextImages = { ...this.cache.images };
    let changed = false;
    for (const d of decoded) {
      if (!d) continue;
      const current = nextImages[d.ref];
      // Same photo, and the incoming rung is not blurrier than the one on screen. Lifts finish out of order once a
      // wave times out: a late medium must never overwrite a big that landed meanwhile, and a big must still land
      // over a late medium that overtook it — so the guard compares rungs, not «still shows the predecessor».
      if (!current || current.imageId !== d.image.imageId || (d.image.rungPx ?? 0) < (current.rungPx ?? 0)) continue;
      nextImages[d.ref] = d.image;
      changed = true;
    }

    if (!changed) return;
    this.cache.images = nextImages;
    // Мигание: репейнт волны гасил плитку, и на время растра + перевзвода экран падал на капнутый мыльный
    // буфер — по волне на пульс. Пиксели плитки — те же фото, только прежней ступенью, так что держим её до
    // следующей (подмена в presentFrame атомарна). Только пока камера стоит там же, где плитку сняли: сдвиг
    // или зум ставит её кадр не на своё место, и там гасить обязательно.
    const cam = this.camera;
    const tileCam = this.sharpTileCamera;
    this.keepTileForUpgrade =
      !!cam && !!tileCam && cam.originX === tileCam.originX && cam.originY === tileCam.originY && cam.zoom === tileCam.zoom;
    this.paintSuperseding();
  }

  /** The batch ranked biggest-on-screen first — visible area in book px², from drawableRects (offset- and
   *  scale-correct) clipped to the camera viewport. Zoom anchors the CURSOR, not the camera centre, so «biggest on
   *  screen» is the product proxy for «the frame being looked at» — no cursor tracking. A frame off the viewport
   *  scores 0, and so does every frame when there is no camera; the sort is stable, so those keep their scene order
   *  at the tail. They are never dropped: the whole spread must reach its sharp rendition, just last, so a pan
   *  meets photos already upgraded. */
  private rankByVisibleArea(batch: readonly TierEntry[]): { entry: TierEntry; area: number }[] {
    const cam = this.camera;
    if (!cam) return batch.map(entry => ({ entry, area: 0 }));
    const host = this.hostSize();
    const view = { x: cam.originX, y: cam.originY, width: host.width / cam.zoom, height: host.height / cam.zoom };
    return batch
      .map(entry => {
        const rect = this.drawableRects.get(entry.ref);
        if (!rect) return { entry, area: 0 };
        const b = this.sceneRectBookAABB(rect);
        const w = Math.min(b.x + b.width, view.x + view.width) - Math.max(b.x, view.x);
        const h = Math.min(b.y + b.height, view.y + view.height) - Math.max(b.y, view.y);
        return { entry, area: Math.max(0, w) * Math.max(0, h) };
      })
      .sort((a, b) => b.area - a.area);
  }

  /** The lift whose frame covers most of the screen right now — the head of the ranking, when it is on screen at
   *  all. Nothing visible (or no camera) yields undefined and the settle upgrades the batch in scene order. */
  private mostVisibleEntry(batch: readonly TierEntry[]): TierEntry | undefined {
    const [best] = this.rankByVisibleArea(batch);
    return best && best.area > 0 ? best.entry : undefined;
  }

  // The currently un-clipped boxes. Only a box under an active manipulation — a move, a transform gesture, or inline
  // crop — shows its full extent past the design edge; a hovered or merely selected box clips like the rest, so a
  // cursor sweep can never repaint the pasteboard.
  private overflowIds(): string[] {
    const ids = new Set<string>();
    if (this.cropBoxId) ids.add(this.cropBoxId);
    if (this.trimBoxId) ids.add(this.trimBoxId);
    const gesture = this.gesture;
    if (gesture) {
      if (gesture.kind === 'group') for (const member of gesture.members) ids.add(member.id);
      else ids.add(gesture.id);
    }

    const press = this.press;
    if (press?.active && press.mode === 'move') for (const id of press.moveIds) ids.add(id);
    return [...ids];
  }

  // Overflow region (scene px for render_region, book px for CSS): the spread, grown in camera mode to enclose the
  // given boxes — so their off-spread content renders while the rest stays clipped.
  private regionForIds(
    ids: readonly string[],
    viewport: { width: number; height: number },
    renderScale: number,
  ): {
    scene: { ox: number; oy: number; w: number; h: number };
    book: { x: number; y: number; w: number; h: number };
    spills: boolean;
  } {
    const sw = viewport.width * renderScale;
    const sh = viewport.height * renderScale;
    if (!this.camera) {
      return {
        scene: { ox: 0, oy: 0, w: Math.round(sw), h: Math.round(sh) },
        book: { x: 0, y: 0, w: viewport.width, h: viewport.height },
        spills: false,
      };
    }

    // Chrome strokes straddle the box edge, so a frame flush with the spread edge would lose its outer half to the
    // region bounds. The region carries a bleed, and the selection frame draws whole, slightly past the spread.
    const bleed = CHROME_BLEED_PX * this.cameraDpr();
    let minX = -bleed;
    let minY = -bleed;
    let maxX = sw + bleed;
    let maxY = sh + bleed;
    // The overflow LAYER must key off the real spill, not off the returned rect: that rect is bleed-padded whether
    // or not anything spilled, so a box hanging off by less than one bleed leaves it unchanged. Half a book px of
    // slack, same tolerance the region comparison used before the bleed existed.
    const eps = 0.5 * renderScale;
    let spills = false;
    for (const id of ids) {
      const r0 = this.drawableRects.get(id);
      if (!r0) continue;
      const r = inkExpanded(r0);
      if (r.x < -eps || r.y < -eps || r.x + r.width > sw + eps || r.y + r.height > sh + eps) spills = true;
      if (r.x < minX) minX = r.x;
      if (r.y < minY) minY = r.y;
      if (r.x + r.width > maxX) maxX = r.x + r.width;
      if (r.y + r.height > maxY) maxY = r.y + r.height;
    }

    const w = Math.min(Math.round(maxX - minX), CHROME_MAX_DIM);
    const h = Math.min(Math.round(maxY - minY), CHROME_MAX_DIM);
    return {
      scene: { ox: -minX, oy: -minY, w, h },
      book: { x: minX / renderScale, y: minY / renderScale, w: (maxX - minX) / renderScale, h: (maxY - minY) / renderScale },
      spills,
    };
  }

  private computeContentRegion(
    viewport: { width: number; height: number },
    renderScale: number,
  ): ReturnType<RustOverlayController['regionForIds']> {
    return this.regionForIds(this.overflowIds(), viewport, renderScale);
  }

  // The ONE place the host model enters the overlay: the decode map is built here (renderNow) and the scene from the
  // very same boxes, so stripping a rendition on only one of them would leave the two disagreeing about what the ref
  // holds. The scene is always built by build_scene, which carries no `sourceVersions` mirror — `withoutSourceTiers`
  // says why it drops smart-object source renditions.
  private renderBoxes(): RenderBox[] {
    return [...withoutSourceTiers(this.cfg.getRenderBoxes())];
  }

  // Builds the content scene through wasm build_scene (the print adapter) for every render site.
  private async composeSceneJson(
    input: readonly RenderBox[],
    cache: OverlayCache,
    liveCropOverrides?: ReadonlyMap<string, LiveCropRect>,
    liveTrimOverrides?: ReadonlyMap<string, LiveTrimOverride>,
    // Геометрия сцены. По умолчанию — масштаб буфера разворота; резкая плитка собирает своё окно
    // в разрешении устройства и передаёт СВОЙ масштаб, иначе она была бы растяжкой того же буфера.
    scale = cache.renderScale,
  ): Promise<SpreadScene> {
    if (this.dropEvictedStubs()) this.scheduleRender(0);
    const hint = this.cfg.getPlaceholderPaint?.();
    const boxes = hint ? applyPlaceholderPaint(input, hint.color) : input;
    const binaryRefs = collectBinaryRefs(boxes);
    if (binaryRefs.length) await this.cfg.wasm.prepareBinaryResources(binaryRefs);
    // Альбомный дефолт обводки: подставляется на отрисовке, поэтому едет отдельным полем сцены, а не
    // впечатывается в боксы. Ширина СЫРАЯ, в книжных px — масштаб её накладывает уже рисовальщик.
    const frameStroke = this.cfg.getFrameStroke?.();
    // Кому альбомный дефолт вообще положен. Считается ОТДЕЛЬНО от `zones`: `zones` заодно включает
    // отрисовку технических зон, которую сцена не ведёт (см. ниже), а запрет нужен всегда.
    const strokeScope = cache.zones ? albumStrokeScopeOf(cache.zones) : undefined;
    // The technical zones no longer ride the scene — drawChrome() paints them on the screen-resolution chrome
    // layer, where the camera cannot stretch them. Leaving them here too would draw every zone twice.
    const zones = undefined;
    const renditionScale = this.renditionScale(cache.renderScale);
    // Live crop/trim overrides ride the boxes' own persisted fields — the wire model stays a pure RenderBox mirror.
    const overridden = applyLiveOverridesToBoxes(boxes, liveCropOverrides, liveTrimOverrides);
    const built = await buildRustScene(this.cfg.wasm, {
      boxes: overridden,
      viewport: cache.viewport,
      renderScale: scale,
      fonts: cache.fonts,
      fontRefOf: cache.fontRefOf,
      mappedFace: cache.mappedFace,
      frameStroke,
      albumStrokeScope: strokeScope,
      // Мини-сцена жеста рисует одну рамку, а режет её слой-маска из полного разворота.
      maskLayers: boxes !== cache.boxes ? cache.boxes : undefined,
    });
    const sceneJson = appendZoneShapes(built, scale, zones, this.zoneVisibility(), this.cfg.zoneStyle);
    return { sceneJson, imageRefs: collectImageRefs(overridden, renditionScale, cache.boxes), binaryRefs };
  }

  /**
   * The content scene exactly as the overlay composes it — inline-edit text live-overridden, lifted boxes cut out.
   * Inline edit: once the lift is live the box renders on editCanvas, so the main overlay IS the backdrop
   * (scene minus the box); until then, fall back to live-overriding the text in the full scene.
   * The sharp tile re-composes through this same path at its own render scale, so the two never diverge.
   */
  private composeContentScene(
    cache: OverlayCache,
    excluded: ReadonlySet<string>,
    scale = cache.renderScale,
  ): Promise<SpreadScene> {
    const edited =
      this.editingBoxId && !excluded.has(this.editingBoxId) && this.liveEditText !== undefined
        ? applyTextOverride(
            cache.boxes,
            this.editingBoxId,
            this.liveEditText,
            this.liveEditRect,
            this.liveEditRuns,
            this.liveEditParagraphRuns,
          )
        : cache.boxes;
    return this.composeSceneJson(
      excluded.size ? excludeBoxes(edited, excluded) : edited,
      cache,
      this.cropBoxId ? this.liveCropOverrides : undefined,
      this.trimBoxId ? this.liveTrimOverrides : undefined,
      scale,
    );
  }

  // Selection/hover/chrome paint on separate cheap canvases, so a click or zone toggle never re-composites content.
  private async paint(gen: number, content = false): Promise<void> {
    // Снимаем флаг ПЕРВОЙ строкой, до любого выхода: держание живёт ровно один paint и не утекает в чужой.
    const keepTile = this.keepTileForUpgrade;
    this.keepTileForUpgrade = false;
    const cache = this.cache;
    if (!cache || this.destroyed) return;
    // Keep the last good frame rather than paint evicted photos as holes: the full render re-decodes them.
    if (this.dropEvictedStubs()) {
      this.scheduleRender(0);
      return;
    }

    // a fresh spread render reclaims the view; the tile re-arms on the next settle — кроме репейнта
    // апгрейд-волны на неподвижной камере, где старая плитка остаётся до своей замены (см. applyRenditionWave)
    if (!keepTile) this.hideSharpTile();
    // Кадр хосту отдаём ДО тяжёлой работы, а не между стиранием и заливкой: композиция и растр печатных
    // текстур держат поток дольше секунды, и без коммита экрана скелет хоста до пользователя не доедет.
    // В черновике СЛОЖИВШИЙСЯ блит стоит единицы мс — ждать нечего; тик, у которого блит не сложился,
    // берёт свой кадр ниже, перед самым растром (узнать это заранее нельзя — снимок судится после композиции).
    // Черновик для этой развилки судим ОДИН раз, на входе: флаг снимается и посреди тика (Esc, клик мимо,
    // отпускание ручки на том же значении), генерацию это не бампает, и обе половины проверки по живому
    // флагу разошлись бы — верхняя уже видит черновик, нижняя уже нет, а растр идёт без кадра вовсе.
    const draft = this.draftResolution;
    if (content && !draft) await afterNextCommittedFrame();
    if (gen !== this.generation || this.destroyed) return;
    const { viewport, renderScale, fonts, images } = cache;
    // Which boxes a live lift owns RIGHT NOW — recomputed after the draws below, because this compose is async and
    // the answer can change while it is in flight.
    const excludedAtCompose = this.liftExcludedIds();
    const { sceneJson } = await this.composeContentScene(cache, excludedAtCompose);
    if (gen !== this.generation || this.destroyed) return; // a newer render superseded this async build
    const { width: sw, height: sh } = spreadBufferSize(viewport, renderScale);
    // Main overlay is the spread buffer — content past the design edge clips here; only active boxes overflow (below).
    const canvas = this.ensureOverlayCanvas();
    this.ensureChromeCanvas();
    this.ensureSelectionCanvas();
    this.ensureHandleCanvas();
    this.updateInputSurface();
    try {
      // Тик жеста хоста собирается блитами по снимку входа: полный растр стоит десятки мс на кадр, а ручка
      // ползунка просит кадр на каждое своё движение. Всё, что ниже, не меняется — прямоугольники, регион,
      // хром и выделение по-прежнему считаются от СВЕЖЕЙ сцены, отличается только источник пикселей.
      // Отпускание жеста: буфер держит прошлый кадр, вне области поднятых боксов он уже верен. Буфер другого
      // размера при ensureFrameBuffer стирается — тогда только полный растр.
      const prior = this.frameBuffers.get(canvas);
      const settle =
        excludedAtCompose.size === 0 && prior?.width === sw && prior.height === sh && !this.settleOverridesLive()
          ? this.dragSettleBase
          : undefined;
      const buffer = this.ensureFrameBuffer(canvas, sw, sh);
      let nextRects: Map<string, SceneRect> | undefined;
      let composited = false;
      if (this.draftResolution) {
        nextRects = indexDrawableRects(sceneJson);
        composited = this.paintDraftFrame(buffer, nextRects);
      }

      if (!composited) {
        // Блит не сложился: первый тик жеста (снимок ещё разбирается на слои) или снимок заблокирован —
        // повёрнутая рамка, эффект, подпись в ряду. Дальше идёт ПОЛНЫЙ растр, а не единицы мс, поэтому
        // кадр хосту отдаём здесь — иначе ручка ползунка стоит на месте, пока считается кадр. Ждать
        // безопасно: растр пишет в невидимый буфер, видимый холст получит размер и пиксели одним блоком.
        if (content && draft) await afterNextCommittedFrame();
        if (gen !== this.generation || this.destroyed) return;
        const dirty = settle && this.settleDirtyRegion(settle, cache, (nextRects ??= indexDrawableRects(sceneJson)), sw, sh);
        if (dirty) await this.renderSettlePatch(buffer, sceneJson, fonts, images, dirty);
        else await this.cfg.wasm.renderSceneToCanvas(buffer, sceneJson, fonts, images);
      }

      this.fillPaper(buffer);

      if (gen !== this.generation || this.destroyed) return; // a newer render superseded this one

      // Готовый кадр — на экран. Размер видимого холста едет тем же синхронным блоком, что и пиксели:
      // отдельного «стереть сейчас, дорисовать потом» на нём больше нет.
      this.spreadSceneSize = { width: sw, height: sh };
      this.presentFrame(canvas, buffer);

      // Hit-test truth = painted truth. The scene swap waits for the raster above: between a commit and its settle
      // paint the SCREEN still shows the old glyphs, and swapping early made hitTest answer from geometry the user
      // cannot see — a double-click on a visibly wide box landed outside its already-shrunk hit zone and silently
      // opened nothing. A superseded render (gen check above) never swaps, so state always matches the pixels.
      const prevRects = this.drawableRects;
      this.sceneJson = sceneJson;
      this.drawableRects = nextRects ?? indexDrawableRects(sceneJson);
      // A lifted box is EXCLUDED from this scene but very much alive on its lift canvas — re-seed its previous
      // rect (the renderEditLift trick) so a lift is never indistinguishable from a removal: the prune below
      // would otherwise drop the dragged selection mid-gesture and the transformer would vanish under the hand.
      for (const id of excludedAtCompose) {
        const prev = prevRects.get(id);
        if (prev && !this.drawableRects.has(id)) this.drawableRects.set(id, prev);
      }

      // Region truth = the rects just swapped in: marking it earlier read the PREVIOUS frame during a gesture.
      const region = this.computeContentRegion(viewport, renderScale);
      this.contentRegionScene = region.scene;
      this.overlayRegionBook = region.book;
      this.overlayRegionSpills = region.spills;
      this.ensureHoverCanvas(region.scene.w, region.scene.h);
      this.pruneStaleSelection();
      this.reposition();
      await this.renderActiveOverflow();
      // Mid-gesture the frame belongs to the handle canvas, which follows the pointer; this one is drawn from the
      // committed scene rects and would paint the box's PRE-gesture outline back over the live one (the gesture start
      // cleared it for exactly that reason). A model write during the drag — e.g. a resized text dropping auto-width —
      // is enough to bring this repaint round.
      if (!this.gestureFrameLive()) this.drawSelection(this.selectedIds);
      this.drawHover(this.hoverId);
      this.drawHandles();
      if (this.cropBoxId) this.drawCropPreview();
      this.drawChrome();
      // A live card contents-trade preview rides on the two buffers this method has just rewritten — the raster owns the
      // backdrop the emptied slot is patched onto, and drawHandles() has cleared the drop-target frame. Every
      // repaint (a progressive image tier, a host resize, a zone toggle) therefore has to re-establish it here,
      // or the slot vanishes mid-gesture and the pre-repaint bitmap is stamped back over the fresh page on leave.
      this.reapplyVacatedSlot(false);
      this.reapplyCascade(true);
      this.reapplySwapTarget();
      // Self-heal for every late-landing frame: this paint composed its scene BEFORE the awaits above, and a lift
      // (inline edit, resize/rotate drag) may have claimed boxes since — the frame just drawn then carries the box's
      // committed pixels UNDER the lift's live ones, which the user reads as doubled text. Rather than trying to
      // enumerate every ordering that can produce this, compare the exclusion the frame used with the exclusion the
      // current state demands, and recompose once when they differ. The follow-up frame's sets match, so it settles.
      const excludedNow = this.liftExcludedIds();
      if (excludedNow.size !== excludedAtCompose.size || [...excludedNow].some(id => !excludedAtCompose.has(id))) {
        this.schedulePaint(0);
      }

      if (this.dragSettlePending) {
        this.dragSettlePending = false;
        this.dragSettleBase = undefined;
        // A crop that has already DRAWN its ghost owns this buffer: a settle raised before the crop started (a card
        // swap released into a slow paint) must not eat that fresh ghost on its way out. Keyed on the bitmap and not
        // on the session, because until the decode lands the layer still carries someone else's raster — the dropped
        // card, the previous crop's stand-in — and sparing THAT strands it over the spread for the whole decode.
        if (!this.cropGhostBitmap) this.removeDragCanvas();
        // The cascade layer stood in for the reordered cards until this render; from here the model owns them.
        this.removeCascadeCanvas();
      }

      if (this.editSettlePending) {
        this.editSettlePending = false;
        this.removeEditCanvas();
      }

      if (this.styleSettlePending) {
        this.styleSettlePending = false;
        this.removeStyleCanvas();
      }

      // A full render during a live edit repainted the backdrop (possibly at a new scale) — refresh the lift over it.
      if (this.editingBoxId && this.editLiftReady) this.scheduleEditTick();

      if (content) {
        this.contentOnScreen = true;
        this.screenContentId = this.paintedContentId;
        if (this.screenShowsHostSpread()) this.cfg.onContentPainted?.(this.paintedContentStamp);
      }

      this.scheduleSharpTileRearm();
    } catch (error) {
      console.error('[engine=rust] render failed', error);
      // No frame of the host's spread yet (none at all, or the previous spread's): swallowing would let renderNow
      // finish without a frame and never tell the host (renderCoalesced's onRenderFailed). Only renderNow awaits
      // content paints, so this never goes unhandled.
      if (content && !this.screenShowsHostSpread()) throw error;
    }
  }

  /**
   * Every repaint drops the sharp tile at the top of paint(), because a fresh spread buffer reclaims the view.
   * Past the resolution cap that buffer is precisely the blurry one, so any repaint that does NOT touch the camera
   * — a technical-zone toggle, a progressive image tier, a host resize — used to strand the view on it until the
   * user happened to zoom again. Re-arm on settle instead: debounced so a burst costs one tile, and never during a
   * gesture, where the tile is either CSS-panned along (translate-only) or deliberately down (zoom).
   */
  private scheduleSharpTileRearm(): void {
    if (this.sharpRearmTimer !== undefined) clearTimeout(this.sharpRearmTimer);
    this.sharpRearmTimer = setTimeout(() => {
      this.sharpRearmTimer = undefined;
      if (this.destroyed || !this.cache || !this.camera) return;
      // Три занятых состояния — временные, но их выход НЕ гарантирует кадра: renderNow() уходит из себя раньше
      // paint() (уничтоженный контроллер, нулевой вьюпорт), а re-arm живёт только внутри paint(). Умерев здесь,
      // плитка не вернётся ничем, кроме нового жеста камеры — ровно та мыльная картинка на запаркованном зуме,
      // которую ловит пользователь. Ждём ещё период вместо смерти: таймер один (перевзвод чистит предыдущий),
      // а следующий paint() перевзведёт его сам, так что это не цикл, а отложенная попытка.
      if (this.press?.active || this.renderInFlight || this.renderTimer !== undefined) {
        this.scheduleSharpTileRearm();
        return;
      }

      // Жест хоста живёт вне press (ручка ползунка — обычный DOM-элемент): полноразмерная плитка посреди
      // черновика стоит дороже всего кадра и сразу же гасится следующим тиком.
      if (this.draftResolution) return;
      if (this.manipulationLive()) return; // renderSharpTile refuses too; skipping the call keeps the timer cheap
      if (!this.tileMode()) return;
      void this.renderSharpTile();
    }, SHARP_TILE_REARM_MS);
  }

  /**
   * A box under manipulation lives on its OWN lift layer and is excluded from the scene the tile renders, so a
   * tile arriving mid-session would paint the spread without it over the lift. The pointer is up between crop
   * adjustments, so `press.active` alone does not cover this — the session flags do.
   * The exit of every session repaints (commitCrop/cancelCrop → scheduleRender, a drag settle → paint), and every
   * paint re-arms the tile at its tail, so a skipped tile always comes back once the session is over.
   */
  private manipulationLive(): boolean {
    return !!(this.cropBoxId || this.trimBoxId || this.editingBoxId || this.pathEditBoxId || this.dragCanvas);
  }

  /** Разворот показан крупнее капнутого буфера — резкость держит только плитка окна. */
  private tileMode(): boolean {
    if (!this.camera || !this.cache) return false;
    return shouldSharpTile(this.camera.zoom, this.cache.viewport.width, this.cameraDpr(), this.resolutionCaps());
  }

  private cameraDpr(): number {
    return cameraDpr();
  }

  /**
   * A ds-theme token resolved against the live host, so canvas chrome follows the theme the page is wearing.
   * Empty when the stylesheet hasn't reached this subtree — the caller then skips that stroke rather than
   * inventing a colour. During a gesture the resolved value is cached (see gestureTokenCache) so a raw
   * mousemove stream doesn't force a getComputedStyle call per token per frame.
   */
  private themeToken(token: string): string {
    if (!this.gestureActive) return getComputedStyle(this.cfg.host).getPropertyValue(token).trim();
    let value = this.gestureTokenCache.get(token);
    if (value === undefined) {
      value = getComputedStyle(this.cfg.host).getPropertyValue(token).trim();
      this.gestureTokenCache.set(token, value);
    }

    return value;
  }

  // Canvas2D base transform (scene px → full-host device px) for chrome drawn through the camera so it isn't clipped to
  // the spread. `ex/ey` are the row translate for ctx.setTransform(zoom,0,0,zoom,ex,ey). Undefined outside camera mode.
  private cameraDeviceTransform(): { zoom: number; ex: number; ey: number; outW: number; outH: number } | undefined {
    if (!this.camera || !this.cache) return undefined;
    const host = this.hostSize();
    const args = cameraRenderArgs(this.camera, this.cache.renderScale, host.width, host.height, this.cameraDpr());
    return { zoom: args.zoom, ex: -args.originX * args.zoom, ey: -args.originY * args.zoom, outW: args.outW, outH: args.outH };
  }

  // A stub has no pixels: once a renderer reset freed its texture, only a fresh decode brings the photo back.
  private dropEvictedStubs(): boolean {
    const cache = this.cache;
    if (!cache) return false;
    const evicted = evictedStubRefs(cache.images, (ref, version) => this.cfg.wasm.hasResidentImage?.(ref, version));
    if (evicted.length === 0) return false;
    const gone = new Set(evicted);
    cache.images = Object.fromEntries(Object.entries(cache.images).filter(([ref]) => !gone.has(ref)));
    return true;
  }

  /** Re-paints content + zones from cache (no image re-decode) — for zone-toggle changes. */
  repaint(): void {
    if (this.destroyed || !this.cache) return;
    // Skip while a full render is scheduled/in-flight — it repaints zones from fresh content anyway.
    if (this.renderInFlight || this.renderTimer !== undefined) return;
    void this.paint(++this.generation);
  }

  // A lift starts before any re-render, and idle boxes are clipped to the spread — so the region has to grow to the
  // grabbed boxes here, or the drag buffers would be spread-sized and cut off whatever the drag pulls off-spread.
  private growRegionForLift(ids: readonly string[]): void {
    const cache = this.cache;
    if (!cache) return;
    const region = this.regionForIds(ids, cache.viewport, cache.renderScale);
    this.contentRegionScene = region.scene;
    this.spreadSceneSize = spreadBufferSize(cache.viewport, cache.renderScale);
    this.overlayRegionBook = region.book;
    this.overlayRegionSpills = region.spills;
    this.dragRegionBook = region.book;
    this.ensureHoverCanvas(region.scene.w, region.scene.h);
    this.ensureSelectionCanvas();
    this.reposition();
  }

  /**
   * Lift the boxes onto a separate canvas + repaint the main one WITHOUT them, so the lift reveals real content.
   * `withBakedTiles: false` — a rotation: the commit never turns the tiles, so the lift must not turn them either.
   */
  async beginDrag(pressed: readonly string[], withBakedTiles = true): Promise<void> {
    if (this.destroyed || !this.cache || pressed.length === 0) return;
    // A previous commit's parked stand-in still claims the shared layer: left in place, reposition() keeps routing
    // it to the ghost painter and this lift never gets its region box (nor its page clip) back.
    this.releaseCropSettleGhost();
    const token = ++this.dragRenderToken;
    const clipped = this.clipPassengersOf(pressed);
    this.dragTiles = withBakedTiles ? this.bakedTilesOf(pressed) : new Map();
    const passengers = [...clipped, ...this.dragTiles.keys()];
    this.dragPassengers = new Set(passengers);
    this.dragRastersLive = clipped.length > 0 || this.anyClippedByStandingLayer(pressed);
    this.dragStartRects = new Map(
      [...pressed, ...this.dragTiles.keys()].flatMap(id => {
        const box = findRenderBox(this.cache?.boxes ?? [], id);
        return box ? [[id, { ...box.rect }] as const] : [];
      }),
    );
    const ids = [...pressed, ...passengers];
    this.dragLiftIds = new Set(ids);
    this.growRegionForLift(ids);
    this.applyDragChrome();
    // The lifted box now renders on the region-sized drag canvas (which covers off-spread), so its overflow-canvas copy
    // is a stale duplicate for the whole gesture — hide it (settle render re-arms it on release). Without this a resize/
    // rotate gesture leaves the original box visible on the overflow layer, since that layer is only re-evaluated on
    // paint()/hover — both suppressed during a gesture — and its hide-guard historically only covered a move press.
    this.hideOverflowCanvas();
    // Same for the sharp tile: it bakes the committed scene WITH the lifted box, cannot follow the gesture, and no
    // full paint runs during one — drop it now, the settle render re-arms it.
    this.hideSharpTile();
    // Instant lift: blit the box pixels straight off the rendered overlay (Canvas2D, sub-ms, zero WASM) so the
    // box is grabbable on the SAME frame the gesture starts. The clean transparent WASM lift swaps in a frame later.
    this.instantLift(ids);
    const cardLift = this.press?.mode === 'move' && this.liftsWholeCell(ids);
    if (cardLift && this.dragCanvas) this.dragCanvas.style.opacity = CARD_LIFT_OPACITY;
    // Snapshot for the region-only settle, while drawableRects still hold the pre-gesture geometry. An unpainted
    // settle or render still owed means the screen may lag the cache — that release repaints in full.
    const cache = this.cache;
    this.dragLiftBase =
      cache && !this.dragSettlePending && !this.renderInFlight && this.renderTimer === undefined && !this.settleOverridesLive()
        ? { ids: [...ids], region: this.tightRegionForIds(ids, cache), rest: this.settleFingerprint(cache, ids) }
        : undefined;
    // Backdrop first, lift right behind it in the same tick: the instant blit already shows the box lifted, so the
    // old spot must clear now — queued behind the lift (a whole content-region raster in camera mode) it stood
    // painted for frames, a ghost under the lift. paintDragLayer arms previewInFlight synchronously, so a move
    // landing meanwhile still coalesces behind the lift.
    // A crossing that beat the bbox patch cut the target's restore bitmap with the lifted box's ink inside the patch;
    // re-cut that part the moment the patch lands, while the buffer there is still clean. Later, a crossing tints it.
    const backdrop = this.repaintMainWithout(new Set(ids), region => {
      if (token === this.dragRenderToken && !this.destroyed) this.recaptureCrossedSlot(region);
    });
    await this.paintDragLayer(ids);
    // The clean WASM lift replaced the instant blit's pixels — re-stroke the frame onto it (the pressed boxes only:
    // a passenger is not selected and gets no frame). The origin copy is cut first, so it carries no frame.
    if (token === this.dragRenderToken && !this.destroyed) {
      if (cardLift) this.showCardLiftOrigin();
      this.strokeLiftFrames(pressed);
    }

    const fullRewrite = await backdrop;
    // Two awaits back: the gesture may have ended or been re-grabbed, and this call mutates gesture-shared state
    // (the slot bitmap + the sampled page colour) — a stale lift must not re-cut a slot the new press owns.
    if (token !== this.dragRenderToken || this.destroyed) return;
    // The only moment the backdrop truly shows the source frame empty — sample the page colour here, once. After a
    // bbox patch the buffer under a crossed target may carry the tint, so the bitmap is not re-cut here: its patched
    // part was re-cut as the patch landed, and a crossing after that cut clean pixels.
    this.reapplyVacatedSlot(true, fullRewrite);
    // Same one-shot sample for the card cascade: every vacated slot it tints later is filled with this colour, and
    // this is the one moment the backdrop shows the held card's own slot as bare page.
    if (this.press?.mode === 'card-swap' && this.press.swapSourceId) {
      this.swapSlotFill = this.sampleCardPageFill(this.press.swapSourceId);
      // A crossing that beat the repaint above tinted its slot with the fallback colour (and a full rewrite took
      // that tint away). Re-lay it now that the page colour is known.
      this.reapplyCascade(false);
    }
  }

  // a drag shows no chrome — the lifted content is the indicator; selection/handles return at the drop via the settle render
  private applyDragChrome(): void {
    this.clearSelectionCanvas();
    this.clearHandleCanvas();
    this.drawHover(undefined);
    // The dashed text/invalid frames and the font-error badges are painted from the COMMITTED rects and only on
    // reposition() — during a gesture they would hang at the lifted box's old spot while its pixels ride the drag
    // canvas, so the frame looked nailed down while the text moved. Both painters skip the lifted ids for the whole
    // gesture; the settle render's reposition() brings them back at the new place.
    this.drawChrome();
    this.emitFontErrorBadges();
  }

  private clearHandleCanvas(): void {
    const canvas = this.handleCanvas;
    if (!canvas) return;
    canvas.style.transform = '';
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    ctx.setTransform(1, 0, 0, 1, 0, 0); // a paint pass can leave its device-scale transform in force
    ctx.clearRect(0, 0, canvas.width, canvas.height);
  }

  // Drop a drag's transient chrome: the hover frame and its move-follow translate, and the keep-selection flag.
  private endDragChrome(): void {
    if (this.hoverFadeRaf) {
      cancelAnimationFrame(this.hoverFadeRaf);
      this.hoverFadeRaf = 0;
    }

    this.hoverPaint = null;
    this.clearHoverCanvas();
    this.hoverId = undefined;
  }

  // Copy each lifted box's region from the already-painted overlay onto the drag canvas — no rasterise, no await.
  private instantLift(ids: readonly string[]): void {
    const overlay = this.overlayCanvas;
    if (!overlay) return;
    // Camera: blit spread-buffer pixels for an instant grab; render_region refines off-spread a frame later.
    const ox = this.camera ? this.contentRegionScene.ox : 0;
    const oy = this.camera ? this.contentRegionScene.oy : 0;
    const cw = this.camera ? this.contentRegionScene.w : overlay.width;
    const ch = this.camera ? this.contentRegionScene.h : overlay.height;
    const drag = this.ensureDragCanvas();
    const buffer = this.ensureFrameBuffer(drag, cw, ch);
    const ctx = buffer.getContext('2d');
    if (!ctx) return; // headless/jsdom — the WASM lift still fills the canvas
    ctx.clearRect(0, 0, cw, ch);
    for (const id of ids) {
      const r = this.drawableRects.get(id);
      if (!r) continue;
      const { sx, sy, sw, sh } = liftSourceRect(inkExpanded(r), overlay.width, overlay.height);
      if (sw <= 0 || sh <= 0) continue;
      ctx.drawImage(overlay, sx, sy, sw, sh, sx + ox, sy + oy, sw, sh);
    }

    this.presentFrame(drag, buffer);
    this.strokeLiftFrames(ids);
  }

  // A move drag shows no chrome at all (applyDragChrome), so the lift alone has to say where the BOX lands — and a
  // text frame is far bigger than its ink, so without this the photographer drags naked glyphs and cannot see the
  // frame settle. Stroked INTO the lift pixels: the gesture's CSS transform then carries the outline for free, with
  // the rotation/skew already baked into the raster. Move press only — a transform gesture draws its live frame on
  // the handle canvas, and the trim/crop lift is its own preview.
  private strokeLiftFrames(ids: readonly string[]): void {
    const canvas = this.dragCanvas;
    const renderScale = this.cache?.renderScale;
    if (!canvas || !renderScale || this.press?.mode !== 'move') return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    const ox = this.camera ? this.contentRegionScene.ox : 0;
    const oy = this.camera ? this.contentRegionScene.oy : 0;
    ctx.save();
    ctx.setTransform(1, 0, 0, 1, 0, 0);
    ctx.strokeStyle = SELECTION_STROKE_COLOR;
    ctx.lineWidth = Math.max(1, renderScale / (this.viewScale || 1)); // one CSS px at the current zoom
    ctx.setLineDash([]);
    for (const id of ids) {
      const r = this.drawableRects.get(id);
      if (!r) continue;
      const corners = quadCorners(r);
      ctx.beginPath();
      corners.forEach((p, i) => (i === 0 ? ctx.moveTo(p.x + ox, p.y + oy) : ctx.lineTo(p.x + ox, p.y + oy)));
      ctx.closePath();
      ctx.stroke();
    }

    ctx.restore();
  }

  /** Move the lifted boxes by a book-px delta via a compositor transform — no raster per frame. */
  setDragOffset(dx: number, dy: number): void {
    const s = this.viewScale;
    this.cfg.onDragOffset?.({ ids: [...(this.dragLiftIds ?? [])], x: dx * s, y: dy * s });
    if (this.dragRastersLive) {
      this.previewMoveWithPassengers(dx, dy);
      return;
    }

    const t = `translate(${dx * s}px, ${dy * s}px)`;
    if (this.dragCanvas) this.dragCanvas.style.transform = t;
  }

  /**
   * Перенос слоя-маски с пассажирами: сдвиг компоновщика здесь не годится — он унёс бы и вложенные рамки, а у них
   * должно двигаться только окно видимости. Поэтому кадр за кадром растрируется вся поднятая связка: маска в
   * `начало + сдвиг`, рамки на своих местах, обрезанные по её живому положению. Один растр на кадр анимации.
   */
  private previewMoveWithPassengers(dx: number, dy: number): void {
    const cache = this.cache;
    const starts = this.dragStartRects;
    if (!cache || !starts || !this.dragCanvas) return;
    const walk = (b: RenderBox): void => {
      const start = starts.get(b.id);
      if (start) b.rect = { ...b.rect, x: start.x + dx, y: start.y + dy };
      if (b.type === 'group') for (const c of (b as RenderGroupBox).children) walk(c);
    };

    for (const b of cache.boxes) walk(b);
    this.dragCanvas.style.transform = '';
    if (this.dragMoveRaf !== undefined) return;
    this.dragMoveRaf = requestAnimationFrame(() => {
      this.dragMoveRaf = undefined;
      this.flushDragMove();
    });
  }

  /**
   * Рисует поднятые боксы по текущим rect'ам кэша. Кадр, заставший прошлый рендер в полёте, не теряется:
   * transform уже снят, и без повтора маска с пассажирами замирали бы на шаг позади курсора до следующего
   * движения.
   */
  private flushDragMove(): void {
    const ids = this.dragLiftIds;
    if (!ids || !this.dragCanvas) return;
    if (this.previewInFlight) {
      this.dragMoveDirty = true;
      return;
    }

    this.previewInFlight = true;
    void this.renderLiftTo(this.dragCanvas, [...ids]).finally(() => {
      this.previewInFlight = false;
      if (this.dragMoveDirty) {
        this.dragMoveDirty = false;
        this.flushDragMove();
      }
    });
  }

  /** Среди поднятых есть бокс, обрезанный слоем-маской, который остаётся стоять: его вид меняется по ходу движения. */
  private anyClippedByStandingLayer(ids: readonly string[]): boolean {
    const moving = new Set(ids);
    return ids.some(id => {
      const base = (findRenderBox(this.cache?.boxes ?? [], id) as { maskBaseBoxId?: string } | undefined)?.maskBaseBoxId;
      return !!base && !moving.has(base);
    });
  }

  /** Рамки, обрезанные одним из этих слоёв (`maskBaseBoxId`), кроме тех, что двигаются сами. */
  private clipPassengersOf(ids: readonly string[]): string[] {
    const cache = this.cache;
    if (!cache) return [];
    const moving = new Set(ids);
    const out: string[] = [];
    const walk = (b: RenderBox): void => {
      const base = (b as { maskBaseBoxId?: string }).maskBaseBoxId;
      if (base && moving.has(base) && !moving.has(b.id)) out.push(b.id);
      if (b.type === 'group') for (const c of (b as RenderGroupBox).children) walk(c);
    };

    for (const b of cache.boxes) walk(b);
    return out;
  }

  /**
   * Baked effect tiles (`<owner>-frame-above-0`…) of these boxes. The owner is the one the document resolved per
   * collage (`bakedTileOwner`), so the preview lifts exactly the tiles the commit refits.
   */
  private bakedTilesOf(ids: readonly string[]): Map<string, DragTile> {
    const out = new Map<string, DragTile>();
    const cache = this.cache;
    if (!cache) return out;
    const pressed = new Set(ids);
    // Group children are group-relative: each box carries the offset of its groups to spread space.
    const offsets = new Map<string, { dx: number; dy: number }>();
    const tiles: RenderBox[] = [];
    const walk = (b: RenderBox, dx: number, dy: number): void => {
      offsets.set(b.id, { dx, dy });
      if (b.bakedTileOwner && pressed.has(b.bakedTileOwner) && !pressed.has(b.id)) tiles.push(b);
      if (b.type === 'group') for (const c of (b as RenderGroupBox).children) walk(c, dx + b.rect.x, dy + b.rect.y);
    };

    for (const b of cache.boxes) walk(b, 0, 0);
    for (const tile of tiles) {
      const owner = tile.bakedTileOwner as string;
      const at = offsets.get(owner);
      const own = offsets.get(tile.id);
      if (at && own) out.set(tile.id, { owner, dx: at.dx, dy: at.dy, tileDx: own.dx, tileDy: own.dy });
    }

    return out;
  }

  /** Lifted tiles follow their owner's live rect by the commit's rule (`refitBakedTileRect`), from frozen starts. */
  private refitDragTiles(): void {
    const starts = this.dragStartRects;
    const boxes = this.cache?.boxes ?? [];
    if (!starts) return;
    for (const [id, { owner, dx, dy, tileDx, tileDy }] of this.dragTiles) {
      const tile = findRenderBox(boxes, id);
      const from = starts.get(owner);
      const live = findRenderBox(boxes, owner)?.rect;
      const start = starts.get(id);
      if (!tile || !from || !live || !start) continue;
      const rect = refitBakedTileRect(
        [start.x + tileDx, start.y + tileDy, start.width, start.height],
        [from.x + dx, from.y + dy, from.width, from.height],
        [live.x + dx, live.y + dy, live.width, live.height],
      );
      if (rect) tile.rect = { ...tile.rect, x: rect[0] - tileDx, y: rect[1] - tileDy, width: rect[2], height: rect[3] };
    }
  }

  /** End a drag: settle ASAP (no debounce) — the box keeps showing via the drag canvas until the repaint lands. */
  endDrag(): void {
    this.dropDragLift();
    const base = this.dragLiftBase;
    this.dragLiftBase = undefined;
    this.dragPassengers = new Set();
    this.dragTiles = new Map();
    this.dragRastersLive = false;
    this.dragStartRects = undefined;
    this.dragMoveDirty = false;
    if (this.dragMoveRaf !== undefined) {
      cancelAnimationFrame(this.dragMoveRaf);
      this.dragMoveRaf = undefined;
    }

    if (this.destroyed || !this.dragCanvas) return;
    this.endDragChrome();
    this.settleDragCanvasToSpread();
    this.dragSettlePending = true;
    this.dragSettleBase = base;
    this.dragRenderToken++;
    this.scheduleRender(0);
  }

  /** The host draws the spine badge off the reported offset, so no lift may end without reporting null. */
  private dropDragLift(): void {
    this.dragLiftIds = undefined;
    this.cfg.onDragOffset?.(null);
    this.liftOriginCanvas?.remove();
    this.liftOriginCanvas = undefined;
    if (this.dragCanvas) this.dragCanvas.style.opacity = '';
  }

  /** A vignette cell (a group carrying a people role) lifted whole — a single box inside it is not. Checked on the
   *  leaves: setSelection expands a whole-group selection, so the group id itself never reaches the lift. Searched at
   *  any depth: the design host wraps the spread in one typed root group, which holds the cells and is no cell. */
  private liftsWholeCell(ids: readonly string[]): boolean {
    const isPeopleGroup = (b: RenderBox): boolean => b.type === 'group' && !!(b as RenderGroupBox).peopleType;
    const lifted = (boxes: readonly RenderBox[]): boolean =>
      boxes.some(b => {
        if (b.type !== 'group') return false;
        const { children } = b as RenderGroupBox;
        if (lifted(children)) return true;
        if (!isPeopleGroup(b) || children.some(isPeopleGroup)) return false;
        const leaves = collectLeafIds(children);
        return leaves.length > 0 && leaves.every(leaf => ids.includes(leaf));
      });
    return lifted(this.cache?.boxes ?? []);
  }

  /** Cut from the clean lift BEFORE the backdrop repaints without the cell, so the start slot never reads empty. */
  private showCardLiftOrigin(): void {
    const drag = this.dragCanvas;
    if (!drag) return;
    const origin = document.createElement('canvas');
    // eslint-disable-next-line no-restricted-syntax -- fresh canvas outside the DOM: nothing to clear
    origin.width = drag.width;
    // eslint-disable-next-line no-restricted-syntax -- fresh canvas outside the DOM: nothing to clear
    origin.height = drag.height;
    origin.getContext('2d')?.drawImage(drag, 0, 0);
    origin.style.cssText = drag.style.cssText; // same region box and page clip as the lift
    origin.style.transform = '';
    origin.style.opacity = CARD_ORIGIN_OPACITY;
    origin.setAttribute('data-lift-origin', '');
    this.liftOriginCanvas?.remove();
    this.cfg.host.insertBefore(origin, drag);
    this.liftOriginCanvas = origin;
  }

  /** Spread switch under a live press/transform whose release never arrived: the lifted boxes belong to the spread
   *  that left — drop the gesture unwritten and take its lift layer down, else it hangs over the next spread. */
  abandonGesture(): void {
    this.gesture = undefined;
    this.reflowLive = undefined;
    this.cancelResizePreview();
    this.disarmWindowDrag(this.onPointerMove, this.onPointerUp);
    this.endPress();
    this.dropDragLift();
    this.dragLiftBase = undefined;
    this.dragRenderToken++; // an in-flight lift raster must not present onto the next spread
    if (!this.cropBoxId && !this.trimBoxId && !this.cropSettleGhost) this.removeDragCanvas();
  }

  // Crop, trim and inline-edit overrides reach the scene past `cache.boxes`, where the settle fingerprint cannot see
  // them change, and a draft frame leaves low-resolution pixels outside the dirty region: only a full raster is safe.
  private settleOverridesLive(): boolean {
    return !!(this.cropBoxId || this.trimBoxId || this.editingBoxId) || this.draftResolution;
  }

  // Everything a region-only settle assumes did NOT change since the grab: the model minus the lifted boxes, the
  // decoded images, the fonts, the geometry and the scene-wide paint inputs. Any difference → full raster.
  private settleFingerprint(cache: OverlayCache, ids: readonly string[]): string {
    const lifted = new Set(ids);
    const images = Object.keys(cache.images)
      .filter(ref => !lifted.has(ref))
      .sort()
      .map(ref => {
        const img = cache.images[ref];
        return `${ref}:${img.version}:${img.width}x${img.height}:${img.data.length}`;
      });
    return JSON.stringify([
      excludeBoxes(cache.boxes, lifted),
      cache.viewport,
      cache.renderScale,
      cache.zones,
      Object.keys(cache.fonts).sort(),
      images,
      this.zoneVisibility(),
      this.cfg.getFrameStroke?.(),
      this.cfg.getPlaceholderPaint?.(),
    ]);
  }

  // Settle dirty region = the lifted boxes' pre-gesture region ∪ their settled one (+ AA margin), clamped to the
  // spread buffer. Undefined when anything else in the model moved since the grab.
  private settleDirtyRegion(
    base: DragSettleBase,
    cache: OverlayCache,
    rects: ReadonlyMap<string, SceneRect>,
    sw: number,
    sh: number,
  ): { ox: number; oy: number; w: number; h: number } | undefined {
    if (typeof this.cfg.wasm.renderRegionToCanvas !== 'function') return undefined;
    if (this.settleFingerprint(cache, base.ids) !== base.rest) return undefined;
    const pre = base.region;
    const post = this.tightRegionForIds(base.ids, cache, rects);
    const x0 = Math.max(0, Math.min(pre.ox, post.ox) - SETTLE_MARGIN_PX);
    const y0 = Math.max(0, Math.min(pre.oy, post.oy) - SETTLE_MARGIN_PX);
    const x1 = Math.min(sw, Math.max(pre.ox + pre.w, post.ox + post.w) + SETTLE_MARGIN_PX);
    const y1 = Math.min(sh, Math.max(pre.oy + pre.h, post.oy + post.h) + SETTLE_MARGIN_PX);
    if (x1 <= x0 || y1 <= y0) return undefined;
    return { ox: x0, oy: y0, w: x1 - x0, h: y1 - y0 };
  }

  // The FULL scene rastered only inside `region` (every box under/over the moved one keeps its order), laid into
  // the spread buffer at the region origin; the rest of the buffer keeps the previous frame.
  private async renderSettlePatch(
    buffer: HTMLCanvasElement,
    sceneJson: string,
    fonts: Record<string, Uint8Array>,
    images: Record<string, RustImage>,
    region: { ox: number; oy: number; w: number; h: number },
  ): Promise<void> {
    const patch = document.createElement('canvas');
    // eslint-disable-next-line no-restricted-syntax -- холст вне DOM: стирать нечего
    patch.width = region.w;
    // eslint-disable-next-line no-restricted-syntax -- холст вне DOM: стирать нечего
    patch.height = region.h;
    // render_region: pixel (0,0) maps to scene (-ox,-oy) — a window at (x0,y0) needs the NEGATED origin.
    await this.cfg.wasm.renderRegionToCanvas(patch, sceneJson, fonts, images, -region.ox, -region.oy);
    const ctx = buffer.getContext('2d');
    if (!ctx) return;
    ctx.clearRect(region.ox, region.oy, region.w, region.h);
    ctx.drawImage(patch, region.ox, region.oy);
  }

  // The lift layer stands in for the overlay until the settle render lands — hundreds of ms away, since that render
  // re-resolves fonts, decodes images and rasterises the whole spread. Shrink it onto the spread the moment the gesture
  // ends: everything past the design edge is gone on the release frame instead of hanging in the pasteboard, and the
  // layer stops belonging to a region that is about to shrink under it (a later reposition would slide it sideways).
  private settleDragCanvasToSpread(): void {
    const canvas = this.dragCanvas;
    const cache = this.cache;
    if (!canvas || !cache || !this.camera) return;
    const { width, height } = spreadBufferSize(cache.viewport, cache.renderScale);
    const baked = this.bakeDragPixels(canvas, width, height);
    if (!baked) return; // a real layer we cannot redraw — leave it alone rather than blank it

    canvas.style.transform = '';
    canvas.style.transformOrigin = '';
    this.presentFrame(canvas, baked);
    this.dragRegionBook = { x: 0, y: 0, w: cache.viewport.width, h: cache.viewport.height };
    this.positionRegionCanvas(canvas, this.dragRegionBook);
  }

  // Canvas-local px → spread px: undo the region origin, then replay the element's own CSS transform (a move's
  // translate, a rotate/skew matrix) about its origin, scaled from CSS px into backing px.
  private bakeDragPixels(canvas: HTMLCanvasElement, width: number, height: number): HTMLCanvasElement | undefined {
    const cssWidth = parseFloat(canvas.style.width);
    if (!cssWidth) return undefined;
    const baked = document.createElement('canvas');
    // eslint-disable-next-line no-restricted-syntax -- холст вне DOM: стирать нечего
    baked.width = width;
    // eslint-disable-next-line no-restricted-syntax -- холст вне DOM: стирать нечего
    baked.height = height;
    const ctx = baked.getContext('2d');
    if (!ctx) return undefined;
    // jsdom composites nothing and has no DOMMatrix: there are no pixels to carry across, only geometry to settle.
    if (typeof DOMMatrix === 'undefined') return baked;

    const perCssPx = canvas.width / cssWidth;
    const style = getComputedStyle(canvas);
    const m = new DOMMatrix(style.transform === 'none' ? undefined : style.transform);
    const [originX = 0, originY = 0] = style.transformOrigin.split(' ').map(parseFloat);
    const { ox, oy } = this.contentRegionScene;
    ctx.translate(-ox + originX * perCssPx, -oy + originY * perCssPx);
    ctx.transform(m.a, m.b, m.c, m.d, m.e * perCssPx, m.f * perCssPx);
    ctx.translate(-originX * perCssPx, -originY * perCssPx);
    ctx.drawImage(canvas, 0, 0);
    return baked;
  }

  private async paintDragLayer(ids: readonly string[]): Promise<SceneRect[]> {
    const cache = this.cache;
    if (!cache) return [];
    // The first lift owns the shared drag canvas like any live preview: a move that lands meanwhile waits and is
    // painted right after it, instead of rasterising at another size and offset into the same buffer.
    this.previewInFlight = true;
    try {
      // Drag canvas matches the content region so a box dragged off-spread stays whole on the lift layer.
      return await this.renderLiftTo(this.ensureDragCanvas(), ids, {
        width: this.contentRegionScene.w,
        height: this.contentRegionScene.h,
      });
    } finally {
      this.previewInFlight = false;
      if (this.dragMoveDirty) {
        this.dragMoveDirty = false;
        this.flushDragMove();
      }
    }
  }

  private async renderContentTo(
    canvas: HTMLCanvasElement,
    sceneJson: string,
    fonts: Record<string, Uint8Array>,
    images: Record<string, RustImage>,
    origin: { ox: number; oy: number } = this.contentRegionScene,
  ): Promise<void> {
    if (this.camera) {
      const { ox, oy } = origin;
      await this.cfg.wasm.renderRegionToCanvas(canvas, sceneJson, fonts, images, ox, oy);
    } else {
      await this.cfg.wasm.renderSceneToCanvas(canvas, sceneJson, fonts, images);
    }
  }

  // Whether the active region actually spills past the spread (else the overflow layer is a needless copy).
  // The answer comes from regionForIds, which measures the boxes against the BARE spread — re-deriving it from the
  // returned rect cannot work, since that rect is bleed-padded for every box, spilling or not.
  private activeOverflows(): boolean {
    return !!this.camera && !!this.cache && this.overflowIds().length > 0 && this.overlayRegionSpills;
  }

  private async renderActiveOverflow(): Promise<void> {
    if (!this.activeOverflows() || this.press?.active || this.gesture) {
      this.hideOverflowCanvas();
      return;
    }

    const canvas = this.ensureOverflowCanvas();
    await this.renderLiftTo(canvas, this.overflowIds(), {
      width: this.contentRegionScene.w,
      height: this.contentRegionScene.h,
    });
    this.clearSpreadFromOverflow(canvas);
    this.positionRegionCanvas(canvas);
    canvas.style.display = 'block';
  }

  // The overflow layer paints OVER the main overlay, so an opaque active box (e.g. a full-bleed background) would hide the
  // rest of the spread. Erase the spread rect from it — only the off-spread part stays on top; on-spread content (in z-order)
  // shows through from the main overlay beneath.
  private clearSpreadFromOverflow(canvas: HTMLCanvasElement): void {
    const cache = this.cache;
    if (!cache) return;
    const { ox, oy } = this.contentRegionScene;
    const sw = Math.round(cache.viewport.width * cache.renderScale);
    const sh = Math.round(cache.viewport.height * cache.renderScale);
    canvas.getContext('2d')?.clearRect(ox, oy, sw, sh);
  }

  // CSS-place a region-buffer canvas: its book origin (≤ 0) can start left/above the spread.
  private positionRegionCanvas(canvas: HTMLCanvasElement, region?: { x: number; y: number; w: number; h: number }): void {
    const { scale, viewportOffset: vo, chromeOffset: co } = this.viewState;
    const rb = region ?? this.overlayRegionBook;
    const box: LayerBox = {
      left: co.x - (vo.x - rb.x) * scale,
      top: co.y - (vo.y - rb.y) * scale,
      width: rb.w * scale,
      height: rb.h * scale,
    };
    setLayerBox(canvas, box);
    // Painted content (hover wash, drag/edit lifts) ends where the page does. The selection frame does not — but it
    // is no longer a region buffer at all, so only page-bound painters reach here.
    const surface = this.surfaceLayerBox(this.sheetLayerBox());
    if (!surface) return;
    this.clipToSurface(canvas, box, surface);
  }

  // Selection/hover changed without a content re-render: recompute the region and refresh the overflow + frames.
  private async refreshActiveOverflow(): Promise<void> {
    const cache = this.cache;
    if (!cache || this.destroyed) return;
    const region = this.computeContentRegion(cache.viewport, cache.renderScale);
    const prev = this.contentRegionScene;
    const sameRegion =
      region.scene.ox === prev.ox && region.scene.oy === prev.oy && region.scene.w === prev.w && region.scene.h === prev.h;
    // Unconditional: a sub-bleed spill leaves the region rect identical, so gating this on `sameRegion` would
    // keep the previous selection's answer.
    this.overlayRegionSpills = region.spills;
    if (!sameRegion) {
      this.contentRegionScene = region.scene;
      this.spreadSceneSize = spreadBufferSize(cache.viewport, cache.renderScale);
      this.overlayRegionBook = region.book;
      this.ensureHoverCanvas(region.scene.w, region.scene.h);
      this.ensureSelectionCanvas();
      this.reposition();
    }

    await this.renderActiveOverflow();
  }

  /** Только заводит холст: размер и пиксели даёт `presentFrame` — см. `ensureOverlayCanvas`. */
  private ensureOverflowCanvas(): HTMLCanvasElement {
    let canvas = this.overflowCanvas;
    if (!canvas) {
      canvas = document.createElement('canvas');
      canvas.style.position = 'absolute';
      canvas.style.pointerEvents = 'none'; // pure visual; clicks fall through to the content overlay
      canvas.style.zIndex = '10';
      canvas.setAttribute('data-overflow', '');
      this.cfg.host.appendChild(canvas);
      this.overflowCanvas = canvas;
    }

    return canvas;
  }

  private hideOverflowCanvas(): void {
    if (this.overflowCanvas) this.overflowCanvas.style.display = 'none';
  }

  /** Лифт — тоже видимый слой с контентом: растр идёт в невидимый буфер и уезжает на экран одним `presentFrame`.
   *  `size` по умолчанию — текущий размер холста: превью тримa и ресайза целятся в уже показанный слой жеста. */
  private async renderLiftTo(
    canvas: HTMLCanvasElement,
    ids: readonly string[],
    size: { width: number; height: number } = { width: canvas.width, height: canvas.height },
  ): Promise<SceneRect[]> {
    const cache = this.cache;
    if (!cache) return [];
    const dragToken = this.dragRenderToken;
    const isDragCanvas = canvas === this.dragCanvas;
    const stale = (): boolean => this.destroyed || (isDragCanvas && dragToken !== this.dragRenderToken);
    const include = new Set(ids);
    const boxes = filterBoxes(cache.boxes, id => include.has(id));
    // A trim drag previews through this path, so the lifted box must carry the in-progress window/content.
    const { sceneJson, imageRefs } = await this.composeSceneJson(
      boxes,
      cache,
      undefined,
      this.trimBoxId ? this.liveTrimOverrides : undefined,
    );
    const images: Record<string, RustImage> = {};
    for (const { ref } of imageRefs) if (cache.images[ref]) images[ref] = cache.images[ref];
    // Gesture ended/re-grabbed mid-compose: the grab-time origin would now bleed into the spread-sized canvas — drop it.
    if (stale()) return [];
    const rects = indexDrawableRects(sceneJson);
    const buffer = this.ensureFrameBuffer(canvas, size.width, size.height);
    if (!this.camera && typeof this.cfg.wasm.renderRegionToCanvas === 'function') {
      await this.renderLiftPatch(buffer, sceneJson, cache, ids, rects, images);
    } else {
      await this.renderContentTo(buffer, sceneJson, cache.fonts, images);
    }

    if (stale()) return []; // gesture moved on (or the overlay died) while the raster was in flight
    this.presentFrame(canvas, buffer);
    return Array.from(rects.values());
  }

  // Non-camera grab/resize lift: paint the mini-scene into a scratch canvas sized to the lifted boxes' own bbox
  // (+bleed), then blit that patch onto the (spread-sized) lift canvas — the WASM raster stays bbox-sized instead
  // of a full render() of a scene that only has 1-2 boxes in it.
  private async renderLiftPatch(
    canvas: HTMLCanvasElement,
    sceneJson: string,
    cache: OverlayCache,
    ids: readonly string[],
    rects: ReadonlyMap<string, SceneRect>,
    images: Record<string, RustImage>,
  ): Promise<void> {
    const region = this.tightRegionForIds(ids, cache, rects);
    const patch = this.ensurePatchCanvas('lift', region.w, region.h);
    await this.cfg.wasm.renderRegionToCanvas(patch, sceneJson, cache.fonts, images, -region.ox, -region.oy);
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    ctx.drawImage(patch, region.ox, region.oy);
  }

  // Repaints the main overlay with the scene MINUS the lifted boxes — background + siblings stay put, so the
  // lifted box (on the drag canvas above) reveals the real content beneath it, not a hole or a white patch.
  // Resolves true when the whole overlay was rewritten (no region renderer); a bbox patch reports its rect to
  // `onPatch` synchronously, right as it hits the buffer.
  private async repaintMainWithout(exclude: ReadonlySet<string>, onPatch?: (region: SpreadRegion) => void): Promise<boolean> {
    const canvas = this.overlayCanvas;
    if (!canvas) return false;
    if (this.cache && typeof this.cfg.wasm.renderRegionToCanvas === 'function') {
      await this.renderBackdropPatch(canvas, exclude, onPatch);
      return false;
    }

    await this.renderBackdropTo(canvas, exclude);
    return true;
  }

  // Grab backdrop: everywhere outside the lifted boxes' own bbox is already correct on the main overlay (the last
  // full paint) — only that bbox needs a scene-without-boxes patch, not a full-spread re-render of every sibling box.
  // The main overlay is the spread buffer in camera mode too (the camera only places it via CSS), so the spread-px
  // region holds in both. Renders the patch into a scratch canvas, then blits just that rect in place.
  private async renderBackdropPatch(
    canvas: HTMLCanvasElement,
    exclude: ReadonlySet<string>,
    onPatch?: (region: SpreadRegion) => void,
  ): Promise<void> {
    const cache = this.cache;
    if (!cache) return;
    const ids = [...exclude];
    const boxes = excludeBoxes(cache.boxes, exclude);
    const { sceneJson, imageRefs } = await this.composeSceneJson(boxes, cache);
    const images: Record<string, RustImage> = {};
    for (const { ref } of imageRefs) if (cache.images[ref]) images[ref] = cache.images[ref];
    const region = this.tightRegionForIds(ids, cache);
    const patch = this.ensurePatchCanvas('backdrop', region.w, region.h);
    await this.cfg.wasm.renderRegionToCanvas(patch, sceneJson, cache.fonts, images, -region.ox, -region.oy);
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    ctx.clearRect(region.ox, region.oy, region.w, region.h);
    ctx.drawImage(patch, region.ox, region.oy);
    this.fillPaper(canvas);
    onPatch?.(region);
  }

  private ensurePatchCanvas(which: 'lift' | 'backdrop', width: number, height: number): HTMLCanvasElement {
    let canvas = which === 'lift' ? this.liftPatchCanvas : this.backdropPatchCanvas;
    if (!canvas) {
      canvas = document.createElement('canvas'); // never appended — pure scratch, blitted onto a shown canvas
      if (which === 'lift') this.liftPatchCanvas = canvas;
      else this.backdropPatchCanvas = canvas;
    }

    // eslint-disable-next-line no-restricted-syntax -- холст вне DOM: стирать нечего
    if (canvas.width !== width) canvas.width = width;
    // eslint-disable-next-line no-restricted-syntax -- холст вне DOM: стирать нечего
    if (canvas.height !== height) canvas.height = height;
    return canvas;
  }

  // Tight bbox (spread px, clamped to the spread) of the given ids' drawable rects, padded by their max effect
  // bleed — the shared region math for the drag/style lifts and the backdrop patch. A rotated/sheared box draws
  // outside its upright rect, so the bbox goes through liftSourceRect (transform-aware) rather than the raw rect.
  // Groups expand to their leaf ids first (only leaves carry a drawable rect); `liveRects` (the just-composed
  // mini-scene) is preferred over the possibly-stale cached rect so a live resize/style tick keeps growing the
  // region with the box.
  private tightRegionForIds(
    ids: readonly string[],
    cache: OverlayCache,
    liveRects?: ReadonlyMap<string, SceneRect>,
  ): { ox: number; oy: number; w: number; h: number } {
    const leafIds: string[] = [];
    for (const id of ids) {
      const box = findRenderBox(cache.boxes, id);
      if (box?.type === 'group') collectLeafIds((box as RenderGroupBox).children, leafIds);
      else leafIds.push(id);
    }

    const s = cache.renderScale || 1;
    let pad = 0;
    for (const id of leafIds) pad = Math.max(pad, effectBleed(findRenderBox(cache.boxes, id)?.effects));
    pad *= s;
    const spreadW = Math.round(cache.viewport.width * s);
    const spreadH = Math.round(cache.viewport.height * s);
    let x0 = Infinity;
    let y0 = Infinity;
    let x1 = -Infinity;
    let y1 = -Infinity;
    for (const id of leafIds) {
      const rect = liveRects?.get(id) ?? this.drawableRects.get(id);
      if (!rect) continue;
      const { sx, sy, sw, sh } = liftSourceRect(inkExpanded(rect), spreadW, spreadH, pad);
      x0 = Math.min(x0, sx);
      y0 = Math.min(y0, sy);
      x1 = Math.max(x1, sx + sw);
      y1 = Math.max(y1, sy + sh);
    }

    // no known rect (e.g. a bare group) — safe fallback
    if (!Number.isFinite(x0)) return { ox: 0, oy: 0, w: spreadW, h: spreadH };
    // A leaf fully past the spread edge degenerates to an unclamped origin with zero size — cap the union
    // so the region never outgrows the spread.
    x1 = Math.min(x1, spreadW);
    y1 = Math.min(y1, spreadH);
    return { ox: x0, oy: y0, w: Math.max(1, x1 - x0), h: Math.max(1, y1 - y0) };
  }

  /** Подложка без поднятых боксов — на ВИДИМЫЙ холст (входы: лифт правки, лифт стиля, захват без регионального
   *  рендера). Растр идёт в невидимый буфер и уезжает на экран одним `presentFrame`: между стиранием
   *  холста и приходом пикселей лежит целый растр сцены, и всё это время экран стоял бы бумажным. */
  private async renderBackdropTo(canvas: HTMLCanvasElement, exclude: ReadonlySet<string>): Promise<void> {
    const cache = this.cache;
    if (!cache) return;
    // Подложка целится в буфер ТОГО ЖЕ видимого холста, что и полный `paint()`, и растр её держит долго.
    // Без проверки поколения кадр, доехавший последним, побеждает: свежий полный кадр уже на экране, а
    // подложка, стартовавшая раньше, кладёт поверх него сцену прошлого поколения. Гейт тот же, что у
    // `paint()` перед его `presentFrame`; догонит экран следующий кадр нового поколения.
    const gen = this.generation;
    const { width, height } = spreadBufferSize(cache.viewport, cache.renderScale);
    const buffer = this.ensureFrameBuffer(canvas, width, height);
    await this.renderSliceTo(buffer, excludeBoxes(cache.boxes, exclude), cache, true);
    if (this.destroyed || gen !== this.generation) return;
    this.presentFrame(canvas, buffer);
  }

  /** Часть сцены в свой буфер. `paper`: подложка обязана быть непрозрачной, накладка — наоборот прозрачной,
   *  иначе бумага накладки закрасила бы всё, что лежит под ней. */
  private async renderSliceTo(
    canvas: HTMLCanvasElement,
    boxes: readonly RenderBox[],
    cache: OverlayCache,
    paper: boolean,
  ): Promise<void> {
    const { sceneJson, imageRefs } = await this.composeSceneJson(boxes, cache);
    const images: Record<string, RustImage> = {};
    for (const { ref } of imageRefs) if (cache.images[ref]) images[ref] = cache.images[ref];
    await this.cfg.wasm.renderSceneToCanvas(canvas, sceneJson, cache.fonts, images);
    if (paper) this.fillPaper(canvas);
  }

  private removeDragCanvas(): void {
    this.dragRegionBook = undefined;
    this.releaseCropSettleGhost(); // the parked placement exists only to be drawn on this layer
    this.dragCanvas?.remove();
    this.dragCanvas = undefined;
  }

  private releaseCropSettleGhost(): void {
    this.cropSettleGhost?.bitmap.close();
    this.cropSettleGhost = undefined;
  }

  // ==================== Inline-edit lift — live typing renders ONLY the edited box ====================

  // Effect spill in book px: shadows/glows/strokes paint past the drawable rect, so the tick region must
  // include them or typing would clip the effect at the region edge. Over-padding is harmless.
  private editEffectPad(box: RenderBox | undefined): number {
    return effectBleed(box?.effects);
  }

  /** Enter the edit lift: the box moves onto its own region canvas and the main overlay becomes the backdrop
   *  (scene minus the box) — entry costs one region render for the lift plus one full render for the backdrop. */
  private async enterEditLift(id: string): Promise<void> {
    if (!this.cache || this.destroyed) return;
    // No region renderer (headless/jsdom, older wasm mock) → stay on the debounced full-paint fallback.
    if (typeof this.cfg.wasm.renderRegionToCanvas !== 'function') return;
    this.editSettlePending = false;
    // Session token, NOT editTickToken — renderEditLift bumps the tick token itself, so entry needs its own staleness.
    const session = ++this.editSessionToken;
    // Lift first, backdrop second: the box is briefly double-drawn (identically) instead of missing a frame.
    await this.renderEditLift(id);
    if (this.destroyed || this.editingBoxId !== id || session !== this.editSessionToken) return;
    const overlay = this.overlayCanvas;
    if (!overlay) return;
    this.generation++; // supersede any in-flight full paint so it can't re-bake the box over the backdrop
    await this.renderBackdropTo(overlay, this.editLiftIds(id));
    if (this.destroyed || this.editingBoxId !== id || session !== this.editSessionToken) return;

    this.editLiftReady = true;
    this.scheduleEditTick(); // text may have changed while the backdrop rendered — catch up
  }

  private scheduleEditTick(): void {
    if (this.destroyed || this.editTickRaf !== undefined) return;
    const tick = (): void => {
      this.editTickRaf = undefined;
      void this.paintEditTick();
    };

    this.editTickRaf =
      typeof requestAnimationFrame === 'function' ? requestAnimationFrame(tick) : (setTimeout(tick, 16) as unknown as number);
  }

  private async paintEditTick(): Promise<void> {
    const id = this.editingBoxId;
    if (this.destroyed || !this.editLiftReady || !id) return;
    if (this.editTickInFlight) {
      this.editTickPending = true;
      return;
    }

    this.editTickInFlight = true;
    try {
      await this.renderEditLift(id);
    } finally {
      this.editTickInFlight = false;
      if (this.editTickPending && !this.destroyed) {
        this.editTickPending = false;
        this.scheduleEditTick();
      }
    }
  }

  // Mini-scene of ONLY this box (live text spliced in) → region-scoped raster: a keystroke costs one shaped
  // box + a bbox-sized buffer, not the whole spread.
  private async renderEditLift(id: string): Promise<void> {
    const cache = this.cache;
    if (!cache || this.destroyed) return;
    const token = ++this.editTickToken;
    // A text cut by a live clip run lifts the whole scene over paper, still in its own region: only the full
    // composite lays the clippers on the live glyphs with their blend and order. Baked clippers stay out, as below.
    const inRun = this.inLiveClipRun(id);
    const subset = inRun
      ? excludeBoxes(cache.boxes, new Set(clipCompanionRenderIds(cache.boxes, [id])))
      : filterBoxes(cache.boxes, bid => bid === id);
    if (subset.length === 0) return;
    const boxes =
      this.liveEditText !== undefined
        ? applyTextOverride(subset, id, this.liveEditText, this.liveEditRect, this.liveEditRuns, this.liveEditParagraphRuns)
        : subset;
    const { sceneJson, imageRefs } = await this.composeSceneJson(boxes, cache);
    if (this.destroyed || token !== this.editTickToken || this.editingBoxId !== id) return;
    const rects = indexDrawableRects(sceneJson);
    // Empty text collapses the drawable — keep the previous region so stale glyphs still get cleared.
    const rect = rects.get(id) ?? this.drawableRects.get(id);
    if (!rect) return;
    const s = cache.renderScale || 1;
    const pad = this.editEffectPad(findRenderBox(cache.boxes, id)) * s;
    const spreadW = Math.round(cache.viewport.width * s);
    const spreadH = Math.round(cache.viewport.height * s);
    // Rotation/shear lives in the draw affine, not in rect — an upright bbox would clip a turned box's glyphs.
    const { sx, sy, sw, sh } = liftSourceRect(inkExpanded(rect), spreadW, spreadH, pad);
    const region = { ox: sx, oy: sy, w: Math.max(1, sw), h: Math.max(1, sh) };
    const canvas = this.ensureEditCanvas();
    const buffer = this.ensureFrameBuffer(canvas, region.w, region.h);
    this.editRegionBook = { x: region.ox / s, y: region.oy / s, w: region.w / s, h: region.h / s };
    this.positionRegionCanvas(canvas, this.editRegionBook);
    const images: Record<string, RustImage> = {};
    for (const { ref } of imageRefs) if (cache.images[ref]) images[ref] = cache.images[ref];
    // render_region: pixel (0,0) maps to scene (-ox,-oy) — a window at (x0,y0) needs the NEGATED origin.
    await this.cfg.wasm.renderRegionToCanvas(buffer, sceneJson, cache.fonts, images, -region.ox, -region.oy);
    if (this.destroyed || token !== this.editTickToken) return;
    if (inRun) this.fillPaper(buffer);
    this.presentFrame(canvas, buffer);
    // The backdrop scene dropped this box from the full hit index — keep its rect current from the mini-scene.
    for (const [rid, r] of rects) this.drawableRects.set(rid, r);
    // Chrome lives on its own canvases and is not part of this raster, so an auto-width frame that just grew around
    // the typed glyphs would keep the outline it was entered with until blur. Redraw it from the rects above.
    this.drawSelection(this.selectedIds);
    this.drawHandles();
  }

  /** Только заводит холст: размер и пиксели даёт `presentFrame` — см. `ensureOverlayCanvas`. */
  private ensureEditCanvas(): HTMLCanvasElement {
    let canvas = this.editCanvas;
    if (!canvas) {
      canvas = document.createElement('canvas');
      canvas.style.position = 'absolute';
      canvas.style.pointerEvents = 'none';
      canvas.style.zIndex = '10';
      this.cfg.host.appendChild(canvas);
      this.editCanvas = canvas;
    }

    return canvas;
  }

  private removeEditCanvas(): void {
    this.editRegionBook = undefined;
    this.editCanvas?.remove();
    this.editCanvas = undefined;
  }

  // The lift stands in for the box until the settle render (with the committed text) lands — same rule as drag.
  private endEditLift(): void {
    this.editLiftReady = false;
    this.editTickPending = false;
    this.editTickToken++;
    this.editSessionToken++;
    if (this.editTickRaf !== undefined) {
      if (typeof cancelAnimationFrame === 'function') cancelAnimationFrame(this.editTickRaf);
      else clearTimeout(this.editTickRaf as unknown as ReturnType<typeof setTimeout>);
      this.editTickRaf = undefined;
    }

    this.editSettlePending = true;
  }

  // ==================== Style lift — a shadow/stroke slider drag renders ONLY the affected boxes ====================

  /** Enter the style lift: the boxes move onto their own region canvas and the main overlay becomes the
   *  backdrop (scene minus the boxes) — a slider tick then costs a bbox-sized region, not a full spread. */
  async beginStyleLift(ids: readonly string[]): Promise<void> {
    if (!this.cache || this.destroyed || ids.length === 0) return;
    // No region renderer (headless/jsdom, older wasm mock) → patch ticks degrade to debounced full paints.
    if (typeof this.cfg.wasm.renderRegionToCanvas !== 'function') {
      this.styleLiftIds = [...ids];
      return;
    }

    this.styleSettlePending = false;
    this.styleLiftIds = [...ids];
    const session = ++this.styleSessionToken;
    // Lift first, backdrop second: the boxes are briefly double-drawn (identically) instead of missing a frame.
    await this.renderStyleLift();
    if (this.destroyed || session !== this.styleSessionToken) return;
    const overlay = this.overlayCanvas;
    if (!overlay) return;
    this.generation++; // supersede any in-flight full paint so it can't re-bake the boxes over the backdrop
    await this.renderBackdropTo(overlay, new Set(this.styleLiftIds));
    if (this.destroyed || session !== this.styleSessionToken) return;
    this.styleLiftReady = true;
    this.scheduleStyleTick(); // a slider tick may have landed while the backdrop rendered — catch up
  }

  /** One slider tick: the host's boxes already carry the preview values — re-render only the lifted ones. */
  patchStyleLift(): void {
    if (this.destroyed || this.styleLiftIds.length === 0) return;
    if (typeof this.cfg.wasm.renderRegionToCanvas !== 'function') {
      this.scheduleRender(); // degraded but correct: today's debounced full render
      return;
    }

    this.scheduleStyleTick();
  }

  /** End the session: the lift stands in until the settle render (with the committed model) lands. */
  endStyleLift(): void {
    if (this.styleLiftIds.length === 0 && !this.styleCanvas) return;
    this.styleLiftIds = [];
    this.styleLiftReady = false;
    this.styleTickPending = false;
    this.styleTickToken++;
    this.styleSessionToken++;
    if (this.styleTickRaf !== undefined) {
      if (typeof cancelAnimationFrame === 'function') cancelAnimationFrame(this.styleTickRaf);
      else clearTimeout(this.styleTickRaf as unknown as ReturnType<typeof setTimeout>);
      this.styleTickRaf = undefined;
    }

    this.styleSettlePending = true;
    // The commit's content bump normally schedules the settle render; a no-op commit wouldn't — force one.
    this.scheduleRender(0);
  }

  private scheduleStyleTick(): void {
    if (this.destroyed || this.styleTickRaf !== undefined) return;
    const tick = (): void => {
      this.styleTickRaf = undefined;
      void this.paintStyleTick();
    };

    this.styleTickRaf =
      typeof requestAnimationFrame === 'function' ? requestAnimationFrame(tick) : (setTimeout(tick, 16) as unknown as number);
  }

  private async paintStyleTick(): Promise<void> {
    if (this.destroyed || !this.styleLiftReady || this.styleLiftIds.length === 0) return;
    if (this.styleTickInFlight) {
      this.styleTickPending = true;
      return;
    }

    this.styleTickInFlight = true;
    try {
      await this.renderStyleLift();
    } finally {
      this.styleTickInFlight = false;
      if (this.styleTickPending && !this.destroyed) {
        this.styleTickPending = false;
        this.scheduleStyleTick();
      }
    }
  }

  // The cache snapshot predates the drag; the host's boxes carry the live preview patch — swap just those subtrees.
  private refreshCacheBoxes(ids: readonly string[]): void {
    const cache = this.cache;
    if (!cache) return;
    const fresh = new Map<string, RenderBox>();
    const collect = (list: readonly RenderBox[]): void => {
      for (const b of list) {
        if (ids.includes(b.id)) fresh.set(b.id, b);
        if (b.type === 'group') collect((b as RenderGroupBox).children);
      }
    };

    collect(this.renderBoxes());
    const swap = (list: RenderBox[]): void => {
      for (let i = 0; i < list.length; i++) {
        const nb = fresh.get(list[i].id);
        if (nb) list[i] = nb;
        else if (list[i].type === 'group') swap((list[i] as RenderGroupBox).children);
      }
    };

    swap(cache.boxes);
  }

  // Mini-scene of only the lifted boxes → region-scoped raster, mirroring renderEditLift for N ids.
  private async renderStyleLift(): Promise<void> {
    const cache = this.cache;
    const ids = this.styleLiftIds;
    if (!cache || this.destroyed || ids.length === 0) return;
    const token = ++this.styleTickToken;
    this.refreshCacheBoxes(ids);
    const subset = filterBoxes(cache.boxes, bid => ids.includes(bid));
    if (subset.length === 0) return;
    const { sceneJson, imageRefs } = await this.composeSceneJson(subset, cache);
    if (this.destroyed || token !== this.styleTickToken) return;
    const rects = indexDrawableRects(sceneJson);
    const s = cache.renderScale || 1;
    // refreshCacheBoxes above swapped the live boxes into the cache, so the shared region math pads from
    // the CURRENT effect values — a blur growing on this very tick widens the region with it.
    const region = this.tightRegionForIds(ids, cache, rects);
    const canvas = this.ensureStyleCanvas();
    const buffer = this.ensureFrameBuffer(canvas, region.w, region.h);
    this.styleRegionBook = { x: region.ox / s, y: region.oy / s, w: region.w / s, h: region.h / s };
    this.positionRegionCanvas(canvas, this.styleRegionBook);
    const images: Record<string, RustImage> = {};
    for (const { ref } of imageRefs) if (cache.images[ref]) images[ref] = cache.images[ref];
    // render_region: pixel (0,0) maps to scene (-ox,-oy) — a window at (x0,y0) needs the NEGATED origin.
    await this.cfg.wasm.renderRegionToCanvas(buffer, sceneJson, cache.fonts, images, -region.ox, -region.oy);
    if (this.destroyed || token !== this.styleTickToken) return;
    this.presentFrame(canvas, buffer);
    // The backdrop scene dropped these boxes from the full hit index — keep their rects current.
    for (const [rid, r] of rects) this.drawableRects.set(rid, r);
  }

  /** Только заводит холст: размер и пиксели даёт `presentFrame` — см. `ensureOverlayCanvas`. */
  private ensureStyleCanvas(): HTMLCanvasElement {
    let canvas = this.styleCanvas;
    if (!canvas) {
      canvas = document.createElement('canvas');
      canvas.style.position = 'absolute';
      canvas.style.pointerEvents = 'none';
      canvas.style.zIndex = '10';
      this.cfg.host.appendChild(canvas);
      this.styleCanvas = canvas;
    }

    return canvas;
  }

  private removeStyleCanvas(): void {
    this.styleRegionBook = undefined;
    this.styleCanvas?.remove();
    this.styleCanvas = undefined;
  }

  private zoneVisibility(): ZoneVisibility | undefined {
    return this.cfg.getZoneVisibility?.();
  }

  private transformsEnabled(): boolean {
    return (this.cfg.getInteractionMode?.() ?? 'full') !== 'select';
  }

  // Boxes can vanish between renders (delete/undo/template) — else their outline/handles linger and nudge targets ghosts.
  private pruneStaleSelection(): void {
    if (this.selectedIds.length === 0) return;
    // The ready-edit backdrop excludes the edited box from the scene — its rect returns on the next lift tick.
    // A box the CURRENT model still carries is not stale either: the scene painted so far may simply not hold it
    // yet (the first pass composes what is decoded). Dropping it strands a host-driven selection — the panel keeps
    // the row selected while the action bar loses the rect it anchors to and never gets it back (FKS-12624).
    // Truly removed boxes leave the cache and prune as before. A HIDDEN box stays in the cache with
    // `visible: false` (the engine skips the whole subtree, the model still carries it) — it draws
    // nothing, so it prunes by visibility, not by presence. Visibility is inherited: a child of a
    // hidden group carries no flag of its own, hence the search descends visible boxes only.
    const alive = this.selectedIds.filter(
      id =>
        this.drawableRects.has(id) ||
        id === this.editingBoxId ||
        (this.cache ? !!findVisibleRenderBox(this.cache.boxes, id) : false),
    );
    if (alive.length === this.selectedIds.length) return;
    this.selectedIds = alive;
    this.cfg.onSelectionRect?.(this.computeSelectionScreenRect());
  }

  private onClick = async (event: MouseEvent): Promise<void> => {
    if (this.cropBoxId) return; // crop mode owns clicks (handled on pointer-down)
    if (this.trimBoxId) return; // trim mode owns clicks (handled on pointer-down)
    if (this.pathEditBoxId) return; // path-edit mode owns clicks (handled on pointer-down)
    if (this.suppressClick) {
      this.suppressClick = false;
      return;
    }

    if (this.isPlaceMode) {
      this.cfg.onPlace?.(this.clientToBook(event.clientX, event.clientY));
      return;
    }

    const canvas = this.overlayCanvas;
    if (!canvas || !this.sceneJson) return;
    const candidate = await this.clickCandidateAt(this.clientToScene(event.clientX, event.clientY));
    if (candidate === null) return;
    const targetable = candidate && this.isTargetable(candidate) ? candidate : undefined;
    // Попадание есть, но тип не таргетабелен для этой роли: сам клик — молчаливый deselect, а хосту
    // есть что сказать (ученик тыкает в текст при выключенном «менять своё имя» — нужен тост-отказ).
    if (!targetable && candidate) this.cfg.onUntargetableClick?.(candidate);
    const id = targetable ? this.clipTargetOf(targetable) : undefined;
    const multiSelect = event.shiftKey || event.ctrlKey || event.metaKey;
    if (this.cfg.onSelect) {
      this.cfg.onSelect(id, multiSelect); // host owns selection; it calls back setSelection(...)
      this.maybeClickTextEdit(id, multiSelect, event);
      return;
    }

    // Standalone fallback (hosts that don't wire selection state): toggle internally.
    const next = id ? (this.selectedIds[0] === id ? [] : [id]) : [];
    await this.setSelection(next);
    this.maybeClickTextEdit(id, multiSelect, event);
  };

  // Click-to-edit (enableClickTextEdit): a plain single click on an editable text box enters inline edit right away.
  // Person-bound texts keep the select-then-dblclick flow — a single click must not open the person modal.
  private maybeClickTextEdit(id: string | undefined, multiSelect: boolean, event: MouseEvent): void {
    if (!id || multiSelect || !this.cfg.enableClickTextEdit || this.editingBoxId === id) return;
    const box = this.getRenderBox(id);
    if (box?.type !== 'text') return;
    if (this.personOwnerOfText(id)) return;
    this.enterTextEditFor(id, box, { clientX: event.clientX, clientY: event.clientY }, 'click');
  }

  // Одна точка «кому принадлежит этот текст»: её спрашивают и одиночный клик, и двойной. Врозь они
  // разошлись бы — рамка правилась бы на месте только одним из двух жестов.
  private personOwnerOfText(id: string): string | undefined {
    const ownedByType = this.cfg.isPersonOwnedText?.(id);
    if (ownedByType === false) return undefined;
    const grouped = this.cache ? findPersonGroupId(this.cache.boxes, id) : undefined;
    // Тип сказал «поле персоны», а группы нет (рамка лежит на обложке верхним уровнем): владельца не
    // знаем, но править на месте всё равно нельзя — подстановка на чтении затрёт набранное молча.
    return grouped ?? (ownedByType ? PERSON_TEXT_WITHOUT_OWNER : undefined);
  }

  // A touch tap the host consumed (pointer capture): mobile Safari may deliver no compat click to the canvas, so the
  // host replays the release here — the same select path as the mouse click, vetoes and empty-tap deselect included.
  // The host already told a tap from a drag, so a trailing-click swallow left by an earlier compat mouse press is void.
  tap(event: MouseEvent): Promise<void> {
    this.suppressClick = false;
    return this.onClick(event);
  }

  // The box a click at this point selects (the click chain: hit → lock fallback → targetable → clip target), or
  // undefined. The scene point is read synchronously, so a camera move started right after keeps the tapped spot.
  selectableAt(clientX: number, clientY: number): Promise<string | undefined> {
    if (!this.sceneJson) return Promise.resolve(undefined);
    return this.clickCandidateAt(this.clientToScene(clientX, clientY)).then(c =>
      c && this.isTargetable(c) ? this.clipTargetOf(c) : undefined,
    );
  }

  // null = the hit test failed (the click is ignored); undefined = empty paper.
  private async clickCandidateAt(p: Point | undefined): Promise<string | undefined | null> {
    if (!this.sceneJson) return null;
    const hit = await this.hitTestOrNull(this.sceneJson, p);
    if (hit === null) return null;
    // Only a LOCKED layer swallowing the click earns the bbox fallback; elsewhere a miss still means "deselect".
    const lockAte = !!p && lockedRectCovers(this.drawableRects, p.x, p.y);
    return hit ?? (p && lockAte ? topmostRectAt(this.drawableRects, p.x, p.y) : undefined);
  }

  async hitTestBoxIdAt(clientX: number, clientY: number): Promise<string | undefined> {
    if (!this.sceneJson) return undefined;
    const p = this.clientToScene(clientX, clientY);
    return p ? this.cfg.wasm.hitTest(this.sceneJson, p.x, p.y) : undefined;
  }

  // Right-click selects the hit box (if not already) and lets the host open its menu; the event bubbles (no preventDefault).
  private onContextMenu = async (event: MouseEvent): Promise<void> => {
    // Placement mode swallows the right-click (no context menu); the user cancels via Esc / click-off-spread.
    if (this.isPlaceMode) {
      event.preventDefault();
      return;
    }

    if (!this.cfg.onContextMenu) return;
    const canvas = this.overlayCanvas;
    if (!canvas || !this.sceneJson) {
      this.cfg.onContextMenu(undefined, event);
      return;
    }

    const p = this.clientToScene(event.clientX, event.clientY);
    const hit = await this.hitTestOrNull(this.sceneJson, p);
    if (hit === null) return;
    const id = hit && this.isTargetable(hit) ? this.clipTargetOf(hit) : undefined;
    if (id && !this.selectedIds.includes(id)) this.cfg.onSelect?.(id, false);
    this.cfg.onContextMenu(id ?? undefined, event);
  };

  // Double-click a text box → enter inline edit. Browser fires click(select) then dblclick(edit): select-then-edit.
  private onDblClick = async (event: MouseEvent): Promise<void> => {
    // Path-edit owns dblclick (add-point) — it must never fall through to inline text editing.
    if (this.pathEditBoxId) {
      this.onPathEditDblClick(event);
      return;
    }

    // Trim toggles off the way it toggled on: a second dblclick on the decor commits and leaves. Outside the frame
    // the press already committed, so only the inside case is left to handle.
    if (this.trimBoxId) {
      const book = this.clientToBook(event.clientX, event.clientY);
      if (book && this.pointInTrimFrame(book)) this.commitTrim();
      return;
    }

    const canvas = this.overlayCanvas;
    if (!canvas || !this.sceneJson || this.gesture || this.press?.active || this.cropBoxId) return;
    const p = this.clientToScene(event.clientX, event.clientY);
    if (p && this.openTextUnder(p, canvas, event)) return;
    const hit = await this.hitTestOrNull(this.sceneJson, p);
    if (hit === null) return;
    const id = hit && this.isTargetable(hit) ? hit : undefined;
    if (!id) {
      // Пустое место — это фон страницы или вообще ничто; объект, до которого этой роли не дотянуться, пустым не считается.
      const hitType = hit ? this.getRenderBox(hit)?.type : undefined;
      if (!hitType || hitType === 'background') this.cfg.onEmptyDblClick?.();
      return;
    }

    if (this.cfg.tryDrillIn?.(id, event.shiftKey)) return;
    const box = this.getRenderBox(id);
    if (box?.type === 'image' && this.cfg.enableInlineCrop) {
      this.enterCropMode(id, box as RenderImageBox);
      return;
    }

    if (canTrimDecorBox(box) && this.cfg.enableDecorTrim) {
      this.enterTrimMode(id, box);
      return;
    }

    if (box?.type !== 'text') return;
    this.enterTextEditFor(id, box, { clientX: event.clientX, clientY: event.clientY });
  };

  // Double-click is the Type tool: it opens the topmost text under the pointer through any decor drawn over it.
  // A text this role may not edit leaves the gesture to the layer the click hit.
  private openTextUnder(p: Point, canvas: HTMLCanvasElement, event: MouseEvent): boolean {
    const shown = canvas.getBoundingClientRect().width;
    const slop = shown > 0 ? (DBLCLICK_TEXT_SLOP_PX * canvas.width) / shown : 0;
    const id = topmostTextAt(this.sceneJson ?? '', p.x, p.y, slop);
    if (!id || !this.isTargetable(id) || this.cfg.canEditText?.(id) === false) return false;
    if (this.cfg.tryDrillIn?.(id, event.shiftKey)) return true;
    const box = this.getRenderBox(id);
    if (box?.type !== 'text') return false;
    if (!this.selectedIds.includes(id)) this.cfg.onSelect?.(id, false);
    this.enterTextEditFor(id, box, { clientX: event.clientX, clientY: event.clientY });
    return true;
  }

  // Shared inline-edit entry for dblclick and (when enableClickTextEdit) single click. `caretAt` is the pointer that
  // opened the session — a shaped (area) box starts its own caret there, since that gesture never reached the textarea.
  private enterTextEditFor(
    id: string,
    box: RenderBox,
    caretAt?: { clientX: number; clientY: number },
    entry: 'click' | 'dblclick' = 'dblclick',
    opts?: { dockHost?: HTMLElement },
  ): void {
    if (box.type !== 'text') return;
    // Idempotent: with click-to-edit on, the click already opened this box and the trailing dblclick still lands
    // on the canvas (the caret-height textarea leaves the frame's lower part exposed, and the engine hit-tests the
    // WHOLE frame). Re-entering would make enter() cancel the live session, and that cancel clears editingBoxId —
    // leaving a mounted textarea the controller no longer tracks, so live repaint stops.
    if (this.editingBoxId === id) {
      // The swallowed dblclick still owes the user its word selection — forward it to the live session.
      if (entry === 'dblclick' && caretAt) this.textEdit?.selectWordAt(caretAt.clientX, caretAt.clientY);

      return;
    }

    if (this.cfg.canEditText && !this.cfg.canEditText(id)) return;
    // A personId-bound group owns its texts: open the person flow, never inline text edit (legacy parity).
    const personId = this.personOwnerOfText(id);
    if (personId) {
      this.cfg.onPersonSelect?.(personId === PERSON_TEXT_WITHOUT_OWNER ? null : personId, id);
      return;
    }

    const quad = this.getTextBoxScreenQuad(id);
    if (!quad) return;

    const textBox = box as RenderTextBox;
    const frameFitOf = this.cache?.frameFitOf;
    const r = this.drawableRects.get(id);
    const k = (this.viewScale || 1) / (this.cache?.renderScale || 1);
    // unstretched dims → the matrix applies the box's real scale to the glyphs
    const localWidth = (r?.width ?? 0) * k;
    const localHeight = (r?.height ?? 0) * k;
    this.editingBoxId = id;
    // Frozen for the camera re-seat: the live drawable rect starts tracking the auto-width growth right away.
    this.editEntrySceneRect = r ? { ...r, transform: r.transform ? [...r.transform] : undefined } : undefined;
    // The sharp tile is a baked snapshot of the committed scene, box included, sitting over the overlay. Nothing in
    // the edit path repaints it, so it kept showing the pre-edit text over the live one for the whole session — at
    // the zoom levels where the tile arms, that read as doubled text that "clears after a while" (the next full
    // paint). The rearm guard already refuses to re-arm while `editingBoxId` is set; the session entry must drop the
    // tile that is ALREADY up.
    this.hideSharpTile();
    this.liveEditScaleX = undefined;
    this.liveEditSpaceAdvance = undefined;
    this.liveEditBareWidth = undefined;
    this.livePointFit = undefined;
    // A placeholder box carries the hint glyphs, not content: seeding them would let a plain blur commit the hint.
    // The just-committed text outranks the cache: the click that committed re-enters before the settle render
    // folds the commit into the cache, and a stale seed would resurrect the pre-commit string.
    const sessionText =
      this.pendingCommittedText?.id === id ? this.pendingCommittedText.text : textBox.isPlaceholder ? '' : (textBox.text ?? '');
    this.liveEditPrevText = sessionText;
    this.liveEditRuns = this.pendingCommittedText?.id === id ? this.pendingCommittedText.styleRuns : textBox.runs;
    this.liveEditParagraphRuns =
      this.pendingCommittedText?.id === id ? this.pendingCommittedText.paragraphRuns : textBox.paragraphRuns;
    // The frame may already be behind its text (a layer whose font or size changed since it was last edited).
    this.liveEditRect = this.autoWidthFrame(id, sessionText);
    this.cfg.onTextEditStart?.(id);
    const shapedCaret = this.shapedCaretSource(textBox, localWidth, localHeight);
    // The engine keeps rendering the box; the caret-only textarea sits on top — no hiding (that caused the jump).
    this.ensureTextEdit().enter({
      id,
      text: sessionText,
      quad,
      localWidth,
      localHeight,
      style: this.textEditStyle(textBox),
      shapedCaret,
      fixedSurface: !!shapedCaret && !!textBox.textPath?.d,
      caretAt,
      entry,
      rules: this.cfg.textEditRules?.(id) ?? undefined,
      // The clamp asks the ENGINE whether a candidate fits, so wrap tolerance, glyph ink, super/subscript scaling
      // and per-run metrics all come out right by construction — the editor never re-derives the frame itself.
      // Gated by form exactly like the clamp: an approximate layout must not shape the caret either.
      measureFit: frameFitOf && this.rectangularFitForm(textBox) ? (text: string) => frameFitOf(textBox, text) : undefined,
      // A frame with its own capacity (vignette series quote hugged to its text, R10) caps by character count:
      // the hugged frame edge would stop typing at the current quote's length.
      overCapacity: quoteOverCapacity(textBox),
      verticalShift: frameFitOf ? (text: string) => this.caretVerticalShiftPx(textBox, text, frameFitOf) : undefined,
      contentWidthRatio: frameFitOf ? (text: string) => this.caretContentWidthRatio(textBox, text, frameFitOf) : undefined,
      dockHost: opts?.dockHost,
      clampToFrame:
        !!this.cfg.enableTextClampToFrame &&
        !!frameFitOf &&
        this.containerFramedText(textBox) &&
        // A frame that can GROW needs no border stop: auto-width chases the line, a fixed block follows in
        // height (fixedHeightRect). The stop (FKS-10897) keeps guarding hosts whose frames cannot grow — and
        // the quote frame, which even in a follow host only the photographer resizes (autoWidthFrame keeps it
        // out of fixedHeightRect), so typing has nowhere to spill: the frame IS the limit (FKS-12432).
        (!this.cfg.enableTextFrameFollow || textBox.textType === TextTypeEnum.PERSON_QUOTE) &&
        this.rectangularFitForm(textBox),
    });
    void this.enterEditLift(id);
  }

  /** Phone: the same inline session, its field docked in `dockHost` (the shell routes the gates' answers before). */
  startDockedTextEdit(id: string, dockHost: HTMLElement): void {
    // A switch saves the previous text: enter() alone would CANCEL the live session.
    if (this.editingBoxId && this.editingBoxId !== id) this.commitActiveTextEdit();
    const box = this.getRenderBox(id);
    if (box) this.enterTextEditFor(id, box, undefined, 'click', { dockHost });
  }

  /**
   * Text whose frame is a CONTAINER the clamp may defend. Only area (`block`) text has one — Photoshop wraps it
   * inside that frame and hides whatever spills, so the frame is a limit the designer authored and the user sees.
   * Point text has no frame in Photoshop at all: the rect we store is the ink box the parser derived around the
   * glyphs as they were authored. On a display letter it hugs that letter, leaving no room for even one more
   * character, so clamping to it refused every keystroke and the layer could not be typed into (FKS-11344).
   */
  private containerFramedText(tb: RenderTextBox): boolean {
    return tb.textMode === 'block';
  }

  /**
   * Text whose frame CHASES the content instead of bounding it: an auto-width block in a host that can store the
   * grown rect. Such a box never wraps, so the three rectangular-frame behaviours all step aside for it — the
   * clamp has no fixed frame to defend, the caret surface flows as one line, and the engine's caret grid (built
   * from the stored rect, not the growing one) would place the caret where the text no longer breaks.
   */
  private framePursuesText(tb: RenderTextBox): boolean {
    return !!tb.autoWidth && !!this.cfg.enableTextFrameFollow;
  }

  /**
   * Layouts the rectangular overlay can represent EXACTLY. Path-bent and vertical text put the caret nowhere near
   * the glyphs; area-shaped, multi-run and indented boxes place it only approximately — those keep free growth and
   * an unshaped caret rather than obeying a rectangular measure the user cannot see themselves filling.
   */
  private rectangularFitForm(tb: RenderTextBox): boolean {
    return (
      !tb.textPath?.d &&
      tb.orientation !== 'vertical' &&
      !(tb.textMode === 'block' && tb.areaShape?.d) &&
      !wrapZonesCrossFrame(tb) &&
      !tb.runs &&
      !tb.startIndent &&
      !tb.firstLineIndent &&
      !tb.endIndent &&
      !tb.paragraphRuns?.length
    );
  }

  /** The box as the live session lays it out: the typed text with its style and paragraph runs carried along. */
  private liveTextBox(box: RenderTextBox, text: string): RenderTextBox {
    // The grid rebuilds before `onInput` remaps the session runs: take that step here, set as `applyTextOverride` does.
    const prev = this.liveEditPrevText ?? this.liveEditText ?? '';
    const runs = remapStyleRunsForTextChange(this.liveEditRuns, prev, text) as RenderTextBox['runs'];
    const live: RenderTextBox = { ...box, text, runs: runs && runs.length > 0 ? runs : undefined };
    const paragraphRuns = remapParagraphRunsForTextChange(this.liveEditParagraphRuns, prev, text);
    if (paragraphRuns === undefined) return live;
    return { ...live, paragraphRuns: paragraphRuns.length > 0 ? paragraphRuns : undefined };
  }

  /**
   * Каретка area-текста (FKS-11074). Движок льёт такой текст по полосам контура, а textarea верстается
   * браузером по прямоугольнику рамки — сетки строк расходятся, и браузерная каретка встаёт в чужое место.
   * Здесь сетка движка переводится из canvas-px бокса в локальные экранные px рамки (в них работает оверлей).
   * `undefined` для всего остального текста — обычный block/free редактируется ровно как раньше.
   */
  private shapedCaretSource(box: RenderTextBox, localWidth: number, localHeight: number): ShapedCaretSource | undefined {
    if (box.textPath?.d) return this.pathCaretSource(box, localWidth, localHeight);
    // A followed frame never wraps: browser caret, unless runs leave the box case (grid in `autoWidthFrame`).
    const pursues = this.framePursuesText(box);
    if (pursues && !runCapsDivergeFromBox(box)) return undefined;
    // Re-entered before the settle render: the session sits on the drawn committed frame, the cached box on the stored one.
    const committed = this.pendingCommittedText;
    const entryRect = (pursues && committed?.id === box.id && committed.rect) || box.rect;
    const { width: rectWidth, height: rectHeight } = entryRect;
    if (!(rectWidth > 0) || !(rectHeight > 0) || !(localWidth > 0) || !(localHeight > 0)) return undefined;
    // The session frame's scale (view × box), fixed at entry: the grown frame only moves the grid inside it.
    const kx = localWidth / rectWidth;
    const ky = localHeight / rectHeight;
    const caretBox = (text: string): { live: RenderTextBox; dx: number; dy: number } => {
      const live = this.liveTextBox(box, text);
      const rect = pursues ? this.autoWidthFrame(box.id, text) : undefined;
      if (!rect) return { live, dx: 0, dy: 0 };
      const { dx, dy } = frameOriginShift(entryRect, rect);
      return { live: { ...live, rect }, dx: dx * kx, dy: dy * ky };
    };

    const engine = this.cache?.engineCaretOf;
    if (engine && engineCaretNeeded(box) && engine(box)) {
      const frame = { kx, ky };
      return (text: string) => {
        const { live, dx, dy } = caretBox(text);
        const geometry = engine(live);
        return geometry && shiftShapedCaret(engineShapedCaret(geometry, frame), dx, dy);
      };
    }

    const build = this.cache?.caretLayoutOf;
    if (!build) return undefined;
    // Расходится ли раскладка с браузерной — решает построитель по САМОМУ боксу; дальше сессия держится
    // на сетке движка принудительно, иначе правка, убравшая последний хвостовой пробел, отдала бы
    // каретку браузеру посреди набора (а браузерная у нас выключена — каретка бы просто пропала).
    if (!build(box)) return undefined;
    return (text: string) => {
      const { live, dx, dy } = caretBox(text);
      const layout = build(live, true);
      if (!layout) return undefined;
      return shiftShapedCaret(
        {
          slotAt: index => {
            const slot = caretSlotAt(layout, index);
            return slot && { x: slot.x * kx, top: slot.top * ky, height: (slot.bottom - slot.top) * ky };
          },
          indexAt: (x, y) => caretIndexAt(layout, x / kx, y / ky),
          indexByLineStep: (index, delta, desiredX) =>
            caretIndexByLineStep(layout, index, delta, desiredX === undefined ? undefined : desiredX / kx),
          lineEdge: (index, edge) => caretLineEdge(layout, index, edge),
          bandsFor: (from, to) =>
            caretSelectionBands(layout, from, to).map(b => ({
              x: b.x * kx,
              top: b.top * ky,
              width: b.width * kx,
              height: b.height * ky,
            })),
        },
        dx,
        dy,
      );
    };
  }

  // The textarea lays text on a path out as straight lines across the frame — caret, click and highlight follow the glyphs.
  private pathCaretSource(box: RenderTextBox, localWidth: number, localHeight: number): ShapedCaretSource | undefined {
    const build = this.cache?.pathCaretLayoutOf;
    const { x, y, width, height } = box.rect;
    if (!build || !(width > 0) || !(height > 0) || !(localWidth > 0) || !(localHeight > 0) || !build(box)) return undefined;
    const frame = { x, y, kx: localWidth / width, ky: localHeight / height };
    return (text: string) => {
      const layout = build({ ...box, text });
      return layout && pathShapedCaret(layout, text.length, frame);
    };
  }

  /**
   * How far DOWN the engine pushes this box's first line for its vertical align, in screen px.
   *
   * Mirrors the renderer's own offset — `(rect.height - inkHeight)` shared by centre/bottom — but reads `inkHeight`
   * off the fit probe (`heightOver = inkHeight - rect.height`) instead of re-deriving it, so one measured pass answers
   * it. Area-shaped text pins its lines to the contour bands and is never shifted, exactly as the renderer has it.
   */
  private caretVerticalShiftPx(box: RenderTextBox, text: string, fit: (b: RenderTextBox, t: string) => TextFrameFit): number {
    const align = resolveVerticalAlign(box.verticalAlign);
    // Text set around page objects (`wrapZones` across the frame) is not vertically aligned either.
    if (align === 'top' || box.areaShape?.d || wrapZonesCrossFrame(box)) return 0;
    const heightOver = fit(this.liveTextBox(box, text), text).heightOver;
    // Mirrors the renderer with NO clamp: once the text outgrows the frame, centring pushes the first line ABOVE the
    // frame top (a negative shift). Clamping at 0 held the caret surface at the frame while the engine's lines walked
    // up — every Enter left the caret one half-line lower than the glyphs.
    const shiftBook = align === 'center' ? -heightOver / 2 : -heightOver;
    return shiftBook * (this.viewScale || 1);
  }

  /**
   * The engine's longest laid-out line for `text`, as a share of the box's frame width.
   *
   * Point-mode against a ZERO frame makes `widthOver` the content width itself; the session's cached residual glyph
   * scale turns it into frame px. A ratio, not px, so the caret surface applies it to its own width and every scale
   * between the model and the screen cancels instead of being re-derived on the other side.
   */
  private caretContentWidthRatio(box: RenderTextBox, text: string, fit: (b: RenderTextBox, t: string) => TextFrameFit): number {
    if (!(box.rect.width > 0)) return 0;
    this.liveEditScaleX ??= residualScaleX(box, this.memoPointFit(fit));
    const k = this.liveEditScaleX;
    if (!(k !== undefined && k > 0)) return 0;
    const content = this.memoPointFit(fit)({ ...box, textMode: 'free', text, rect: { ...box.rect, width: 0 } }, text).widthOver;
    return (Math.max(0, content) * k) / box.rect.width;
  }

  /** Memoizes ONLY the zero-width point probe — every other measure passes straight through. */
  private memoPointFit(fit: FrameFitProbe): FrameFitProbe {
    return (b, t) => {
      if (b.textMode !== 'free' || b.rect.width !== 0) return fit(b, t);
      const c = this.livePointFit;
      if (c && c.id === b.id && c.text === t && c.rectHeight === b.rect.height) return c.fit;
      const measured = fit(b, t);
      this.livePointFit = { id: b.id, text: t, rectHeight: b.rect.height, fit: measured };
      return measured;
    };
  }

  /**
   * Keeps every compound-clip mask in step with its source text (FKS-12999). Runs after a settled render, so it
   * sees the model exactly as painted, whoever changed it. The first sighting of a clipper records its key: the
   * mask it arrived with is trusted, and opening a spread never rewrites it. A later key change re-bakes — one
   * flight at a time; a result whose key moved on while it rendered is dropped and the next sync re-plans.
   */
  private hasClipMask(clipperId: string): boolean {
    const box = findRenderBox(this.renderBoxes(), clipperId);
    return !!box && !!(box as RenderObjectBox).maskDataUrl;
  }

  private syncCompoundClipMasks(): void {
    if (!this.cfg.onCompoundClipMasks || this.destroyed) return;
    // A live edit or style gesture paints transient text; the model settles on commit and the next render syncs.
    if (this.editingBoxId !== undefined || this.styleLiftIds.length > 0) return;
    if (this.clipRebakeRunning) {
      this.clipRebakeQueued = true;
      return;
    }

    const stale: CompoundClipRebakePlan[] = [];
    const bonded = new Set<string>();
    const tick = this.clipSettleTick;
    for (const plan of planCompoundClipRebakes(this.renderBoxes())) {
      const id = plan.clipperBoxId;
      bonded.add(id);
      const known = this.clipMaskKeys.get(id);
      if (!this.hasClipMask(id)) {
        // Recipe without a mask is always baked — a fresh bond, a re-clip after release, an undo that brought the
        // recipe back, or a host that dropped the last result. The same key is retried once per settled render.
        if (known === plan.key && this.clipBakeTicks.get(id) === tick) continue;
        stale.push(plan);
      } else if (known === undefined) {
        // A mask the spread opened with is trusted; opening a spread never rewrites it.
        this.clipMaskKeys.set(id, plan.key);
      } else if (known !== plan.key) stale.push(plan);
    }

    // A released or deleted clipper leaves the plan; its history must not vouch for a future bond.
    for (const id of [...this.clipMaskKeys.keys()]) {
      if (bonded.has(id)) continue;
      this.clipMaskKeys.delete(id);
      this.clipBakeTicks.delete(id);
    }

    if (stale.length === 0) return;
    this.clipRebakeRunning = true;
    void this.rebakeCompoundClipMasks(stale)
      .then(baked => {
        if (this.destroyed) return;
        const current = new Map(planCompoundClipRebakes(this.renderBoxes()).map(p => [p.clipperBoxId, p.key]));
        const fresh = baked.filter(b => current.get(b.plan.clipperBoxId) === b.plan.key);
        for (const b of fresh) {
          this.clipMaskKeys.set(b.plan.clipperBoxId, b.plan.key);
          this.clipBakeTicks.set(b.plan.clipperBoxId, tick);
        }

        if (fresh.length > 0) {
          this.cfg.onCompoundClipMasks?.(
            fresh.map(b => ({
              boxId: b.plan.clipperBoxId,
              maskDataUrl: b.maskDataUrl,
              ...(b.plan.maskRect && { maskRect: b.plan.maskRect }),
            })),
          );
        }

        if (fresh.length < baked.length) this.clipRebakeQueued = true;
      })
      .catch(error => console.error('[compound-clip] mask re-bake failed', error))
      .finally(() => {
        this.clipRebakeRunning = false;
        if (!this.clipRebakeQueued) return;
        this.clipRebakeQueued = false;
        this.syncCompoundClipMasks();
      });
  }

  /**
   * Draws each plan's source text through the SAME engine that paints the canvas and keeps the alpha —
   * so the silhouette the texture is cut to is the one the user sees, on screen and later in print.
   */
  private async rebakeCompoundClipMasks(
    plans: readonly CompoundClipRebakePlan[],
  ): Promise<{ plan: CompoundClipRebakePlan; maskDataUrl: string }[]> {
    const baked: { plan: CompoundClipRebakePlan; maskDataUrl: string }[] = [];
    for (const plan of plans) {
      const maskDataUrl = await this.renderMask(plan);
      if (maskDataUrl) baked.push({ plan, maskDataUrl });
    }

    return baked;
  }

  private async renderMask(plan: CompoundClipRebakePlan): Promise<string | undefined> {
    const resolved = await this.cfg.resolveFonts(collectTextBoxes(plan.boxes));
    if (this.destroyed) return undefined;

    const cache: OverlayCache = {
      boxes: [...plan.boxes],
      viewport: { width: plan.width, height: plan.height },
      renderScale: plan.scale,
      fonts: resolved.fonts,
      mappedFace: resolved.mappedFace,
      fontRefOf: resolved.fontRefOf,
      zones: undefined,
      images: {},
    };
    // Non-text sources (a group base's pixel leaves) reuse the textures the canvas already uploaded under the same ids.
    const scene = await this.composeSceneJson(plan.boxes, cache, undefined, undefined, plan.scale);
    if (this.destroyed) return undefined;

    const rgba = await this.cfg.wasm.renderRegionToBytes(scene.sceneJson, resolved.fonts, {}, plan.width, plan.height);
    const canvas = document.createElement('canvas');
    // eslint-disable-next-line no-restricted-syntax -- холст вне DOM: только кодирует маску в PNG
    canvas.width = plan.width;
    // eslint-disable-next-line no-restricted-syntax -- холст вне DOM: только кодирует маску в PNG
    canvas.height = plan.height;

    return maskDataUrlFromRgba(new Uint8ClampedArray(rgba.buffer, rgba.byteOffset, rgba.length), canvas);
  }

  private ensureTextEdit(): OverlayTextEdit {
    if (!this.textEdit) {
      this.textEdit = new OverlayTextEdit(this.cfg.host, {
        onCommit: (id, text) => {
          const rect = this.liveEditRect;
          const styleRuns = this.liveEditRuns;
          const paragraphRuns = this.liveEditParagraphRuns;
          this.editingBoxId = undefined;
          this.liveEditText = undefined;
          this.liveEditRuns = undefined;
          this.liveEditParagraphRuns = undefined;
          this.liveEditPrevText = undefined;
          this.liveEditRect = undefined;
          this.liveEditRuns = undefined;
          this.liveEditPrevText = undefined;
          this.liveEditScaleX = undefined;
          this.liveEditSpaceAdvance = undefined;
          this.liveEditBareWidth = undefined;
          this.livePointFit = undefined;
          this.editEntrySceneRect = undefined;
          this.pendingCommittedText = { id, text, styleRuns, paragraphRuns, rect };
          this.endEditLift();
          // Бокс без прогонов зовёт хост старой сигнатурой — прогоны в аргументах только когда они есть.
          if (paragraphRuns !== undefined) this.cfg.onTextEditCommit?.(id, text, rect, styleRuns, paragraphRuns);
          else if (styleRuns !== undefined) this.cfg.onTextEditCommit?.(id, text, rect, styleRuns);
          else this.cfg.onTextEditCommit?.(id, text, rect);
          this.scheduleRender(0); // reconcile from the host model (now carrying the committed text)
        },
        onCancel: () => {
          this.editingBoxId = undefined;
          this.liveEditText = undefined;
          this.liveEditRuns = undefined;
          this.liveEditParagraphRuns = undefined;
          this.liveEditPrevText = undefined;
          this.liveEditRect = undefined;
          this.liveEditRuns = undefined;
          this.liveEditPrevText = undefined;
          this.liveEditScaleX = undefined;
          this.liveEditSpaceAdvance = undefined;
          this.liveEditBareWidth = undefined;
          this.livePointFit = undefined;
          this.editEntrySceneRect = undefined;
          this.endEditLift();
          this.scheduleRender(0); // revert to the model text
        },
        onEnd: id => this.cfg.onTextEditEnd?.(id, this.textEdit?.lastSelection),
        onSelectionChange: (id, sel) => this.cfg.onTextEditSelectionChange?.(id, sel),
        onInput: (id, text) => {
          const prev = this.liveEditPrevText ?? this.liveEditText ?? '';
          const next = remapStyleRunsForTextChange(this.liveEditRuns, prev, text);
          // Пустой список НЕ схлопывать в undefined: «сессия стёрла прогоны» ≠ «прогонов не было» —
          // undefined увёл бы коммит в диффовый фолбэк стора по протухшему тексту, воскрешая снятое.
          if (next !== undefined) this.liveEditRuns = next as RenderTextBox['runs'];
          // Абзацные прогоны — тем же шагом пред→след: правки в двух местах сессии не схлопываются в одну.
          const paragraphs = remapParagraphRunsForTextChange(this.liveEditParagraphRuns, prev, text);
          if (paragraphs !== undefined) this.liveEditParagraphRuns = paragraphs;
          this.liveEditPrevText = text;
          this.liveEditText = text;
          // Measured against the STORED frame every keystroke, so deleting text lets the frame fall back to it
          // instead of keeping the widest thing ever typed.
          this.liveEditRect = this.autoWidthFrame(id, text);
          if (this.editLiftReady) {
            this.scheduleEditTick();
          } else {
            // Lift not up yet — coalesce burst typing into ~30fps full repaints (paint reuses the cache).
            this.schedulePaint(LIVE_TEXT_PAINT_DEBOUNCE_MS);
          }
        },
      });
    }

    return this.textEdit;
  }

  /**
   * The frame the edited box wants for `text`; `undefined` when the stored frame already matches.
   * An auto-width block hugs its content on every side; a fixed block keeps its width and follows the text in
   * height only — except a quote frame, whose rect only the photographer sets (the text wraps inside it).
   * BOTH run only in a host that can STORE the grown rect (enableTextFrameFollow — the design
   * and album editors): in a follow-less host an auto-width layer must keep fixed-frame semantics, or the frame
   * grows on screen, the host drops the commit's rect, and the settle render snaps it back.
   */
  private autoWidthFrame(id: string, text: string): RenderRect | undefined {
    if (!this.cfg.enableTextFrameFollow) return undefined;
    const box = this.cache ? findRenderBox(this.cache.boxes, id) : undefined;
    const fit = this.cache?.frameFitOf;
    if (box?.type !== 'text' || !fit) return undefined;
    const tb = box as RenderTextBox;
    // FREE mode wins over a dormant flag: the panel shows «Свободный», so input must not hug the frame.
    if (tb.textMode === 'block' && tb.autoWidth) {
      this.liveEditScaleX ??= residualScaleX(tb, this.memoPointFit(fit));
      // Сырым fit, не мемо: вспомогательным замерам не место в однослотовом кэше каретки.
      this.liveEditSpaceAdvance ??= spaceAdvanceOf(tb, fit);
      const measureBare = (bare: string): number => {
        if (this.liveEditBareWidth?.text !== bare) {
          const width = Math.max(0, fit({ ...tb, textMode: 'free', text: bare, rect: { ...tb.rect, width: 0 } }, bare).widthOver);
          this.liveEditBareWidth = { text: bare, width };
        }

        return this.liveEditBareWidth.width;
      };

      return autoWidthRect(tb, text, this.memoPointFit(fit), this.liveEditScaleX, this.liveEditSpaceAdvance, measureBare);
    }

    if (this.heightFollowsText(tb)) return fixedHeightRect(tb, text, fit);

    return undefined;
  }

  /**
   * Рамка-КОНТЕЙНЕР: текст переносится по её ширине, поэтому рамка обязана держать свой текст — и при вводе, и при
   * ресайзе. Авто-ширина сюда не входит (её рамка сама обжимает содержимое), как и текст по контуру/по дуге, чей
   * прямоугольник не описывает раскладку. Только в хосте, умеющем СОХРАНИТЬ новую рамку (`enableTextFrameFollow`).
   */
  private wrappingContainerText(tb: RenderTextBox): boolean {
    return (
      !!this.cfg.enableTextFrameFollow && this.containerFramedText(tb) && !tb.autoWidth && !tb.areaShape?.d && !tb.textPath?.d
    );
  }

  /**
   * Контейнер, чья ВЫСОТА идёт за текстом: ширину держит пользователь, строки переносятся, рамка растёт вниз и
   * поджимается обратно. Рамку цитаты двигает только фотограф — набор её не растит и не поджимает.
   */
  private heightFollowsText(tb: RenderTextBox): boolean {
    return this.wrappingContainerText(tb) && tb.textType !== TextTypeEnum.PERSON_QUOTE;
  }

  /** Frame that hugs this text layer's glyphs on every side — the explicit «Автоширина» conversion asks for it. */
  hugTextFrame(boxId: string): RenderRect | undefined {
    // Only a host that can STORE the grown rect may convert: elsewhere the hug would fire once and never follow.
    if (!this.cfg.enableTextFrameFollow) return undefined;
    const box = this.cache ? findRenderBox(this.cache.boxes, boxId) : undefined;
    const fit = this.cache?.frameFitOf;
    if (box?.type !== 'text' || !fit) return undefined;
    const tb = box as RenderTextBox;
    return autoWidthRect(tb, tb.text ?? '', fit) ?? tb.rect;
  }

  /**
   * One-shot frame after a panel font-size change, measured at `fontSize` before the scene re-renders: free and
   * auto-width text hug their glyphs, a fixed block follows in height only. `undefined` = keep the stored frame —
   * person-owned text (generation lays it out by the stored rect), text on a path or in a shape, cold scene.
   */
  textFrameAtFontSize(boxId: string, fontSize: number): RenderRect | undefined {
    if (!this.cfg.enableTextFrameFollow || this.cfg.isPersonOwnedText?.(boxId)) return undefined;
    const box = this.cache ? findRenderBox(this.cache.boxes, boxId) : undefined;
    const fit = this.cache?.frameFitOf;
    if (box?.type !== 'text' || !fit) return undefined;
    // ponytail: style runs keep their own size here; a mixed-size layer is measured with the old run sizes.
    const src = box as RenderTextBox;
    // Same metrics patchTextBox persists: leading keeps its ratio to the size, letter-spacing keeps its px width.
    const k = src.fontSize > 0 && fontSize > 0 ? fontSize / src.fontSize : 1;
    const tb: RenderTextBox = {
      ...src,
      fontSize,
      lineHeight: src.lineHeight * k,
      ...(src.tracking != null && { tracking: src.tracking / k }),
    };
    if (tb.textPath?.d || tb.areaShape?.d || wrapZonesCrossFrame(tb)) return undefined;
    if (tb.textMode !== 'block' || tb.autoWidth) return autoWidthRect(tb, tb.text ?? '', fit);
    return this.heightFollowsText(tb) ? fixedHeightRect(tb, tb.text ?? '', fit) : undefined;
  }

  /**
   * Frame for a clone of this text layer, or `undefined` when the clone must stay exactly what the source is.
   * The design editor asks before duplicating: a one-line layer becomes an auto-width block around its own glyphs
   * (the engine hit-tests text by its frame, so an inherited point-text frame would leave the retyped copy
   * unclickable); a multi-line one keeps the layout it was authored with.
   */
  naturalTextFrame(boxId: string): RenderRect | undefined {
    // A clone hugged into an auto-width block needs the frame-follow host too — elsewhere it clones verbatim.
    if (!this.cfg.enableTextFrameFollow) return undefined;
    const box = this.cache ? findRenderBox(this.cache.boxes, boxId) : undefined;
    const fit = this.cache?.frameFitOf;
    if (box?.type !== 'text' || !fit) return undefined;
    return textCloneFrame(box as RenderTextBox, fit);
  }

  /**
   * Rect origin shift + height per style part (reading order, `from` = its first char in `source.text`) that keep
   * every line where the source draws it. Measured on the engine's caret grid, never mirrored: the first baseline
   * has several engine rules. `undefined` = no grid, or a part would wrap its lines differently.
   */
  textPartPlacement(
    source: RenderTextBox,
    parts: { from: number; box: RenderTextBox }[],
  ): { dx: number; dy: number; height: number }[] | undefined {
    const gridOf = this.cache?.engineCaretOf;
    const src = gridOf?.(source);
    if (!gridOf || !src) return undefined;
    const anisoY = textMatrixAnisotropy(source.transform)[1];
    // Stop bottom = baseline + the engine's caret descent of THAT box (`glyph_layout.rs` CaretTraceLine).
    const baseline = (box: RenderTextBox, line: EngineCaretLine): number =>
      (line.stops[0]?.[3] ?? NaN) - CARET_BASELINE_DESCENT_RATIO * box.fontSize * (box.verticalScale ?? 1) * anisoY;
    const near = (a: number, b: number): boolean => Math.abs(a - b) <= 0.5;
    const measured: { delta: number; extent: number }[] = [];
    for (const { from, box } of parts) {
      const lines = gridOf(box)?.lines;
      const k = src.lines.findIndex(l => l.charStart === from);
      if (!lines?.length || k < 0) return undefined;
      const delta = baseline(source, src.lines[k]) - baseline(box, lines[0]);
      const sameLines = lines.every((line, i) => {
        const s = src.lines[k + i];
        return (
          !!s &&
          line.charEnd - line.charStart === s.charEnd - s.charStart &&
          near(line.stops[0]?.[0] ?? NaN, s.stops[0]?.[0] ?? NaN) &&
          near(baseline(box, line) + delta, baseline(source, s))
        );
      });
      if (!sameLines) return undefined;
      // Never below the ink height the overflow flag measures, or the part is born with the warn frame.
      const fit = this.cache?.frameFitOf?.(box, box.text ?? '');
      const ink = fit ? box.rect.height + fit.heightOver : 0;
      measured.push({ delta, extent: Math.max(ink, ...lines.flatMap(l => l.stops.map(s => s[3])).map(v => v / anisoY)) });
    }

    // Local (laid-out) px → rect px: the draw affine's linear part is the matrix over its mean scale.
    const m = source.transform;
    const avg = m ? textMatrixScale(m) : undefined;
    return measured.map(({ delta, extent }, j) => {
      const top = delta / anisoY;
      const next = measured[j + 1];
      const span = source.textMode === 'block' ? (next ? next.delta / anisoY : source.rect.height) - top : extent;
      return {
        dx: m && avg ? (m[2] * top) / avg : 0,
        dy: m && avg ? (m[3] * top) / avg : top,
        height: Math.max(span, extent),
      };
    });
  }

  private textEditStyle(box: RenderTextBox): TextEditStyle {
    const scale = this.viewScale || 1;
    const fontSize = box.fontSize || 0;
    const lineHeightRatio = fontSize > 0 ? box.lineHeight / fontSize : 1.2;
    const trackingPx = ((box.tracking ?? 0) / 1000) * fontSize; // PSD tracking is 1/1000 em
    // Engine's own first-line cap metrics (book px) → screen px: the caret's vertical offset must use the SAME
    // cap-height the engine draws to, not a re-measured Canvas 'H', or it drifts above/below the glyphs.
    // A followed frame draws the typed text unwrapped in its grown frame: measure the first line there.
    const grown = this.framePursuesText(box) ? this.liveEditRect : undefined;
    const cap = this.cache?.caretMetrics?.(grown ? { ...box, rect: grown } : box);
    return {
      // The host registers this exact FontFace (engine font, exact weight); fall back to the raw name if it failed.
      fontFamily: `"${editCssFontFamily(box)}", "${box.fontName}"`,
      // The CSS face is the file's bytes as they are; the engine draws them at the box's axis values (a named instance).
      fontVariationSettings: cssFontVariationSettings(this.cache?.fontVariationOf?.(box)),
      fontSizePx: fontSize * scale,
      lineHeightRatio,
      letterSpacingPx: trackingPx * scale,
      color: box.color,
      // Выключку textarea не воспроизведёт (браузер переносит по прямоугольнику, движок — по полосам
      // контура), а левый край у выключенной строки флэшевый — каретке ближе всего left.
      align: box.align === 'justify' ? 'left' : box.align,
      // The edit surface takes the case the layout draws: no small caps on a path, no case for vertical text off one.
      fontCaps: boxCaseTheLayoutDraws(box),
      // Auto-width blocks never wrap — their frame follows the line — so the caret surface must flow like point
      // text, or the browser would break the line where the engine does not and the caret would leave the glyphs.
      block: box.textMode === 'block' && !this.framePursuesText(box),
      horizontalScale: box.horizontalScale ?? 1,
      verticalScale: box.verticalScale ?? 1,
      engineCapPx: cap ? cap.capHeightPx * scale : undefined,
      ascenderDeltaPx: cap ? cap.ascenderDeltaPx * scale : undefined,
      softReturns: textLayoutAtLeast(box.textLayout, TEXT_LAYOUT_PARAGRAPHS),
    };
  }

  // ==================== Inline image crop (pan / zoom / rotate the photo within its frame) ====================

  /** Programmatic crop entry (toolbar / menu button) — same path as a double-click on the photo. */
  /**
   * Canvas hidden under a host page / shown again. Hiding settles every open session the way a teardown does
   * (pending nudge, crop, trim, path edit, text edit) and keeps the window listeners deaf until shown again.
   */
  setInputSuspended(suspended: boolean): void {
    if (suspended === this.inputSuspended) return;
    this.inputSuspended = suspended;
    if (!suspended) return;
    if (this.nudgeTimer) clearTimeout(this.nudgeTimer);
    this.commitNudge();
    this.commitActiveCrop();
    this.commitActiveTrim();
    this.commitActiveTextPath();
    this.commitActiveTextEdit();
  }

  startCrop(id: string): boolean {
    if (this.destroyed || this.inputSuspended || !this.cfg.enableInlineCrop) return false;
    if (this.cropBoxId === id) return true;
    if (this.cropBoxId) this.cancelCrop();
    if (this.trimBoxId) this.cancelTrim();
    const box = this.getRenderBox(id);
    if (box?.type !== 'image') return false;
    this.enterCropMode(id, box as RenderImageBox);
    return this.cropBoxId === id; // enterCropMode no-ops if the photo isn't decoded yet
  }

  /** Apply an in-flight crop and exit; a no-op when not cropping. Settles the overlay instead of stranding it. */
  commitActiveCrop(): void {
    if (!this.isCropping()) return;
    this.commitCrop();
  }

  /** Drop an in-flight crop and exit; a no-op when not cropping. Ends the session exactly where Escape ends it —
   *  nothing is emitted to the host, so neither the model nor its history moves. */
  cancelActiveCrop(): void {
    if (!this.isCropping()) return;
    this.cancelCrop();
  }

  /** Step the inline-crop zoom by a percent of the allowed scale range (explicit +/- buttons alongside the wheel).
   *  Clamped by the same math as the wheel, so pan/rotation survive and the ends just stop. No-op when not cropping. */
  cropZoomStep(percentDelta: number): void {
    if (!this.cropBoxId || !this.cropState) return;
    this.cropState = zoomStateByPercentDelta(this.cropState, percentDelta);
    this.renderCropFrame();
  }

  /** Current inline-crop zoom as 0–100 across the allowed scale range; null when not cropping. */
  cropZoomPercent(): number | null {
    return this.cropState ? scaleToPercent(this.cropState) : null;
  }

  /** Turn the inline-crop photo by `deltaDeg` on top of its angle — the gesture's own path: clamp ±90°, auto-scale
   *  keeps the frame covered. No-op when not cropping. */
  cropRotateStep(deltaDeg: number): void {
    if (!this.cropBoxId || !this.cropState) return;
    this.cropState = applyRotationWithAutoScale(this.cropState, this.cropState.rotation + deltaDeg);
    this.renderCropFrame();
  }

  /** Current inline-crop photo angle in degrees; null when not cropping. */
  cropRotation(): number | null {
    return this.cropState ? this.cropState.rotation : null;
  }

  private enterCropMode(id: string, box: RenderImageBox): void {
    if (this.cfg.canCropBox && !this.cfg.canCropBox(id)) return; // вето хоста одинаково для двойного клика и кнопки
    const img = this.cache?.images[id];
    if (!img || !img.width || !img.height) return; // photo not decoded → nothing to crop
    const ir: CropImageRect | null = box.imageRect
      ? {
          x: box.imageRect.x,
          y: box.imageRect.y,
          width: box.imageRect.width,
          height: box.imageRect.height,
          rotation: box.imageRect.rotation ?? 0,
        }
      : null;
    const state = createCropStateFromRects({ width: box.rect.width, height: box.rect.height }, ir, {
      width: img.width,
      height: img.height,
    });
    if (!state) return;

    this.releaseCropSettleGhost(); // a previous commit's stand-in; this session owns the layer from here
    this.cropBoxId = id;
    this.cropState = state;
    this.cropInitial = { ...state };
    this.liveCropOverrides.set(id, cropStateToImageRect(state));
    if (this.cursorCanvas) this.cursorCanvas.style.cursor = 'grab';
    this.clearSelectionCanvas();
    this.drawHover(undefined);
    this.cfg.onCropStart?.(id);
    this.cropZoomEmitted = undefined;
    this.emitCropZoom();
    window.addEventListener('wheel', this.onCropWheel, { passive: false });
    window.addEventListener('keydown', this.onCropKeyDown);
    void this.decodeCropGhost(img, ++this.cropSessionToken);
    this.paintSuperseding();
  }

  private async decodeCropGhost(img: RustImage, session: number): Promise<void> {
    this.cropGhostBitmap?.close();
    this.cropGhostBitmap = undefined;
    const id = this.cropBoxId;
    try {
      // A renderer-resident entry carries no pixels — its texture lives in wasm — so the ghost decodes the source anew.
      const src =
        img.data.byteLength || !img.version
          ? img
          : img.preservePngRgba
            ? await this.cfg.decodeImage(img.sourceUrl ?? img.version, { preservePngRgba: true })
            : await this.cfg.decodeImage(img.version);
      // Superseded while the source decoded — drop it before the full-resolution copy, not after.
      if (this.cropSessionToken !== session) return;
      const len = src.width * src.height * 4;
      if (src.data.byteLength < len) return;
      // Copy into a fresh ArrayBuffer-backed array — ImageData rejects SharedArrayBuffer-backed views.
      const data = new Uint8ClampedArray(src.data.subarray(0, len));
      const bmp = await createImageBitmap(new ImageData(data, src.width, src.height));
      if (this.cropBoxId === id && this.cropSessionToken === session) {
        this.cropGhostBitmap = bmp;
        this.drawCropPreview();
      } else {
        bmp.close();
      }
    } catch (error) {
      console.warn('[engine=rust] crop ghost decode failed', error);
    }
  }

  private cropFrameLayout(): HandleLayout | undefined {
    return this.cropBoxId ? this.sceneHandleLayout(this.cropBoxId) : undefined;
  }

  // Photo centre in book px (container origin + crop placement) — the fixed pivot for scale/rotate gestures.
  private cropGhostCenterBook(): Point | undefined {
    if (!this.cropState || !this.cropBoxId) return undefined;
    const box = this.getRenderBox(this.cropBoxId);
    if (!box) return undefined;
    const off = this.groupOffsets.get(this.cropBoxId);
    const ir = cropStateToImageRect(this.cropState);
    return {
      x: box.rect.x + (off?.x ?? 0) + ir.x + ir.width / 2,
      y: box.rect.y + (off?.y ?? 0) + ir.y + ir.height / 2,
    };
  }

  private pointInCropFrame(book: Point): boolean {
    if (!this.cropBoxId) return false;
    const box = this.getRenderBox(this.cropBoxId);
    if (!box) return false;
    const off = this.groupOffsets.get(this.cropBoxId);
    const r = { ...box.rect, x: box.rect.x + (off?.x ?? 0), y: box.rect.y + (off?.y ?? 0) };
    const rot = r.rotation ?? 0;
    const p = rot ? rotatePointAround(book, { x: r.x + r.width / 2, y: r.y + r.height / 2 }, -rot) : book;
    return p.x >= r.x && p.x <= r.x + r.width && p.y >= r.y && p.y <= r.y + r.height;
  }

  private onCropPointerDown(event: MouseEvent): void {
    if (!this.cropState || !this.cropBoxId) return;
    const ptr = this.pointerToHandlePx(event.clientX, event.clientY);
    const book = this.clientToBook(event.clientX, event.clientY);
    const layout = this.cropFrameLayout();
    if (!ptr || !book || !layout) return;
    event.preventDefault();
    event.stopPropagation();

    if (this.cropModified()) {
      const rp = this.cropResetPos(layout);
      if (Math.hypot(ptr.x - rp.x, ptr.y - rp.y) <= CROP_RESET_R_PX + 2) {
        this.resetCrop();
        return;
      }
    }

    const a = layout.anchors;
    const within = (p: Point): boolean => Math.hypot(ptr.x - p.x, ptr.y - p.y) <= HANDLE_HIT_PX;
    const center = this.cropGhostCenterBook();

    if (center && (within(a['top-left']) || within(a['top-right']) || within(a['bottom-right']) || within(a['bottom-left']))) {
      this.cropGesture = {
        kind: 'rotate',
        centerBook: center,
        startAngleRad: Math.atan2(book.y - center.y, book.x - center.x),
        startRotation: this.cropState.rotation,
      };
      this.beginCropPointerTracking();
      return;
    }

    if (
      center &&
      (within(a['top-center']) || within(a['bottom-center']) || within(a['middle-left']) || within(a['middle-right']))
    ) {
      const dist = Math.hypot(book.x - center.x, book.y - center.y) || 1;
      this.cropGesture = { kind: 'scale', centerBook: center, startDist: dist, startScale: this.cropState.scale };
      this.beginCropPointerTracking();
      return;
    }

    if (this.pointInCropFrame(book)) {
      this.cropGesture = {
        kind: 'pan',
        startBook: book,
        startOffsetX: this.cropState.offsetX,
        startOffsetY: this.cropState.offsetY,
      };
      if (this.cursorCanvas) this.cursorCanvas.style.cursor = 'grabbing';
      this.beginCropPointerTracking();
      return;
    }

    this.commitCrop({ suppressTrailingClick: true }); // click outside the frame applies the crop and exits
  }

  private beginCropPointerTracking(): void {
    this.armWindowDrag(this.onCropMove, this.onCropUp);
  }

  private onCropMove = (event: MouseEvent): void => {
    const g = this.cropGesture;
    if (!g || !this.cropState) return;
    const book = this.clientToBook(event.clientX, event.clientY);
    if (!book) return;
    if (g.kind === 'pan') {
      this.cropState = panState(this.cropState, g.startOffsetX, g.startOffsetY, book.x - g.startBook.x, book.y - g.startBook.y);
    } else if (g.kind === 'scale') {
      const dist = Math.hypot(book.x - g.centerBook.x, book.y - g.centerBook.y);
      this.cropState = scaleStateByRatio(this.cropState, g.startScale, g.startDist > 0 ? dist / g.startDist : 1);
    } else {
      const ang = Math.atan2(book.y - g.centerBook.y, book.x - g.centerBook.x);
      const deltaDeg = ((ang - g.startAngleRad) * 180) / Math.PI;
      // Raw angle from the gesture start — the magnet lives in applyRotationWithAutoScale, so 0° is escapable.
      this.cropState = applyRotationWithAutoScale(this.cropState, g.startRotation + deltaDeg);
    }

    this.renderCropFrame();
  };

  private onCropUp = (): void => {
    this.cropGesture = undefined;
    this.disarmWindowDrag(this.onCropMove, this.onCropUp);
    if (this.cursorCanvas && this.cropBoxId) this.cursorCanvas.style.cursor = 'grab';
  };

  private onCropWheel = (event: WheelEvent): void => {
    if (this.inputSuspended || !this.cropBoxId || !this.cropState || !this.overlayCanvas) return;
    const rect = this.overlayCanvas.getBoundingClientRect();
    const inside =
      event.clientX >= rect.left && event.clientX <= rect.right && event.clientY >= rect.top && event.clientY <= rect.bottom;
    if (!inside) return;
    event.preventDefault();
    event.stopPropagation();
    if (event.altKey) {
      // Alt+wheel rotates (use the larger axis — macOS Option+scroll redirects delta to X).
      const raw = Math.abs(event.deltaY) >= Math.abs(event.deltaX) ? event.deltaY : event.deltaX;
      const delta = Math.sign(raw) * Math.min(Math.abs(raw) * WHEEL_ROTATION_SENSITIVITY, WHEEL_ROTATION_MAX_DELTA);
      // Accumulate the unmagnetised angle across the session, else every tick inside ±2° snaps back to 0 and the
      // wheel can never leave it. The accumulator is stale once anything else moved the angle → reseed from state.
      const carried =
        this.cropWheelRotationRaw !== undefined && clampAndMagnetRotation(this.cropWheelRotationRaw) === this.cropState.rotation
          ? this.cropWheelRotationRaw
          : this.cropState.rotation;
      this.cropWheelRotationRaw = Math.max(-MAX_ROTATION, Math.min(MAX_ROTATION, carried + delta));
      this.cropState = applyRotationWithAutoScale(this.cropState, this.cropWheelRotationRaw);
    } else {
      // Scroll/pinch up = zoom in (negate deltaY). Browsers mark trackpad pinch with ctrlKey.
      const boost = event.ctrlKey || event.metaKey ? PINCH_ZOOM_BOOST : 1;
      const delta =
        Math.sign(-event.deltaY) *
        Math.min(Math.abs(event.deltaY) * WHEEL_ZOOM_SENSITIVITY * boost, WHEEL_ZOOM_MAX_DELTA * boost);
      this.cropState = zoomStateByPercentDelta(this.cropState, delta);
    }

    this.renderCropFrame();
  };

  private onCropKeyDown = (event: KeyboardEvent): void => {
    if (this.inputSuspended || !this.cropBoxId) return;
    if (event.key === 'Escape') {
      event.preventDefault();
      this.cancelCrop();
    } else if (event.key === 'Enter') {
      event.preventDefault();
      this.commitCrop();
    }
  };

  private renderCropFrame(): void {
    if (!this.cropBoxId || !this.cropState) return;
    const ir = cropStateToImageRect(this.cropState);
    this.liveCropOverrides.set(this.cropBoxId, ir);
    this.cfg.onCropRectChange?.(this.cropBoxId, { x: ir.x, y: ir.y, width: ir.width, height: ir.height, rotation: ir.rotation });
    this.drawCropPreview();
    this.drawHandles();
    this.emitCropZoom();
  }

  // Every crop frame passes through here, but only a real scale change reaches the host — a pan drag would
  // otherwise cost the host a change-detection pass per mousemove for a number that never moved.
  private emitCropZoom(): void {
    if (!this.cropBoxId || !this.cropState) return;
    const percent = scaleToPercent(this.cropState);
    if (this.cropZoomEmitted !== undefined && Math.abs(percent - this.cropZoomEmitted) < 0.05) return;
    this.cropZoomEmitted = percent;
    this.cfg.onCropZoomChange?.(this.cropBoxId, percent);
  }

  private cropModified(): boolean {
    if (!this.cropState || !this.cropInitial) return false;
    return (
      Math.abs(this.cropState.rotation - this.cropInitial.rotation) > 0.01 ||
      Math.abs(this.cropState.scale - this.cropInitial.scale) > 1e-4 ||
      Math.abs(this.cropState.offsetX - this.cropInitial.offsetX) > 0.5 ||
      Math.abs(this.cropState.offsetY - this.cropInitial.offsetY) > 0.5
    );
  }

  private resetCrop(): void {
    if (!this.cropInitial) return;
    this.cropState = { ...this.cropInitial };
    this.renderCropFrame();
  }

  private commitCrop(opts?: { suppressTrailingClick?: boolean }): void {
    const id = this.cropBoxId;
    if (!id || !this.cropState) {
      this.exitCrop();
      return;
    }

    const ir = cropStateToImageRect(this.cropState);
    const rect = { x: ir.x, y: ir.y, width: ir.width, height: ir.height, rotation: ir.rotation };
    // The final placement goes out on the live channel too, so a session committed without a single change
    // still leaves the host on the committed numbers.
    this.cfg.onCropRectChange?.(id, rect);
    this.cfg.onCropCommit?.(id, rect);
    // A pointer commit has a trailing click to swallow; Enter/programmatic settles do not (else the next click is eaten).
    if (opts?.suppressTrailingClick) this.suppressClick = true;
    // Drop the drag canvas only at settle — the overlay is still at the pre-crop position, so dropping it now would flash.
    // Park the placement the layer is showing along with it: from here the crop session is over, so nothing else can
    // repaint that layer, and a camera move (the panes a mode switch closes, a pan, a zoom) would otherwise leave it
    // baked at the pre-move transform — a second, shifted copy of the photo over the spread that already slid.
    if (this.cropGhostBitmap) {
      this.cropSettleGhost = { id, rect: ir, bitmap: this.cropGhostBitmap };
      this.cropGhostBitmap = undefined; // ownership moves to the parked record, so exitCrop must not close it
    }

    this.dragSettlePending = true;
    this.exitCrop({ keepDragCanvas: true });
    this.scheduleRender(0); // reconcile from the host model (now carrying the committed crop)
  }

  private cancelCrop(): void {
    this.exitCrop();
    this.scheduleRender(0); // revert to the model's stored crop
  }

  private exitCrop(opts?: { keepDragCanvas?: boolean }): void {
    const id = this.cropBoxId;
    this.cropGesture = undefined;
    this.cropBoxId = undefined;
    this.cropState = undefined;
    this.cropInitial = undefined;
    this.cropZoomEmitted = undefined;
    // Накопитель колёсика живёт ровно одну сессию — иначе в следующей первая насечка не примагнитится к нулю.
    this.cropWheelRotationRaw = undefined;
    this.liveCropOverrides.clear();
    this.cropGhostBitmap?.close();
    this.cropGhostBitmap = undefined;
    this.disarmWindowDrag(this.onCropMove, this.onCropUp);
    window.removeEventListener('wheel', this.onCropWheel);
    window.removeEventListener('keydown', this.onCropKeyDown);
    if (this.cursorCanvas) this.cursorCanvas.style.cursor = 'default';
    if (!opts?.keepDragCanvas) this.removeDragCanvas();
    if (id) this.cfg.onCropEnd?.(id);
  }

  private updateCropCursor(event: MouseEvent): void {
    const canvas = this.cursorCanvas;
    if (!canvas) return;
    const ptr = this.pointerToHandlePx(event.clientX, event.clientY);
    const layout = this.cropFrameLayout();
    if (!ptr || !layout) {
      canvas.style.cursor = 'grab';
      return;
    }

    const a = layout.anchors;
    const within = (p: Point): boolean => Math.hypot(ptr.x - p.x, ptr.y - p.y) <= HANDLE_HIT_PX;
    for (const corner of ['top-left', 'top-right', 'bottom-right', 'bottom-left'] as const) {
      if (within(a[corner])) {
        canvas.style.cursor = CROP_ROTATE_CURSOR[corner];
        return;
      }
    }

    // A side handle zooms along its own axis, so the arrows point the way the photo will travel.
    if (within(a['middle-left']) || within(a['middle-right'])) {
      canvas.style.cursor = CROP_ZOOM_CURSOR.horizontal;
      return;
    }

    if (within(a['top-center']) || within(a['bottom-center'])) {
      canvas.style.cursor = CROP_ZOOM_CURSOR.vertical;
      return;
    }

    const book = this.clientToBook(event.clientX, event.clientY);
    canvas.style.cursor = book && this.pointInCropFrame(book) ? 'grab' : 'default';
  }

  private cropResetPos(layout: HandleLayout): Point {
    const tr = layout.anchors['top-right'];
    const tl = layout.anchors['top-left'];
    const br = layout.anchors['bottom-right'];
    const center = { x: (tl.x + br.x) / 2, y: (tl.y + br.y) / 2 };
    const dx = center.x - tr.x;
    const dy = center.y - tr.y;
    const len = Math.hypot(dx, dy) || 1;
    return { x: tr.x + (dx / len) * CROP_RESET_PAD_PX, y: tr.y + (dy / len) * CROP_RESET_PAD_PX };
  }

  // ==================== Decor trim (edge-resize the window inside fixed content) ====================

  /** Programmatic trim entry (toolbar / menu button) — same path as a double-click on the decor box. */
  startTrim(id: string): boolean {
    if (this.destroyed || this.inputSuspended || !this.cfg.enableDecorTrim) return false;
    if (this.trimBoxId === id) return true;
    if (this.trimBoxId) this.cancelTrim();
    if (this.cropBoxId) this.cancelCrop();
    const box = this.getRenderBox(id);
    if (!canTrimDecorBox(box)) return false;
    this.enterTrimMode(id, box);
    return this.trimBoxId === id;
  }

  /** Apply an in-flight trim and exit; a no-op when not trimming. */
  commitActiveTrim(): void {
    if (!this.trimBoxId) return;
    this.commitTrim();
  }

  private enterTrimMode(id: string, box: RenderObjectBox | RenderSmartObjectBox): void {
    const off = this.groupOffsets.get(id);
    const seed = beginTrim(box);
    this.trimBoxId = id;
    this.trimState = {
      window: {
        x: seed.window.x + (off?.x ?? 0),
        y: seed.window.y + (off?.y ?? 0),
        width: seed.window.width,
        height: seed.window.height,
      },
      content: {
        x: seed.content.x + (off?.x ?? 0),
        y: seed.content.y + (off?.y ?? 0),
        width: seed.content.width,
        height: seed.content.height,
      },
    };
    this.clearSelectionCanvas();
    this.drawHover(undefined);
    window.addEventListener('keydown', this.onTrimKeyDown);
    this.renderTrimFrame();
  }

  // Trim's own handle frame — a plain (unrotated-about-viewer, box-rotation-aware) resize layout, since trim never
  // rotates the box and drawableRects would lag a frame behind the live window during the gesture.
  private trimFrameLayout(): HandleLayout | undefined {
    if (!this.trimBoxId || !this.trimState) return undefined;
    const box = this.getRenderBox(this.trimBoxId);
    if (!box) return undefined;
    return this.bookHandleLayout(this.trimState.window, box.rect.rotation ?? 0);
  }

  private onTrimPointerDown(event: MouseEvent): void {
    if (!this.trimState || !this.trimBoxId) return;
    const ptr = this.pointerToHandlePx(event.clientX, event.clientY);
    const book = this.clientToBook(event.clientX, event.clientY);
    const layout = this.trimFrameLayout();
    if (!ptr || !book || !layout) return;
    event.preventDefault();
    event.stopPropagation();

    const handle = this.handleAt(ptr, layout);
    if (!handle || handle === 'rotate') {
      // Trim has no pan and no rotate: a body press is a missed grab and keeps the mode; clicking OUTSIDE the frame
      // applies and exits, same as crop.
      if (!this.pointInTrimFrame(book)) this.commitTrim({ suppressTrailingClick: true });
      return;
    }

    this.trimGesture = { handle, startWindow: { ...this.trimState.window }, startPointer: book };
    // Same lift as a resize gesture: the box moves to the drag canvas and the rest of the spread is painted once as a
    // static backdrop, so a drag frame re-renders ONE box instead of the whole scene.
    void this.beginDrag([this.trimBoxId]);
    this.beginTrimPointerTracking();
  }

  private beginTrimPointerTracking(): void {
    this.armWindowDrag(this.onTrimMove, this.onTrimUp);
  }

  private onTrimMove = (event: MouseEvent): void => {
    const g = this.trimGesture;
    if (!g || !this.trimState || !this.trimBoxId) return;
    const book = this.clientToBook(event.clientX, event.clientY);
    if (!book) return;
    const box = this.getRenderBox(this.trimBoxId);
    const raw = resizeRect({
      anchor: g.handle,
      startRect: g.startWindow,
      rotationDeg: box?.rect.rotation ?? 0,
      pointer: book,
      minSize: TRIM_MIN_SIZE_PX,
    });
    this.trimState = {
      window: clampWindowToContent(raw, this.trimState.content, TRIM_MIN_SIZE_PX),
      content: this.trimState.content,
    };
    this.renderTrimFrame();
  };

  private onTrimUp = (): void => {
    this.trimGesture = undefined;
    this.cancelTrimPreview();
    this.disarmWindowDrag(this.onTrimMove, this.onTrimUp);
    this.endDrag();
  };

  private onTrimKeyDown = (event: KeyboardEvent): void => {
    if (this.inputSuspended || !this.trimBoxId) return;
    if (event.key === 'Escape') {
      event.preventDefault();
      this.cancelTrim();
    } else if (event.key === 'Enter') {
      event.preventDefault();
      this.commitTrim();
    }
  };

  // Live window/content override + chrome redraw on every gesture step; the actual clipped content repaints on a
  // short debounce (schedulePaint) — same split as a live text-edit keystroke, handles stay synchronous, pixels catch up.
  private renderTrimFrame(): void {
    if (!this.trimBoxId || !this.trimState) return;
    const id = this.trimBoxId;
    const box = this.getRenderBox(id);
    this.liveTrimOverrides.set(id, {
      window: { ...this.toLocalTrimRect(id, this.trimState.window), rotation: box?.rect.rotation },
      content: this.toLocalTrimRect(id, this.trimState.content),
    });
    this.drawHandles();
    if (this.trimGesture) this.emitTrimPreview();
    else this.scheduleLivePaint(); // no lift armed (enter/exit, programmatic) — the full scene is the only surface
  }

  // Mirrors emitResizePreview: one apply per frame, and previewInFlight drops a frame whose predecessor is still
  // rasterising. Without that guard the applies queue faster than they paint and the drag stutters.
  private emitTrimPreview(): void {
    if (this.trimPreviewRaf !== undefined) return;
    this.trimPreviewRaf = requestAnimationFrame(() => {
      this.trimPreviewRaf = undefined;
      const id = this.trimBoxId;
      if (!id || !this.dragCanvas || this.previewInFlight) return;
      this.previewInFlight = true;
      void this.renderLiftTo(this.dragCanvas, [id]).finally(() => {
        this.previewInFlight = false;
      });
    });
  }

  private cancelTrimPreview(): void {
    if (this.trimPreviewRaf === undefined) return;
    cancelAnimationFrame(this.trimPreviewRaf);
    this.trimPreviewRaf = undefined;
  }

  // Same box-local subtraction as toOwnRect, but keeps the TrimRect object shape trim's write-back needs (not a tuple).
  private toLocalTrimRect(id: string, r: TrimRect): TrimRect {
    const off = this.groupOffsets.get(id);
    return { x: r.x - (off?.x ?? 0), y: r.y - (off?.y ?? 0), width: r.width, height: r.height };
  }

  private commitTrim(opts?: { suppressTrailingClick?: boolean }): void {
    const id = this.trimBoxId;
    const state = this.trimState;
    if (!id || !state) {
      this.exitTrim();
      return;
    }

    const container = this.toLocalTrimRect(id, state.window);
    const content = this.toLocalTrimRect(id, state.content);
    this.cfg.onTrimCommit?.(id, {
      container,
      content: isUntrimmed(container, content) ? undefined : content,
    });
    // A pointer commit has a trailing click to swallow; Enter/programmatic settles do not (else the next click is eaten).
    if (opts?.suppressTrailingClick) this.suppressClick = true;
    this.exitTrim();
    this.scheduleRender(0);
  }

  // Mirrors pointInCropFrame, but the frame is the trim WINDOW (absolute book px), not the box rect.
  private pointInTrimFrame(book: Point): boolean {
    const state = this.trimState;
    if (!state || !this.trimBoxId) return false;
    const r = state.window;
    const rot = this.getRenderBox(this.trimBoxId)?.rect.rotation ?? 0;
    const p = rot ? rotatePointAround(book, { x: r.x + r.width / 2, y: r.y + r.height / 2 }, -rot) : book;
    return p.x >= r.x && p.x <= r.x + r.width && p.y >= r.y && p.y <= r.y + r.height;
  }

  private cancelTrim(): void {
    this.exitTrim();
    this.scheduleRender(0);
  }

  private exitTrim(): void {
    this.trimGesture = undefined;
    this.trimBoxId = undefined;
    this.trimState = undefined;
    this.liveTrimOverrides.clear();
    this.disarmWindowDrag(this.onTrimMove, this.onTrimUp);
    window.removeEventListener('keydown', this.onTrimKeyDown);
    if (this.cursorCanvas) this.cursorCanvas.style.cursor = 'default';
  }

  private updateTrimCursor(event: MouseEvent): void {
    const canvas = this.cursorCanvas;
    if (!canvas) return;
    const ptr = this.pointerToHandlePx(event.clientX, event.clientY);
    const layout = this.trimFrameLayout();
    const handle = ptr && layout ? this.handleAt(ptr, layout) : undefined;
    this.setHoveredHandle(handle && handle !== 'rotate' ? handle : undefined);
    if (!handle || handle === 'rotate' || !layout) {
      canvas.style.cursor = 'default';
      return;
    }

    canvas.style.cursor = handleCursorFor(layout.anchors, handle);
  }

  // ==================== Text-on-path editing (curve anchors / text position along the curve) ====================

  /** True while path-edit mode owns the pointer (host camera/selection step aside). */
  isPathEditing(): boolean {
    return this.pathEditBoxId !== undefined;
  }

  /** Panel entry («Кривая» = 'edit', «Положение» = 'range') — only for a text box that carries a textPath. */
  startTextPathEdit(id: string, mode: PathEditMode): boolean {
    if (this.destroyed || this.inputSuspended || this.gesture || this.press?.active || this.editingBoxId) return false;
    if (this.cropBoxId) this.commitActiveCrop();
    if (this.trimBoxId) this.commitActiveTrim();
    if (this.pathEditBoxId === id) {
      this.setTextPathEditMode(mode);
      return true;
    }

    if (this.pathEditBoxId) this.exitPathEdit();
    const box = this.getRenderBox(id);
    if (box?.type !== 'text') return false;
    const tp = (box as RenderTextBox).textPath;
    if (!tp?.d) return false;

    this.pathEditBoxId = id;
    this.pathEditMode = mode;
    this.pathEditLiveD = tp.d;
    this.pathEditTp = {
      d: tp.d,
      closed: !!tp.closed,
      startOffset: tp.startOffset,
      pathRangeStart: tp.pathRangeStart,
      pathRangeEnd: tp.pathRangeEnd,
      flip: tp.flip,
      source: tp.source,
    };
    this.pathEditSession = createPathEditSession(this.pathEditTp);
    // The codec re-emits L segments as cubics — normalize the reference d so a no-op gesture never commits.
    this.pathEditTp.d = sessionD(this.pathEditSession);
    this.pathEditSelected = undefined;
    this.clearSelectionCanvas();
    this.drawHover(undefined);
    if (this.cursorCanvas) this.cursorCanvas.style.cursor = 'default';
    window.addEventListener('keydown', this.onPathEditKeyDown);
    this.cfg.onTextPathModeChange?.(id, mode);
    this.drawHandles();
    return true;
  }

  /** Switch range↔edit in place without re-entering the mode. */
  setTextPathEditMode(mode: PathEditMode): void {
    if (!this.pathEditBoxId || this.pathEditMode === mode) return;
    this.pathEditMode = mode;
    this.pathEditSelected = undefined;
    this.cfg.onTextPathModeChange?.(this.pathEditBoxId, mode);
    this.drawHandles();
  }

  /** Settle path-edit mode (selection change / teardown); per-gesture commits have already landed. */
  commitActiveTextPath(): void {
    if (this.pathEditBoxId) this.exitPathEdit();
  }

  /** Save an open inline text edit (host switches the spread under it — no blur ever fires). */
  commitActiveTextEdit(): void {
    // Not editingBoxId: entering a box over a still-composing session clears it while the new session stays open.
    if (this.textEdit?.active) this.textEdit.commit(true);
  }

  cancelTextPathEdit(): void {
    if (this.pathEditBoxId) this.exitPathEdit();
  }

  private exitPathEdit(opts?: { suppressTrailingClick?: boolean }): void {
    const id = this.pathEditBoxId;
    this.pathEditGesture = undefined;
    this.pathEditBoxId = undefined;
    this.pathEditMode = undefined;
    this.pathEditTp = undefined;
    this.pathEditLiveD = undefined;
    this.pathEditSession = undefined;
    this.pathEditSelected = undefined;
    this.disarmWindowDrag(this.onPathEditMove, this.onPathEditUp);
    window.removeEventListener('keydown', this.onPathEditKeyDown);
    if (this.cursorCanvas) this.cursorCanvas.style.cursor = 'default';
    if (opts?.suppressTrailingClick) this.suppressClick = true;
    if (id) this.cfg.onTextPathModeChange?.(id, null);
    this.scheduleRender(0); // restore selection chrome and reconcile committed geometry from the host model
  }

  // The path `d` lives in the box's coordinate frame — group children need the group origin added (crop parity).
  private pathEditOffset(): Point {
    const off = this.pathEditBoxId ? this.groupOffsets.get(this.pathEditBoxId) : undefined;
    return off ?? { x: 0, y: 0 };
  }

  private bookToPathPt(book: Point): Point {
    const o = this.pathEditOffset();
    return { x: book.x - o.x, y: book.y - o.y };
  }

  private pathPtToHandlePx(p: { x: number; y: number }): Point {
    const o = this.pathEditOffset();
    const s = this.viewScale || 1;
    return { x: (p.x + o.x) * s, y: (p.y + o.y) * s };
  }

  private pathEditHitTolerance(): number {
    return HANDLE_HIT_PX / (this.viewScale || 1);
  }

  private onPathEditPointerDown(event: MouseEvent): void {
    const tp = this.pathEditTp;
    const session = this.pathEditSession;
    if (!tp || !session || !this.pathEditBoxId) return;
    const book = this.clientToBook(event.clientX, event.clientY);
    if (!book) return;
    event.preventDefault();
    event.stopPropagation();
    const p = this.bookToPathPt(book);
    const tol = this.pathEditHitTolerance();

    if (this.pathEditMode === 'edit') {
      const sel = this.pathEditSelected;
      const selKnot = sel != null ? session.knots[sel] : undefined;
      if (sel != null && selKnot) {
        for (const side of ['in', 'out'] as const) {
          const h = selKnot[side];
          if (Math.hypot(p.x - h[0], p.y - h[1]) <= tol) {
            this.pathEditGesture = { kind: 'handle', index: sel, side, startKnots: session.knots.map(k => ({ ...k })) };
            this.armWindowDrag(this.onPathEditMove, this.onPathEditUp);
            return;
          }
        }
      }

      const ai = session.knots.findIndex(k => Math.hypot(p.x - k.anchor[0], p.y - k.anchor[1]) <= tol);
      if (ai >= 0) {
        this.pathEditSelected = ai;
        this.pathEditGesture = { kind: 'anchor', index: ai, startBook: p, startKnots: session.knots.map(k => ({ ...k })) };
        this.armWindowDrag(this.onPathEditMove, this.onPathEditUp);
        this.drawHandles();
        return;
      }

      // Near the curve → just drop the anchor selection; away from it → apply and leave the mode (crop parity).
      if (nearestFraction(session.sampler, p).distance <= tol * 2) {
        this.pathEditSelected = undefined;
        this.drawHandles();
        return;
      }

      this.exitPathEdit({ suppressTrailingClick: true });
      return;
    }

    const { start, end } = rangeFractions(tp);
    const sp = pointAtFraction(session.sampler, start);
    const ep = pointAtFraction(session.sampler, Math.min(end, 1));
    if (Math.hypot(p.x - sp.x, p.y - sp.y) <= tol) {
      this.pathEditGesture = { kind: 'bracket', which: 'start', startTp: { ...tp } };
    } else if (Math.hypot(p.x - ep.x, p.y - ep.y) <= tol) {
      this.pathEditGesture = { kind: 'bracket', which: 'end', startTp: { ...tp } };
    } else if (nearestFraction(session.sampler, p).distance <= tol * 2) {
      this.pathEditGesture = { kind: 'text', startFrac: nearestFraction(session.sampler, p).frac, startTp: { ...tp } };
      if (this.cursorCanvas) this.cursorCanvas.style.cursor = 'grabbing';
    } else {
      this.exitPathEdit({ suppressTrailingClick: true });
      return;
    }

    this.armWindowDrag(this.onPathEditMove, this.onPathEditUp);
  }

  private onPathEditMove = (event: MouseEvent): void => {
    const g = this.pathEditGesture;
    const tp = this.pathEditTp;
    const session = this.pathEditSession;
    if (!g || !tp || !session) return;
    const book = this.clientToBook(event.clientX, event.clientY);
    if (!book) return;
    const p = this.bookToPathPt(book);
    if (g.kind === 'bracket') {
      const frac = nearestFraction(session.sampler, p).frac;
      if (g.which === 'start') tp.pathRangeStart = frac;
      else tp.pathRangeEnd = frac;
    } else if (g.kind === 'text') {
      const frac = nearestFraction(session.sampler, p).frac;
      const base = rangeFractions(g.startTp);
      const moved = shiftRange(base.start, base.end, frac - g.startFrac, session.closed);
      tp.pathRangeStart = moved.start;
      tp.pathRangeEnd = moved.end;
    } else if (g.kind === 'anchor') {
      session.knots = moveKnot(g.startKnots, g.index, p.x - g.startBook.x, p.y - g.startBook.y);
    } else {
      session.knots = moveKnotHandle(g.startKnots, g.index, g.side, p);
    }

    this.drawHandles();
  };

  private onPathEditUp = (): void => {
    const g = this.pathEditGesture;
    const id = this.pathEditBoxId;
    const tp = this.pathEditTp;
    const session = this.pathEditSession;
    this.pathEditGesture = undefined;
    this.disarmWindowDrag(this.onPathEditMove, this.onPathEditUp);
    if (this.cursorCanvas && id) this.cursorCanvas.style.cursor = 'default';
    if (!g || !id || !tp || !session) return;

    if (g.kind === 'bracket' || g.kind === 'text') {
      const { start, end } = rangeFractions(tp);
      const was = rangeFractions(g.startTp);
      if (start !== was.start || end !== was.end) this.cfg.onTextPathRange?.(id, start, end);
      return;
    }

    const d = sessionD(session);
    if (d !== tp.d) {
      tp.d = d;
      this.pathEditLiveD = d;
      refreshSessionSampler(session);
      this.cfg.onTextPathD?.(id, d);
    }
  };

  private onPathEditDblClick(event: MouseEvent): void {
    const session = this.pathEditSession;
    const tp = this.pathEditTp;
    if (this.pathEditMode !== 'edit' || !session || !tp || !this.pathEditBoxId) return;
    const book = this.clientToBook(event.clientX, event.clientY);
    if (!book) return;
    const p = this.bookToPathPt(book);
    const near = nearestFraction(session.sampler, p);
    if (near.distance > this.pathEditHitTolerance() * 2) return;

    const { knots, index } = insertKnotAtFraction(session, near.frac);
    session.knots = knots;
    this.pathEditSelected = index;
    const d = sessionD(session);
    tp.d = d;
    this.pathEditLiveD = d;
    refreshSessionSampler(session);
    this.cfg.onTextPathD?.(this.pathEditBoxId, d);
    this.drawHandles();
  }

  private onPathEditKeyDown = (event: KeyboardEvent): void => {
    if (this.inputSuspended || !this.pathEditBoxId) return;
    if (event.key === 'Escape') {
      event.preventDefault();
      const g = this.pathEditGesture;
      if (g) {
        // Revert only the in-flight gesture; committed steps stay (they are individual undo entries).
        if ((g.kind === 'bracket' || g.kind === 'text') && this.pathEditTp) {
          this.pathEditTp.pathRangeStart = g.startTp.pathRangeStart;
          this.pathEditTp.pathRangeEnd = g.startTp.pathRangeEnd;
        } else if ((g.kind === 'anchor' || g.kind === 'handle') && this.pathEditSession) {
          this.pathEditSession.knots = g.startKnots;
        }

        this.pathEditGesture = undefined;
        this.disarmWindowDrag(this.onPathEditMove, this.onPathEditUp);
        this.drawHandles();
        return;
      }

      this.exitPathEdit();
    } else if (event.key === 'Enter') {
      event.preventDefault();
      this.exitPathEdit();
    } else if ((event.key === 'Delete' || event.key === 'Backspace') && this.pathEditMode === 'edit') {
      const session = this.pathEditSession;
      const tp = this.pathEditTp;
      if (this.pathEditSelected == null || !session || !tp) return;
      event.preventDefault();
      const next = removeKnot(session.knots, session.closed, this.pathEditSelected);
      if (!next) return; // minimum 2 anchors on an open curve / 3 on a closed one
      session.knots = next;
      // Снятое выделение = гарантия «зажатая клавиша — один шаг»: автоповтору уже нечего удалять.
      this.pathEditSelected = undefined;
      const d = sessionD(session);
      tp.d = d;
      this.pathEditLiveD = d;
      refreshSessionSampler(session);
      this.cfg.onTextPathD?.(this.pathEditBoxId, d);
      this.drawHandles();
    }
  };

  private updatePathEditCursor(event: MouseEvent): void {
    const canvas = this.cursorCanvas;
    const session = this.pathEditSession;
    const tp = this.pathEditTp;
    if (!canvas || !session || !tp || this.pathEditGesture) return;
    const book = this.clientToBook(event.clientX, event.clientY);
    if (!book) {
      canvas.style.cursor = 'default';
      return;
    }

    const p = this.bookToPathPt(book);
    const tol = this.pathEditHitTolerance();
    let hot = false;
    if (this.pathEditMode === 'edit') {
      hot = session.knots.some(k => Math.hypot(p.x - k.anchor[0], p.y - k.anchor[1]) <= tol);
      const sel = this.pathEditSelected != null ? session.knots[this.pathEditSelected] : undefined;
      if (!hot && sel) {
        hot = (['in', 'out'] as const).some(side => Math.hypot(p.x - sel[side][0], p.y - sel[side][1]) <= tol);
      }
    } else {
      const { start, end } = rangeFractions(tp);
      const sp = pointAtFraction(session.sampler, start);
      const ep = pointAtFraction(session.sampler, Math.min(end, 1));
      hot = Math.hypot(p.x - sp.x, p.y - sp.y) <= tol || Math.hypot(p.x - ep.x, p.y - ep.y) <= tol;
      if (!hot && nearestFraction(session.sampler, p).distance <= tol * 2) {
        canvas.style.cursor = 'grab';
        return;
      }
    }

    canvas.style.cursor = hot ? 'pointer' : 'default';
  }

  /** The model's textPath diverged from the session (undo/redo/panel while in mode). */
  private pathEditModelChanged(liveTp: NonNullable<RenderTextBox['textPath']>): boolean {
    const tp = this.pathEditTp;
    if (!tp) return false;
    if (liveTp.d !== this.pathEditLiveD || !!liveTp.flip !== !!tp.flip) return true;
    const live = rangeFractions({ ...liveTp });
    const ours = rangeFractions(tp);
    return live.start !== ours.start || live.end !== ours.end;
  }

  private resyncPathEditFromLive(liveTp: NonNullable<RenderTextBox['textPath']>): void {
    this.pathEditLiveD = liveTp.d;
    this.pathEditTp = {
      d: liveTp.d,
      closed: !!liveTp.closed,
      startOffset: liveTp.startOffset,
      pathRangeStart: liveTp.pathRangeStart,
      pathRangeEnd: liveTp.pathRangeEnd,
      flip: liveTp.flip,
      source: liveTp.source,
    };
    this.pathEditSession = createPathEditSession(this.pathEditTp);
    this.pathEditTp.d = sessionD(this.pathEditSession);
    this.pathEditSelected = undefined;
  }

  private drawPathEditChrome(ctx: CanvasRenderingContext2D): void {
    const id = this.pathEditBoxId;
    if (!id || !this.pathEditSession || !this.pathEditTp) return;
    const live = this.getRenderBox(id);
    const liveTp = live?.type === 'text' ? (live as RenderTextBox).textPath : undefined;
    if (!liveTp?.d) {
      // The box or its curve vanished under the mode (delete/straighten/undo) — leave outside the draw pass.
      queueMicrotask(() => this.exitPathEdit());
      return;
    }

    if (!this.pathEditGesture && this.pathEditModelChanged(liveTp)) this.resyncPathEditFromLive(liveTp);
    const session = this.pathEditSession;
    const tp = this.pathEditTp;

    const mode = this.pathEditMode ?? 'range';
    const color = mode === 'edit' ? PATH_EDIT_COLOR : PATH_RANGE_COLOR;

    ctx.lineWidth = 1.5;
    ctx.strokeStyle = color;
    ctx.setLineDash([]);
    ctx.beginPath();
    for (let k = 0; k <= PATH_CURVE_SAMPLES; k++) {
      const pt = session.sampler.pointAtLength((k / PATH_CURVE_SAMPLES) * session.sampler.totalLength);
      const hp = this.pathPtToHandlePx(pt);
      if (k === 0) ctx.moveTo(hp.x, hp.y);
      else ctx.lineTo(hp.x, hp.y);
    }

    ctx.stroke();

    if (mode === 'range') {
      const { start, end } = rangeFractions(tp);
      for (const frac of [start, Math.min(end, 1)]) {
        const pt = pointAtFraction(session.sampler, frac);
        const hp = this.pathPtToHandlePx(pt);
        const n = pt.tangent + Math.PI / 2;
        const half = PATH_BRACKET_LEN_PX / 2;
        ctx.lineWidth = 3;
        ctx.beginPath();
        ctx.moveTo(hp.x - Math.cos(n) * half, hp.y - Math.sin(n) * half);
        ctx.lineTo(hp.x + Math.cos(n) * half, hp.y + Math.sin(n) * half);
        ctx.stroke();
        ctx.lineWidth = 1;
        ctx.fillStyle = HANDLE_FILL;
        ctx.beginPath();
        ctx.arc(hp.x, hp.y, CROP_HANDLE_R_PX, 0, Math.PI * 2);
        ctx.fill();
        ctx.stroke();
      }

      return;
    }

    const sel = this.pathEditSelected;
    if (sel != null && session.knots[sel]) {
      const k = session.knots[sel];
      const a = this.pathPtToHandlePx({ x: k.anchor[0], y: k.anchor[1] });
      ctx.lineWidth = 1;
      for (const side of ['in', 'out'] as const) {
        const h = this.pathPtToHandlePx({ x: k[side][0], y: k[side][1] });
        ctx.beginPath();
        ctx.moveTo(a.x, a.y);
        ctx.lineTo(h.x, h.y);
        ctx.stroke();
        ctx.fillStyle = HANDLE_FILL;
        ctx.beginPath();
        ctx.arc(h.x, h.y, PATH_HANDLE_R_PX, 0, Math.PI * 2);
        ctx.fill();
        ctx.stroke();
      }
    }

    session.knots.forEach((k, i) => {
      const a = this.pathPtToHandlePx({ x: k.anchor[0], y: k.anchor[1] });
      ctx.lineWidth = 1;
      ctx.fillStyle = i === sel ? color : HANDLE_FILL;
      ctx.beginPath();
      ctx.rect(a.x - PATH_ANCHOR_R_PX, a.y - PATH_ANCHOR_R_PX, PATH_ANCHOR_R_PX * 2, PATH_ANCHOR_R_PX * 2);
      ctx.fill();
      ctx.stroke();
    });
  }

  // While one photo is cropped its neighbours step back: a paper veil over every OTHER photo frame, laid on its own
  // quad (a rotated frame is veiled where it renders). Decor, text, the page and EMPTY frames stay untouched — an
  // empty frame's «+» must stay readable. The veil rides the crop layer UNDER the ghost, so the part of the photo
  // hanging outside its frame keeps its own alpha instead of being washed out by paper drawn over it.
  private drawCropSiblingVeil(ctx: CanvasRenderingContext2D): void {
    if (!this.cropBoxId) return; // на послекропном призраке вуалить уже некого
    ctx.save();
    ctx.fillStyle = SPREAD_PAPER;
    ctx.globalAlpha = CROP_DIM_ALPHA;
    for (const [id, r] of this.drawableRects) {
      if (id === this.cropBoxId) continue;
      if (!this.isPhotoFrame(id)) continue;
      ctx.beginPath();
      quadCorners(r).forEach((p, i) => (i === 0 ? ctx.moveTo(p.x, p.y) : ctx.lineTo(p.x, p.y)));
      ctx.closePath();
      ctx.fill();
    }

    ctx.restore();
  }

  private drawCropChrome(ctx: CanvasRenderingContext2D): void {
    const layout = this.cropFrameLayout();
    if (!layout) return;
    const a = layout.anchors;
    const tl = a['top-left'];
    const tr = a['top-right'];
    const br = a['bottom-right'];
    const bl = a['bottom-left'];
    const lerp = (p: Point, q: Point, t: number): Point => ({ x: p.x + (q.x - p.x) * t, y: p.y + (q.y - p.y) * t });

    ctx.setLineDash([]);
    ctx.lineWidth = 1.5;
    ctx.strokeStyle = CROP_BORDER_COLOR;
    ctx.beginPath();
    ctx.moveTo(tl.x, tl.y);
    ctx.lineTo(tr.x, tr.y);
    ctx.lineTo(br.x, br.y);
    ctx.lineTo(bl.x, bl.y);
    ctx.closePath();
    ctx.stroke();

    ctx.strokeStyle = CROP_GRID_COLOR;
    ctx.lineWidth = 1;
    for (const t of [1 / 3, 2 / 3]) {
      const top = lerp(tl, tr, t);
      const bot = lerp(bl, br, t);
      ctx.beginPath();
      ctx.moveTo(top.x, top.y);
      ctx.lineTo(bot.x, bot.y);
      ctx.stroke();
      const left = lerp(tl, bl, t);
      const right = lerp(tr, br, t);
      ctx.beginPath();
      ctx.moveTo(left.x, left.y);
      ctx.lineTo(right.x, right.y);
      ctx.stroke();
    }

    ctx.fillStyle = CROP_HANDLE_FILL;
    ctx.strokeStyle = CROP_BORDER_COLOR;
    ctx.lineWidth = 1.5;
    for (const c of [tl, tr, br, bl]) {
      ctx.beginPath();
      ctx.arc(c.x, c.y, CROP_HANDLE_R_PX, 0, Math.PI * 2);
      ctx.fill();
      ctx.stroke();
    }

    for (const m of [a['top-center'], a['middle-right'], a['bottom-center'], a['middle-left']]) {
      ctx.beginPath();
      ctx.rect(m.x - CROP_HANDLE_R_PX, m.y - CROP_HANDLE_R_PX, CROP_HANDLE_R_PX * 2, CROP_HANDLE_R_PX * 2);
      ctx.fill();
      ctx.stroke();
    }

    if (this.cropModified()) this.drawCropResetButton(ctx, this.cropResetPos(layout));
  }

  private drawCropResetButton(ctx: CanvasRenderingContext2D, pos: Point): void {
    ctx.save();
    ctx.beginPath();
    ctx.arc(pos.x, pos.y, CROP_RESET_R_PX, 0, Math.PI * 2);
    ctx.fillStyle = 'rgba(0,0,0,0.55)';
    ctx.fill();
    ctx.strokeStyle = '#ffffff';
    ctx.lineWidth = 1.5;
    ctx.lineCap = 'round';
    const h = CROP_RESET_R_PX * 0.45;
    ctx.beginPath();
    ctx.moveTo(pos.x - h, pos.y - h);
    ctx.lineTo(pos.x + h, pos.y + h);
    ctx.moveTo(pos.x + h, pos.y - h);
    ctx.lineTo(pos.x - h, pos.y + h);
    ctx.stroke();
    ctx.restore();
  }

  // Trim chrome mirrors the photo-crop affordance (distinct blue frame + rule-of-thirds + crop-style handles)
  // so trim mode reads clearly as "cropping", not a plain selection. No rotate handle — trim never rotates or pans.
  private drawTrimChrome(ctx: CanvasRenderingContext2D): void {
    const layout = this.trimFrameLayout();
    if (!layout) return;
    const a = layout.anchors;
    const tl = a['top-left'];
    const tr = a['top-right'];
    const br = a['bottom-right'];
    const bl = a['bottom-left'];
    const lerp = (p: Point, q: Point, t: number): Point => ({ x: p.x + (q.x - p.x) * t, y: p.y + (q.y - p.y) * t });

    ctx.lineWidth = 1.5;
    ctx.strokeStyle = CROP_BORDER_COLOR;
    strokeDashedQuad(ctx, [tl, tr, br, bl], TRIM_FRAME_DASH);
    ctx.setLineDash([]);

    ctx.strokeStyle = CROP_GRID_COLOR;
    ctx.lineWidth = 1;
    for (const t of [1 / 3, 2 / 3]) {
      const top = lerp(tl, tr, t);
      const bot = lerp(bl, br, t);
      ctx.beginPath();
      ctx.moveTo(top.x, top.y);
      ctx.lineTo(bot.x, bot.y);
      ctx.stroke();
      const left = lerp(tl, bl, t);
      const right = lerp(tr, br, t);
      ctx.beginPath();
      ctx.moveTo(left.x, left.y);
      ctx.lineTo(right.x, right.y);
      ctx.stroke();
    }

    // Handles follow the frame's own edge vectors, not the screen axes, so they stay put on a rotated decor.
    const unit = (p: Point, q: Point): Point => {
      const dx = q.x - p.x;
      const dy = q.y - p.y;
      const len = Math.hypot(dx, dy) || 1;
      return { x: dx / len, y: dy / len };
    };

    const dist = (p: Point, q: Point): number => Math.hypot(q.x - p.x, q.y - p.y);
    const arm = (c: Point, n: Point): number => Math.min(TRIM_CORNER_ARM_PX, dist(c, n) * TRIM_CORNER_MAX_EDGE_FRACTION);
    const centre = {
      x: (tl.x + tr.x + br.x + bl.x) / 4,
      y: (tl.y + tr.y + br.y + bl.y) / 4,
    };
    // Which perpendicular points away from the box — the frame may be rotated, so this can't be assumed from sign.
    const outward = (d: Point, at: Point): Point => {
      const n = { x: -d.y, y: d.x };
      return (at.x - centre.x) * n.x + (at.y - centre.y) * n.y >= 0 ? n : { x: -n.x, y: -n.y };
    };

    ctx.fillStyle = CROP_BORDER_COLOR;
    ctx.lineJoin = 'miter';
    // Solid L-bracket hugging the OUTSIDE of the corner: the arms run along the edges, the body sits clear of the
    // frame line so it never covers the content being trimmed.
    for (const [c, p, n] of [
      [tl, bl, tr],
      [tr, tl, br],
      [br, tr, bl],
      [bl, br, tl],
    ] as const) {
      const v = unit(c, p);
      const u = unit(c, n);
      const lp = arm(c, p);
      const ln = arm(c, n);
      const t = TRIM_CORNER_W_PX;
      ctx.beginPath();
      ctx.moveTo(c.x - u.x * t - v.x * t, c.y - u.y * t - v.y * t);
      ctx.lineTo(c.x + u.x * ln - v.x * t, c.y + u.y * ln - v.y * t);
      ctx.lineTo(c.x + u.x * ln, c.y + u.y * ln);
      ctx.lineTo(c.x, c.y);
      ctx.lineTo(c.x + v.x * lp, c.y + v.y * lp);
      ctx.lineTo(c.x + v.x * lp - u.x * t, c.y + v.y * lp - u.y * t);
      ctx.closePath();
      ctx.fill();
    }

    // Edge bars sit outside too, same solid fill — the frame line is their inner edge.
    for (const [m, s, e] of [
      [a['top-center'], tl, tr],
      [a['middle-right'], tr, br],
      [a['bottom-center'], bl, br],
      [a['middle-left'], tl, bl],
    ] as const) {
      const d = unit(s, e);
      const o = outward(d, m);
      const half = Math.min(TRIM_EDGE_BAR_LONG_PX, dist(s, e) * TRIM_CORNER_MAX_EDGE_FRACTION) / 2;
      const t = TRIM_EDGE_BAR_SHORT_PX;
      ctx.beginPath();
      ctx.moveTo(m.x - d.x * half, m.y - d.y * half);
      ctx.lineTo(m.x + d.x * half, m.y + d.y * half);
      ctx.lineTo(m.x + d.x * half + o.x * t, m.y + d.y * half + o.y * t);
      ctx.lineTo(m.x - d.x * half + o.x * t, m.y - d.y * half + o.y * t);
      ctx.closePath();
      ctx.fill();
    }
  }

  /** Who owns the ghost layer right now: the live crop session, or — between a commit and the render that replaces
   *  it — the parked committed placement. The parked one skips the dim pass: the crop is over, so what falls outside
   *  the frame is no longer part of the picture and drawing it would read as a second copy of the photo. */
  private cropGhostToDraw(): { id: string; rect: CropImageRect; bitmap: ImageBitmap; dimOutside: boolean } | undefined {
    if (this.cropBoxId && this.cropState && this.cropGhostBitmap) {
      const rect = cropStateToImageRect(this.cropState);
      return { id: this.cropBoxId, rect, bitmap: this.cropGhostBitmap, dimOutside: true };
    }

    return this.cropSettleGhost ? { ...this.cropSettleGhost, dimOutside: false } : undefined;
  }

  // The dimmed full photo peeking outside the frame — Canvas2D on the drag canvas (under the chrome, over the clipped overlay).
  // The clipped layer always covers the frame (crop guarantees coverage), so the static overlay below never shows through.
  private drawCropPreview(): void {
    const ghost = this.cropGhostToDraw();
    if (!ghost || !this.cache) return;
    const box = this.getRenderBox(ghost.id) as RenderImageBox | undefined;
    // Use the rendered rect (carries the spread offset, e.g. the right page); box.rect is page-local → wrong origin.
    const frame = this.drawableRects.get(ghost.id);
    if (!box || !frame) {
      // The scene moved on (a spread or album switch) and the box is no longer on stage: a parked layer standing in
      // for it has nothing left to cover and would hang over the new spread until some later paint dropped it.
      if (!this.cropBoxId) this.removeDragCanvas();
      return;
    }

    const rs = this.cache.renderScale;
    const cam = this.cameraDeviceTransform();
    this.dragRegionBook = undefined; // the crop ghost sizes its own buffer (full host in camera mode), not a lift region
    const drag = this.ensureDragCanvas();
    let buffer: HTMLCanvasElement;
    let ctx: CanvasRenderingContext2D | null;
    if (cam) {
      // Camera mode: draw the ghost into a full-host buffer through the camera, so the photo overflowing the spread
      // (and the spread edges) stays visible instead of being clipped to the spread-sized scene buffer.
      const host = this.hostSize();
      drag.style.left = '0px';
      drag.style.top = '0px';
      drag.style.width = `${host.width}px`;
      drag.style.height = `${host.height}px`;
      // Showing what falls outside the frame IS the point of crop, and this buffer is the whole host — a page
      // clip left over from its drag-lift duty would cut the ghost along the spread edge.
      drag.style.clipPath = '';
      buffer = this.ensureFrameBuffer(drag, cam.outW, cam.outH);
      ctx = buffer.getContext('2d');
      if (!ctx) return;
      ctx.setTransform(1, 0, 0, 1, 0, 0);
      ctx.clearRect(0, 0, cam.outW, cam.outH);
      ctx.setTransform(cam.zoom, 0, 0, cam.zoom, cam.ex, cam.ey);
    } else {
      const { width: w, height: h } = spreadBufferSize(this.cache.viewport, rs);
      buffer = this.ensureFrameBuffer(drag, w, h);
      ctx = buffer.getContext('2d');
      if (!ctx) return;
      ctx.clearRect(0, 0, w, h);
    }

    this.drawCropSiblingVeil(ctx); // под призраком: соседи гаснут, а вылетающая за рамку часть фото остаётся читаемой
    const ir = ghost.rect;
    const cx = frame.x + (ir.x + ir.width / 2) * rs;
    const cy = frame.y + (ir.y + ir.height / 2) * rs;
    const iw = ir.width * rs;
    const ih = ir.height * rs;
    const boxRot = frame.rotation ?? 0;
    const bcx = frame.x + frame.width / 2;
    const bcy = frame.y + frame.height / 2;
    const fx = box.flipHorizontal ? -1 : 1;
    const fy = box.flipVertical ? -1 : 1;
    const applyBoxRotation = (): void => {
      if (!boxRot) return;
      ctx.translate(bcx, bcy);
      ctx.rotate((boxRot * Math.PI) / 180);
      ctx.translate(-bcx, -bcy);
    };

    const drawPhoto = (): void => {
      ctx.translate(cx, cy);
      ctx.rotate((ir.rotation * Math.PI) / 180);
      if (fx !== 1 || fy !== 1) ctx.scale(fx, fy);
      ctx.drawImage(ghost.bitmap, -iw / 2, -ih / 2, iw, ih);
    };

    if (ghost.dimOutside) {
      ctx.save();
      ctx.globalAlpha = CROP_GHOST_ALPHA;
      applyBoxRotation();
      drawPhoto();
      ctx.restore();
    }

    ctx.save();
    applyBoxRotation();
    ctx.beginPath();
    ctx.rect(frame.x, frame.y, frame.width, frame.height);
    ctx.clip();
    drawPhoto();
    ctx.restore();
    ctx.setTransform(1, 0, 0, 1, 0, 0); // don't leak the camera base transform to later drag-canvas blits
    this.presentFrame(drag, buffer);
  }

  private onMouseMove = (event: MouseEvent): void => {
    if (this.cropBoxId) {
      this.updateCropCursor(event);
      return;
    }

    if (this.trimBoxId) {
      this.updateTrimCursor(event);
      return;
    }

    if (this.pathEditBoxId) {
      this.updatePathEditCursor(event);
      return;
    }

    if (this.isPlaceMode) {
      const canvas = this.cursorCanvas;
      if (canvas && canvas.style.cursor !== PLACE_CURSOR) canvas.style.cursor = PLACE_CURSOR;
      return;
    }

    const ptr = this.pointerToHandlePx(event.clientX, event.clientY);
    if (ptr) {
      this.lastCursorPtr = ptr;
      this.updateHandleCursor(ptr, event.metaKey);
    }

    // No hover hit-test during a transform/move — it would fire a WASM hitTest + drawHover every frame and stall the drag.
    if (this.gesture || this.press || this.hoverRaf || !this.sceneJson || this.dropHovering) return;
    this.hoverRaf = requestAnimationFrame(async () => {
      this.hoverRaf = 0;
      const canvas = this.overlayCanvas;
      if (!canvas || !this.sceneJson || this.destroyed) return;
      const p = this.clientToScene(event.clientX, event.clientY);
      const hit = await this.hitTestOrNull(this.sceneJson, p);
      if (hit === null) return;
      const id = hit && this.isTargetable(hit) ? hit : undefined;
      if (id === this.hoverId) return;
      this.hoverId = id;
      this.cfg.onHover?.(id);
      await this.refreshActiveOverflow();
      this.drawHover(id);
    });
  };

  private stopPropagation = (event: Event): void => event.stopPropagation();

  private onMouseLeave = (): void => {
    this.setHoveredHandle(undefined);
    if (this.hoverId === undefined) return;
    this.hoverId = undefined;
    this.cfg.onHover?.(undefined);
    void this.refreshActiveOverflow();
    this.drawHover(undefined);
  };

  // Photoshop affordance: resize arrows on anchors, a rotate cursor, and a skew cursor when Cmd is over a side handle.
  private updateHandleCursor(ptr: Point, meta: boolean): void {
    const canvas = this.cursorCanvas;
    if (!canvas || this.gesture || this.press?.active) return;
    // Select-only mode (photographer's Редактор): no transformer chrome is drawn and no handle gesture can
    // start, so the resize/rotate/skew cursors must not appear either — they promised a rotation the mode
    // does not allow. Same gate as drawHandles and hitTestSelectionHandle.
    if (!this.transformsEnabled()) {
      this.setHoveredHandle(undefined);
      if (canvas.style.cursor !== 'default') canvas.style.cursor = 'default';
      return;
    }

    const sel = this.handleScreenLayout();
    const handle = sel ? this.handleAt(ptr, sel.layout) : undefined;
    this.setHoveredHandle(handle);
    if (!handle || !sel) {
      if (canvas.style.cursor !== 'default') canvas.style.cursor = 'default';
      return;
    }

    if (handle === 'rotate') {
      canvas.style.cursor = ROTATE_CURSOR;
      return;
    }

    if (meta && SIDE_HANDLES.has(handle) && this.canSkew(handle)) {
      canvas.style.cursor = handle === 'top-center' || handle === 'bottom-center' ? SKEW_CURSOR_H : SKEW_CURSOR_V;
      return;
    }

    canvas.style.cursor = handleCursorFor(sel.layout.anchors, handle);
  }

  private setHoveredHandle(handle: HandleId | undefined): void {
    if (this.hoveredHandle === handle) return;
    this.hoveredHandle = handle;
    this.drawHandles();
  }

  private handleAt(ptr: Point, layout: HandleLayout): HandleId | undefined {
    const handle = handleAtPoint(ptr, layout.anchors, layout.rotate, HANDLE_HIT_PX);
    return handle && this.dropsVerticalHandle(handle) ? undefined : handle;
  }

  // У свободного текста высота — производная кегля, отдельной высоты нет. Ручки верх/низ пообещали бы
  // её тянуть, поэтому их у такого блока просто нет (рисуются только углы, так что убираем попадание).
  private dropsVerticalHandle(handle: HandleId): boolean {
    if (handle !== 'top-center' && handle !== 'bottom-center') return false;
    if (this.cfg.enableTextCornerScale !== 'free' || this.selectedIds.length !== 1) return false;
    const box = this.getRenderBox(this.selectedIds[0]);
    if (box?.type !== 'text') return false;
    const text = box as RenderTextBox;
    return text.textMode !== 'block' && !text.transform && text.fontSize > 0;
  }

  // ⌘ down/up over a side handle flips the cursor between resize and skew without needing a mouse move (Photoshop
  // parity); ⌥/Alt carries no cursor meaning here — it is only the photo-swap force key.
  private onMetaKey = (event: KeyboardEvent): void => {
    if (event.key !== 'Meta' && event.key !== 'Alt') return;
    if (event.key !== 'Meta' || this.inputSuspended || this.gesture || !this.lastCursorPtr) return;
    this.updateHandleCursor(this.lastCursorPtr, event.type === 'keydown');
  };

  // Arrow keys nudge the selection (Shift = coarse). A burst debounces into ONE commit = one undo entry.
  private onKeyNudge = (event: KeyboardEvent): void => {
    const dir = NUDGE_DIRECTIONS[event.key];
    // Шелл слушает document и срабатывает раньше этого window-слушателя: погашенную им ↑/↓ (перебор
    // шаблона разворота) сдвигать рамкой нельзя — иначе одна клавиша делает два дела сразу.
    if (!dir || event.defaultPrevented || this.inputSuspended) return;
    if (this.gesture || this.press || this.editingBoxId || this.cropBoxId || this.trimBoxId || !this.cache) return;
    if (isEditableEventTarget(event.target) || isModalLayerTarget(event.target)) return;
    if (this.selectedIds.length === 0 || (!this.cfg.onNudge && !this.cfg.onMove) || !this.transformsEnabled()) return;
    event.preventDefault();
    const step = event.shiftKey ? NUDGE_STEP_COARSE_PX : NUDGE_STEP_PX;
    if (!this.nudge) {
      const start = new Map<string, { rect: TransformRect; rotation: number; transform?: number[] }>();
      for (const m of this.groupMembers()) {
        start.set(m.id, { rect: m.rect, rotation: m.rotation, transform: this.drawableRects.get(m.id)?.transform });
      }

      this.nudge = { start, dx: 0, dy: 0, parentTransformIds: this.parentTransformIds };
    }

    this.nudge.dx += dir.x * step;
    this.nudge.dy += dir.y * step;
    this.previewNudge(this.nudge);
    if (this.nudgeTimer) clearTimeout(this.nudgeTimer);
    this.nudgeTimer = setTimeout(() => this.commitNudge(), NUDGE_DEBOUNCE_MS);
  };

  // Every press moves the frame the exact step NOW — the debounce only coalesces the model write into one
  // undo entry. Without this the whole burst lands as one delayed jump and reads as «рывками, тормозит».
  private previewNudge(n: NonNullable<RustOverlayController['nudge']>): void {
    const rs = this.cache?.renderScale ?? 1;
    for (const [id, s] of n.start) {
      this.drawableRects.set(id, {
        x: (s.rect.x + n.dx) * rs,
        y: (s.rect.y + n.dy) * rs,
        width: s.rect.width * rs,
        height: s.rect.height * rs,
        rotation: s.rotation,
        transform: nudgedAffine(s.transform, n.dx * rs, n.dy * rs),
      });
    }

    this.drawHandles();
  }

  private commitNudge(): void {
    const n = this.nudge;
    this.nudge = undefined;
    this.nudgeTimer = undefined;
    if (!n || (n.dx === 0 && n.dy === 0)) return;
    const moved: MoveUpdate[] = [...n.start].map(([id, s]) => ({
      id,
      rect: this.toOwnRect(id, { x: s.rect.x + n.dx, y: s.rect.y + n.dy, width: s.rect.width, height: s.rect.height }),
      rotation: s.rotation,
      contentRect: this.translatedContentRect(id, n.dx, n.dy),
      ...(n.parentTransformIds?.has(id) && { parentTransform: true }),
    }));
    if (moved.length === 0) return; // an all-locked selection has nothing to commit — no empty undo entry
    (this.cfg.onNudge ?? this.cfg.onMove)?.(moved);
  }

  private selectedBoxGeometry(): { id: string; rect: TransformRect; rotation: number } | undefined {
    if (this.selectedIds.length !== 1 || !this.cache) return undefined;
    const id = this.selectedIds[0];
    if (this.isLocked(id)) return undefined;
    const box = findRenderBox(this.cache.boxes, id);
    if (!box) return undefined;
    const r = box.rect;
    const off = this.groupOffsets.get(id);
    return {
      id,
      rect: { x: r.x + (off?.x ?? 0), y: r.y + (off?.y ?? 0), width: r.width, height: r.height },
      rotation: r.rotation ?? 0,
    };
  }

  // Commits travel in the box's own (parent-relative) coord space — the write-back contract both editors share.
  private toOwnRect(id: string, r: TransformRect): [number, number, number, number] {
    const off = this.groupOffsets.get(id);
    return [r.x - (off?.x ?? 0), r.y - (off?.y ?? 0), r.width, r.height];
  }

  // Pointer client coords → scene coords (book × renderScale) for the WASM hit-test. The content overlay is a
  // scene buffer CSS-positioned by the camera (or stage), so the canvas-relative mapping handles both modes.
  // A render worker that crashes rejects the hit-tests queued on it: null means «no answer» and the caller drops the
  // gesture — never an unhandled rejection or a press stuck in pending. The next press asks the restarted worker.
  private hitTestOrNull(sceneJson: string, p: Point | undefined): Promise<string | undefined | null> {
    return p ? this.cfg.wasm.hitTest(sceneJson, p.x, p.y).catch((): null => null) : Promise.resolve(undefined);
  }

  private clientToScene(clientX: number, clientY: number): Point | undefined {
    const canvas = this.overlayCanvas;
    if (!canvas || !this.cache) return undefined;
    const rect = canvas.getBoundingClientRect();
    return overlayPointToScene(clientX, clientY, rect, canvas.width, canvas.height);
  }

  private clientToBook(clientX: number, clientY: number): Point | undefined {
    const canvas = this.overlayCanvas;
    if (!canvas || !this.cache) return undefined;
    const rect = canvas.getBoundingClientRect();
    if (rect.width === 0 || rect.height === 0) return undefined;
    const scene = this.clientToScene(clientX, clientY);
    return scene ? { x: scene.x / this.cache.renderScale, y: scene.y / this.cache.renderScale } : undefined;
  }

  /** Client px -> book coords (spread space) — hosts use it to place external drops. */
  clientToBookPoint(clientX: number, clientY: number): Point | undefined {
    return this.clientToBook(clientX, clientY);
  }

  private isLocked(id: string): boolean {
    return this.drawableRects.get(id)?.locked === true;
  }

  // A layer the rubber-band passes through: a marquee may start over it and never selects it (a click still selects it).
  // An id the engine reported but this role may not act on: treated as a pointer MISS everywhere a hit-test
  // becomes an action. An id with no render box stays targetable — the unknown is never silently blocked.
  private isTargetable(id: string): boolean {
    // Вето хоста именное и старше типа: оно знает про бокс то, чего тип не говорит (чужая карточка виньетки).
    if (this.cfg.canTargetBox?.(id) === false) return false;
    const types = this.cfg.getTargetableTypes?.();
    if (!types) return true;
    const box = this.getRenderBox(id);
    return box ? types.includes(box.type) : true;
  }

  private isMarqueeTransparent(id: string): boolean {
    // TODO(refactor): derive from an explicit per-box flag (e.g. marqueeTransparent), not the 'background' type.
    return (this.cache ? findRenderBox(this.cache.boxes, id) : undefined)?.type === 'background';
  }

  // Backgrounds never anchor a snap, nor a scene-only drawable with no render box (the empty frame's photo glyph);
  // the host may veto further (design editor: decor/clipart is scenery).
  private isSnapCandidate(id: string): boolean {
    const type = (this.cache ? findRenderBox(this.cache.boxes, id) : undefined)?.type;
    if (!type || type === 'background') return false;
    return this.cfg.isSnapTarget?.(type, id) ?? true;
  }

  /** Selected members for a group transform (each in book px) — group container ids contribute their own rect. */
  private groupMembers(): GroupMember[] {
    if (!this.cache) return [];
    const rs = this.cache.renderScale;
    const out: GroupMember[] = [];
    for (const id of this.selectedIds) {
      if (this.isLocked(id)) continue;
      const box = findRenderBox(this.cache.boxes, id);
      if (!box) continue;
      const r = box.rect;
      const off = this.groupOffsets.get(id);
      out.push({
        id,
        rect: { x: r.x + (off?.x ?? 0), y: r.y + (off?.y ?? 0), width: r.width, height: r.height },
        rotation: r.rotation ?? 0,
        pivot: this.drawableVisualCenter(id, rs),
      });
    }

    return out;
  }

  // The member's drawn (transformed) centre in book px — the point a group transform orbits, matching where the
  // renderer pivots rect.rotation. Undefined when the drawable isn't indexed yet (callers fall back to the rect centre).
  private drawableVisualCenter(id: string, renderScale: number): Point | undefined {
    const r = this.drawableRects.get(id);
    if (!r) return undefined;
    const corners = quadCorners(r);
    let sx = 0;
    let sy = 0;
    for (const p of corners) {
      sx += p.x;
      sy += p.y;
    }

    return { x: sx / corners.length / renderScale, y: sy / corners.length / renderScale };
  }

  /** The box's flat rect and draw affine in book px — a hit test inverse-maps through it, as the engine does.
   *  Undefined until the drawable is indexed. */
  drawnFrameOf(id: string): { x: number; y: number; width: number; height: number; transform?: number[] } | undefined {
    const r = this.drawableRects.get(id);
    const rs = this.cache?.renderScale;
    if (!r || !rs) return undefined;
    const t = r.transform?.length === 6 ? r.transform : undefined;
    return {
      x: r.x / rs,
      y: r.y / rs,
      width: r.width / rs,
      height: r.height / rs,
      transform: t && [t[0], t[1], t[2], t[3], t[4] / rs, t[5] / rs],
    };
  }

  /** The box's drawn (transformed) AABB in book px — where its glyphs actually render (quarter-turned text sits off
   *  its flat rect). Hosts arrange/align by this, not the parser rect. Undefined until the drawable is indexed. */
  visualBoundsOf(id: string): TransformRect | undefined {
    const r = this.drawableRects.get(id);
    const rs = this.cache?.renderScale;
    if (!r || !rs) return undefined;
    let minX = Infinity;
    let minY = Infinity;
    let maxX = -Infinity;
    let maxY = -Infinity;
    for (const p of quadCorners(r)) {
      const x = p.x / rs;
      const y = p.y / rs;
      if (x < minX) minX = x;
      if (y < minY) minY = y;
      if (x > maxX) maxX = x;
      if (y > maxY) maxY = y;
    }

    return { x: minX, y: minY, width: maxX - minX, height: maxY - minY };
  }

  // The multi-select frame must wrap where the members RENDER, not their flat rects: quarter-turned text keeps
  // its rotation in the draw-transform (rect.rotation stays 0), so its flat rect sits off the drawn glyphs. Union
  // the drawables' transformed corners (book px), as the single-selection frame does; fall back to the flat AABB.
  private transformAwareGroupBounds(members: readonly GroupMember[]): TransformRect | undefined {
    const rs = this.cache?.renderScale;
    if (!rs || members.length === 0) return undefined;
    let minX = Infinity;
    let minY = Infinity;
    let maxX = -Infinity;
    let maxY = -Infinity;
    const fold = (x: number, y: number): void => {
      if (x < minX) minX = x;
      if (y < minY) minY = y;
      if (x > maxX) maxX = x;
      if (y > maxY) maxY = y;
    };

    for (const m of members) {
      const r = this.drawableRects.get(m.id);
      if (r) {
        for (const p of quadCorners(r)) fold(p.x / rs, p.y / rs);
      } else {
        const b = rotatedAABB(m.rect, m.rotation);
        fold(b.x, b.y);
        fold(b.x + b.width, b.y + b.height);
      }
    }

    return { x: minX, y: minY, width: maxX - minX, height: maxY - minY };
  }

  // On a handle-miss we don't preventDefault, so the same press still reaches click-select, a move or a marquee.
  private onPointerDown = (event: MouseEvent): void => {
    // Only the left button arms gestures — a right-click is select + context menu, never a drag.
    if (event.button !== 0) return;
    // A press reaching the CANVAS is by definition outside the inline-edit textarea (it sits above and consumes its
    // own events) — i.e. a click-away. The browser would normally blur-commit it, but the paths below preventDefault
    // the press, so the blur never fired: the session stayed alive invisibly, kept painting its live text over
    // whatever the model rendered next, and the typed text was silently never committed. Commit it here, always.
    if (this.editingBoxId) this.textEdit?.commit();
    this.releasePanelFocus(event);
    // Placement mode: the click itself reports the point (onClick); never arm a drag/marquee here.
    if (this.isPlaceMode) return;
    if (this.cropBoxId) {
      this.onCropPointerDown(event);
      return;
    }

    if (this.trimBoxId) {
      this.onTrimPointerDown(event);
      return;
    }

    if (this.pathEditBoxId) {
      this.onPathEditPointerDown(event);
      return;
    }

    if (this.gesture || this.press || this.destroyed) return;
    const ptr = this.pointerToHandlePx(event.clientX, event.clientY);
    const book = this.clientToBook(event.clientX, event.clientY);
    if (!ptr || !book) return;

    const hit = this.hitTestSelectionHandle(ptr);
    if (hit) {
      event.preventDefault();
      event.stopPropagation();
      if (event.metaKey && hit.scope === 'single' && this.canSkew(hit.handle)) {
        this.startSkewGesture(hit.handle as ResizeAnchor, book, event.altKey);
      } else {
        this.startGesture(hit.scope, hit.handle, book);
      }

      return;
    }

    // WebKit treats a press it was not told to keep as the start of a native selection/element drag, and that drag
    // swallows the whole mousemove stream — the armed swap never passes its travel threshold and the gesture reads as
    // a silent no-op (Chromium keeps delivering the moves). Claim the press only where a swap can arm: 'full' mode
    // keeps the default, so text selection, focus and native scroll elsewhere are untouched.
    if (this.swapLanesArmable()) event.preventDefault();
    this.armPress(event, book);
  };

  // Most paths below claim the press (preventDefault: handles, swap lanes, crop, trim, path edit), and a claimed press
  // never moves focus: a panel button or layer row pressed before kept it, and Tab on the canvas then walked the panel
  // instead of hiding it (ENG-1338). Drop that focus by hand, as an unclaimed press would; a live inline-edit textarea
  // is left alone — its blur commits or, over the cap, discards the text.
  private releasePanelFocus(event: MouseEvent): void {
    if (this.textEdit?.active) return;
    const active = this.cfg.host.ownerDocument.activeElement;
    if (active instanceof HTMLElement && active !== event.currentTarget && active !== active.ownerDocument.body) active.blur();
  }

  /** Select-only mode with a swap channel wired: any press here can become a photo- or card-swap drag. */
  private swapLanesArmable(): boolean {
    if (this.transformsEnabled()) return false;
    const photo = !!this.cfg.onPhotoSwap && !!this.cfg.getPhotoSwapEnabled?.();
    const card = !!this.cfg.onGroupSwap && !!this.cfg.getCardSwapEnabled?.();
    return photo || card;
  }

  // Which transformer handle (if any) the pointer is over, on the current single/group selection frame.
  private hitTestSelectionHandle(ptr: Point): { scope: 'single' | 'group'; handle: HandleId } | undefined {
    if (!this.transformsEnabled()) return undefined;
    const sel = this.handleScreenLayout();
    if (!sel) return undefined;
    const handle = this.handleAt(ptr, sel.layout);

    if (!handle) return undefined;
    return { scope: this.selectedIds.length > 1 ? 'group' : 'single', handle };
  }

  private startGesture(scope: 'single' | 'group', handle: HandleId, book: Point): void {
    if (scope === 'group') {
      const members = this.groupMembers();
      const startGroup = this.transformAwareGroupBounds(members) ?? groupBounds(members);
      if (!startGroup) return;
      const startContentRects = new Map<string, TrimRect>();
      for (const m of members) {
        const content = this.decorContentRectOf(m.id);
        if (content) startContentRects.set(m.id, content);
      }

      this.gesture = {
        kind: 'group',
        handle,
        startGroup,
        groupCenter: { x: startGroup.x + startGroup.width / 2, y: startGroup.y + startGroup.height / 2 },
        startPointer: book,
        members,
        startContentRects,
        captionScales: this.cellCaptionSnapshots(handle, members),
        current: {
          frameRect: startGroup,
          frameRotation: 0,
          updates: members.map(m => ({ id: m.id, rect: m.rect, rotation: m.rotation })),
        },
      };
    } else {
      const geo = this.selectedBoxGeometry();
      if (!geo) return;
      const frame = this.gestureFrameContext(geo);
      this.gesture = {
        kind: 'single',
        handle,
        id: geo.id,
        startRect: geo.rect,
        startRotation: geo.rotation,
        center: frame.center,
        startPointer: book,
        frameAffine: frame.frameAffine,
        startContentRect: this.decorContentRectOf(geo.id),
        startImageRect: this.imageRectOf(geo.id),
        textScale: this.textScaleSnapshot(geo.id, handle, geo.rect, !!frame.frameAffine),
        current: { rect: geo.rect, rotation: geo.rotation },
      };
    }

    this.gestureMoved = false;
    this.setGestureActive(true);
    this.armWindowDrag(this.onPointerMove, this.onPointerUp);
    this.clearSelectionCanvas(); // hide the stale rust frame(s); the live frame is drawn on the handle canvas
    this.drawHandles();
    // Lift the box(es) onto the drag canvas + paint the main overlay WITHOUT them (a static backdrop, rendered once).
    // Rotate then CSS-transforms the lift; a live resize re-renders ONLY the lifted box per frame (text reflow / photo
    // cover) — either way the rest of the scene is never re-rendered during the gesture (that full re-render was the lag).
    const g = this.gesture;
    this.reflowLive = g && g.handle !== 'rotate' ? this.armResizeReflow(g) : undefined;
    if (g) {
      const own = g.kind === 'group' ? g.members.map(m => m.id) : [g.id];
      void this.beginDrag([...own, ...(this.reflowLive?.ids ?? [])], g.handle !== 'rotate');
    }

    const resizeStops =
      g?.kind === 'single' && g.handle !== 'rotate' && g.startRotation === 0 && !g.frameAffine
        ? this.buildSnapStops(new Set([g.id]))
        : undefined;
    this.resizeSnap = resizeStops ? { ...resizeStops, hold: {} } : undefined;
    this.snapGuides = [];
    this.sizeGuides = [];
  }

  // The draw-affine context a single gesture works in: the box's pure-matrix part (scene px, the
  // interactive rotation stripped about its fixed point) and the visual centre the gesture pivots on
  // (book px) — for a matrix-positioned box the drawn quad's centre, not the plain rect centre.
  private gestureFrameContext(geo: { id: string; rect: TransformRect; rotation: number }): {
    frameAffine?: Affine;
    center: Point;
  } {
    const rectCenter = { x: geo.rect.x + geo.rect.width / 2, y: geo.rect.y + geo.rect.height / 2 };
    const sceneT = this.drawableRects.get(geo.id)?.transform;
    if (sceneT?.length !== 6) return { center: rectCenter };
    const composite = [...sceneT] as Affine;
    const rs = this.cache?.renderScale || 1;
    // The composite fixes the visual centre (rotation pivots on the matrix image of the rect centre),
    // so composite(centre) both locates the pivot and lets the matrix part be recovered exactly.
    const pivotScene = applyAffinePoint(composite, { x: rectCenter.x * rs, y: rectCenter.y * rs });
    const matrix = geo.rotation ? mul(rotationAboutPoint(-geo.rotation, pivotScene.x, pivotScene.y), composite) : composite;
    return {
      frameAffine: isIdentityAffine(matrix) ? undefined : matrix,
      center: { x: pivotScene.x / rs, y: pivotScene.y / rs },
    };
  }

  // ⌘-drag a side handle skews a text box (its per-box affine); inert unless the host wires onTransform.
  private canSkew(handle: HandleId): boolean {
    if (handle === 'rotate' || !SIDE_HANDLES.has(handle) || !this.cfg.onTransform) return false;
    const geo = this.selectedBoxGeometry();
    if (!geo || !this.cache) return false;
    return findRenderBox(this.cache.boxes, geo.id)?.type === 'text';
  }

  private startSkewGesture(handle: ResizeAnchor, book: Point, fromCenter: boolean): void {
    const geo = this.selectedBoxGeometry();
    if (!geo || !this.cache) return;
    const sceneBase = this.drawableRects.get(geo.id);
    if (!sceneBase) return;
    const box = findRenderBox(this.cache.boxes, geo.id) as RenderTextBox | undefined;
    const baseMatrix: Affine = box?.transform ? ([...box.transform] as Affine) : [1, 0, 0, 1, 0, 0];
    this.gesture = {
      kind: 'skew',
      handle,
      id: geo.id,
      axis: handle === 'top-center' || handle === 'bottom-center' ? 'horizontal' : 'vertical',
      rect: geo.rect,
      baseMatrix,
      sceneBase,
      startPointer: book,
      fromCenter,
      current: { matrix: baseMatrix, shx: 0, shy: 0 },
    };
    this.gestureMoved = false;
    this.setGestureActive(true);
    this.armWindowDrag(this.onPointerMove, this.onPointerUp);
    this.clearSelectionCanvas();
    this.drawHandles();
  }

  // One edge-triggered signal for every manipulation gesture; a marquee is a selection intent, not a manipulation,
  // so it never raises it. Released only after the press/gesture teardown, i.e. after a drop's trailing reselect.
  /** Clear the selection (and with it the host's action bar) the way a click on empty space would. */
  private dropSelection(): void {
    if (this.selectedIds.length === 0) return;
    if (this.cfg.onSelect)
      this.cfg.onSelect(undefined, false); // host owns selection; it calls back setSelection([])
    else void this.setSelection([]);
  }

  /** Give the selection (and the host's action bar) back after a gesture that had to drop it. */
  private restoreSelection(id: string): void {
    if (this.cfg.onSelect) this.cfg.onSelect(id, false);
    else void this.setSelection([id]);
  }

  private setGestureActive(active: boolean): void {
    if (this.gestureActive === active) return;
    this.gestureActive = active;
    this.gestureTokenCache.clear(); // тема может смениться между жестами — кэш живёт ровно один жест
    this.cfg.onGestureActiveChange?.(active);
  }

  // Arm a press: an async hit-test classifies it (content box → move; empty/floor → marquee). Travel past the
  // threshold activates it; a release without travel falls through to click-select.
  private armPress(event: MouseEvent, book: Point): void {
    const canvas = this.overlayCanvas;
    if (!canvas || !this.sceneJson) return;
    const armed: PressState = {
      mode: 'pending',
      startBook: book,
      current: book,
      startClient: { x: event.clientX, y: event.clientY },
      active: false,
      moveIds: [],
      startRects: new Map(),
    };
    this.press = armed;
    this.armWindowDrag(this.onPressMove, this.onPressUp);
    const p = this.clientToScene(event.clientX, event.clientY);
    void this.hitTestOrNull(this.sceneJson, p).then(id => {
      if (this.destroyed || this.press !== armed) return;
      if (armed.mode !== 'pending') return; // a late answer must not trample the swap the first move already classified
      if (id === null) {
        this.endPress();
        return;
      }

      // A press inside the current selection's bounds drags the whole selection — Figma-style, over interior gaps/background too.
      if (this.cfg.onMove && this.transformsEnabled() && this.selectedIds.length > 0 && this.pressInsideSelectionBounds(book)) {
        // A locked member rides along in the selection (it can be picked from the layers panel) but must not move.
        const movable = this.withClipCompanions(this.selectedIds).filter(x => !this.isLocked(x));
        if (movable.length > 0) {
          armed.mode = 'move';
          armed.moveIds = movable;
          armed.parentTransformIds = this.parentTransformIds;
          armed.startRects = this.snapshotRects(movable);
          return;
        }
      }

      if (id && !this.isMarqueeTransparent(id) && !this.isLocked(id) && this.isTargetable(id)) {
        // Select-only mode: a press on a person-card group arms the card-swap drag (vignette port of the
        // legacy Konva drag-to-swap); everything else still falls through to click-select.
        if (!this.transformsEnabled() && this.cfg.onGroupSwap && this.cfg.getCardSwapEnabled?.()) {
          const group = findTopLevelGroup(this.cache?.boxes ?? [], id);
          if (group) {
            armed.mode = 'card-swap';
            armed.swapSourceId = group.id;
            armed.moveIds = collectLeafIds(group.children);
            return;
          }
        }

        // Select-only mode: a press on a FILLED image frame arms the photo-swap drag; an empty frame has nothing
        // to carry and still falls through to click-select.
        if (!this.transformsEnabled() && this.cfg.onPhotoSwap && this.cfg.getPhotoSwapEnabled?.() && this.hasPhoto(id)) {
          armed.mode = 'photo-swap';
          armed.swapSourceId = id;
          armed.moveIds = [id];
          return;
        }

        if (!this.cfg.onMove || !this.transformsEnabled()) {
          this.endPress(); // no move channel / select-only mode → can't move; let the trailing click select
          return;
        }

        armed.mode = 'move';
        // A clipper grabbed by its letters is the pair (FKS-12999): the press lifts to the base, which brings the
        // clipper back as a companion. Picked alone in the layers panel it still moves alone.
        const grabbed = this.clipTargetOf(id);
        const held = this.selectedIds.includes(grabbed);
        // An unselected box moves as the unit a click on it would select (a cell grabbed by its photo brings its
        // captions); the drop's reselect then selects that same unit.
        const unit = held ? undefined : this.cfg.pressUnitOf?.(grabbed, this.selectedIds).filter(x => !this.isLocked(x));
        armed.moveIds = this.withClipCompanions(held ? this.selectedIds : unit?.length ? unit : [grabbed]);
        armed.parentTransformIds = held ? this.parentTransformIds : this.explicitParentLeaves(unit?.length ? unit : [grabbed]);
        armed.reselectId = held ? undefined : grabbed;
        armed.startRects = this.snapshotRects(armed.moveIds);
      } else {
        armed.mode = 'marquee'; // empty space OR the marquee-transparent floor
      }
    });
  }

  private snapshotRects(ids: readonly string[]): Map<string, { rect: TransformRect; rotation: number }> {
    const out = new Map<string, { rect: TransformRect; rotation: number }>();
    for (const id of ids) {
      const box = this.cache ? findRenderBox(this.cache.boxes, id) : undefined;
      if (box) {
        const r = box.rect;
        const off = this.groupOffsets.get(id);
        out.set(id, {
          rect: { x: r.x + (off?.x ?? 0), y: r.y + (off?.y ?? 0), width: r.width, height: r.height },
          rotation: r.rotation ?? 0,
        });
      }
    }

    return out;
  }

  private onPressMove = (event: MouseEvent): void => {
    const p = this.press;
    if (!p) return;
    const book = this.clientToBook(event.clientX, event.clientY);
    if (book) p.current = book;
    if (p.mode === 'pending' && !this.resolvePendingSwapPress(p, event)) return; // wait for the hit-test to classify the press
    if (!p.active) {
      const dx = event.clientX - p.startClient.x;
      const dy = event.clientY - p.startClient.y;
      if (Math.hypot(dx, dy) < MARQUEE_THRESHOLD_PX) return;
      p.active = true;
      if (p.mode === 'move') {
        this.setGestureActive(true);
        p.snap = this.buildMoveSnap(p); // captured at gesture start, not on first move, so the first frame can't jump
        void this.beginDrag(p.moveIds); // lift content; frame + handles stay visible and translate with it (no snap-on-drop)
      } else if (p.mode === 'card-swap') {
        this.setGestureActive(true);
        void this.beginDrag(p.moveIds); // lift the whole card; no snapping — the drop target is another card, not a guide
      } else if (p.mode === 'photo-swap') {
        // No lift: the source photo stays drawn in its own frame for the whole drag, so nothing flickers and no
        // dashed empty slot opens behind the pointer. What travels is a ghost copy under the cursor.
        this.setGestureActive(true);
        // The other gestures hide their chrome through the lift; this one has no lift, so the selection has to
        // go outright — dragged frame or any other, a drag shows only the travelling copy and the frame it would
        // land in. The trailing click is suppressed at the drop, so nothing re-selects behind the drag.
        // The release gives it back (restoreSelection).
        this.dropSelection();
        this.showSwapGhost(p.swapSourceId, event.clientX, event.clientY);
      }
    }

    if (p.mode === 'marquee') {
      this.drawHandles();
    } else if (p.mode === 'photo-swap') {
      p.lastClient = { x: event.clientX, y: event.clientY }; // the anchor a key-only re-read hangs the hint on
      this.moveSwapGhost(event.clientX, event.clientY);
      this.updateSwapTarget(p);
    } else if (p.mode === 'card-swap') {
      this.setDragOffset(p.current.x - p.startBook.x, p.current.y - p.startBook.y);
      this.updateSwapTarget(p);
    } else {
      const rawDx = p.current.x - p.startBook.x;
      const rawDy = p.current.y - p.startBook.y;
      p.axisLock = event.shiftKey ? (Math.abs(rawDx) > Math.abs(rawDy) ? 'y' : 'x') : undefined;
      const d = this.pressDelta(p);
      const snapped = this.applyMoveSnap(p, d.dx, d.dy);
      this.setDragOffset(snapped.dx, snapped.dy);
      this.drawMoveGuides(snapped.guides, snapped.gapGuides);
    }
  };

  /** The wasm hit-test can answer late — or never: its promise is fire-and-forget, so one rejection leaves the press
   *  'pending' for the whole gesture and every move returns early (the reported silent no-op). Once the pointer has
   *  travelled, classify the photo-swap lane from the same JS AABB geometry the drop target already resolves by. */
  private resolvePendingSwapPress(p: PressState, event: MouseEvent): boolean {
    if (!this.swapLanesArmable() || !this.cfg.onPhotoSwap || !this.cfg.getPhotoSwapEnabled?.()) return false;
    if (Math.hypot(event.clientX - p.startClient.x, event.clientY - p.startClient.y) < MARQUEE_THRESHOLD_PX) return false;
    const frame = this.photoFrameAtBookPoint(p.startBook);
    if (!frame || !this.hasPhoto(frame.id) || !this.isTargetable(frame.id)) return false;
    p.mode = 'photo-swap';
    p.swapSourceId = frame.id;
    p.moveIds = [frame.id];
    return true;
  }

  private updateSwapTarget(p: PressState): void {
    const target = p.mode === 'photo-swap' ? this.photoFrameAtBookPoint(p.current, p.swapSourceId) : this.cardAtBookPoint(p);
    // Legacy's lastHoveredGroupId: while the pointer stays on the same card nothing re-fires, so a cursor resting
    // on a slot boundary cannot flip the chain open and shut frame after frame.
    if (p.swapTargetId === target?.id) return;
    p.swapTargetId = target?.id;
    if (p.mode !== 'photo-swap') {
      this.previewCardSwap(p, target as RenderGroupBox | undefined);
      return;
    }

    // ONE drop language for both lanes: the frame under the cursor fills periwinkle — exactly what a drag out of the
    // photo panel paints. The dashed frame is the CARD swap's own sign and would only read as a third, weaker state.
    // Обмен отказа не знает (S7 R2): не сошлись ориентации — страницы пересобираются, а кадры всё равно меняются.
    this.drawSwapTarget(undefined);
    this.setSwapHover(target?.id, 'allow');
  }

  /** Page under the release point, or undefined when the gesture was ABORTED (blur/pointercancel — no drop
   *  happened), never travelled, or ended over the pasteboard. Same split the panel drop targets by. */
  private releasePageSide(p: PressState, event?: Event): 0 | 1 | undefined {
    if (event?.type !== 'mouseup' && event?.type !== 'pointerup') return undefined;
    const at = p.lastClient;
    const side = at ? this.zoneSideAt(at.x, at.y) : null;
    return side ? (side === 'left' ? 0 : 1) : undefined;
  }

  /** Which half of the spread the box sits in — by its centre, so a frame touching the spine still has one page. */
  private pageSideOfBox(id: string): 0 | 1 | undefined {
    const box = this.cache ? findRenderBox(this.cache.boxes, id) : undefined;
    if (!box) return undefined;
    return box.rect.x + box.rect.width / 2 < this.overlayBook.w / 2 ? 0 : 1;
  }

  /** The target is painted by the SAME hover painter the panel drop uses, so the two lanes read alike. */
  private setSwapHover(id: string | undefined, verdict?: DropHoverVerdict, force = false): void {
    const next = id ? verdict : undefined;
    if (!force && this.dropHoverId === id && this.dropHoverVerdict === next) return;
    this.dropHoverId = id;
    this.dropHoverVerdict = next;
    void this.drawDropHover(id, next);
  }

  // A repaint mid-drag clears the hover buffer, and this gesture only paints when the target CHANGES — without this
  // the highlight vanishes under a landing image tier.
  private reapplySwapTarget(): void {
    const p = this.press;
    if (p?.mode !== 'photo-swap' || !p.active || !p.swapTargetId) return;
    this.setSwapHover(p.swapTargetId, 'allow', true);
  }

  // A photo frame is a scene drawable, so its buffer rect is indexed. A CARD is a top-level group, flattened
  // away before the scene is built, so it has no index entry — its slot comes off the render tree's book rect.
  // ONE resolver, so the whole two-sided preview (capture → vacate → restore → hold) serves both kinds of drag
  // instead of growing a second copy of itself for cards.
  private previewSlotRect(id: string): SceneRect | undefined {
    const indexed = this.drawableRects.get(id);
    if (indexed) return indexed;
    const group = this.topLevelGroupBox(id);
    if (!group) return undefined;
    const rs = this.cache?.renderScale ?? 1;
    const r = group.rect;
    return { x: r.x * rs, y: r.y * rs, width: r.width * rs, height: r.height * rs };
  }

  // Capture, patch and restore all go through THIS upright rect, so the patch can never write a pixel the
  // capture did not save — a rotated frame's quad reaches outside its own rect and would be unrestorable.
  private slotRect(r: SceneRect): { x: number; y: number; w: number; h: number } {
    return {
      x: Math.round(r.x),
      y: Math.round(r.y),
      w: Math.max(1, Math.round(r.width)),
      h: Math.max(1, Math.round(r.height)),
    };
  }

  // Blit the box straight off the painted overlay — same trick as instantLift, no rasterise and no image load.
  // ONE buffer for the whole gesture: a full-res frame is tens of MB, and a fresh canvas per pointer crossing
  // hands the collector enough garbage to stall a frame.
  private captureBoxPixels(id: string): HTMLCanvasElement | null {
    const overlay = this.overlayCanvas;
    const r = this.previewSlotRect(id);
    if (!overlay || !r) return null;
    const { x, y, w, h } = this.slotRect(r);
    const off = (this.slotCapture ??= document.createElement('canvas'));
    const ctx = off.getContext('2d');
    if (!ctx) return null;
    if (off.width !== w || off.height !== h) {
      // eslint-disable-next-line no-restricted-syntax -- холст вне DOM: стирать нечего
      off.width = w;
      // eslint-disable-next-line no-restricted-syntax -- холст вне DOM: стирать нечего
      off.height = h;
    } else {
      ctx.clearRect(0, 0, w, h);
    }

    ctx.drawImage(overlay, x, y, w, h, 0, 0, w, h);
    return off;
  }

  // Re-cut the previewed slot's restore bitmap only where it meets the just-landed patch `region`, straight off the
  // buffer in the same slot-rect frame. The patch has just wiped the tint there, so no tint gets baked in.
  private recaptureCrossedSlot(region: SpreadRegion): void {
    const overlay = this.overlayCanvas;
    const id = this.previewedSlotId;
    const bitmap = this.swapPreview?.bitmap;
    const r = id ? this.previewSlotRect(id) : undefined;
    const ctx = bitmap?.getContext('2d');
    if (!overlay || !r || !ctx) return;
    const slot = this.slotRect(r);
    const x0 = Math.max(slot.x, region.ox);
    const y0 = Math.max(slot.y, region.oy);
    const x1 = Math.min(slot.x + slot.w, region.ox + region.w);
    const y1 = Math.min(slot.y + slot.h, region.oy + region.h);
    if (x1 <= x0 || y1 <= y0) return;
    ctx.clearRect(x0 - slot.x, y0 - slot.y, x1 - x0, y1 - y0);
    ctx.drawImage(overlay, x0, y0, x1 - x0, y1 - y0, x0 - slot.x, y0 - slot.y, x1 - x0, y1 - y0);
  }

  // Own buffer, not the shared slotCapture one: the ghost outlives every pointer crossing of the drag, while that
  // buffer is re-cut on each one. Blitted off the painted overlay, so a cross-origin photo never taints anything —
  // the pixels are shown, never exported.
  private captureGhostPixels(id: string): HTMLCanvasElement | null {
    const overlay = this.overlayCanvas;
    const r = this.previewSlotRect(id);
    if (!overlay || !r) return null;
    const { x, y, w, h } = this.slotRect(r);
    const off = document.createElement('canvas');
    // eslint-disable-next-line no-restricted-syntax -- холст вне DOM: стирать нечего
    off.width = w;
    // eslint-disable-next-line no-restricted-syntax -- холст вне DOM: стирать нечего
    off.height = h;
    off.getContext('2d')?.drawImage(overlay, x, y, w, h, 0, 0, w, h);
    return off;
  }

  /** Hangs the held photo's copy on the cursor. On `document.body` and `position: fixed`, so no host transform,
   *  clip or stacking context can cut it — the same freedom the browser's own drag image has. */
  private showSwapGhost(sourceId: string | undefined, clientX: number, clientY: number): void {
    this.hideSwapGhost();
    if (!sourceId) return;
    const pixels = this.captureGhostPixels(sourceId);
    if (!pixels || pixels.width <= 0 || pixels.height <= 0) return;
    const scale = SWAP_GHOST_MAX_PX / Math.max(pixels.width, pixels.height);
    const el = document.createElement('div');
    el.setAttribute('data-swap-ghost', '');
    el.style.cssText = SWAP_GHOST_STYLE;
    el.style.zIndex = SWAP_GHOST_Z_INDEX;
    el.style.width = `${Math.max(1, Math.round(pixels.width * scale))}px`;
    el.style.height = `${Math.max(1, Math.round(pixels.height * scale))}px`;
    pixels.style.width = 'var(--size-full)';
    pixels.style.height = 'var(--size-full)';
    pixels.style.display = 'block';
    el.appendChild(pixels);
    document.body.appendChild(el);
    this.swapGhostEl = el;
    this.moveSwapGhost(clientX, clientY);
  }

  private moveSwapGhost(clientX: number, clientY: number): void {
    const el = this.swapGhostEl;
    if (!el) return;
    // Centred on the cursor, like the panel drag's own image; transform (not left/top) keeps it off the layout path.
    el.style.transform = `translate(${clientX - el.offsetWidth / 2}px, ${clientY - el.offsetHeight / 2}px)`;
  }

  private hideSwapGhost(): void {
    this.swapGhostEl?.remove();
    this.swapGhostEl = undefined;
  }

  // The WASM raster owns the overlay pixels: it lands AFTER the Canvas2D slot patch and erases it, and the bitmap
  // captured before it would be stamped back over the fresh page on leave. Re-cut the patch from the pixels now on
  // the buffer, and redraw the preview chrome the same rewrite cleared.
  // `recapture` only right after a FULL-buffer rewrite: a buffer that still carries the tint would bake it into the
  // bitmap. After a bbox patch the bitmap stays: its patched part was re-cut as the patch landed (recaptureCrossedSlot),
  // before any later crossing tinted the buffer; the rest keeps its pre-crossing pixels.
  private reapplyVacatedSlot(resample: boolean, recapture = true): void {
    const press = this.press;
    const preview = this.swapPreview;
    const targetId = this.previewedSlotId;
    const swapping = press?.mode === 'photo-swap' || press?.mode === 'card-swap';
    if (!this.camera || !preview || !targetId || !swapping || !press?.swapSourceId) return;
    if (resample) {
      this.swapSlotFill =
        press.mode === 'card-swap' ? this.sampleCardPageFill(press.swapSourceId) : this.sampleEmptyPageFill(press.swapSourceId);
    }

    if (recapture) preview.bitmap = this.captureBoxPixels(targetId);
    this.paintVacatedSlot(targetId, press.swapSourceId);
    this.paintSwapPreview();
  }

  // The page colour behind the lifted photo, read off the backdrop at the source frame's centre.
  private sampleEmptyPageFill(sourceId: string): string {
    const src = this.drawableRects.get(sourceId);
    if (!src) return this.swapSlotFill ?? SWAP_SLOT_EMPTY_FALLBACK;
    return this.samplePageFillAt(src.x + src.width / 2, src.y + src.height / 2);
  }

  // A top-level GROUP never reaches the wasm scene (it is flattened to leaves), so it has no drawableRects entry —
  // the held card's centre comes off its book rect instead.
  private sampleCardPageFill(sourceId: string): string {
    const g = this.topLevelGroupBox(sourceId);
    if (!g) return this.swapSlotFill ?? SWAP_SLOT_EMPTY_FALLBACK;
    const rs = this.cache?.renderScale ?? 1;
    return this.samplePageFillAt((g.rect.x + g.rect.width / 2) * rs, (g.rect.y + g.rect.height / 2) * rs);
  }

  private samplePageFillAt(bufferX: number, bufferY: number): string {
    const ctx = this.overlayCanvas?.getContext('2d');
    if (!ctx) return this.swapSlotFill ?? SWAP_SLOT_EMPTY_FALLBACK;
    const px = ctx.getImageData(Math.round(bufferX), Math.round(bufferY), 1, 1).data;
    if (px[3] === 0) return this.swapSlotFill ?? SWAP_SLOT_EMPTY_FALLBACK;
    return `rgb(${px[0]},${px[1]},${px[2]})`;
  }

  private releaseSlotCapture(): void {
    this.swapSlotFill = undefined;
    if (!this.slotCapture) return;
    // eslint-disable-next-line no-restricted-syntax -- холст вне DOM: стирать нечего
    this.slotCapture.width = 0;
    // eslint-disable-next-line no-restricted-syntax -- холст вне DOM: стирать нечего
    this.slotCapture.height = 0;
    this.slotCapture = undefined;
  }

  /** Paint the target frame as an emptied slot, on the backdrop — under the drag canvas, so the held photo
   *  floats over it untinted. The fill colour is sampled from the source frame, whichever page colour it shows. */
  private paintVacatedSlot(targetId: string, sourceId: string): void {
    const canvas = this.overlayCanvas;
    const r = this.previewSlotRect(targetId);
    const ctx = canvas?.getContext('2d');
    if (!canvas || !r || !ctx) return;
    // Memoised by the beginDrag re-cut; the fallback sample only runs when the pointer crossed into a target
    // before the lift's repaint landed, and that crossing's colour is corrected a tick later.
    const empty = (this.swapSlotFill ??= this.sampleEmptyPageFill(sourceId));
    const saved = this.slotRect(r);
    ctx.save();
    ctx.beginPath();
    ctx.rect(saved.x, saved.y, saved.w, saved.h);
    ctx.clip();
    ctx.beginPath();
    quadCorners(r).forEach((p, i) => (i === 0 ? ctx.moveTo(p.x, p.y) : ctx.lineTo(p.x, p.y)));
    ctx.closePath();
    ctx.fillStyle = empty;
    ctx.fill();
    ctx.fillStyle = SWAP_SLOT_FILL;
    ctx.fill();
    ctx.restore();
  }

  /** Drop the traded photo into the vacated source frame, on the already-settled drag layer, so the swap is
   *  on screen before the model's render lands and nothing blinks through. */
  private holdTradedPhoto(bitmap: HTMLCanvasElement, sourceId: string): void {
    const canvas = this.dragCanvas;
    const r = this.previewSlotRect(sourceId);
    const ctx = canvas?.getContext('2d');
    if (!canvas || !r || !ctx || bitmap.width === 0 || bitmap.height === 0) return;
    ctx.save();
    ctx.beginPath();
    quadCorners(r).forEach((p, i) => (i === 0 ? ctx.moveTo(p.x, p.y) : ctx.lineTo(p.x, p.y)));
    ctx.closePath();
    ctx.clip();
    const k = Math.max(r.width / bitmap.width, r.height / bitmap.height);
    const w = bitmap.width * k;
    const h = bitmap.height * k;
    ctx.drawImage(bitmap, r.x + (r.width - w) / 2, r.y + (r.height - h) / 2, w, h);
    ctx.restore();
  }

  /** Put the previewed frame's own pixels back on the backdrop — the cheap inverse of paintVacatedSlot. */
  private restorePreviewedSlot(): void {
    const id = this.previewedSlotId;
    const bitmap = this.swapPreview?.bitmap;
    const canvas = this.overlayCanvas;
    const r = id ? this.previewSlotRect(id) : undefined;
    const ctx = canvas?.getContext('2d');
    this.previewedSlotId = undefined;
    if (!id || !bitmap || !canvas || !r || !ctx) return;
    const saved = this.slotRect(r);
    ctx.drawImage(bitmap, saved.x, saved.y);
  }

  private paintSwapPreview(): void {
    const pv = this.swapPreview;
    const canvas = this.handleCanvas;
    const ctx = canvas?.getContext('2d');
    if (!pv || !canvas || !ctx) return;
    const s = this.viewScale || 1;
    const t = Math.min(1, (performance.now() - pv.start) / SWAP_PREVIEW_MS);
    const e = t * (2 - t);
    const lerp = (a: number, b: number): number => a + (b - a) * e;
    ctx.save();
    this.beginHandlePass(canvas, ctx);

    const slot = {
      x: pv.from.x * s,
      y: pv.from.y * s,
      w: pv.from.width * s,
      h: pv.from.height * s,
    };
    ctx.save();
    ctx.lineWidth = SWAP_TARGET_STROKE_WIDTH;
    const out = SWAP_TARGET_STROKE_WIDTH / 2;
    const frame = [slot.x - out, slot.y - out, slot.w + SWAP_TARGET_STROKE_WIDTH, slot.h + SWAP_TARGET_STROKE_WIDTH] as const;
    ctx.setLineDash([]);
    ctx.strokeStyle = SWAP_TARGET_BASE_COLOR;
    ctx.strokeRect(...frame);
    ctx.setLineDash(SWAP_TARGET_DASH);
    ctx.strokeStyle = SWAP_TARGET_DASH_COLOR;
    ctx.strokeRect(...frame);
    ctx.restore();

    if (pv.bitmap) {
      const d = {
        x: lerp(pv.from.x, pv.to.x) * s,
        y: lerp(pv.from.y, pv.to.y) * s,
        w: lerp(pv.from.width, pv.to.width) * s,
        h: lerp(pv.from.height, pv.to.height) * s,
      };
      ctx.save();
      ctx.beginPath();
      ctx.rect(d.x, d.y, d.w, d.h);
      ctx.clip();
      const k = Math.max(d.w / pv.bitmap.width, d.h / pv.bitmap.height);
      const bw = pv.bitmap.width * k;
      const bh = pv.bitmap.height * k;
      ctx.drawImage(pv.bitmap, d.x + (d.w - bw) / 2, d.y + (d.h - bh) / 2, bw, bh);
      ctx.restore();
    }

    ctx.restore();

    // A repaint re-draws the preview out of band, so drop any frame already queued — otherwise each out-of-band
    // redraw forks a second self-rescheduling chain that outlives cancelSwapPreview()/destroy().
    if (pv.raf) cancelAnimationFrame(pv.raf);
    pv.raf = t < 1 ? requestAnimationFrame(() => this.paintSwapPreview()) : 0;
  }

  // The drop's settle render repaints the backdrop from the model, so the slot pixels are NOT restored here —
  // blitting the old photo back would flash it for a frame before the swapped model lands.
  private cancelSwapPreview(): void {
    const pv = this.swapPreview;
    if (pv?.raf) cancelAnimationFrame(pv.raf);
    this.swapPreview = null;
    this.previewedSlotId = undefined;
    this.clearHandleCanvas();
  }

  // Both ends of a photo swap stay TOP-LEVEL: a nested frame's rect is group-relative, and group-bound
  // content (vignette cards) trades through the card-swap path instead.
  private topLevelImageBox(id: string): RenderImageBox | undefined {
    const box = this.cache?.boxes.find(b => b.id === id);
    return box?.type === 'image' ? (box as RenderImageBox) : undefined;
  }

  // The adapter attaches imageAlbum only for a frame that actually carries a photo — an empty frame has neither.
  private hasPhoto(id: string): boolean {
    const box = this.topLevelImageBox(id);
    return !!box && (!!box.imageAlbum || !!box.imageUrl);
  }

  // Any depth: a nested frame is still a photo under the pointer, and its selection needs the same contrast.
  private isPhotoFrame(id: string): boolean {
    const box = this.getRenderBox(id);
    if (box?.type !== 'image') return false;
    const image = box as RenderImageBox;
    return !!image.imageAlbum || !!image.imageUrl;
  }

  // An empty frame IS a legal drop target — the photo moves there and the source frame is left empty. A hidden
  // one is not: the scene never draws it, so a drop would post the photo into a frame nothing on screen shows.
  private photoFrameAtBookPoint(pt: Point, excludeId?: string): RenderImageBox | undefined {
    let found: RenderImageBox | undefined;
    for (const b of this.cache?.boxes ?? []) {
      if (b.type !== 'image' || b.id === excludeId || b.locked || b.visible === false) continue;
      const r = b.rect;
      if (pt.x >= r.x && pt.x <= r.x + r.width && pt.y >= r.y && pt.y <= r.y + r.height) found = b as RenderImageBox;
    }

    return found;
  }

  // Groups never reach the wasm scene (flattened to leaves), so the drop target resolves by pure
  // JS AABB math over the host-supplied render tree; the last hit wins (paint order = topmost).
  private groupAtBookPoint(pt: Point, excludeId?: string): RenderGroupBox | undefined {
    let found: RenderGroupBox | undefined;
    for (const b of this.cache?.boxes ?? []) {
      if (b.type !== 'group' || b.id === excludeId) continue;
      const r = b.rect;
      if (pt.x >= r.x && pt.x <= r.x + r.width && pt.y >= r.y && pt.y <= r.y + r.height) found = b as RenderGroupBox;
    }

    return found;
  }

  private topLevelGroupBox(id: string): RenderGroupBox | undefined {
    const box = this.cache?.boxes.find(b => b.id === id);
    return box?.type === 'group' ? (box as RenderGroupBox) : undefined;
  }

  // The cascade parks cards away from their model rect, so the drop candidate has to be resolved against where each
  // card is NOW — a card that already handed over its slot must stop answering for it, or the pointer keeps
  // re-triggering on the slot it left. Reads the tween TARGET, not the interpolated position: hit-testing a moving
  // rect makes the boundary between two cards oscillate for the length of every shift.
  private cardAtBookPoint(p: PressState): RenderGroupBox | undefined {
    const cards = p.cascade?.cards;
    let found: RenderGroupBox | undefined;
    for (const b of this.cache?.boxes ?? []) {
      if (b.type !== 'group' || b.id === p.swapSourceId) continue;
      const r = b.rect;
      const at = cards?.get(b.id)?.slot ?? r;
      if (p.current.x >= at.x && p.current.x <= at.x + r.width && p.current.y >= at.y && p.current.y <= at.y + r.height) {
        found = b as RenderGroupBox;
      }
    }

    return found;
  }

  // Mirrors the model's own gate (spread-edit.ops swapGroups): only same-size, unpinned cards trade PLACES —
  // everything else trades CONTENTS with both frames staying put. Keeping the two in step is what makes the drop
  // land on exactly the arrangement the drag previewed.
  private canTradePlaces(a: RenderGroupBox, b: RenderGroupBox): boolean {
    if (a.draggable === false || b.draggable === false) return false;
    return Math.abs(a.rect.width - b.rect.width) < 1 && Math.abs(a.rect.height - b.rect.height) < 1;
  }

  /** Live preview of the reorder: the hovered card steps into the slot the held card left, and the pointer walking
   *  on picks up the next one — the legacy Konva cascade, rebuilt on the Canvas2D layers. */
  private previewCardSwap(p: PressState, target: RenderGroupBox | undefined): void {
    const source = p.swapSourceId ? this.topLevelGroupBox(p.swapSourceId) : undefined;
    if (!source) return;
    // No cascade channel wired, a differently-sized card, or a pinned frame: the arrangement stays put and the two
    // frames trade CONTENTS instead. Leaving the chain untouched here is deliberate — the legacy drag kept its
    // shifts while the pointer crossed an ineligible card, and only unwound them if the drop landed on one.
    if (!target || !this.cfg.onGroupCascade || !this.canTradePlaces(source, target)) {
      this.previewContentsTrade(p, source, target);
      return;
    }

    this.previewContentsTrade(p, source, undefined); // stepping onto an eligible card drops any contents preview
    const cascade = this.ensureCascade(p, source);
    const held = cascade.chain.indexOf(target.id);
    if (held >= 0) this.unwindCascade(cascade, held);
    else this.pushCascade(cascade, target);
    this.paintCascade(cascade);
    this.drawCascadeDropFrame(cascade);
  }

  /** Two cards that keep their frames and trade what is inside them:
   *  the target's own pixels fly into the held card's slot while the held card's ghost covers the target's, and
   *  the target frame reads as emptied underneath. One-sided it looked like the target's photo was deleted. The
   *  chain is deliberately untouched — a contents trade moves no neighbours (legacy parity). */
  private previewContentsTrade(p: PressState, source: RenderGroupBox, target: RenderGroupBox | undefined): void {
    const wasPreviewing = !!this.swapPreview;
    if (!target && !wasPreviewing) {
      if (!p.cascade) this.drawSwapTarget(undefined); // bare page with no chain: nothing to mark at all
      return;
    }

    this.restorePreviewedSlot(); // reads the OUTGOING preview, so it must run before the state is dropped
    this.cancelSwapPreview();
    if (!target || !p.swapSourceId) {
      // The chain's own landing slot takes the accent (and the frame) back the moment the pointer leaves.
      if (p.cascade) this.tintVacantSlot(p.cascade);
      this.drawCascadeDropFrame(p.cascade);
      return;
    }

    if (p.cascade) this.clearCascadeTint(p.cascade); // the drop under the cursor is this trade, not the chain's slot
    const bitmap = this.captureBoxPixels(target.id);
    this.previewedSlotId = target.id;
    this.paintVacatedSlot(target.id, p.swapSourceId);
    this.swapPreview = { bitmap, from: target.rect, to: source.rect, start: performance.now(), raf: 0 };
    this.paintSwapPreview(); // draws the dashed target frame itself — drawSwapTarget would only fight it for the canvas
  }

  /** Same dashed frame the contents trade shows, on the slot the chain's drop lands in. The cascade used to leave
   *  it out ("the cards stepping aside ARE the feedback"), which made the two drags speak different languages —
   *  and on an unwind the landing slot is NOT the hovered card's own rect, so the frame has to follow the chain. */
  private drawCascadeDropFrame(c: CascadeState | undefined): void {
    this.drawSwapTargetRect(c ? this.vacantRect(c) : undefined);
  }

  private ensureCascade(p: PressState, source: RenderGroupBox): CascadeState {
    if (p.cascade) return p.cascade;
    const home = { x: source.rect.x, y: source.rect.y };
    p.cascade = { chain: [], cards: new Map(), vacant: { ...home }, sourceHome: home, raf: 0 };
    return p.cascade;
  }

  /** The pointer reached a fresh card: it takes over the vacant slot, and its own becomes the new vacant one. */
  private pushCascade(c: CascadeState, target: RenderGroupBox): void {
    const card = this.cascadeCard(c, target);
    card.from = this.cascadePos(card);
    card.slot = { ...c.vacant };
    card.start = performance.now();
    c.chain.push(target.id);
    c.vacant = { ...card.home };
    this.tintVacantSlot(c);
  }

  /** The pointer came back onto a card it had already displaced: that card and every one picked up after it go
   *  home, and the vacant slot rewinds to whatever the shortened chain last left empty. */
  private unwindCascade(c: CascadeState, from: number): void {
    const now = performance.now();
    for (const id of c.chain.splice(from)) {
      const card = c.cards.get(id);
      if (!card) continue;
      card.from = this.cascadePos(card); // resume from where it is on screen — an interrupted shift must not jump
      card.slot = { ...card.home };
      card.start = now;
    }

    const last = c.chain.length > 0 ? c.cards.get(c.chain[c.chain.length - 1]) : undefined;
    c.vacant = { ...(last?.home ?? c.sourceHome) };
    this.tintVacantSlot(c);
  }

  // Registered on first displacement, and kept for the rest of the gesture: once its home has been tinted as the
  // vacant slot, this bitmap is the only thing that can put the card back on screen — even after it unwinds home.
  private cascadeCard(c: CascadeState, box: RenderGroupBox): CascadeCard {
    const existing = c.cards.get(box.id);
    if (existing) return existing;
    const home = { x: box.rect.x, y: box.rect.y };
    const card: CascadeCard = {
      home,
      slot: { ...home },
      from: { ...home },
      start: performance.now(),
      width: box.rect.width,
      height: box.rect.height,
      bitmap: this.captureBookRect(box.rect), // cut while the backdrop still shows this card untouched
    };
    c.cards.set(box.id, card);
    return card;
  }

  private cascadePos(card: CascadeCard): Point {
    const t = Math.min(1, (performance.now() - card.start) / CASCADE_SHIFT_MS);
    const e = t < 0.5 ? 2 * t * t : 1 - (-2 * t + 2) ** 2 / 2; // ease-in-out — the step starts and lands softly
    return { x: card.from.x + (card.slot.x - card.from.x) * e, y: card.from.y + (card.slot.y - card.from.y) * e };
  }

  // Book px → backdrop buffer px, the coordinates drawableRects and the cascade layer both live in.
  private bufferRect(r: { x: number; y: number; width: number; height: number }): {
    x: number;
    y: number;
    w: number;
    h: number;
  } {
    const rs = this.cache?.renderScale ?? 1;
    return {
      x: Math.round(r.x * rs),
      y: Math.round(r.y * rs),
      w: Math.max(1, Math.round(r.width * rs)),
      h: Math.max(1, Math.round(r.height * rs)),
    };
  }

  // Same blit trick as captureBoxPixels, but each card needs its OWN buffer: a chain keeps several alive at once,
  // so the single gesture-wide scratch canvas the photo swap reuses would overwrite the previous card.
  private captureBookRect(r: { x: number; y: number; width: number; height: number }): HTMLCanvasElement | null {
    const overlay = this.overlayCanvas;
    if (!overlay) return null;
    const b = this.bufferRect(r);
    const off = document.createElement('canvas');
    // eslint-disable-next-line no-restricted-syntax -- холст вне DOM: стирать нечего
    off.width = b.w;
    // eslint-disable-next-line no-restricted-syntax -- холст вне DOM: стирать нечего
    off.height = b.h;
    const ctx = off.getContext('2d');
    if (!ctx) return null; // headless/jsdom
    ctx.drawImage(overlay, b.x, b.y, b.w, b.h, 0, 0, b.w, b.h);
    return off;
  }

  /** The slot the held card would land in, in book px — the one rect that carries the accent placeholder.
   *  An empty chain means that slot IS the held card's own home, which the lift's repaint already cleared;
   *  marking it would put a placeholder on screen that the plain pairwise drag never had. */
  private vacantRect(c: CascadeState): { x: number; y: number; width: number; height: number } | undefined {
    const size = c.chain.length > 0 ? c.cards.get(c.chain[0]) : undefined;
    return size ? { x: c.vacant.x, y: c.vacant.y, width: size.width, height: size.height } : undefined;
  }

  /** Move the single accent placeholder to wherever the drop now lands. The accent used to be write-only —
   *  the previous slot was left for the incoming card's 150 ms shift to cover — so two hover crossings inside one
   *  shift (a flick across the row) left two purple gaps standing. The slot a card is travelling into goes back to
   *  bare page instead; only the landing slot stays accented, and only one ever is. */
  private tintVacantSlot(c: CascadeState): void {
    const ctx = this.overlayCanvas?.getContext('2d');
    if (!ctx) return;
    this.clearCascadeTint(c);
    const next = this.vacantRect(c);
    // A live contents-trade preview owns the accent for as long as the pointer rests on its frame: the drop
    // under the cursor is that trade, not the chain's landing slot.
    if (!next || this.swapPreview) return;
    this.fillCardSlot(ctx, next, true);
    c.tinted = next;
  }

  /** Repaint the accent placeholder away, back to bare page. The slot stays EMPTY — a card is either travelling
   *  into it or the chain is simply holding it open — so only the "drop lands here" marker goes. */
  private clearCascadeTint(c: CascadeState): void {
    const ctx = this.overlayCanvas?.getContext('2d');
    if (!ctx || !c.tinted) return;
    this.fillCardSlot(ctx, c.tinted, false);
    c.tinted = undefined;
  }

  private fillCardSlot(
    ctx: CanvasRenderingContext2D,
    r: { x: number; y: number; width: number; height: number },
    accent: boolean,
  ): void {
    const b = this.bufferRect(r);
    ctx.save();
    ctx.fillStyle = this.swapSlotFill ?? SWAP_SLOT_EMPTY_FALLBACK;
    ctx.fillRect(b.x, b.y, b.w, b.h);
    if (accent) {
      ctx.fillStyle = SWAP_SLOT_FILL;
      ctx.fillRect(b.x, b.y, b.w, b.h);
    }

    ctx.restore();
  }

  private paintCascade(c: CascadeState): void {
    if (c.raf) {
      cancelAnimationFrame(c.raf);
      c.raf = 0;
    }

    const canvas = this.ensureCascadeCanvas();
    const overlay = this.overlayCanvas;
    if (!canvas || !overlay) return;
    const buffer = this.ensureFrameBuffer(canvas, overlay.width, overlay.height);
    const ctx = buffer.getContext('2d');
    if (!ctx) return;
    ctx.clearRect(0, 0, buffer.width, buffer.height);
    const now = performance.now();
    let moving = false;
    for (const card of c.cards.values()) {
      const settled = now - card.start >= CASCADE_SHIFT_MS;
      moving ||= !settled;
      if (!card.bitmap) continue;
      const at = settled ? card.slot : this.cascadePos(card);
      const b = this.bufferRect({ x: at.x, y: at.y, width: card.width, height: card.height });
      // Destination size given explicitly: a repaint at a new render scale mid-gesture resizes the layer under
      // bitmaps cut at the old one, and a 1:1 blit would then paint them at the wrong size.
      ctx.drawImage(card.bitmap, b.x, b.y, b.w, b.h);
    }

    this.presentFrame(canvas, buffer);
    // A settled layer holds its last frame, so a drag that is merely travelling between cards schedules nothing —
    // only a live step costs a frame, and it costs one clear plus a handful of drawImage calls.
    c.raf = moving && !this.destroyed ? requestAnimationFrame(() => this.paintCascade(c)) : 0;
  }

  // Legacy stopAllTweens(): the release lands on the final arrangement, never on a card caught mid-step.
  private settleCascade(c: CascadeState): void {
    for (const card of c.cards.values()) {
      card.from = { ...card.slot };
      card.start = 0;
    }

    this.paintCascade(c);
  }

  private ensureCascadeCanvas(): HTMLCanvasElement | undefined {
    const overlay = this.overlayCanvas;
    if (!overlay) return undefined;
    let canvas = this.cascadeCanvas;
    if (!canvas) {
      canvas = document.createElement('canvas');
      canvas.style.position = 'absolute';
      canvas.style.pointerEvents = 'none';
      canvas.style.zIndex = overlay.style.zIndex; // same stacking level as the backdrop and the drag layer…
      canvas.setAttribute('data-cascade', '');
      // …so DOM order decides, and the shifted cards have to land between the two: above the backdrop they were cut
      // from, below the held card floating over them. insertBefore holds that whether or not a drag layer exists yet.
      this.cfg.host.insertBefore(canvas, this.dragCanvas ?? null);
      this.cascadeCanvas = canvas;
    }

    this.positionCascadeCanvas();
    return canvas;
  }

  // The cascade layer shares the backdrop's CSS box, so one book→buffer mapping serves both.
  private positionCascadeCanvas(): void {
    const canvas = this.cascadeCanvas;
    const overlay = this.overlayCanvas;
    if (!canvas || !overlay) return;
    canvas.style.left = overlay.style.left;
    canvas.style.top = overlay.style.top;
    canvas.style.width = overlay.style.width;
    canvas.style.height = overlay.style.height;
  }

  private removeCascadeCanvas(): void {
    this.cascadeCanvas?.remove();
    this.cascadeCanvas = undefined;
  }

  // Full-res card bitmaps are megabytes apiece — a chain of them must not outlive the gesture. The layer keeps the
  // pixels it has already painted, so dropping the buffers here costs nothing on screen.
  private releaseCascade(c: CascadeState | undefined): void {
    if (!c) return;
    if (c.raf) cancelAnimationFrame(c.raf);
    c.raf = 0;
    for (const card of c.cards.values()) {
      if (!card.bitmap) continue;
      // eslint-disable-next-line no-restricted-syntax -- холст вне DOM: стирать нечего
      card.bitmap.width = 0;
      // eslint-disable-next-line no-restricted-syntax -- холст вне DOM: стирать нечего
      card.bitmap.height = 0;
      card.bitmap = null;
    }

    c.cards.clear();
    c.chain.length = 0;
  }

  // Same contract as reapplyVacatedSlot, for the cascade: a raster has just rewritten the backdrop and taken the
  // whole patch with it. Called with `false` right after the lift's own repaint, which already left every emptied
  // slot bare — there only the accent has to be laid down.
  private reapplyCascade(afterFullRaster: boolean): void {
    const p = this.press;
    // An ARMED press (mousedown, travel still under the threshold) has lifted nothing: the backdrop legitimately
    // shows the card, and cutting its slot here would blank it with no drag layer standing in — until the next
    // raster, which a mere click never schedules.
    if (p?.mode !== 'card-swap' || !p.swapSourceId || !p.active) return;
    const c = p.cascade;
    const ctx = afterFullRaster ? this.overlayCanvas?.getContext('2d') : undefined;
    if (ctx) {
      // The raster rebuilt the backdrop from the MODEL, so every emptied slot shows its own card again — the held
      // one included. The cascade layer only hides the slots a settled card already covers, so without this a shift
      // caught mid-flight (and the held card for the whole gesture) renders twice: at home and in the air.
      if (c) c.tinted = undefined; // …and the accent went with it, so the remembered marker is stale
      const src = this.topLevelGroupBox(p.swapSourceId);
      if (src) this.fillCardSlot(ctx, src.rect, false);
      for (const card of c ? c.chain.map(id => c.cards.get(id)) : []) {
        if (card) this.fillCardSlot(ctx, { x: card.home.x, y: card.home.y, width: card.width, height: card.height }, false);
      }
    }

    if (!c) return;
    this.tintVacantSlot(c);
    this.paintCascade(c);
  }

  private commitCardSwap(p: PressState): void {
    const c = p.cascade;
    const source = p.swapSourceId ? this.topLevelGroupBox(p.swapSourceId) : undefined;
    // The DROP resolves against the model rects, not the cascade's live ones — legacy's own asymmetry
    // (visual hit-test while dragging, findDrawableAt at the release). It is what makes a release over the gap a
    // displaced card left land on THAT card, which is the slot the preview showed the held card taking.
    const target = this.groupAtBookPoint(p.current, p.swapSourceId);
    // Same gate as the preview and as spread-edit.ops: a same-size unpinned pair trades PLACES, and only then is
    // the chain the truth about the drop. Anything else — bare page, an enlarged card, a pinned one, or a host
    // with no cascade channel — leaves the arrangement alone, so the preview must be rolled back first.
    const placeTrade = !!source && !!target && !!this.cfg.onGroupCascade && this.canTradePlaces(source, target);
    const chain = placeTrade && c ? [...c.chain] : [];
    const landing = chain.length > 0 && c ? { ...c.vacant } : undefined;

    if (c) {
      if (chain.length === 0) this.unwindCascade(c, 0); // roll the preview back before parking it
      this.settleCascade(c);
    }

    // A contents trade parks exactly like the photo swap: the held ghost settles onto the target frame while the
    // target's own pixels stay behind in the source frame, so the release frame already reads as the finished trade.
    // Only when the release lands on the frame the preview was actually showing — the drop resolves against model
    // rects and the hover against live ones, so the two can name different cards.
    const traded = !placeTrade && target && this.previewedSlotId === target.id ? this.swapPreview : null;
    const tradedPixels = traded?.bitmap ?? null;

    // Park the held card where the preview said it lands (or back on its own slot). The settle render is ~100 ms
    // out; without this the release frame flashes the card back under the cursor before the model's render arrives.
    if (traded) this.setDragOffset(traded.from.x - traded.to.x, traded.from.y - traded.to.y);
    else if (landing && c) this.setDragOffset(landing.x - c.sourceHome.x, landing.y - c.sourceHome.y);
    else this.setDragOffset(0, 0);

    if (!traded) this.restorePreviewedSlot(); // a preview the drop did not honour must not leave its frame emptied
    this.cancelSwapPreview();
    this.drawSwapTarget(undefined);
    if (p.swapSourceId && target) {
      // An unwound chain under a place-trade drop means the drag ended up changing nothing — commit neither, or a
      // hover still latched on the last card would smuggle a swap past a preview that showed none.
      if (chain.length > 0) this.cfg.onGroupCascade?.([p.swapSourceId, ...chain]);
      else if (!placeTrade) this.cfg.onGroupSwap?.(p.swapSourceId, target.id);
    }

    this.endDrag();
    if (tradedPixels && p.swapSourceId) this.holdTradedPhoto(tradedPixels, p.swapSourceId);
  }

  // Legacy Konva parents the highlight to the target card's own node, so it inherits that card's transform and can
  // never be mispositioned; the camera overlay has no such node, so the frame is drawn at the card's book rect through
  // handleDrawOffset() (the same book→handle-canvas mapping drawHandles uses) — omitting it floats the frame off the card.
  private drawSwapTarget(target: RenderGroupBox | RenderImageBox | undefined): void {
    this.drawSwapTargetRect(target?.rect);
  }

  // The cascade's drop slot is not any box's rect (on an unwind it is the PREVIOUS chain member's home), so the
  // frame is driven by a plain book rect — one painter for both drags, one dashed language on screen.
  private drawSwapTargetRect(r: { x: number; y: number; width: number; height: number } | undefined): void {
    if (this.swapTargetFadeRaf) {
      cancelAnimationFrame(this.swapTargetFadeRaf);
      this.swapTargetFadeRaf = 0;
    }

    const canvas = this.handleCanvas;
    const ctx = canvas?.getContext('2d');
    if (!canvas || !ctx) return;
    ctx.setTransform(1, 0, 0, 1, 0, 0);
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    if (!r) return;
    const s = this.viewScale || 1;
    // stroke straddles the path — push it out half its width so the frame hugs the card without covering it
    const out = SWAP_TARGET_STROKE_WIDTH / 2;
    const box = {
      x: r.x * s - out,
      y: r.y * s - out,
      w: r.width * s + SWAP_TARGET_STROKE_WIDTH,
      h: r.height * s + SWAP_TARGET_STROKE_WIDTH,
    };
    const start = performance.now();
    const paint = (): void => {
      if (this.destroyed) return;
      const alpha = Math.min(1, (performance.now() - start) / SWAP_TARGET_FADE_MS);
      ctx.save();
      this.beginHandlePass(canvas, ctx);
      ctx.globalAlpha = alpha;
      ctx.lineWidth = SWAP_TARGET_STROKE_WIDTH;
      ctx.setLineDash([]);
      ctx.strokeStyle = SWAP_TARGET_BASE_COLOR;
      ctx.strokeRect(box.x, box.y, box.w, box.h);
      ctx.setLineDash(SWAP_TARGET_DASH);
      ctx.strokeStyle = SWAP_TARGET_DASH_COLOR;
      ctx.strokeRect(box.x, box.y, box.w, box.h);
      ctx.restore();
      this.swapTargetFadeRaf = alpha < 1 ? requestAnimationFrame(paint) : 0;
    };

    paint();
  }

  // Takes the release event (mouseup/pointerup/pointercancel/blur): the force key must be read AT the drop, or an
  // ⌥/Alt pressed after the last pointer move would be lost and the swap silently refused.
  private onPressUp = (event?: Event): void => {
    const p = this.press;
    if (!p || !p.active) {
      this.endPress(); // a release without a drag → let the trailing click select/deselect
      return;
    }

    this.suppressClick = true;
    try {
      if (p.mode === 'marquee') {
        this.applyMarqueeSelection(this.marqueeSelect(marqueeRect(p.startBook, p.current)));
      } else if (p.mode === 'card-swap') {
        this.commitCardSwap(p);
      } else if (p.mode === 'photo-swap') {
        const { swapSourceId, swapTargetId } = p;
        this.drawSwapTarget(undefined);
        // Nothing was lifted, so there is nothing to park: the model's settle render IS the swap, in one moment.
        // A drop on a sibling frame commits it always: обмен отказа не знает (S7 R2).
        if (swapSourceId && swapTargetId) {
          // The two frames are about to repaint with each other's photo, already fully decoded a moment ago — the
          // settle render must hold their pixels instead of flashing the new frame's thumbHash blur first.
          this.pendingSwapSettleRefs = new Set([swapSourceId, swapTargetId]);
          this.cfg.onPhotoSwap?.(swapSourceId, swapTargetId);
        } else if (swapSourceId) {
          const side = this.releasePageSide(p, event);
          // Мимо рамок: кадр отпущен на свободное место СОСЕДНЕЙ страницы — это перенос (S8 R5). Пасспарту,
          // своя страница и оборванный жест (blur/pointercancel) дропом не считаются — там не происходит ничего.
          if (side !== undefined && side !== this.pageSideOfBox(swapSourceId))
            this.cfg.onPhotoDragRelease?.(swapSourceId, { side });
        }

        // The gesture start dropped the selection to clear the chrome; the release gives it back — the user
        // stays on the frame they were working with, traded photo or not.
        if (swapSourceId) this.restoreSelection(swapSourceId);
      } else if (p.mode === 'move') {
        const d = this.pressDelta(p);
        const snapped = this.applyMoveSnap(p, d.dx, d.dy);
        this.commitMove(p, snapped.dx, snapped.dy); // commit the snapped offset so the drop matches the guides
        this.endDrag(); // settle render reconciles from the updated model
        if (p.reselectId) this.cfg.onSelect?.(p.reselectId, false); // dragging an unselected box selects it
      }
    } finally {
      // end the press AFTER commitMove patched drawableRects, so the frame lands on the drop, not the pre-drag
      // spot — but end it even when a commit throws: a stuck press swallows every later mousedown silently
      // (onPointerDown bails on truthy this.press) and the editor reads as «рамка не двигается».
      this.endPress();
    }
  };

  // Raw drag delta with the Shift axis-lock applied — the single source both the live frame and the commit read.
  private pressDelta(p: PressState): { dx: number; dy: number } {
    const dx = p.current.x - p.startBook.x;
    const dy = p.current.y - p.startBook.y;
    if (p.axisLock === 'x') return { dx: 0, dy };
    if (p.axisLock === 'y') return { dx, dy: 0 };
    return { dx, dy };
  }

  private commitMove(p: PressState, dx: number, dy: number): void {
    const moved: MoveUpdate[] = [];
    const rs = this.cache?.renderScale ?? 1;
    for (const id of p.moveIds) {
      const s = p.startRects.get(id);
      if (!s) continue;
      const x = s.rect.x + dx;
      const y = s.rect.y + dy;
      moved.push({
        id,
        rect: this.toOwnRect(id, { x, y, width: s.rect.width, height: s.rect.height }),
        rotation: s.rotation,
        contentRect: this.translatedContentRect(id, dx, dy),
        ...(p.parentTransformIds?.has(id) && { parentTransform: true }),
      });
      // Keep the frame on the moved geometry until the settle render re-indexes drawableRects (else it snaps
      // back to the pre-drag rect for ~1 frame). paint() overwrites this canonically.
      const prev = this.drawableRects.get(id);
      this.drawableRects.set(id, {
        x: x * rs,
        y: y * rs,
        width: s.rect.width * rs,
        height: s.rect.height * rs,
        rotation: s.rotation,
        transform: prev?.transform,
      });
    }

    if (moved.length > 0) this.cfg.onMove?.(moved); // single or batch move = one undo entry
  }

  // Snap frame captured once at drag start; radii are fixed screen-px distances in book px so they hold at any zoom.
  private buildMoveSnap(p: PressState): PressState['snap'] {
    const startAABB = groupBounds([...p.startRects.values()]);
    if (!startAABB) return undefined;
    const base = this.buildSnapStops(new Set(p.moveIds));
    if (!base) return undefined;

    return { startAABB, ...base, hold: {}, gapHold: {}, snapOwner: {} };
  }

  // Align stops + neighbour AABBs (for equal-gap snap) + the gap capture radius (book px) — shared by both.
  // `undefined` on a tiered host means the dragged box takes no magnet at all: decor moves free.
  private buildSnapStops(excludeIds: ReadonlySet<string>):
    | {
        stops: SnapStops;
        pageStops: SnapStops;
        tiered: boolean;
        radiusScale: number;
        gapRadii: GapSnapRadii;
        gapMax: GapMaxByAxis;
        neighbors: SnapNeighbor[];
        draggedKind?: SnapNeighborKind;
        channels?: GapChannelsByAxis;
      }
    | undefined {
    if (this.cfg.getSnapEnabled?.() === false) return undefined;
    const scale = this.viewScale || 1;
    const snapPages = this.cfg.getSnapPages?.() ?? [];
    const tiered = snapPages.length > 0;
    // The class of what is being dragged decides what may anchor it: an image frame lines up on image frames,
    // text on frames first and other text second, decor on nothing — the host's snap veto marks decor, so a
    // selection with no snappable member simply gets no magnet.
    const draggedKind = tiered ? this.draggedSnapKind(excludeIds) : undefined;
    if (tiered && !draggedKind) return undefined;

    const others: SnapNeighbor[] = [];
    for (const [id, r] of this.drawableRects) {
      if (excludeIds.has(id) || !this.isSnapCandidate(id)) continue;
      const kind = this.snapNeighborKind(id);
      if (draggedKind && snapKindRank(draggedKind, kind) === undefined) continue;
      others.push({ ...this.sceneRectBookAABB(r), id, kind });
    }

    const vp = this.cache?.viewport;
    const prof = this.cfg.snapProfile;
    const pages: TransformRect[] = tiered ? snapPages : vp ? [{ x: 0, y: 0, width: vp.width, height: vp.height }] : [];
    // A host without page geometry keeps the historical uniform snap: every stop at 2 screen px, no tiering.
    const legacyRadius = (prof?.moveSnapThresholdPx ?? MOVE_SNAP_THRESHOLD_PX) / scale;
    const radiiOverride = tiered ? undefined : { capture: legacyRadius, release: legacyRadius };
    let pageStops = collectSnapStops(pages, 'page', 1 / scale, radiiOverride);
    // Lines the host draws itself: on a spread that is each page's own visible-area centre, which no rect stop
    // can offer — the sheet's midpoint is the spine and the bleed lies outside the trim.
    const hostLines = vp ? this.cfg.getSnapLines?.() : undefined;
    if (vp && hostLines && (hostLines.vertical.length || hostLines.horizontal.length)) {
      pageStops = mergeSnapStops(pageStops, collectLineStops(hostLines, vp, 1 / scale, radiiOverride));
    }

    if (vp && (prof?.cutLineStops || prof?.pageFractionStops?.length)) {
      const extra: { vertical: number[]; horizontal: number[] } = { vertical: [], horizontal: [] };
      const zones = prof.cutLineStops ? this.cfg.getZones() : undefined;
      if (zones) {
        extra.vertical.push(zones.cut_horizontal, vp.width - zones.cut_horizontal);
        extra.horizontal.push(zones.cut_vertical, vp.height - zones.cut_vertical);
      }

      for (const f of prof.pageFractionStops ?? []) {
        extra.vertical.push(vp.width * f);
        extra.horizontal.push(vp.height * f);
      }

      pageStops = mergeSnapStops(pageStops, collectLineStops(extra, vp, 1 / scale, radiiOverride));
    }

    // Safe line = where the drawn safe frame sits (trim + safe inset), outer edges only. Always page-edge radii,
    // never the legacy 2 px: the edge must visibly hold on the line before it may cross into bleed. Safe 0 = no line.
    const safeZones = vp && prof?.safeLineStops ? this.cfg.getZones() : undefined;
    if (vp && safeZones) {
      const b = bleedOffset(safeZones);
      const sx = b.x + safeZones.safe_horizontal;
      const sy = b.y + safeZones.safe_vertical;
      const lines = {
        vertical: safeZones.safe_horizontal > 0 ? [sx, vp.width - sx] : [],
        horizontal: safeZones.safe_vertical > 0 ? [sy, vp.height - sy] : [],
      };
      pageStops = mergeSnapStops(pageStops, collectLineStops(lines, vp, 1 / scale));
    }

    const stops = mergeSnapStops(pageStops, collectSnapStops(others, 'element', 1 / scale, radiiOverride));
    const gapRadii: GapSnapRadii = tiered
      ? { capture: SNAP_GAP_RADII_PX.capture / scale, release: SNAP_GAP_RADII_PX.release / scale, holdEps: 1 / scale }
      : { capture: legacyRadius, release: legacyRadius, holdEps: 1 / scale };
    const gapMax: GapMaxByAxis = tiered ? { V: pages[0].width * 0.25, H: pages[0].height * 0.25 } : { V: Infinity, H: Infinity };

    return {
      stops,
      pageStops,
      tiered,
      radiusScale: 1 / scale,
      gapRadii,
      gapMax,
      neighbors: others,
      draggedKind,
      // A distance is always measured to the box's DIRECT neighbour, but its value may be copied from a pair
      // elsewhere on the spread — that is how a caption lands exactly as far under its photo as the group two
      // elements over. The reference pair's own ruler is drawn beside it.
      channels: computeGapChannelsByAxis(others),
    };
  }

  // Which class the dragged selection counts as: text only when every snappable member is text, otherwise frame.
  // `undefined` = nothing in the selection may snap at all (decor, clipart, background) — no magnet, free movement.
  private draggedSnapKind(ids: ReadonlySet<string>): SnapNeighborKind | undefined {
    const kinds = [...ids].filter(id => this.isSnapCandidate(id)).map(id => this.snapNeighborKind(id));
    if (kinds.length === 0) return undefined;
    return kinds.every(k => k === 'text') ? 'text' : 'frame';
  }

  // The anchor class of a neighbour. Decor, clipart and background never get here (isSnapCandidate), so
  // everything left is one or the other.
  private snapNeighborKind(id: string): SnapNeighborKind {
    return (this.cache ? findRenderBox(this.cache.boxes, id) : undefined)?.type === 'text' ? 'text' : 'frame';
  }

  // Drag delta after pulling toward the nearest edge or equal-gap position, plus its guides; raw when unset.
  private applyMoveSnap(
    p: PressState,
    rawDx: number,
    rawDy: number,
  ): { dx: number; dy: number; guides: SnapGuide[]; gapGuides: GapGuide[] } {
    const snap = p.snap;
    if (!snap) return { dx: rawDx, dy: rawDy, guides: [], gapGuides: [] };
    const cand: TransformRect = {
      x: snap.startAABB.x + rawDx,
      y: snap.startAABB.y + rawDy,
      width: snap.startAABB.width,
      height: snap.startAABB.height,
    };
    // Tiered hosts re-pick the neighbourhood every frame — the magnet must follow the box, not the layout the
    // gesture began in. A legacy host keeps the gesture-start stop set exactly as before.
    const stops =
      snap.tiered && snap.draggedKind
        ? mergeSnapStops(snap.pageStops, selectNeighborStops(cand, snap.neighbors, snap.radiusScale, snap.draggedKind))
        : snap.stops;
    const align = computeMoveSnap(cand, stops, snap.hold);
    // `draggedKind` puts the gap path on the tiered rules — direct neighbour of the best class, rhythm match only.
    // Legacy hosts (the album editor is one) pass none and keep today's candidates, sandwiching included.
    const gap = computeEqualGapSnap(
      cand,
      snap.neighbors,
      snap.gapRadii,
      snap.channels,
      snap.gapHold,
      snap.gapMax,
      snap.draggedKind,
    );
    const combined = combineMoveSnap(
      align,
      gap,
      snap.tiered
        ? {
            owner: snap.snapOwner,
            margin: SNAP_ARBITRATION_MARGIN_PX * snap.radiusScale,
            dualGuideEps: DUAL_GUIDE_EPS_PX * snap.radiusScale,
            gapHeld: gap.heldAxes,
          }
        : undefined,
    );
    // Hold ownership: only the axis winner keeps its hold. Committing both would leave the loser «held»,
    // and next frame that stale hold outranks a fresh capture and steals the axis back — the flapping the
    // sticky feel exists to prevent.
    snap.snapOwner = combined.owner;
    if (snap.tiered) {
      snap.hold = {
        v: combined.owner.V === 'align' ? align.hold.v : undefined,
        h: combined.owner.H === 'align' ? align.hold.h : undefined,
      };
      snap.gapHold = {
        v: combined.owner.V === 'gap' ? gap.hold.v : undefined,
        h: combined.owner.H === 'gap' ? gap.hold.h : undefined,
      };
    } else {
      snap.hold = align.hold;
      snap.gapHold = gap.hold;
    }

    return { dx: rawDx + combined.dx, dy: rawDy + combined.dy, guides: combined.guides, gapGuides: combined.gapGuides };
  }

  // Alignment lines + equal-gap dimension lines on the (otherwise empty during a drag) handle canvas.
  private drawMoveGuides(guides: readonly SnapGuide[], gapGuides: readonly GapGuide[]): void {
    const canvas = this.handleCanvas;
    const ctx = canvas?.getContext('2d');
    if (!canvas || !ctx) return;
    ctx.save();
    this.beginHandlePass(canvas, ctx);
    this.drawSnapGuideLines(ctx, guides);
    this.drawGapGuideLines(ctx, gapGuides);
    ctx.restore();
  }

  // Equal-gap highlight: a line with perpendicular end-ticks per matched gap — both gaps light up so the
  // equality itself is the signal. Deliberately NO numeric label: «насрать сколько пикселей — просто
  // подсвечиваешь, что пространство одинаковое».
  private drawGapGuideLines(ctx: CanvasRenderingContext2D, guides: readonly (GapGuide | SizeGuide)[]): void {
    if (guides.length === 0) return;
    const color = this.themeToken(SNAP_GUIDE_TOKEN);
    if (!color) return;
    const s = this.viewScale || 1;
    const tick = 4;
    ctx.save();
    ctx.strokeStyle = color;
    ctx.lineWidth = 1;
    ctx.setLineDash([]);
    for (const g of guides) {
      for (const seg of g.segments) {
        const isV = g.axis === 'V';
        const x1 = (isV ? seg.from : seg.cross) * s;
        const y1 = (isV ? seg.cross : seg.from) * s;
        const x2 = (isV ? seg.to : seg.cross) * s;
        const y2 = (isV ? seg.cross : seg.to) * s;
        ctx.beginPath();
        ctx.moveTo(x1, y1);
        ctx.lineTo(x2, y2);
        if (isV) {
          ctx.moveTo(x1, y1 - tick);
          ctx.lineTo(x1, y1 + tick);
          ctx.moveTo(x2, y2 - tick);
          ctx.lineTo(x2, y2 + tick);
        } else {
          ctx.moveTo(x1 - tick, y1);
          ctx.lineTo(x1 + tick, y1);
          ctx.moveTo(x2 - tick, y2);
          ctx.lineTo(x2 + tick, y2);
        }

        ctx.stroke();
      }
    }

    ctx.restore();
  }

  // Thin dark dashed lines, each spanning only from its anchor object through the dragged box (guide.from/to,
  // book px) with a small CSS-px overshoot — a full-viewport line reads as a cut «в никуда» instead of a
  // relation. The ctx arrives in the shared pass transform, so both axes are book×viewScale coordinates.
  private drawSnapGuideLines(ctx: CanvasRenderingContext2D, guides: readonly SnapGuide[]): void {
    if (guides.length === 0) return;
    const color = this.themeToken(SNAP_GUIDE_TOKEN);
    if (!color) return;
    const s = this.viewScale || 1;
    const pad = 10; // css px past each end so an exact-overlap span still reads as a line
    ctx.save();
    ctx.strokeStyle = color;
    ctx.lineWidth = 1;
    ctx.setLineDash(SNAP_GUIDE_DASH);
    for (const g of guides) {
      ctx.beginPath();
      if (g.axis === 'V') {
        const x = g.pos * s;
        ctx.moveTo(x, g.from * s - pad);
        ctx.lineTo(x, g.to * s + pad);
      } else {
        const y = g.pos * s;
        ctx.moveTo(g.from * s - pad, y);
        ctx.lineTo(g.to * s + pad, y);
      }

      ctx.stroke();
    }

    ctx.restore();
  }

  // A guide line through the rotation pivot at the nearest snap angle, while a rotate gesture is near it.
  private drawRotationGuide(ctx: CanvasRenderingContext2D): void {
    const g = this.gesture;
    if (!g || (g.kind !== 'single' && g.kind !== 'group') || g.handle !== 'rotate') return;
    const rotation = g.kind === 'single' ? g.current.rotation : g.current.frameRotation;
    const angle = rotationGuideAngle(rotation);
    if (angle === undefined) return;
    const color = this.themeToken(SNAP_GUIDE_TOKEN);
    if (!color) return;
    const center = g.kind === 'single' ? g.center : g.groupCenter;
    const s = this.viewScale || 1;
    const cx = center.x * s;
    const cy = center.y * s;
    const rad = (angle * Math.PI) / 180;
    const len = Math.hypot(ctx.canvas.width, ctx.canvas.height);
    ctx.save();
    ctx.strokeStyle = color;
    ctx.lineWidth = 1;
    ctx.setLineDash(SNAP_GUIDE_DASH);
    ctx.beginPath();
    ctx.moveTo(cx - Math.cos(rad) * len, cy - Math.sin(rad) * len);
    ctx.lineTo(cx + Math.cos(rad) * len, cy + Math.sin(rad) * len);
    ctx.stroke();
    ctx.restore();
  }

  private endPress(): void {
    // Released here, not at endDrag(): the drop's trailing onSelect(reselectId) runs between the two.
    this.setGestureActive(false);
    if (!this.press) return;
    this.cancelSwapPreview(); // a press torn down without a drop (pointercancel) must not leave its raf running
    // Same for the hover chrome: a pointercancel mid-drag would otherwise strand a highlighted frame.
    if (this.press.mode === 'photo-swap') {
      this.hideSwapGhost();
      this.setSwapHover(undefined);
    }

    this.releaseCascade(this.press.cascade);
    this.press = undefined;
    this.releaseSlotCapture();
    this.disarmWindowDrag(this.onPressMove, this.onPressUp);
    this.drawHandles();
  }

  // Selectable drawables (same set as click hit-test) whose rotated/affine AABB intersects the marquee (book px).
  private marqueeSelect(marquee: TransformRect): string[] {
    const ids: string[] = [];
    for (const [id, r] of this.drawableRects) {
      if (r.locked) continue;
      if (this.isMarqueeTransparent(id)) continue; // a rubber-band selects content, never the canvas floor
      if (!this.isTargetable(id)) continue;
      if (rectsIntersect(marquee, this.sceneRectBookAABB(r))) ids.push(id);
    }

    return ids;
  }

  private computeSelectionScreenRect(): { left: number; top: number; width: number; height: number } | null {
    if (this.selectedIds.length === 0) return null;
    const s = this.viewScale || 1;
    let minX = Infinity;
    let minY = Infinity;
    let maxX = -Infinity;
    let maxY = -Infinity;
    for (const id of this.selectedIds) {
      const r = this.drawableRects.get(id);
      if (!r) continue;
      // A frame run to the bleed reports its VISIBLE rect, the one the selection stroke is drawn on
      // (clampToSurface) — the host's own outline and tools then sit on the shown page, never off its edge.
      const b = this.sceneRectBookAABB(this.getRenderBox(id)?.type === 'image' ? this.clampToSurface(r) : r);
      if (b.x < minX) minX = b.x;
      if (b.y < minY) minY = b.y;
      if (b.x + b.width > maxX) maxX = b.x + b.width;
      if (b.y + b.height > maxY) maxY = b.y + b.height;
    }

    if (!isFinite(minX)) return null;
    // Anchor to the content overlay's top-left (book origin) — minX/minY are book px from there. The handle layer can sit
    // elsewhere (full host in camera mode), so reading its rect would offset the result.
    const canvas = this.overlayCanvas ?? this.handleCanvas;
    const r = canvas?.getBoundingClientRect();
    const ox = r?.left ?? 0;
    const oy = r?.top ?? 0;
    // Raise the top by the rotate-handle clearance (screen px, zoom-independent) so the host toolbar sits above it —
    // only when transformer chrome is drawn at all; in select-only mode there is no handle to clear.
    const topPad = this.transformsEnabled() ? ROTATE_HANDLE_DIST_PX + CORNER_R_PX : 0;
    return { left: ox + minX * s, top: oy + minY * s - topPad, width: (maxX - minX) * s, height: (maxY - minY) * s + topPad };
  }

  private pressInsideSelectionBounds(book: Point): boolean {
    const aabbs: TransformRect[] = [];
    for (const id of this.selectedIds) {
      const r = this.drawableRects.get(id);
      if (r) aabbs.push(this.sceneRectBookAABB(r));
    }

    return pointInsideBounds(book, aabbs);
  }

  // Overlay-canvas client-px rect per errored box (same book→screen mapping as the selection rect) — component draws badges.
  private computeFontErrorBadges(): FontErrorBadge[] {
    if (this.fontErrors.size === 0) return [];
    const s = this.viewScale || 1;
    const canvas = this.overlayCanvas ?? this.handleCanvas;
    const rect = canvas?.getBoundingClientRect();
    const ox = rect?.left ?? 0;
    const oy = rect?.top ?? 0;
    const badges: FontErrorBadge[] = [];
    for (const [id, kind] of this.fontErrors) {
      if (this.dragLiftIds?.has(id)) continue; // lifted: its rect is stale for the gesture (see applyDragChrome)
      const r = this.drawableRects.get(id);
      if (!r) continue;
      const b = this.sceneRectBookAABB(r);
      badges.push({
        id,
        kind,
        left: ox + b.x * s,
        top: oy + b.y * s,
        width: b.width * s,
        height: b.height * s,
      });
    }

    return badges;
  }

  // Skip while there are (and were) no errors so a scroll on a clean spread emits nothing.
  private emitFontErrorBadges(): void {
    if (this.fontErrors.size === 0 && !this.fontErrorsEmitted) return;
    const badges = this.computeFontErrorBadges();
    this.fontErrorsEmitted = badges.length > 0;
    this.cfg.onFontError?.(badges);
  }

  private sceneRectBookAABB(r: SceneRect): TransformRect {
    const rs = this.cache?.renderScale || 1;
    const c = { x: r.x + r.width / 2, y: r.y + r.height / 2 };
    const corners: Point[] = [
      { x: r.x, y: r.y },
      { x: r.x + r.width, y: r.y },
      { x: r.x + r.width, y: r.y + r.height },
      { x: r.x, y: r.y + r.height },
    ].map(p => {
      const t = r.transform ? applyAffinePoint(r.transform, p) : rotatePointAround(p, c, r.rotation ?? 0);
      return { x: t.x / rs, y: t.y / rs };
    });
    let minX = Infinity;
    let minY = Infinity;
    let maxX = -Infinity;
    let maxY = -Infinity;
    for (const p of corners) {
      if (p.x < minX) minX = p.x;
      if (p.y < minY) minY = p.y;
      if (p.x > maxX) maxX = p.x;
      if (p.y > maxY) maxY = p.y;
    }

    return { x: minX, y: minY, width: maxX - minX, height: maxY - minY };
  }

  private applyMarqueeSelection(hits: string[]): void {
    const ids = [...new Set(hits.map(id => this.clipBaseOf(id) ?? id))];
    if (this.cfg.onMarqueeSelect) {
      this.cfg.onMarqueeSelect(ids); // host owns selection; it calls back setSelection(...)
      return;
    }

    void this.setSelection(ids); // standalone fallback
  }

  // The selection's transformer layout in screen px: gesture = live preview, static = rendered.
  private handleScreenLayout(): { layout: HandleLayout } | undefined {
    const g = this.gesture;
    if (g?.kind === 'skew') return { layout: this.skewHandleLayout(g) };
    if (g?.kind === 'single') return { layout: this.singleGestureLayout(g) };
    if (g?.kind === 'group') return { layout: this.bookHandleLayout(g.current.frameRect, g.current.frameRotation) };
    if (this.selectedIds.length > 1) {
      if (!this.cfg.onResizeRotate) return undefined;
      const members = this.groupMembers();
      const frame = this.transformAwareGroupBounds(members) ?? groupBounds(members);
      return frame ? { layout: this.bookHandleLayout(frame, 0) } : undefined;
    }

    if (this.selectedIds.length === 1) {
      if (!this.cfg.onResizeRotate) return undefined;
      const geo = this.selectedBoxGeometry();
      const layout = geo ? this.sceneHandleLayout(geo.id) : undefined;
      return layout ? { layout } : undefined;
    }

    return undefined;
  }

  private sceneHandleLayout(id: string): HandleLayout | undefined {
    const r = this.drawableRects.get(id);
    if (!r || !this.cache) return undefined;
    const k = (this.viewScale || 1) / this.cache.renderScale;
    const c = { x: r.x + r.width / 2, y: r.y + r.height / 2 };
    const toScreen = (p: Point): Point => {
      const s = r.transform ? applyAffinePoint(r.transform, p) : rotatePointAround(p, c, r.rotation ?? 0);
      return { x: s.x * k, y: s.y * k };
    };

    return layoutFromCorners(
      toScreen({ x: r.x, y: r.y }),
      toScreen({ x: r.x + r.width, y: r.y }),
      toScreen({ x: r.x + r.width, y: r.y + r.height }),
      toScreen({ x: r.x, y: r.y + r.height }),
      { x: c.x * k, y: c.y * k },
    );
  }

  private bookHandleLayout(rect: TransformRect, rotationDeg: number): HandleLayout {
    const scale = this.viewScale || 1;
    const ap = anchorPositions(rect, rotationDeg);
    const s = (p: Point): Point => ({ x: p.x * scale, y: p.y * scale });
    const center = { x: (rect.x + rect.width / 2) * scale, y: (rect.y + rect.height / 2) * scale };
    return layoutFromCorners(s(ap['top-left']), s(ap['top-right']), s(ap['bottom-right']), s(ap['bottom-left']), center);
  }

  // The live frame follows the same draw affine the idle frame applies (sceneHandleLayout) — else the frame
  // jumps off the rendered glyphs at grab and snaps back at release on matrix-positioned text.
  private singleGestureLayout(g: {
    frameAffine?: Affine;
    startRect: TransformRect;
    current: { rect: TransformRect; rotation: number };
  }): HandleLayout {
    if (!g.frameAffine) return this.bookHandleLayout(g.current.rect, g.current.rotation);
    const r = g.current.rect;
    const live = this.liveFrameAffine(g.frameAffine, g.startRect, r);
    const t = composeBoxDrawTransform(live, g.current.rotation, r.x + r.width / 2, r.y + r.height / 2) ?? live;
    const s = this.viewScale || 1;
    const map = (x: number, y: number): Point => {
      const p = applyAffinePoint(t, { x, y });
      return { x: p.x * s, y: p.y * s };
    };

    const c = applyAffinePoint(t, { x: r.x + r.width / 2, y: r.y + r.height / 2 });
    return layoutFromCorners(
      map(r.x, r.y),
      map(r.x + r.width, r.y),
      map(r.x + r.width, r.y + r.height),
      map(r.x, r.y + r.height),
      { x: c.x * s, y: c.y * s },
    );
  }

  // Book-space draw affine for the live rect: the grab-time linear part with its translation tracked relatively
  // ((I−L)·Δorigin), so the frame is exact at grab and follows the builder's re-anchoring during the drag.
  private liveFrameAffine(sceneT: Affine, startRect: TransformRect, liveRect: TransformRect): Affine {
    const rs = this.cache?.renderScale || 1;
    const shifted = reanchorAffine(sceneT, liveRect.x - startRect.x, liveRect.y - startRect.y);
    return [shifted[0], shifted[1], shifted[2], shifted[3], sceneT[4] / rs + shifted[4], sceneT[5] / rs + shifted[5]];
  }

  // Live skew preview: the rendered (affine/rotated) quad with the in-progress shear applied about the anchored edge.
  private skewHandleLayout(g: SkewGesture): HandleLayout {
    const r = g.sceneBase;
    const k = (this.viewScale || 1) / (this.cache?.renderScale || 1);
    const c = { x: r.x + r.width / 2, y: r.y + r.height / 2 };
    const base = (p: Point): Point => (r.transform ? applyAffinePoint(r.transform, p) : rotatePointAround(p, c, r.rotation ?? 0));
    const tl = base({ x: r.x, y: r.y });
    const tr = base({ x: r.x + r.width, y: r.y });
    const br = base({ x: r.x + r.width, y: r.y + r.height });
    const bl = base({ x: r.x, y: r.y + r.height });
    const center = { x: (tl.x + tr.x + br.x + bl.x) / 4, y: (tl.y + tr.y + br.y + bl.y) / 4 };
    const pivot = g.fromCenter ? center : skewPivotScene(g.handle, tl, tr, br, bl);
    const shear = (p: Point): Point =>
      g.axis === 'horizontal'
        ? { x: p.x + g.current.shx * (p.y - pivot.y), y: p.y }
        : { x: p.x, y: p.y + g.current.shy * (p.x - pivot.x) };
    const s = (p: Point): Point => {
      const sh = shear(p);
      return { x: sh.x * k, y: sh.y * k };
    };

    return layoutFromCorners(s(tl), s(tr), s(br), s(bl), s(center));
  }

  // Pointer → handle space = book-origin CSS px (book × viewScale), matching the anchors from *HandleLayout(). Derived from
  // the content overlay (not the handle canvas) so the hit-test holds wherever the handle layer sits — full-host in camera mode.
  private pointerToHandlePx(clientX: number, clientY: number): Point | undefined {
    const book = this.clientToBook(clientX, clientY);
    if (!book) return undefined;
    const s = this.viewScale;
    return { x: book.x * s, y: book.y * s };
  }

  private onPointerMove = (event: MouseEvent): void => {
    const g = this.gesture;
    if (!g) return;
    const book = this.clientToBook(event.clientX, event.clientY);
    if (!book) return;
    this.gestureMoved = true;
    if (g.kind === 'skew') {
      const factor = g.fromCenter ? 2 : 1;
      let shx = 0;
      let shy = 0;
      if (g.axis === 'horizontal') {
        const sign = g.handle === 'top-center' ? -1 : 1;
        shx = (sign * factor * (book.x - g.startPointer.x)) / (g.rect.height || 1);
      } else {
        const sign = g.handle === 'middle-left' ? -1 : 1;
        shy = (sign * factor * (book.y - g.startPointer.y)) / (g.rect.width || 1);
      }

      // Shear the linear part in output space; keep the base translation (the renderer pivots on rect.x/y anyway).
      const s = mul(shearMatrix(shx, shy), g.baseMatrix);
      g.current = { matrix: [s[0], s[1], s[2], s[3], g.baseMatrix[4], g.baseMatrix[5]], shx, shy };
      this.drawHandles();
      return;
    }

    if (g.kind === 'single') {
      if (g.handle === 'rotate') {
        const rotation = Math.round(
          rotateAngle({
            center: g.center,
            startPointer: g.startPointer,
            pointer: book,
            startRotationDeg: g.startRotation,
            snap: event.shiftKey,
            snaps: this.cfg.snapProfile?.rotationSnaps,
            snapToleranceDeg: this.cfg.snapProfile?.alwaysOnRotationToleranceDeg,
          }),
        );
        g.current = { rect: g.startRect, rotation };
      } else {
        // FKS-12037: у декора угловая ручка держит пропорцию всегда, боковая тянет по своей оси — как со Shift, но без него.
        const decorLock = this.isDecorCornerGesture(g);
        // Для рамки со фото Shift инвертирует правило: без него пропорция держится, с ним — свободное растяжение.
        const imageLock = this.isImageAspectGesture(g);
        g.imageLock = imageLock && !event.shiftKey;
        let rect = g.frameAffine
          ? resizeRectWithMatrix({
              anchor: g.handle,
              startRect: g.startRect,
              affine: scaleAffineTranslation(g.frameAffine, 1 / (this.cache?.renderScale || 1)),
              rotationDeg: g.startRotation,
              pointer: book,
              aspectLock: event.shiftKey !== imageLock || !!g.textScale || decorLock,
              fromCenter: event.altKey,
            })
          : resizeRect({
              anchor: g.handle,
              startRect: g.startRect,
              rotationDeg: g.startRotation,
              pointer: book,
              // FKS-11514: у текстового углового жеста пропорция держится всегда — кегль масштабируется одним числом.
              aspectLock: event.shiftKey !== imageLock || !!g.textScale || decorLock,
              fromCenter: event.altKey,
            });
        if (g.textScale) rect = this.clampTextScaledRect(g, rect, event.altKey);
        const rs = this.resizeSnap;
        // Shift drops the magnet for the whole gesture: every stop clamps ONE axis, which would silently undo the
        // ratio aspect-lock just applied. The promise the modifier makes — proportions hold — outranks the snap.
        // The FKS-11514 text-scale gesture holds the same promise, so it drops the magnet the same way.
        // FKS-13040: у фото-рамки Shift не отменяет магнит — пропорцию тут держит сам жест, поэтому зажим
        // получает только ведущая ось, а вторая идёт за коэффициентом (ниже).
        if (rs && !event.altKey && (!event.shiftKey || imageLock) && !g.textScale && !decorLock) {
          // Tiered-хост перевыбирает прямых соседей каждый кадр, как move-жест: магнит следует за рамкой.
          const stops =
            rs.tiered && rs.draggedKind
              ? mergeSnapStops(rs.pageStops, selectNeighborStops(rect, rs.neighbors, rs.radiusScale, rs.draggedKind))
              : rs.stops;
          // Стопы поперечной оси пропорционального жеста не передаём: её зажим всё равно отменится пересчётом по
          // пропорции, а направляющая пообещала бы прилипание, которого нет.
          const drivenAxis = g.imageLock ? this.drivenResizeAxis(g.handle, g.startRect, g.startRotation, book) : undefined;
          const snapped = computeResizeSnap({
            anchor: g.handle,
            rect,
            stops: !drivenAxis
              ? stops
              : drivenAxis === 'V'
                ? { vertical: stops.vertical, horizontal: [] }
                : { vertical: [], horizontal: stops.horizontal },
            // Dimension matching is a tiered-host behaviour (design editor) — the album editor keeps edge stops only.
            neighbors: rs.tiered ? rs.neighbors : undefined,
            draggedKind: rs.draggedKind,
            radiusScale: rs.radiusScale,
            hold: rs.hold,
          });
          rs.hold = snapped.hold;
          let finalRect = snapped.rect;
          let guides = snapped.guides;
          // A legacy host whose profile asks for size-match (photobook): the simple sibling width/height
          // match runs as a post-pass — the tiered host already matches dimensions natively above.
          if (this.cfg.snapProfile?.sizeMatch && !rs.tiered) {
            const matched = computeSizeMatchSnap(
              g.handle,
              finalRect,
              rs.neighbors,
              (this.cfg.snapProfile.moveSnapThresholdPx ?? MOVE_SNAP_THRESHOLD_PX) * rs.radiusScale,
              new Set(guides.map(gd => gd.axis)),
            );
            finalRect = matched.rect;
            guides = [...guides, ...matched.guides];
          }

          // Пропорцию восстанавливаем ПОСЛЕ всех зажимов: ведущая ось встала на стоп, вторая идёт за тем же
          // коэффициентом, рамка остаётся на своей опорной точке (bare lockAspect вернул бы только w/h и сдвинул её).
          if (drivenAxis && g.startRect.width > 0 && g.startRect.height > 0) {
            const k = drivenAxis === 'V' ? finalRect.width / g.startRect.width : finalRect.height / g.startRect.height;
            finalRect = scaleRectAroundAnchor(g.startRect, g.startRotation, g.handle, k, event.altKey);
          }

          g.current = { rect: this.acceptTextResize(g, finalRect), rotation: g.startRotation };
          this.snapGuides = guides;
          this.sizeGuides = snapped.sizeGuides;
        } else {
          if (rs) rs.hold = {};
          g.current = { rect: this.acceptTextResize(g, rect), rotation: g.startRotation };
          this.snapGuides = [];
          this.sizeGuides = [];
        }
      }
    } else {
      const m = g.members;
      const c = g.groupCenter;
      if (g.handle === 'rotate') {
        const ri = {
          groupCenter: c,
          members: m,
          startPointer: g.startPointer,
          pointer: book,
          snap: event.shiftKey,
          snapStep: this.cfg.snapProfile?.groupRotateSnapStep,
          snapToleranceDeg: this.cfg.snapProfile?.alwaysOnRotationToleranceDeg,
        };
        g.current = { frameRect: g.startGroup, frameRotation: groupRotateDelta(ri), updates: groupRotate(ri) };
      } else {
        const ri = {
          anchor: g.handle,
          startGroup: g.startGroup,
          members: m,
          pointer: book,
          aspectLock: event.shiftKey,
          fromCenter: event.altKey,
        };
        const next = { frameRect: groupResizeBounds(ri), frameRotation: 0, updates: groupResize(ri) };
        // Рамка-контейнер в выделении держит всю группу: тик, где она стала меньше своего текста, не принимается.
        const last = new Map(g.current.updates.map(u => [u.id, u.rect]));
        if (next.updates.every(u => this.textFrameFits(u.id, last.get(u.id) ?? u.rect, u.rect))) g.current = next;
      }
    }

    if (this.isLiveResize(g)) this.emitResizePreview();
    else this.applyGestureLift(g); // CSS-transform the lifted drag canvas (rotate, or resize without a live-preview host)
    this.drawHandles();
  };

  // Transforms the lifted drag canvas to mirror the in-progress resize/rotate — pure CSS compositor, zero raster per frame.
  private applyGestureLift(g: NonNullable<RustOverlayController['gesture']>): void {
    if (!this.dragCanvas || g.kind === 'skew') return;
    const s = this.viewScale || 1;
    if (g.kind === 'single') {
      if (g.handle === 'rotate') {
        const delta = g.current.rotation - g.startRotation;
        this.setDragCss(`rotate(${delta}deg)`, g.center.x * s, g.center.y * s);
      } else {
        const sx = g.startRect.width ? g.current.rect.width / g.startRect.width : 1;
        const sy = g.startRect.height ? g.current.rect.height / g.startRect.height : 1;
        const pivot = anchorPositions(g.startRect, g.startRotation)[OPPOSITE_ANCHOR[g.handle]];
        const rot = g.startRotation;
        this.setDragCss(`rotate(${rot}deg) scale(${sx}, ${sy}) rotate(${-rot}deg)`, pivot.x * s, pivot.y * s);
      }
    } else if (g.handle === 'rotate') {
      this.setDragCss(`rotate(${g.current.frameRotation}deg)`, g.groupCenter.x * s, g.groupCenter.y * s);
    } else {
      const sx = g.startGroup.width ? g.current.frameRect.width / g.startGroup.width : 1;
      const sy = g.startGroup.height ? g.current.frameRect.height / g.startGroup.height : 1;
      const pivot = anchorPositions(g.startGroup, 0)[OPPOSITE_ANCHOR[g.handle]];
      this.setDragCss(`scale(${sx}, ${sy})`, pivot.x * s, pivot.y * s);
    }
  }

  private setDragCss(transform: string, originX: number, originY: number): void {
    if (!this.dragCanvas) return;
    this.dragCanvas.style.transformOrigin = `${originX}px ${originY}px`;
    this.dragCanvas.style.transform = transform;
  }

  // The host's live layout for this grab: its candidates ride the lift, and their fonts and photo placements are frozen
  // so every frame scales from the grab state (the gesture's own fonts are frozen on the gesture).
  private armResizeReflow(
    g: Exclude<NonNullable<RustOverlayController['gesture']>, SkewGesture>,
  ): RustOverlayController['reflowLive'] {
    const provider = this.cfg.resizeReflow?.();
    if (!provider) return undefined;
    const own = g.kind === 'group' ? g.members.map(m => m.id) : [g.id];
    const ids = provider.candidates(own).filter(id => !own.includes(id));
    const texts: NonNullable<RustOverlayController['reflowLive']>['texts'] = new Map();
    const images: NonNullable<RustOverlayController['reflowLive']>['images'] = new Map();
    for (const id of ids) {
      const box = this.getRenderBox(id);
      if (box?.type === 'text') {
        const text = box as RenderTextBox;
        if (text.fontSize > 0) {
          texts.set(id, textScaleSnapshotOf(text));
        }
      }

      const image = box?.type === 'image' ? this.imageRectOf(id) : undefined;
      if (box && image && box.rect.width > 0) images.set(id, { width: box.rect.width, image });
    }

    return { provider, ids, texts, images };
  }

  private isLiveResize(g: NonNullable<RustOverlayController['gesture']>): boolean {
    return g.kind !== 'skew' && g.handle !== 'rotate';
  }

  // Throttle the live resize preview to one apply per animation frame: firing on every mousemove would queue faster
  // than it can paint.
  private emitResizePreview(): void {
    if (this.resizePreviewRaf !== undefined) return;
    this.resizePreviewRaf = requestAnimationFrame(() => {
      this.resizePreviewRaf = undefined;
      const g = this.gesture;
      if (g && this.isLiveResize(g)) this.previewResize(this.buildResizeUpdates(g));
    });
  }

  private cancelResizePreview(): void {
    if (this.resizePreviewRaf === undefined) return;
    cancelAnimationFrame(this.resizePreviewRaf);
    this.resizePreviewRaf = undefined;
  }

  // FKS-11514: proportional text scaling belongs to a CORNER handle of a single non-matrix text box — matrix
  // (PSD-positioned) text keeps a nominal fontSize (its scale lives in the affine, scaling both would double-apply),
  // and side handles keep frame-only resize. Metrics are frozen here, at grab (anti-compounding).
  // FKS-13160: a CORNER on text on a path (`textPath.d`) scales by font even under a matrix — the sinks move the ring by the
  // frame delta and never scale the affine, so fontSize is the only letter scale that follows the frame.
  private textScaleSnapshot(
    id: string,
    handle: HandleId,
    rect: TransformRect,
    matrixFrame: boolean,
  ): TextScaleSnapshot | undefined {
    const mode = this.cfg.enableTextCornerScale;
    if (!mode) return undefined;
    const box = this.getRenderBox(id);
    if (box?.type !== 'text') return undefined;
    const text = box as RenderTextBox;
    if (!this.textFontScaleAllowed(text)) return undefined;
    const corner = (CORNER_ANCHORS as readonly HandleId[]).includes(handle);
    const freeSide = mode === 'free' && (handle === 'middle-left' || handle === 'middle-right');
    if (!corner && !freeSide) return undefined;
    if ((matrixFrame || text.transform) && !(corner && text.textPath?.d)) return undefined;
    if (!(text.fontSize > 0) || !(rect.width > 0)) return undefined;
    return textScaleSnapshotOf(text);
  }

  // `'free'` — масштаб кеглем ТОЛЬКО у свободного текста: угол и боковая ручка (ширина рамки ведёт
  // кегль, высота идёт следом). Блочный текст и автоширина (`textMode 'block'`) остаются на обычном
  // frame-only ресайзе — и углом тоже. `true` (Дизайнер) масштабирует углом любой текст.
  private textFontScaleAllowed(text: RenderTextBox): boolean {
    const mode = this.cfg.enableTextCornerScale;
    return !!mode && !(mode === 'free' && text.textMode === 'block');
  }

  // FKS-12037: декор — картинка, а не рамка: угол всегда масштабирует пропорционально (Shift не нужен), сторона
  // тянет по своей оси. Только `object`-боксы и только там, где хост это включил (редакторы альбома и
  // дизайн-паков): фоторамка, текст, фон, фигуры-`vector`, а также фотокнига сохраняют прежнее поведение.
  private isDecorCornerGesture(g: Extract<NonNullable<RustOverlayController['gesture']>, { kind: 'single' }>): boolean {
    if (!this.cfg.lockDecorAspect) return false;
    if (!(CORNER_ANCHORS as readonly HandleId[]).includes(g.handle)) return false;
    return this.getRenderBox(g.id)?.type === 'object';
  }

  // Рамка с фотографией: любая ручка держит пропорцию (Shift освобождает). Наличие фото берём из МОДЕЛИ, а не из
  // кеша декодированных картинок — правило не может зависеть от того, успел ли снимок загрузиться. Пустая рамка
  // тянется свободно, как раньше.
  private isImageAspectGesture(g: Extract<NonNullable<RustOverlayController['gesture']>, { kind: 'single' }>): boolean {
    if (!this.cfg.lockImageAspect) return false;
    const box = this.getRenderBox(g.id);
    if (box?.type !== 'image' || !(box.imageUrl || box.imageAlbum)) return false;
    const corner = (CORNER_ANCHORS as readonly HandleId[]).includes(g.handle);
    return corner || !this.cfg.freeEdgeAspect?.(g.id);
  }

  // Ведущая ось пропорционального ресайза: у боковой ручки это её собственная ось, у угловой — та, по которой
  // курсор ушёл дальше относительно старта (то же правило, что у lockAspect внутри resizeRect). Свободный прогон
  // жеста здесь точен: магнит живёт только у неповёрнутой рамки без матрицы (см. resizeStops в beginDrag).
  private drivenResizeAxis(anchor: ResizeAnchor, startRect: TransformRect, rotationDeg: number, pointer: Point): SnapAxis {
    const sign = ANCHOR_SIGN[anchor];
    if (sign.x === 0) return 'H';
    if (sign.y === 0) return 'V';
    const free = resizeRect({ anchor, startRect, rotationDeg, pointer });
    return free.width / startRect.width >= free.height / startRect.height ? 'V' : 'H';
  }

  // Сохранённое положение фото внутри рамки (кадр), угол по умолчанию 0 — undefined, когда фото заполняет рамку.
  private imageRectOf(id: string): CropImageRect | undefined {
    const box = this.getRenderBox(id);
    const ir = box?.type === 'image' ? box.imageRect : undefined;
    return ir ? { x: ir.x, y: ir.y, width: ir.width, height: ir.height, rotation: ir.rotation ?? 0 } : undefined;
  }

  // The scale window keeps the START value legal: a font already outside the range (imported PSD) is never
  // dragged back in by force — it just cannot move further out.
  private clampTextScaledRect(
    g: Extract<NonNullable<RustOverlayController['gesture']>, { kind: 'single' }>,
    rect: TransformRect,
    fromCenter: boolean,
  ): TransformRect {
    const ts = g.textScale;
    if (!ts || !g.startRect.width || g.handle === 'rotate') return rect;
    const raw = rect.width / g.startRect.width;
    const lo = Math.min(1, TEXT_SCALE_FONT_MIN_PX / ts.fontSize);
    const hi = Math.max(1, TEXT_SCALE_FONT_MAX_PX / ts.fontSize);
    const k = Math.min(hi, Math.max(lo, raw));
    if (k === raw) return rect;
    // Under a frame matrix the matrix-resized origin is affine in the scale, so interpolating it holds
    // the drawn anchor exactly; rotation-space math would move it by the matrix's shear/rotation.
    if (g.frameAffine) {
      const t = (k - 1) / (raw - 1);
      const s = g.startRect;
      return { x: s.x + t * (rect.x - s.x), y: s.y + t * (rect.y - s.y), width: s.width * k, height: s.height * k };
    }

    return scaleRectAroundAnchor(g.startRect, g.startRotation, g.handle as ResizeAnchor, k, fromCenter);
  }

  /**
   * FKS-12432: тик ресайза текстовой рамки — сначала высота идёт за переносом, потом размер проверяется на вмещение.
   * Угловой жест с масштабом кегля (`textScale`) идёт мимо обоих: там шрифт едет вместе с рамкой, и замер по
   * сохранённому кеглю соврал бы — текст помещается при любом размере.
   */
  private acceptTextResize(
    g: Extract<NonNullable<RustOverlayController['gesture']>, { kind: 'single' }>,
    next: TransformRect,
  ): TransformRect {
    if (g.textScale) return next;
    const fitted = this.containerFollowHeight(g, next);
    return this.textFrameFits(g.id, g.current.rect, fitted) ? fitted : g.current.rect;
  }

  /**
   * FKS-12432: тянут ШИРИНУ контейнера — текст перекладывается по новой границе, и рамка идёт за ним по высоте
   * (уже — строк больше, рамка растёт; шире — поджимается обратно). Ровно тот же `fixedHeightRect`, что растит
   * рамку при вводе, только меряем по рамке-кандидату жеста. Вертикальную ручку не трогаем: высоту там задаёт
   * рука, а ниже своего текста рамку не пустит `textFrameFits`.
   */
  private containerFollowHeight(
    g: Extract<NonNullable<RustOverlayController['gesture']>, { kind: 'single' }>,
    next: TransformRect,
  ): TransformRect {
    if (g.handle !== 'middle-left' && g.handle !== 'middle-right') return next;
    const fit = this.cache?.frameFitOf;
    const box = this.getRenderBox(g.id);
    if (!fit || box?.type !== 'text') return next;
    const tb = box as RenderTextBox;
    if (!tb.text || !this.heightFollowsText(tb)) return next;
    const grown = fixedHeightRect({ ...tb, rect: { ...tb.rect, ...next } }, tb.text, fit);
    return grown ? { ...next, x: grown.x, y: grown.y, height: grown.height } : next;
  }

  /**
   * FKS-12432: рамка-контейнер не ужимается меньше своего текста — уменьшение упирается в размер, где текст ещё
   * помещается (по высоте — в перенос, по ширине — в самое длинное слово). Замер идёт тем же движковым
   * `frameFitOf`, что и клавиатурный стоп, только рамка подменяется на кандидата жеста (`rect` в тех же единицах,
   * что `box.rect`). Рост (обе стороны не меньше принятой) не меряем: он никогда не ломает вмещение.
   * Демо-наполнение мастер-альбома держит рамку наравне со своим текстом: оно больше не переливается под
   * новый размер, поэтому рамка, ужатая под него, обрезала бы текст.
   */
  private textFrameFits(id: string, last: TransformRect, next: TransformRect): boolean {
    if (next.width >= last.width && next.height >= last.height) return true;
    const fit = this.cache?.frameFitOf;
    const box = this.getRenderBox(id);
    if (!fit || box?.type !== 'text') return true;
    const tb = box as RenderTextBox;
    const text = tb.text ?? '';
    if (!text || !this.wrappingContainerText(tb)) return true;
    const probe = fit({ ...tb, rect: { ...tb.rect, width: next.width, height: next.height } }, text);
    return probe.heightOver <= 0 && probe.widthOver <= 0;
  }

  // The font multiplier IS the (already clamped) frame ratio — the gesture held the aspect, width and height agree.
  private textFontScaleOf(g: Extract<NonNullable<RustOverlayController['gesture']>, { kind: 'single' }>): number | undefined {
    if (!g.textScale || !g.startRect.width) return undefined;
    const k = g.current.rect.width / g.startRect.width;
    return k === 1 ? undefined : k;
  }

  // Per-box updates a resize/rotate commits or previews: own-rect + rotation, plus a re-covered imageRect for a resized
  // image box so the photo keeps filling the frame, and a scaled contentRect for a resized trimmed decor so its crop
  // stays put (both skipped for rotate — the frame size is unchanged).
  private buildResizeUpdates(g: NonNullable<RustOverlayController['gesture']>): ResizeRotateUpdate[] {
    if (g.kind === 'skew') return [];
    const withCover = g.handle !== 'rotate';
    if (g.kind === 'single') {
      return [
        {
          id: g.id,
          rect: this.toOwnRect(g.id, g.current.rect),
          rotation: g.current.rotation,
          imageRect: withCover ? (this.lockedImageRect(g) ?? this.coverImageRect(g.id, g.current.rect)) : undefined,
          contentRect: withCover ? this.scaledContentRect(g.id, g.startRect, g.current.rect, g.startContentRect) : undefined,
          frameTakeover: withCover || undefined,
          fontScale: withCover ? this.textFontScaleOf(g) : undefined,
          ...(this.parentTransformIds.has(g.id) && { parentTransform: true }),
        },
      ];
    }

    return g.current.updates.map(u => {
      const startRect = g.members.find(m => m.id === u.id)?.rect;
      const startContentRect = g.startContentRects.get(u.id);
      return {
        id: u.id,
        rect: this.toOwnRect(u.id, u.rect),
        rotation: u.rotation,
        imageRect: withCover ? this.coverImageRect(u.id, u.rect) : undefined,
        contentRect: withCover && startRect ? this.scaledContentRect(u.id, startRect, u.rect, startContentRect) : undefined,
        fontScale: withCover && startRect ? this.cellCaptionFontScale(g, u.id, startRect, u.rect) : undefined,
        ...(this.parentTransformIds.has(u.id) && { parentTransform: true }),
      };
    });
  }

  // A whole vignette cell scaled by a corner: its caption's font follows the cell, by the smaller axis so the text
  // still fits its frame. Any other group, and a loose text in a multi-selection, keeps its font.
  private cellCaptionFontScale(
    g: Extract<NonNullable<RustOverlayController['gesture']>, { kind: 'group' }>,
    id: string,
    start: TransformRect,
    next: TransformRect,
  ): number | undefined {
    if (!g.captionScales?.has(id) || !start.width || !start.height) return undefined;
    const k = Math.min(next.width / start.width, next.height / start.height);
    return k === 1 ? undefined : k;
  }

  // setSelection expands a cell to its leaves, so «the cell is selected» = every leaf of it is a gesture member.
  private cellCaptionSnapshots(handle: HandleId, members: readonly GroupMember[]): Map<string, TextScaleSnapshot> | undefined {
    if (!(CORNER_ANCHORS as readonly HandleId[]).includes(handle)) return undefined;
    const boxes = this.cache?.boxes ?? [];
    const own = new Set(members.map(m => m.id));
    const out = new Map<string, TextScaleSnapshot>();
    for (const { id } of members) {
      const box = this.getRenderBox(id);
      if (box?.type !== 'text') continue;
      const text = box as RenderTextBox;
      if (!(text.fontSize > 0) || !this.textFontScaleAllowed(text)) continue;
      const cell = findTopLevelGroup(boxes, id);
      if (!cell || cell.id === id || !(isVignetteCell(cell) || cell.psdGroup)) continue;
      if (!collectLeafIds(cell.children).every(l => own.has(l))) continue;
      out.set(id, textScaleSnapshotOf(text));
    }

    return out.size > 0 ? out : undefined;
  }

  // Пропорциональный ресайз рамки со фото двигает сам кадр тем же коэффициентом, вместо перекадрирования: imageRect
  // живёт в системе рамки, поэтому один множитель — весь пересчёт. Только из ЗАМОРОЖЕННОГО снимка (previewResize
  // правит кешированный imageRect каждый тик). Undefined → жест не пропорциональный либо фото заполняет рамку.
  private lockedImageRect(
    g: Extract<NonNullable<RustOverlayController['gesture']>, { kind: 'single' }>,
  ): CropImageRect | undefined {
    const start = g.startImageRect;
    if (!g.imageLock || !start || !g.startRect.width) return undefined;
    const k = g.current.rect.width / g.startRect.width;
    return { x: start.x * k, y: start.y * k, width: start.width * k, height: start.height * k, rotation: start.rotation };
  }

  /** Re-covered photo placement for a box resized to `rect` (image boxes only) — the toolbar W/H control's Fill re-fit. */
  coverImageRectFor(id: string, rect: readonly [number, number, number, number]): CropImageRect | undefined {
    return this.coverImageRect(id, { x: rect[0], y: rect[1], width: rect[2], height: rect[3] });
  }

  // Re-fit a photo to Fill (cover) its new frame: keep the user's prior placement/zoom where it still covers, else raise
  // the scale to the cover minimum. Undefined for non-image boxes or an undecoded photo (nothing to re-cover).
  private coverImageRect(id: string, frame: TransformRect): CropImageRect | undefined {
    const box = this.getRenderBox(id);
    if (box?.type !== 'image') return undefined;
    const img = this.cache?.images[id];
    if (!img?.width || !img?.height) return undefined;
    // Fresh cover-fit (imageRect=null): the photo re-centres in the new frame and fills it (Fill), rather than keeping a
    // prior off-centre placement that a resize would otherwise crop asymmetrically.
    const state = createCropStateFromRects({ width: frame.width, height: frame.height }, null, {
      width: img.width,
      height: img.height,
    });
    return state ? cropStateToImageRect(state) : undefined;
  }

  // The box's raw (box-local) contentRect, object/background/smartObject decor only — undefined when untrimmed
  // or not decor. Resize callers must snapshot this at gesture start (previewResize mutates it per tick); move/nudge
  // may read it at commit time only because no move-path preview touches cached geometry.
  private decorContentRectOf(id: string): TrimRect | undefined {
    const box = this.getRenderBox(id);
    return box?.type === 'object' || box?.type === 'background' || box?.type === 'smartObject' ? box.contentRect : undefined;
  }

  // Crop-preserving contentRect re-fit for a box resized from oldFrame to newFrame (object/background/smartObject
  // decor only). `startContent` must be the gesture's FROZEN start snapshot, not a live re-read: previewResize
  // mutates the cached box's contentRect every tick, so re-reading it here would scale an already-scaled result.
  private scaledContentRect(
    id: string,
    oldFrame: TransformRect,
    newFrame: TransformRect,
    startContent: TrimRect | undefined,
  ): TrimRect | undefined {
    if (!startContent) return undefined;
    return scaleContentToFrame(this.toLocalTrimRect(id, oldFrame), this.toLocalTrimRect(id, newFrame), startContent);
  }

  // No frame conversion: the stored contentRect is ALREADY box-local, unlike the absolute rect toOwnRect converts.
  // CONTRACT: reads the live cache — add a move-path preview that mutates cached geometry and this must snapshot
  // at gesture start first, the way startContentRect does for resize.
  private translatedContentRect(id: string, dx: number, dy: number): TrimRect | undefined {
    const content = this.decorContentRectOf(id);
    return content ? { x: content.x + dx, y: content.y + dy, width: content.width, height: content.height } : undefined;
  }

  // Live resize preview: mutate ONLY the dragged box's rect/imageRect/contentRect in the cached scene, then re-render
  // JUST that box onto the drag canvas (the rest of the scene is a static backdrop painted once by beginDrag).
  // Rendering one box is cheap at full quality; the per-frame FULL-scene re-render (hundreds of decor boxes) was the
  // lag. This mirrors how a move lifts one element and only that element moves. The settle render on release repaints
  // everything canonically.
  private previewResize(updates: ResizeRotateUpdate[]): void {
    if (!this.cache || !this.dragCanvas || this.previewInFlight) return;
    const live = this.reflowLive;
    // The host's layout for this frame first, so a gesture box it echoes back keeps the gesture's own update.
    const byId = new Map([...(live?.provider.reflow(updates) ?? []), ...updates].map(u => [u.id, u]));
    const gesture = this.gesture;
    const vectorStarts = gesture && (this.previewVectorStarts.get(gesture) ?? new Map());
    if (gesture && vectorStarts) this.previewVectorStarts.set(gesture, vectorStarts);
    const walk = (b: RenderBox): void => {
      const u = byId.get(b.id);
      if (u) {
        const [x, y, width, height] = u.rect;
        const so = b as RenderSmartObjectBox;
        if (isLiveSourceSmartObject(b) && so.sourceTransform) {
          // Frame deltas compose, so folding THIS tick's delta into the cached affine needs no gesture-start
          // snapshot — unlike contentRect, which previewResize would otherwise scale on top of itself.
          so.sourceTransform = composeSourceTransform(so.sourceTransform, b.rect, { x, y, width, height });
        }

        // The photo clip is box-local: it scales with the frame, as the commit does (`transformTemplateBoxes`).
        const img = b as RenderImageBox;
        if (b.type === 'image' && img.paths?.length && b.rect.width > 0 && b.rect.height > 0) {
          img.paths = scalePaths(img.paths, width / b.rect.width, height / b.rect.height, 0, 0);
        }

        // A vector's outline, ramp and stroke ride the frame by the commit's own law (`scaleVectorParams`), from the
        // gesture's start: a stale outline inside the new frame would jump to the committed shape on release.
        // The origination is page-frame even in a group, while a child's rect is group-relative: both rects are taken
        // to the page by the group origin first, as the commit does — else a bare line would sit off by G·(1 − s).
        if (b.type === 'vector' && vectorStarts) {
          const v = b as RenderVectorBox;
          const start = vectorStarts.get(b.id) ?? { rect: { ...b.rect }, vector: v.vector };
          vectorStarts.set(b.id, start);
          if (start.rect.width > 0 && start.rect.height > 0) {
            const sx = width / start.rect.width;
            const sy = height / start.rect.height;
            const g = this.groupOffsets.get(b.id) ?? { x: 0, y: 0 };
            v.vector = scaleVectorParams(
              start.vector,
              sx,
              sy,
              x + g.x - (start.rect.x + g.x) * sx,
              y + g.y - (start.rect.y + g.y) * sy,
            );
          }
        }

        b.rect = { ...b.rect, x, y, width, height };
        if (u.imageRect && b.type === 'image') (b as RenderImageBox).imageRect = { ...u.imageRect };
        // A host-moved photo without a placement of its own scales its grab placement with the frame.
        const frozen = u.imageRect ? undefined : live?.images.get(b.id);
        if (frozen && b.type === 'image') {
          const k = width / frozen.width;
          const { x: ix, y: iy, width: iw, height: ih, rotation } = frozen.image;
          (b as RenderImageBox).imageRect = { x: ix * k, y: iy * k, width: iw * k, height: ih * k, rotation };
        }

        if (u.contentRect && (b.type === 'object' || b.type === 'background' || b.type === 'smartObject')) {
          (b as RenderObjectBox | RenderSmartObjectBox).contentRect = { ...u.contentRect };
        }

        // FKS-11514: live font preview — always from the gesture's FROZEN snapshot, never the mutated cache
        // (the same anti-compounding rule as contentRect above).
        const ts = this.gestureTextScaleOf(b.id) ?? live?.texts.get(b.id);
        // Every px the model scales on release (ENG-1478): paragraph indents and runs too, not only the font. No
        // `fontScale` this frame (the frame is back at ×1) is the snapshot itself — a cache left at the last frame's
        // scale would show ×1.5 under a pointer back at the grab, while the release lands the font unchanged.
        if (ts && b.type === 'text') applyTextScale(b as RenderTextBox, ts, u.fontScale ?? 1);
      }

      if (b.type === 'group') for (const c of (b as RenderGroupBox).children) walk(c);
    };

    for (const b of this.cache.boxes) walk(b);
    this.refitDragTiles();
    this.dragCanvas.style.transform = ''; // resize re-renders the box in its new scene position (no CSS translate)
    this.previewInFlight = true;
    void this.renderLiftTo(this.dragCanvas, [...updates.map(u => u.id), ...(live?.ids ?? []), ...this.dragPassengers]).finally(
      () => {
        this.previewInFlight = false;
      },
    );
  }

  private gestureTextScaleOf(id: string): TextScaleSnapshot | undefined {
    const g = this.gesture;
    if (g?.kind === 'single') return g.id === id ? g.textScale : undefined;
    return g?.kind === 'group' ? g.captionScales?.get(id) : undefined;
  }

  private onPointerUp = (): void => {
    const g = this.gesture;
    this.gesture = undefined;
    this.reflowLive = undefined;
    this.setGestureActive(false);
    this.resizeSnap = undefined;
    this.snapGuides = [];
    this.sizeGuides = [];
    this.cancelResizePreview();
    this.disarmWindowDrag(this.onPointerMove, this.onPointerUp);
    if (!g) return;
    this.suppressClick = true; // pressing a handle must not let the trailing click re-run select/deselect
    if (!this.gestureMoved) return; // a click on a handle without a drag commits nothing (no no-op save)
    if (g.kind === 'skew') {
      const m = g.current.matrix;
      this.cfg.onTransform?.(g.id, [m[0], m[1], m[2], m[3], m[4], m[5]]);
    } else {
      this.cfg.onResizeRotate?.(this.buildResizeUpdates(g));
    }

    this.patchCommittedRects(g); // keep handles on the new geometry until the debounced render refreshes drawableRects
    // The lifted drag canvas keeps the transformed content visible until the settle render repaints it in place.
    if (this.dragCanvas) this.endDrag();
    else this.scheduleRender();
  };

  // Writes the just-committed geometry into drawableRects so handles/selection don't snap back to the stale rect
  // during the ~150 ms before paint() re-indexes the scene. paint() overwrites this canonically.
  private patchCommittedRects(g: NonNullable<RustOverlayController['gesture']>): void {
    const rs = this.cache?.renderScale ?? 1;
    const scaled = (r: TransformRect, rotation: number, transform?: number[]): SceneRect => ({
      x: r.x * rs,
      y: r.y * rs,
      width: r.width * rs,
      height: r.height * rs,
      rotation,
      transform,
    });
    if (g.kind === 'single') {
      let transform = this.drawableRects.get(g.id)?.transform;
      // Re-anchor the matrix to the committed origin and re-compose the committed rotation about its
      // pivot (the renderer's model), else the frame sits on the stale quad until paint().
      if (g.frameAffine) {
        const r = g.current.rect;
        const live = this.liveFrameAffine(g.frameAffine, g.startRect, r);
        const composed = composeBoxDrawTransform(live, g.current.rotation, r.x + r.width / 2, r.y + r.height / 2);
        transform = composed ? scaleAffineTranslation(composed, rs) : transform;
      }

      this.drawableRects.set(g.id, scaled(g.current.rect, g.current.rotation, transform));
    } else if (g.kind === 'group') {
      for (const u of g.current.updates) {
        this.drawableRects.set(u.id, scaled(u.rect, u.rotation, this.drawableRects.get(u.id)?.transform));
      }
    } else {
      const prev = this.drawableRects.get(g.id);
      if (!prev) return;
      const cx = prev.x + prev.width / 2;
      const cy = prev.y + prev.height / 2;
      const shear = shearAboutPivot(g.current.shx, g.current.shy, cx, cy);
      const base = prev.transform ? mul(shear, prev.transform as Affine) : shear;
      this.drawableRects.set(g.id, { ...prev, transform: base });
    }
  }

  private ensureHandleCanvas(): HTMLCanvasElement {
    let canvas = this.handleCanvas;
    if (!canvas) {
      canvas = document.createElement('canvas');
      canvas.style.position = 'absolute';
      canvas.style.pointerEvents = 'none'; // updateInputSurface() flips this to 'auto' in camera mode (this layer owns input)
      canvas.style.zIndex = '14';
      this.cfg.host.appendChild(canvas);
      this.handleCanvas = canvas;
    }

    return canvas;
  }

  private clearSelectionCanvas(): void {
    const canvas = this.selectionCanvas;
    const ctx = canvas?.getContext('2d');
    if (!canvas || !ctx) return;
    ctx.setTransform(1, 0, 0, 1, 0, 0); // the paint pass leaves a device scale in force; clear the raw backing
    ctx.clearRect(0, 0, canvas.width, canvas.height);
  }

  /**
   * Backing px per CSS px on a host layer. Read off the element rather than assumed, so the CHROME_MAX_DIM clamp
   * on a very large host cannot desync the drawing scale from the buffer it is drawing into.
   */
  private layerDeviceScale(canvas: HTMLCanvasElement | undefined): number {
    if (!canvas) return 1;
    const cssWidth = parseFloat(canvas.style.width);
    if (!(cssWidth > 0)) return 1;
    return canvas.width / cssWidth;
  }

  private selectionDeviceScale(): number {
    return this.layerDeviceScale(this.selectionCanvas);
  }

  /**
   * Open a handle-canvas paint pass: clear the raw backing, then enter the shared transform — device scale ×
   * book-origin shift — so every painter below works in plain CSS px the way drawChrome's painters do. Callers
   * wrap the pass in save()/restore() so the ctx leaves at identity for the next raw-backing clear.
   */
  private beginHandlePass(canvas: HTMLCanvasElement, ctx: CanvasRenderingContext2D): void {
    ctx.setTransform(1, 0, 0, 1, 0, 0);
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    const dpr = this.layerDeviceScale(canvas);
    const off = this.handleDrawOffset();
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    ctx.translate(off.x, off.y);
  }

  // Where book origin (0,0) sits inside the handle canvas. Konva: at its top-left (0). Camera: the handle layer is the full
  // host, so shift by the spread's position within it — anchors stay book×viewScale, now drawn un-clipped past the spread.
  private handleDrawOffset(): Point {
    if (!this.camera) return { x: 0, y: 0 };
    const { scale, viewportOffset: vo, chromeOffset: co } = this.viewState;
    return { x: co.x - vo.x * scale, y: co.y - vo.y * scale };
  }

  private drawHandles(): void {
    const canvas = this.handleCanvas;
    if (!canvas) return;
    canvas.style.transform = ''; // drop any move-follow translate; the frame is redrawn at the committed position
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    ctx.save();
    this.beginHandlePass(canvas, ctx);
    try {
      this.drawHandlesInner(ctx);
    } finally {
      ctx.restore();
    }
  }

  private drawHandlesInner(ctx: CanvasRenderingContext2D): void {
    if (this.cropBoxId && this.cropState) {
      this.drawCropChrome(ctx);
      return;
    }

    if (this.trimBoxId && this.trimState) {
      this.drawTrimChrome(ctx);
      return;
    }

    if (this.pathEditBoxId && this.pathEditSession) {
      this.drawPathEditChrome(ctx);
      return;
    }

    if (this.press?.active) {
      if (this.press.mode === 'marquee') this.paintMarquee(ctx); // an active move shows the lifted layer, no handles
      return;
    }

    if (!this.transformsEnabled()) return; // select-only mode: outline without transformer chrome

    const sel = this.handleScreenLayout(); // transformer chrome only where the host commits transforms
    if (!sel) return;
    const { anchors, rotate } = sel.layout;

    ctx.lineWidth = 1;
    ctx.strokeStyle = CHROME_COLOR;
    // Solid outer selection frame; dashed member outlines (multi-select) are drawn separately in drawSelection.
    ctx.setLineDash([]);
    const corners = ['top-left', 'top-right', 'bottom-right', 'bottom-left'] as const;
    ctx.beginPath();
    corners.forEach((c, i) => {
      const p = anchors[c];
      if (i === 0) ctx.moveTo(p.x, p.y);
      else ctx.lineTo(p.x, p.y);
    });
    ctx.closePath();
    ctx.stroke();
    ctx.setLineDash([]);

    ctx.beginPath();
    ctx.moveTo(anchors['top-center'].x, anchors['top-center'].y);
    ctx.lineTo(rotate.x, rotate.y);
    ctx.stroke();

    // rotate handle sits outside the box (always drawn); on a tiny box only the hovered resize handle draws
    this.drawHandleShape(ctx, 'rotate', rotate, anchors);
    // Corners only: a side is grabbed anywhere along its edge, and the cursor already says which way it will go —
    // a pill at the midpoint would just claim the frame is grabbable there and nowhere else.
    const onlyHovered = this.isSmallBox(anchors);
    for (const anchor of CORNER_ANCHORS) {
      if (onlyHovered && this.hoveredHandle !== anchor && !this.handleHasAxisRoom(anchor, anchors)) continue;
      this.drawHandleShape(ctx, anchor, anchors[anchor], anchors);
    }

    this.drawRotationGuide(ctx);
    this.drawSnapGuideLines(ctx, this.snapGuides);
    this.drawGapGuideLines(ctx, this.sizeGuides); // matched width/height: the same tick-ended ruler
  }

  private isSmallBox(a: Record<ResizeAnchor, Point>): boolean {
    const top = Math.hypot(a['top-right'].x - a['top-left'].x, a['top-right'].y - a['top-left'].y);
    const left = Math.hypot(a['bottom-left'].x - a['top-left'].x, a['bottom-left'].y - a['top-left'].y);
    return Math.min(top, left) < SMALL_BOX_PX;
  }

  // Any side handle stays visible while the box has at least one long edge — so a low, wide text box keeps all
  // four side handles (width + height resize). Corners need both edges long enough to sit clear of the content.
  private handleHasAxisRoom(anchor: ResizeAnchor, a: Record<ResizeAnchor, Point>): boolean {
    const w = Math.hypot(a['top-right'].x - a['top-left'].x, a['top-right'].y - a['top-left'].y);
    const h = Math.hypot(a['bottom-left'].x - a['top-left'].x, a['bottom-left'].y - a['top-left'].y);
    if (SIDE_HANDLES.has(anchor)) return Math.max(w, h) >= SMALL_BOX_PX;
    return w >= SMALL_BOX_PX && h >= SMALL_BOX_PX;
  }

  private drawHandleShape(ctx: CanvasRenderingContext2D, id: HandleId, c: Point, a: Record<ResizeAnchor, Point>): void {
    ctx.lineWidth = 1;
    ctx.strokeStyle = CHROME_COLOR;
    ctx.fillStyle = this.hoveredHandle === id ? CHROME_COLOR : HANDLE_FILL;
    ctx.setLineDash([]);
    if (id === 'rotate' || id === 'top-left' || id === 'top-right' || id === 'bottom-left' || id === 'bottom-right') {
      ctx.beginPath();
      ctx.arc(c.x, c.y, CORNER_R_PX, 0, Math.PI * 2);
      ctx.fill();
      ctx.stroke();
      return;
    }

    const along =
      id === 'top-center' || id === 'bottom-center'
        ? Math.atan2(a['top-right'].y - a['top-left'].y, a['top-right'].x - a['top-left'].x)
        : Math.atan2(a['bottom-left'].y - a['top-left'].y, a['bottom-left'].x - a['top-left'].x);
    ctx.save();
    ctx.translate(c.x, c.y);
    ctx.rotate(along);
    this.capsulePath(ctx, SIDE_PILL_LONG_PX, SIDE_PILL_SHORT_PX);
    ctx.fill();
    ctx.stroke();
    ctx.restore();
  }

  private capsulePath(ctx: CanvasRenderingContext2D, long: number, short: number): void {
    const r = short / 2;
    const hw = Math.max(0, long / 2 - r);
    ctx.beginPath();
    ctx.moveTo(-hw, -r);
    ctx.lineTo(hw, -r);
    ctx.arc(hw, 0, r, -Math.PI / 2, Math.PI / 2);
    ctx.lineTo(-hw, r);
    ctx.arc(-hw, 0, r, Math.PI / 2, -Math.PI / 2);
    ctx.closePath();
  }

  private paintMarquee(ctx: CanvasRenderingContext2D): void {
    const m = this.press;
    if (!m) return;
    const s = this.viewScale || 1;
    const r = marqueeRect(m.startBook, m.current);
    // Live feedback: outline every box the band currently encloses — an edge-grazed item is visible before release.
    ctx.lineWidth = 1;
    ctx.strokeStyle = CHROME_COLOR;
    ctx.setLineDash([]);
    for (const id of this.marqueeSelect(r)) {
      const sr = this.drawableRects.get(id);
      if (!sr) continue;
      const b = this.sceneRectBookAABB(sr);
      ctx.strokeRect(b.x * s, b.y * s, b.width * s, b.height * s);
    }

    ctx.fillStyle = MARQUEE_FILL;
    ctx.fillRect(r.x * s, r.y * s, r.width * s, r.height * s);
    ctx.strokeStyle = MARQUEE_STROKE;
    ctx.setLineDash(MARQUEE_DASH);
    ctx.strokeRect(r.x * s, r.y * s, r.width * s, r.height * s);
    ctx.setLineDash([]);
  }

  /** Host-driven selection: renders a selection box per id (skips ids without a known rect). */
  async setSelection(ids: string[]): Promise<void> {
    this.parentTransformIds = this.explicitParentLeaves(ids);
    // Whole-group selection carries the group-container id; drag/nudge/transform all operate on leaves, so expand it here.
    ids = this.withClipCompanions(ids);
    // Selection moved off the path-edited box — settle the mode (its per-gesture commits already landed).
    if (this.pathEditBoxId && (ids.length !== 1 || ids[0] !== this.pathEditBoxId)) this.exitPathEdit();
    this.selectedIds = ids;
    await this.refreshActiveOverflow();
    this.drawSelection(ids);
    this.drawHandles();
    this.cfg.onSelectionRect?.(this.computeSelectionScreenRect());
    if (this.hoverId && ids.includes(this.hoverId)) this.drawHover(undefined); // selection wins over hover
  }

  /** The render box for an id — the host checks `type === 'text'` before entering inline edit. */
  getRenderBox(id: string): RenderBox | undefined {
    return this.cache ? findRenderBox(this.cache.boxes, id) : undefined;
  }

  /** The inline-edit session's ENTRY frame projected through the CURRENT camera — the camera-only re-seat quad.
   *  Uses the frozen entry rect, not the live drawable (which follows the auto-width growth mid-session). */
  private textEditCameraQuad(): { tl: Point; tr: Point; br: Point; bl: Point } | undefined {
    const r = this.editEntrySceneRect;
    if (!r || !this.cache) return undefined;
    const k = (this.viewScale || 1) / this.cache.renderScale;
    const c = { x: r.x + r.width / 2, y: r.y + r.height / 2 };
    const { scale, viewportOffset: vo, chromeOffset: co } = this.viewState;
    const dx = co.x - vo.x * scale;
    const dy = co.y - vo.y * scale;
    const toScreen = (p: Point): Point => {
      const s = r.transform ? applyAffinePoint(r.transform, p) : rotatePointAround(p, c, r.rotation ?? 0);
      return { x: s.x * k + dx, y: s.y * k + dy };
    };

    return {
      tl: toScreen({ x: r.x, y: r.y }),
      tr: toScreen({ x: r.x + r.width, y: r.y }),
      br: toScreen({ x: r.x + r.width, y: r.y + r.height }),
      bl: toScreen({ x: r.x, y: r.y + r.height }),
    };
  }

  /** Screen-px 4-corner quad (affine-aware) for the inline text-edit overlay; undefined until rendered. */
  getTextBoxScreenQuad(id: string): { tl: Point; tr: Point; br: Point; bl: Point } | undefined {
    const a = this.sceneHandleLayout(id)?.anchors;
    if (!a) return undefined;
    // Shift spread-origin anchors into the host frame by the overlay canvas's CSS offset (camera pan −origin×zoom; 0 in Konva).
    const { scale, viewportOffset: vo, chromeOffset: co } = this.viewState;
    const dx = co.x - vo.x * scale;
    const dy = co.y - vo.y * scale;
    const shift = (p: Point): Point => ({ x: p.x + dx, y: p.y + dy });
    return { tl: shift(a['top-left']), tr: shift(a['top-right']), br: shift(a['bottom-right']), bl: shift(a['bottom-left']) };
  }

  // Resolves lazily — overflowOf re-runs full glyph shaping, too costly to precompute for every text box in the
  // scene on each render() when only the (usually 0-few) selected/drawn ids ever consult it here.
  private isTextOverflowing(id: string): boolean {
    const cache = this.cache;
    // The edited box is reshaped by the edit tick already; asking again per keystroke costs a second full layout
    // (~15 ms on a long text) to colour a frame the user is actively fixing. It is reclassified on commit.
    if (id === this.editingBoxId) return false;
    if (!cache?.overflowOf) return false;
    const box = findRenderBox(cache.boxes, id);
    return box?.type === 'text' && cache.overflowOf(box as RenderTextBox);
  }

  /**
   * Фоторамка навылет заходит за обрез, а хост его не показывает — обводка по её собственному краю легла бы
   * на монтажную область, за пределами показанной страницы. Держим её по видимой поверхности: рамка читается
   * по краю листа. Повёрнутую не трогаем (прямоугольником её не обрезать), там остаётся клип самого слоя.
   */
  private clampToSurface(r: SceneRect): SceneRect {
    const zones = this.cfg.trimPage ? this.cache?.zones : null;
    const renderScale = this.cache?.renderScale;
    if (!zones || !renderScale || r.rotation || r.transform) return r;
    // Рамки живут в СЦЕНЕ (книжные px × renderScale), поверхность считается в книжных — переводим её.
    const surface = editorSurfaceBox(zones, this.cfg.getZoneVisibility?.());
    const x = Math.max(r.x, surface.x * renderScale);
    const y = Math.max(r.y, surface.y * renderScale);
    const right = Math.min(r.x + r.width, (surface.x + surface.width) * renderScale);
    const bottom = Math.min(r.y + r.height, (surface.y + surface.height) * renderScale);
    return right > x && bottom > y ? { ...r, x, y, width: right - x, height: bottom - y } : r;
  }

  private drawSelection(ids: string[]): void {
    const canvas = this.selectionCanvas;
    if (!canvas) return;
    // A selected text box whose wrapped text overflows its rect gets a warn-coloured frame instead of the normal blue.
    const normal: SceneRect[] = [];
    const overflow: SceneRect[] = [];
    const photos: SceneRect[] = [];
    for (const id of ids) {
      const r = this.drawableRects.get(id);
      if (!r) continue;
      if (this.isTextOverflowing(id)) overflow.push(r);
      else if (this.isPhotoFrame(id)) photos.push(this.clampToSurface(r));
      else normal.push(r);
    }

    const ctx = canvas.getContext('2d');
    if ((normal.length === 0 && overflow.length === 0 && photos.length === 0) || !ctx) {
      canvas.style.transform = '';
      this.clearSelectionCanvas();
      return;
    }

    ctx.setTransform(1, 0, 0, 1, 0, 0); // clear the whole backing, whatever the last pass left in force
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    // The backing runs at device resolution; drawing stays in CSS px so authored widths mean on-screen widths.
    const dpr = this.selectionDeviceScale();
    const off = this.handleDrawOffset();
    ctx.save();
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    // The layer is the whole host in screen px, so shift book origin into it the way the handle layer does.
    ctx.translate(off.x, off.y);
    // Multi-selection: each member gets a dashed frame; the solid outer frame is the transform chrome (drawHandles),
    // so we don't draw an AABB frame here — that would double up with the outer frame on shared edges.
    const memberDash = normal.length + overflow.length + photos.length >= 2 ? SELECTION_MEMBER_DASH : [];
    if (normal.length) this.strokeScreenOutlines(ctx, normal, SELECTION_STROKE_COLOR, SELECTION_STROKE_WIDTH, memberDash);
    if (photos.length) {
      // The frame sits ENTIRELY INSIDE the box, its outer edge exactly on the photo's. Nothing of it is ever
      // outside the sheet, so a frame on a photo run to the bleed stays whole — the outward half used to land
      // off the shown page, get eaten by the chrome clip, and leave the white behind as the whole frame. And
      // with nothing painted between the picture and the accent, the frame reads as the photo's own outline
      // rather than as a pale margin the fill never reached.
      const width = PHOTO_SELECTION_WIDTH;
      // The literal mirror of the same token: an unresolved var must never mute the frame — that is the very
      // white frame this draw exists to kill.
      const color = this.themeToken(PHOTO_SELECTION_COLOR_TOKEN) || ACCENT_COLOR;
      this.strokeScreenOutlines(ctx, photos, color, width, memberDash, -width / 2);
    }

    if (overflow.length) {
      this.strokeScreenOutlines(ctx, overflow, SELECTION_OVERFLOW_STROKE_COLOR, SELECTION_OVERFLOW_STROKE_WIDTH, memberDash);
    }

    ctx.restore();
    // drop the move-follow translate only after the repaint lands — else the stale pre-drag pixels flash at the old spot
    if (!this.press?.active) canvas.style.transform = '';
  }

  async showDropHoverAt(clientX: number, clientY: number, verdictOf: (boxId: string) => DropHoverVerdict | null): Promise<void> {
    this.dropHovering = true;
    this.lastDropPt = { clientX, clientY, verdictOf };
    if (this.dropHoverRaf || !this.sceneJson) return;
    this.dropHoverRaf = requestAnimationFrame(async () => {
      this.dropHoverRaf = 0;
      const pt = this.lastDropPt;
      if (this.destroyed || !this.sceneJson || !pt) return;
      const p = this.clientToScene(pt.clientX, pt.clientY);
      const hit = await this.hitTestOrNull(this.sceneJson, p);
      if (hit === null) return;
      const verdict = (hit ? pt.verdictOf(hit) : null) ?? undefined;
      const id = verdict ? hit : undefined;
      // Keyed on the verdict too: the same frame flips allow↔forbid as the force modifier goes down and up,
      // and an id-only guard would leave the stale paint on screen.
      if (id === this.dropHoverId && verdict === this.dropHoverVerdict && !this.dropZoneStale) return;
      this.dropHoverId = id;
      this.dropHoverVerdict = verdict;
      await this.drawDropHover(id, verdict);
    });
  }

  /** Verdict of the hover currently painted — the host hangs its cursor hint on exactly the frame that reads red. */
  currentDropHoverVerdict(): DropHoverVerdict | null {
    return this.dropHoverVerdict ?? null;
  }

  clearDropHover(): void {
    this.dropHovering = false;
    this.lastDropPt = undefined;
    this.dropHoverId = undefined;
    this.dropHoverVerdict = undefined;
    void this.drawDropHover(undefined);
  }

  /** Half-of-spread targeting for template drags: which page the pointer is over. */
  zoneSideAt(clientX: number, clientY: number): 'left' | 'right' | null {
    // book.x is spread-relative book-px (clientToBook divides by renderScale); the page split is the spread midpoint.
    const book = this.clientToBook(clientX, clientY);
    if (!book) return null;
    // Off the spread (the gray pasteboard around it) targets no page — the caller must not apply the drop there.
    if (book.x < 0 || book.y < 0 || book.x > this.overlayBook.w || book.y > this.overlayBook.h) return null;
    return book.x < this.overlayBook.w / 2 ? 'left' : 'right';
  }

  /** Tint the target page (or whole spread) for a template drag. The hover canvas is a content-region buffer offset by
   * contentRegionScene.ox/oy, so the page rect is laid out in spread-scene coords and clamped to the buffer — a zoomed
   * spread tints only its visible slice. Repaints unconditionally: a mid-drag content render wipes the hover canvas. */
  setDropZone(zone: 'left' | 'right' | 'full' | null): void {
    this.dropZone = zone;
    // Mid photo drag the host flips the zone off its own hit-test, a frame ahead of the frame hover's blit. Painting
    // now would show the bare canvas between the page fill and the frame tint: the zone rides the hover's next paint.
    const pt = this.lastDropPt;
    if (this.dropHovering && pt && this.sceneJson) {
      this.dropZoneStale = true;
      void this.showDropHoverAt(pt.clientX, pt.clientY, pt.verdictOf);
      return;
    }

    this.paintDropZone();
  }

  // The frame hover shares this canvas: its clear and its blit both wipe the page tint, so each paints it back.
  private paintDropZone(clear = true): void {
    const canvas = this.hoverCanvas;
    const ctx = canvas?.getContext('2d');
    if (!canvas || !ctx) return;
    if (clear) {
      canvas.style.transform = '';
      ctx.clearRect(0, 0, canvas.width, canvas.height);
    }

    const zone = this.dropZone;
    if (!zone) return;
    const rs = this.cache?.renderScale || 1;
    const ox = this.camera ? this.contentRegionScene.ox : 0;
    const oy = this.camera ? this.contentRegionScene.oy : 0;
    const halfW = (this.overlayBook.w / 2) * rs;
    const sceneX = zone === 'right' ? halfW : 0;
    const sceneW = zone === 'full' ? this.overlayBook.w * rs : halfW;
    const x = Math.max(0, sceneX + ox);
    const y = Math.max(0, oy);
    const w = Math.min(canvas.width, sceneX + ox + sceneW) - x;
    const h = Math.min(canvas.height, oy + this.overlayBook.h * rs) - y;
    if (w <= 0 || h <= 0) return;
    ctx.fillStyle = DROP_ZONE_FILL;
    ctx.fillRect(x, y, w, h);
    ctx.strokeStyle = CHROME_COLOR;
    ctx.lineWidth = 2;
    ctx.strokeRect(x + 1, y + 1, w - 2, h - 2);
  }

  private async drawDropHover(id: string | undefined, verdict?: DropHoverVerdict): Promise<void> {
    const canvas = this.hoverCanvas;
    if (!canvas) return;
    const gen = ++this.dropHoverGen;
    // Both branches paint the zone as it stands when they land.
    this.dropZoneStale = false;
    const rect = id ? this.drawableRects.get(id) : undefined;
    if (!rect) {
      this.paintDropZone();
      return;
    }

    const forbidColor = verdict && verdict !== 'allow' ? this.cfg.getDropForbidColor?.() : undefined;
    const sceneJson = JSON.stringify({
      viewport: { width: canvas.width, height: canvas.height },
      drawables: [buildDropHoverShape(rect, forbidColor)],
    });
    await this.renderContentTo(canvas, sceneJson, {}, {});
    // The blit above is tens of ms of await. A release inside that window cleared the layer SYNCHRONOUSLY, and this
    // paint would then put the red back with nothing left to take it down. A newer paint writes its own pixels — only
    // a teardown (nothing hovered, or the controller gone) needs the wipe.
    if (this.destroyed || gen !== this.dropHoverGen) {
      if (this.destroyed) canvas.getContext('2d')?.clearRect(0, 0, canvas.width, canvas.height);
      else if (!this.dropHoverId) this.paintDropZone();
      return;
    }

    this.paintDropZone(false);
    if (!this.press?.active) canvas.style.transform = '';
  }

  // Shifts a region buffer into scene coordinates and — unless a box is under active manipulation, where the region was
  // deliberately grown and the lift owns what shows off-spread — clips box chrome back to the page. `outside` is how far
  // past its box the caller's own paint reaches: a stroke rides wholly outside, so clipping at the bare page would erase
  // an edge-flush frame; a fill never does, and any head-room granted to it is spill onto the pasteboard.
  private beginRegionPaint(ctx: CanvasRenderingContext2D, outside = 0): void {
    const ox = this.camera ? this.contentRegionScene.ox : 0;
    const oy = this.camera ? this.contentRegionScene.oy : 0;
    ctx.save();
    ctx.translate(ox, oy);
    const cache = this.cache;
    if (!cache || this.overflowIds().length > 0) return;
    const room = Math.min(outside, CHROME_BLEED_PX * this.cameraDpr());
    const { width, height } = this.spreadSceneSize;
    if (width <= 0 || height <= 0) return;
    ctx.beginPath();
    ctx.rect(-room, -room, width + 2 * room, height + 2 * room);
    ctx.clip();
  }

  // Stroke each box's affine outline directly in Canvas2D (camera region offset applied) — far cheaper than a WASM render.
  private strokeOutlines(
    ctx: CanvasRenderingContext2D,
    rects: readonly SceneRect[],
    color: string,
    width: number,
    dash: readonly number[] = [],
    outset = width / 2,
  ): void {
    // The stroke straddles the outset path, so it reaches `outset + width / 2` past the box edge.
    this.beginRegionPaint(ctx, outset + width / 2);
    ctx.lineWidth = width;
    ctx.strokeStyle = color;
    ctx.setLineDash(dash as number[]);
    for (const r of rects) {
      const corners = quadCorners(outset ? outsetRect(r, outset) : r);
      ctx.beginPath();
      corners.forEach((p, i) => (i === 0 ? ctx.moveTo(p.x, p.y) : ctx.lineTo(p.x, p.y)));
      ctx.closePath();
      ctx.stroke();
    }

    ctx.restore();
  }

  /** Scene px → the selection layer's own screen px (that layer is host-sized and 1:1 with CSS px). */
  private sceneToScreen(): number {
    const renderScale = this.cache?.renderScale;
    if (!renderScale || !(renderScale > 0)) return 1;
    return (this.viewScale || 1) / renderScale;
  }

  /**
   * The screen-space twin of beginRegionPaint: clip box chrome back to the spread, sparing the head-room the
   * caller's own paint needs past its box, and leave it un-clipped while a box is under active manipulation —
   * there the lift owns what shows off-spread. Head-room is plain CSS px here, not scene px.
   */
  private beginScreenPaint(ctx: CanvasRenderingContext2D, outside = 0): void {
    ctx.save();
    const cache = this.cache;
    if (!cache || this.overflowIds().length > 0) return;
    const k = this.sceneToScreen();
    const width = this.spreadSceneSize.width * k;
    const height = this.spreadSceneSize.height * k;
    if (width <= 0 || height <= 0) return;
    const room = Math.min(outside, CHROME_BLEED_PX);
    ctx.beginPath();
    ctx.rect(-room, -room, width + 2 * room, height + 2 * room);
    ctx.clip();
  }

  /**
   * Stroke each box's affine outline on the SCREEN-px selection layer. Widths and dashes are CSS px exactly as
   * authored, so the frame holds one on-screen size at any zoom instead of riding a buffer the browser stretches.
   */
  private strokeScreenOutlines(
    ctx: CanvasRenderingContext2D,
    rects: readonly SceneRect[],
    color: string,
    width: number,
    dash: readonly number[] = [],
    outsetScreen = width / 2,
  ): void {
    const k = this.sceneToScreen();
    if (!(k > 0)) return;
    // The stroke straddles the outset path, so it reaches `outset + width / 2` past the box edge.
    this.beginScreenPaint(ctx, outsetScreen + width / 2);
    // Outset back into scene px so it lands before the box's own affine; a uniform scale commutes with that affine,
    // so scaling the resulting corners by k gives the same geometry as outsetting in screen space would.
    const outset = outsetScreen / k;
    ctx.lineWidth = width;
    ctx.strokeStyle = color;
    ctx.setLineDash(dash as number[]);
    for (const r of rects) {
      strokeDashedQuad(
        ctx,
        quadCorners(outset ? outsetRect(r, outset) : r).map(p => ({ x: p.x * k, y: p.y * k })),
        dash,
      );
    }

    ctx.restore();
  }

  private drawHover(id: string | undefined): void {
    const canvas = this.hoverCanvas;
    if (!canvas) return;
    // A photo answers no bare hover: the only frame that may light up is the one a drag would land in. A photo
    // inside a unit a click takes whole (designer: the cell) lights the unit up.
    const unit = id ? this.hoverUnitRects(id) : null;
    const rects =
      id && !this.selectedIds.includes(id) && (unit || !this.isPhotoFrame(id)) ? (unit ?? this.hoverOutlineRects(id)) : [];
    if (rects.length === 0) {
      this.fadeHoverTo(0);
      return;
    }

    // A different box swaps the geometry and restarts at 0, so the new frame fades in instead of popping.
    const hoveredId = id as string;
    const current = this.hoverPaint;
    this.hoverPaint = current?.id === hoveredId ? { ...current, rects } : { id: hoveredId, rects, alpha: 0 };
    this.fadeHoverTo(1);
  }

  private fadeHoverTo(target: number): void {
    const paint = this.hoverPaint;
    if (!paint) return;
    if (this.hoverFadeRaf) {
      cancelAnimationFrame(this.hoverFadeRaf);
      this.hoverFadeRaf = 0;
    }

    const from = paint.alpha;
    const start = performance.now();
    const step = (): void => {
      if (this.destroyed || this.hoverPaint !== paint) return;
      const t = HOVER_FADE_MS > 0 ? Math.min(1, (performance.now() - start) / HOVER_FADE_MS) : 1;
      paint.alpha = from + (target - from) * (t * (2 - t));
      this.paintHover();
      if (t < 1) {
        this.hoverFadeRaf = requestAnimationFrame(step);
        return;
      }

      this.hoverFadeRaf = 0;
      if (target === 0) {
        this.hoverPaint = null;
        this.clearHoverCanvas();
      }
    };

    step();
  }

  private paintHover(): void {
    const canvas = this.hoverCanvas;
    const paint = this.hoverPaint;
    const ctx = canvas?.getContext('2d');
    if (!canvas || !ctx || !paint) return;
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    ctx.save();
    ctx.globalAlpha = Math.max(0, Math.min(1, paint.alpha));
    this.strokeOutlines(ctx, paint.rects, HOVER_STROKE_COLOR, HOVER_STROKE_WIDTH);
    ctx.restore();
    if (!this.press?.active) canvas.style.transform = '';
  }

  private clearHoverCanvas(): void {
    const canvas = this.hoverCanvas;
    if (!canvas) return;
    canvas.style.transform = '';
    canvas.getContext('2d')?.clearRect(0, 0, canvas.width, canvas.height);
  }

  /**
   * What a click on `id` would select, when the host takes more than the box itself (`pressUnitOf`, designer: the
   * whole person cell — its photo, captions, and the shadow that answers for it): the hover outlines that unit, so
   * the cell a press would carry is visible before the press. Null — the box alone decides.
   */
  private hoverUnitRects(id: string): SceneRect[] | null {
    const unit = this.cfg.pressUnitOf?.(id, this.selectedIds);
    if (!unit || unit.length < 2 || unit.some(x => this.selectedIds.includes(x))) return null;
    const rects = unit.map(x => this.drawableRects.get(x)).filter((r): r is SceneRect => !!r);
    if (rects.length === 0) return null;
    const minX = Math.min(...rects.map(r => r.x));
    const minY = Math.min(...rects.map(r => r.y));
    const maxX = Math.max(...rects.map(r => r.x + r.width));
    const maxY = Math.max(...rects.map(r => r.y + r.height));
    return [{ x: minX, y: minY, width: maxX - minX, height: maxY - minY }];
  }

  // Hovering a grouped box outlines the whole group (union of its leaf rects), Figma-style; a lone box outlines itself.
  // Person/vignette groups aren't whole-group-selectable (own replace flow), so they hover as a lone box, matching selection.
  private hoverOutlineRects(id: string): SceneRect[] {
    const group = this.cache ? findClickGroup(this.cache.boxes, id) : undefined;
    if (!group || group.personId != null) {
      const r = this.drawableRects.get(id);
      return r ? [r] : [];
    }

    let minX = Infinity;
    let minY = Infinity;
    let maxX = -Infinity;
    let maxY = -Infinity;
    for (const leafId of collectLeafIds(group.children)) {
      const r = this.drawableRects.get(leafId);
      if (!r) continue;
      if (r.x < minX) minX = r.x;
      if (r.y < minY) minY = r.y;
      if (r.x + r.width > maxX) maxX = r.x + r.width;
      if (r.y + r.height > maxY) maxY = r.y + r.height;
    }

    return Number.isFinite(minX) ? [{ x: minX, y: minY, width: maxX - minX, height: maxY - minY }] : [];
  }

  /** Redraws the editor-chrome line canvas (grid / central guides / user guides). */
  redrawChrome(): void {
    this.drawChrome();
  }

  /**
   * Paint the print technical zones — bleed/wrap, safe, spine, rasstav — from the SAME geometry the engine used to
   * composite into the scene, just on this screen-resolution layer instead. Their weight still scales with the
   * sheet, because a zone is print geometry and always read that way; what changes is that it is now drawn at the
   * zoom the user is actually looking at, so zooming in cannot soften it.
   */
  private drawZones(ctx: CanvasRenderingContext2D, zones: ZonePrintSizes, scale: number, deviceScale: number): void {
    for (const shape of buildZoneShapes(zones, this.zoneVisibility(), this.cfg.zoneStyle)) {
      const { x, y, width, height } = shape.rect;
      const rx = x * scale;
      const ry = y * scale;
      const rw = width * scale;
      const rh = height * scale;
      if (shape.fill) {
        ctx.fillStyle = shape.fill;
        ctx.fillRect(rx, ry, rw, rh);
      }

      if (shape.stroke && shape.strokeWidth) {
        ctx.strokeStyle = shape.stroke;
        // Neither width nor dash is scaled by the camera: scaling them stretched the mark along with the paper, so
        // the same border was a sub-pixel hairline on the whole spread and a heavy dashed bar close in. Only WHERE
        // the border sits moves with the sheet. The width arrives in DEVICE px and is divided by the layer's real
        // backing ratio, so it is one physical pixel on any display — naming it in CSS px made it a translucent
        // half-pixel everywhere except an exactly-2× screen.
        ctx.lineWidth = shape.strokeWidth / (deviceScale > 0 ? deviceScale : 1);
        ctx.setLineDash(shape.dash ?? []);
        ctx.strokeRect(rx, ry, rw, rh);
        ctx.setLineDash([]);
      }
    }
  }

  private drawChrome(): void {
    const canvas = this.chromeCanvas;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    ctx.setTransform(1, 0, 0, 1, 0, 0); // clear the raw backing, whatever transform the last pass left in force
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    const rects = this.cfg.getChromeRects?.() ?? [];
    const lines = this.cfg.getChromeLines?.() ?? [];
    const texts = this.cfg.getChromeTexts?.() ?? [];
    // A lifted box's marks are stale for the whole gesture (see applyDragChrome) — leave them off this pass.
    const lifted = this.dragLiftIds;
    const onScreen = (ids: readonly string[]): readonly string[] => (lifted ? ids.filter(id => !lifted.has(id)) : ids);
    const quoteIds = onScreen(this.cfg.getQuoteFrameBoxIds?.() ?? []);
    const quoteSet = new Set(quoteIds);
    const textFrameIds = onScreen(this.cfg.getTextFrameBoxIds?.() ?? []).filter(id => !quoteSet.has(id));
    // The editing box shows the selection frame instead of its mark.
    const textMarkIds = onScreen(this.cfg.getTextMarkBoxIds?.() ?? []).filter(id => id !== this.editingBoxId);
    const invalid = this.cfg.getInvalidFrames?.();
    const invalidIds = onScreen(invalid?.ids ?? []);
    const warnIds = onScreen(invalid?.warnFrames?.ids ?? []);
    const zones = this.cache?.zones;
    if (
      !zones &&
      rects.length === 0 &&
      lines.length === 0 &&
      texts.length === 0 &&
      textFrameIds.length === 0 &&
      quoteIds.length === 0 &&
      textMarkIds.length === 0 &&
      invalidIds.length === 0 &&
      warnIds.length === 0
    )
      return;
    // The layer is the whole host at device resolution: scale by the ratio and shift book origin into it, the way
    // the selection and handle layers do, so everything below stays in plain book × viewScale coordinates.
    const dpr = this.layerDeviceScale(canvas);
    const off = this.handleDrawOffset();
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    ctx.translate(off.x, off.y);
    const scale = this.viewScale;
    // Zones sit under the guides and the grid, the same order they had when the engine composited them.
    if (zones) this.drawZones(ctx, zones, scale, dpr);
    for (const r of rects) {
      ctx.fillStyle = r.fill;
      ctx.fillRect(r.x * scale, r.y * scale, r.width * scale, r.height * scale);
    }

    for (const line of lines) {
      ctx.beginPath();
      ctx.strokeStyle = line.color;
      ctx.lineWidth = line.width;
      ctx.lineCap = 'butt';
      ctx.globalAlpha = Math.max(0, Math.min(1, line.opacity ?? 1));
      ctx.setLineDash(line.dash ?? []);
      ctx.moveTo(line.x1 * scale, line.y1 * scale);
      ctx.lineTo(line.x2 * scale, line.y2 * scale);
      ctx.stroke();
    }

    ctx.globalAlpha = 1;

    this.drawBoxFrames(ctx, textFrameIds, scale, TEXT_FRAME_COLOR, TEXT_FRAME_WIDTH, TEXT_FRAME_DASH);
    // Not the preview's --scrim-ctrl: tokenColor drops alpha (cssColorToHex), so it would paint solid black.
    this.drawBoxFrames(ctx, quoteIds, scale, tokenColor('--ink-on-light'), QUOTE_FRAME_WIDTH, TEXT_FRAME_DASH);
    this.drawTextMarks(ctx, textMarkIds, scale, dpr);
    if (invalid) {
      const empty = invalid.emptyFrames;
      if (empty) this.drawEmptyFrameMarks(ctx, onScreen(empty.ids), scale, empty.fill, empty.plus ?? invalid.color);
      // An empty photo frame shows ONLY its wash+plus — the error stroke stays with the generic invalid
      // set (the cover-text gate), so a hole reads light and inviting rather than alarmed.
      const emptyIdSet = new Set(empty?.ids ?? []);
      const strokeIds = emptyIdSet.size > 0 ? invalidIds.filter(id => !emptyIdSet.has(id)) : invalidIds;
      this.drawBoxFrames(ctx, strokeIds, scale, invalid.color, INVALID_FRAME_WIDTH, []);
      if (invalid.warnFrames) this.drawBoxFrames(ctx, warnIds, scale, invalid.warnFrames.color, INVALID_FRAME_WIDTH, []);
    }

    ctx.setLineDash([]);
    for (const t of texts) {
      const x = t.x * scale;
      const y = t.y * scale;
      ctx.font = `600 ${t.fontPx}px Inter, sans-serif`;
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      if (t.plateFill) {
        const w = ctx.measureText(t.text).width;
        const padX = t.fontPx * 0.5;
        const h = t.fontPx * 1.5;
        ctx.fillStyle = t.plateFill;
        ctx.beginPath();
        ctx.roundRect(x - w / 2 - padX, y - h / 2, w + padX * 2, h, h / 2);
        ctx.fill();
      }

      ctx.fillStyle = t.color;
      ctx.fillText(t.text, x, y);
    }
  }

  // Empty photo frames read as "add a photo here": a light wash over the frame's own quad (so a rotated
  // frame is washed where it renders) and a plus glyph at its centre. The plus stays axis-aligned — it is
  // UI chrome, an affordance like the transform handles, not part of the page.
  //
  // Рамка навылет уходит за обрез, а показанная страница обрезана — на её собственном центре плюс встаёт
  // сдвинутым от того, что человек видит. Считаем по ВИДИМОЙ части (тот же клип, что у обводки выделения).
  private drawEmptyFrameMarks(
    ctx: CanvasRenderingContext2D,
    ids: readonly string[],
    scale: number,
    fill: string,
    plusColor: string,
  ): void {
    const renderScale = this.cache?.renderScale;
    if (ids.length === 0 || !renderScale) return;
    const k = scale / renderScale;
    for (const id of ids) {
      const raw = this.drawableRects.get(id);
      const r = raw ? this.clampToSurface(raw) : undefined;
      if (!r) continue;
      const corners = quadCorners(r);
      ctx.beginPath();
      corners.forEach((p, i) => (i === 0 ? ctx.moveTo(p.x * k, p.y * k) : ctx.lineTo(p.x * k, p.y * k)));
      ctx.closePath();
      ctx.fillStyle = fill;
      ctx.globalAlpha = EMPTY_FRAME_FILL_ALPHA;
      ctx.fill();

      const cx = ((corners[0].x + corners[1].x + corners[2].x + corners[3].x) / 4) * k;
      const cy = ((corners[0].y + corners[1].y + corners[2].y + corners[3].y) / 4) * k;
      const edgeA = Math.hypot(corners[1].x - corners[0].x, corners[1].y - corners[0].y) * k;
      const edgeB = Math.hypot(corners[2].x - corners[1].x, corners[2].y - corners[1].y) * k;
      const size = Math.min(EMPTY_FRAME_PLUS_PX, Math.min(edgeA, edgeB) * EMPTY_FRAME_PLUS_MAX_EDGE_FRACTION);
      const arm = size / 2;
      ctx.strokeStyle = plusColor;
      ctx.lineWidth = EMPTY_FRAME_PLUS_WIDTH;
      ctx.lineCap = 'round';
      ctx.globalAlpha = EMPTY_FRAME_PLUS_ALPHA;
      ctx.beginPath();
      ctx.moveTo(cx - arm, cy);
      ctx.lineTo(cx + arm, cy);
      ctx.moveTo(cx, cy - arm);
      ctx.lineTo(cx, cy + arm);
      ctx.stroke();
      ctx.lineCap = 'butt';
      ctx.globalAlpha = 1;
    }
  }

  // Frames each box via its draw-transform (quadCorners over the indexed drawable rect), so rotated/flipped
  // text is outlined where the glyphs render. Scene px → chrome px by k = viewScale / renderScale (as sceneHandleLayout).
  private drawBoxFrames(
    ctx: CanvasRenderingContext2D,
    ids: readonly string[],
    scale: number,
    color: string,
    width: number,
    dash: readonly number[],
  ): void {
    const renderScale = this.cache?.renderScale;
    if (ids.length === 0 || !renderScale) return;
    const k = scale / renderScale;
    ctx.strokeStyle = color;
    ctx.lineWidth = width;
    ctx.setLineDash([...dash]);
    for (const id of ids) {
      const r = this.drawableRects.get(id);
      if (!r) continue;
      strokeDashedQuad(
        ctx,
        quadCorners(r).map(p => ({ x: p.x * k, y: p.y * k })),
        dash,
      );
    }

    ctx.setLineDash([]);
  }

  // Phone text marks: dashed frame + pencil chip per box. Sizes are SCREEN px at any zoom; the frame follows the
  // draw-transform like drawBoxFrames, the chip sits on the outward normal of the frame's right edge.
  private drawTextMarks(ctx: CanvasRenderingContext2D, ids: readonly string[], scale: number, dpr: number): void {
    const renderScale = this.cache?.renderScale;
    if (ids.length === 0 || !renderScale) return;
    const k = scale / renderScale;
    const sel = tokenColor('--sel');
    const ink = tokenColor('--on-acc');
    const shadow = /^(-?[\d.]+)(?:px)? (-?[\d.]+)(?:px)? ([\d.]+)(?:px)? (.+)$/.exec(
      getComputedStyle(document.documentElement).getPropertyValue('--shadow-3').trim(),
    );
    const icon = new Path2D(LUCIDE_PENCIL_D);
    for (const id of ids) {
      const r = this.drawableRects.get(id);
      if (!r) continue;
      const c = quadCorners(outsetRect(r, TEXT_MARK_OUTSET_PX / k)).map(p => ({ x: p.x * k, y: p.y * k }));
      ctx.strokeStyle = sel;
      ctx.lineWidth = TEXT_MARK_WIDTH;
      // ponytail: per-side dashed lines give square corners; the 2px radius is sub-visible at 1.5px.
      strokeDashedQuad(ctx, c, TEXT_MARK_DASH);
      ctx.setLineDash([]);

      const mid = { x: (c[1].x + c[2].x) / 2, y: (c[1].y + c[2].y) / 2 };
      const nx = mid.x - (c[0].x + c[3].x) / 2;
      const ny = mid.y - (c[0].y + c[3].y) / 2;
      const len = Math.hypot(nx, ny) || 1;
      const cx = mid.x + (nx / len) * TEXT_MARK_CHIP_OFFSET_PX;
      const cy = mid.y + (ny / len) * TEXT_MARK_CHIP_OFFSET_PX;
      ctx.save();
      if (shadow) {
        // Canvas shadows ignore the transform: device px.
        ctx.shadowOffsetX = Number(shadow[1]) * dpr;
        ctx.shadowOffsetY = Number(shadow[2]) * dpr;
        ctx.shadowBlur = Number(shadow[3]) * dpr;
        ctx.shadowColor = shadow[4];
      }

      ctx.fillStyle = sel;
      ctx.beginPath();
      ctx.arc(cx, cy, TEXT_MARK_CHIP_R, 0, Math.PI * 2);
      ctx.fill();
      ctx.restore();

      ctx.save();
      const s = TEXT_MARK_ICON_PX / 24;
      ctx.translate(cx - TEXT_MARK_ICON_PX / 2, cy - TEXT_MARK_ICON_PX / 2);
      ctx.scale(s, s);
      ctx.strokeStyle = ink;
      ctx.lineWidth = 2;
      ctx.lineCap = 'round';
      ctx.lineJoin = 'round';
      ctx.stroke(icon);
      ctx.restore();
    }
  }

  /** Только заводит холст и его стили. Размер и пиксели даёт `presentFrame` — ресайз без готового кадра в руках
   *  оставил бы на экране прозрачно-бумажный холст на всё время растра (см. `presentFrame`). */
  private ensureOverlayCanvas(): HTMLCanvasElement {
    let canvas = this.overlayCanvas;
    if (!canvas) {
      canvas = document.createElement('canvas');
      canvas.style.position = 'absolute';
      canvas.style.pointerEvents = 'auto'; // overlay owns input in Konva mode; camera mode hands it to the handle layer
      canvas.style.cursor = 'default';
      canvas.style.zIndex = '10';
      // trimPage moves both onto the plate: a shadow around the pre-trim sheet would frame paper that gets cut off.
      if (!this.cfg.trimPage) {
        if (this.cfg.spreadShadow) canvas.style.boxShadow = this.cfg.spreadShadow;
        if (this.cfg.spreadRadius) canvas.style.borderRadius = this.cfg.spreadRadius;
      }

      window.addEventListener('keydown', this.onMetaKey);
      window.addEventListener('keyup', this.onMetaKey);
      window.addEventListener('keydown', this.onKeyNudge);
      this.cfg.host.appendChild(canvas);
      this.overlayCanvas = canvas;
    }

    return canvas;
  }

  /**
   * Невидимый буфер кадра для `target`: сюда пишут движок (`renderSceneToCanvas`, `render_region`, `render_camera`),
   * блиты черновика и лифтов и `fillPaper`, и только готовый результат уезжает на экран через `presentFrame`.
   * Свой буфер на каждый видимый слой: у разворота, плитки резкости и лифта разные размеры, и один общий
   * пересоздавался бы на каждой их паре. Не `OffscreenCanvas` — его нет в jsdom, а метка `data-frame-buffer`
   * отличает буфер от экранного холста и для стражей, и для харнесса.
   * Ресайз здесь безопасен: холст не в DOM, стирать ему нечего.
   */
  private ensureFrameBuffer(target: HTMLCanvasElement, width: number, height: number): HTMLCanvasElement {
    let canvas = this.frameBuffers.get(target);
    if (!canvas) {
      canvas = document.createElement('canvas');
      canvas.setAttribute('data-frame-buffer', '');
      this.frameBuffers.set(target, canvas);
    }

    // eslint-disable-next-line no-restricted-syntax -- холст вне DOM: стирать нечего
    if (canvas.width !== width) canvas.width = width;
    // eslint-disable-next-line no-restricted-syntax -- холст вне DOM: стирать нечего
    if (canvas.height !== height) canvas.height = height;
    return canvas;
  }

  /**
   * Единственный показ кадра на видимом холсте: размер и пиксели одним синхронным блоком, без единого `await`,
   * промиса, таймера или rAF между ними. `'copy'` заменяет буфер целиком одной операцией — пара
   * clearRect+drawImage оставила бы прозрачный зазор, а компоновщик волен показать именно его.
   * Контекст обычный, без `desynchronized`: параметры фиксирует первый `getContext` холста, и на части GPU
   * (Windows/AMD) рассинхронизированный холст под слоем жеста показывался чёрным до конца жеста.
   */
  private presentFrame(canvas: HTMLCanvasElement, buffer: HTMLCanvasElement): void {
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    // Whole CSS pixels only: Chrome resamples a canvas whose CSS box is a fraction of a CSS pixel wide or tall (at
    // DPR 2 that is any odd bitmap dimension), and a crisp band comes out one row thinner on one side. The visible
    // canvas is padded to a multiple of the DPR; the last row/column repeats into the padding so nothing shows through.
    const dpr = window.devicePixelRatio || 1;
    const unit = Number.isInteger(dpr) ? dpr : 1;
    const width = Math.ceil(buffer.width / unit) * unit;
    const height = Math.ceil(buffer.height / unit) * unit;
    // eslint-disable-next-line no-restricted-syntax -- единственный санкционированный ресайз видимого холста
    if (canvas.width !== width) canvas.width = width;
    // eslint-disable-next-line no-restricted-syntax -- единственный санкционированный ресайз видимого холста
    if (canvas.height !== height) canvas.height = height;
    ctx.globalCompositeOperation = 'copy';
    ctx.drawImage(buffer, 0, 0);
    ctx.globalCompositeOperation = 'source-over';
    if (width > buffer.width) {
      ctx.drawImage(buffer, buffer.width - 1, 0, 1, buffer.height, buffer.width, 0, width - buffer.width, buffer.height);
    }

    if (height > buffer.height) {
      ctx.drawImage(canvas, 0, buffer.height - 1, width, 1, 0, buffer.height, width, height - buffer.height);
    }
  }

  // Paper-white belongs IN the raster: as a CSS background it also paints the edge device-pixel the scaled bitmap
  // leaves partly uncovered, and that sliver reads as a light rim around dark content at some zoom levels.
  private fillPaper(canvas: HTMLCanvasElement): void {
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    ctx.save();
    ctx.globalCompositeOperation = 'destination-over';
    ctx.fillStyle = SPREAD_PAPER;
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    ctx.restore();
  }

  /** Только заводит холст: размер и пиксели даёт `presentFrame` — см. `ensureOverlayCanvas`. */
  private ensureDragCanvas(): HTMLCanvasElement {
    let canvas = this.dragCanvas;
    if (!canvas) {
      canvas = document.createElement('canvas');
      canvas.style.position = 'absolute';
      canvas.style.pointerEvents = 'none';
      canvas.style.zIndex = '10';
      this.cfg.host.appendChild(canvas);
      this.dragCanvas = canvas;
      this.reposition();
    }

    canvas.style.transform = ''; // a fresh lift starts at identity (previous gesture may have left a scale/rotate)
    canvas.style.transformOrigin = '';
    return canvas;
  }

  private ensureSharpTileCanvas(): HTMLCanvasElement {
    let canvas = this.sharpTileCanvas;
    if (!canvas) {
      canvas = document.createElement('canvas');
      canvas.style.position = 'absolute';
      canvas.style.pointerEvents = 'none'; // pure visual; hit-test stays on the content overlay underneath
      canvas.style.zIndex = '10';
      canvas.setAttribute('data-sharp-tile', '');
      this.cfg.host.appendChild(canvas);
      this.sharpTileCanvas = canvas;
    }

    return canvas;
  }

  // Render only the visible viewport at device resolution — crisp at any zoom, cost bounded by the host.
  private async renderSharpTile(): Promise<void> {
    const cache = this.cache;
    const cam = this.camera;
    if (!cache || !cam || this.destroyed || !this.sceneJson) return;
    // Guard here and not at the call sites: resharpen() and the rendition-upgrade wave both land straight in this
    // method, and a wave awaits real network decodes — the user can open a crop or start a drag inside that window.
    if (this.manipulationLive()) return;
    // Read `generation` (never ++): a sharp-tile paints its own overlay, so bumping would supersede an in-flight
    // content render() and strand its onContentSettled (skeleton veil never lifts). A newer sharpen bails via sharpTileToken.
    const gen = this.generation;
    const token = ++this.sharpTileToken;
    const host = this.hostSize();
    // Сцена плитки строится в разрешении устройства (scene px на book px), а НЕ делится на cache.renderScale:
    // капнутый буфер разворота как раз и есть мыло, которое старая плитка растягивала камерой. Своего капа тут
    // не нужно — фото упираются в размер исходника (scaled_image), а буфер и так всего outW×outH device px.
    const tileScale = cam.zoom * this.cameraDpr();
    const args = cameraRenderArgs(cam, tileScale, host.width, host.height, this.cameraDpr());
    const canvas = this.ensureSharpTileCanvas();
    const buffer = this.ensureFrameBuffer(canvas, args.outW, args.outH);
    canvas.style.left = '0px';
    canvas.style.top = '0px';
    canvas.style.width = `${host.width}px`;
    canvas.style.height = `${host.height}px`;
    try {
      // Сцена собирается РОВНО в пикселях экрана (zoom × dpr), и плитка рисуется из неё один к одному:
      // движок кладёт обводку на целые пиксели этой же сетки, поэтому все четыре стороны выходят одной
      // толщиной. Прежний путь брал готовую сцену разворота и растягивал её камерой на дробный множитель —
      // ровное кольцо пересемплировалось, и справа оставалось на пиксель меньше, чем сверху. Ступень версии
      // фото при этом остаётся кэшевой: своя сцена меняет геометрию, а не разрешение исходников.
      const { sceneJson } = await this.composeContentScene(cache, this.liftExcludedIds(), tileScale);
      if (gen !== this.generation || token !== this.sharpTileToken || this.destroyed) return;
      // render_region: пиксель (0,0) буфера = точка сцены (-ox,-oy) — окну нужен НЕГАТИВНЫЙ origin.
      await this.cfg.wasm.renderRegionToCanvas(buffer, sceneJson, cache.fonts, cache.images, -args.originX, -args.originY);
      // Манипуляцию проверяем и ЗДЕСЬ: сессия могла открыться, пока считался кадр. Кроп такую плитку сбивает
      // бампом generation (paintSuperseding), а лифт перетаскивания — нет: beginDrag только гасит плитку
      // (repaintMainWithout патчит холст без бампа), и кадр, приехавший следом, зажёг бы её обратно поверх лифта.
      if (gen !== this.generation || token !== this.sharpTileToken || this.destroyed || this.manipulationLive()) {
        // Плитку съел репейнт: пока считался кадр в разрешении устройства, paintSuperseding бампнул generation.
        // Сам разворот плитку не вернёт — он её как раз и погасил, — а его собственный re-arm мог уже отработать
        // и уйти вхолостую. Перевзводим здесь. Свежий sharpen (token разошёлся) владеет видом сам, ему не мешаем.
        if (gen !== this.generation && token === this.sharpTileToken && !this.destroyed) this.scheduleSharpTileRearm();
        return;
      }

      // Opaque like the spread buffer it stands in for: left transparent where the paper is, the tile would let the
      // scaled spread buffer underneath show through — its blurred band edge peeks out past the tile's crisp one.
      this.fillPaper(buffer);
      // Two different edges, both needed: this erases what the tile painted past the SHEET, updatePageChrome
      // then clips the layer down to the printed PAGE when the technical zones are off.
      this.clipTileToSpread(buffer, cam);
      // Плитка ложится поверх готового разворота одним кадром: до этой строки на экране прежняя картинка.
      this.presentFrame(canvas, buffer);
      // Fresh pixels sit exactly where the layout puts them — a pan offset left by the previous gesture must go, and
      // it must go BEFORE updatePageChrome so the page clip below is recomputed instead of kept as the panned one.
      // Но пиксели посчитаны для `cam`, а чистая панорама не бампает ни generation, ни sharpTileToken: камера
      // могла уехать, пока считался кадр, и пустая строка выложила бы их со смещением жеста. Ставим ровно тот
      // CSS-сдвиг, каким setCameraLive ведёт плитку, — она встаёт на место и живёт дальше как обычная плитка.
      const now = this.camera;
      const dx = now && now.zoom === cam.zoom ? (cam.originX - now.originX) * now.zoom : 0;
      const dy = now && now.zoom === cam.zoom ? (cam.originY - now.originY) * now.zoom : 0;
      canvas.style.transform = dx !== 0 || dy !== 0 ? `translate(${dx}px, ${dy}px)` : '';
      this.sharpTileCamera = cam;
      this.updatePageChrome(this.sheetLayerBox());
      canvas.style.display = 'block';
    } catch (error) {
      console.error('[engine=rust] sharp-tile render failed', error);
    }
  }

  // render_region is an infinite canvas by contract — it keeps content past the design edge — but this tile stands in
  // for the spread buffer, which clips there. Mask it back to the page, or decor printed under the trim surfaces on the
  // pasteboard as soon as a zoom settles past the sharp cap.
  // Erasing the four pasteboard bands rather than masking with an opaque fill keeps this a geometry operation: a
  // stencil colour would read as a design value it is not (only its alpha ever mattered). A band that comes out
  // negative lies wholly off-canvas, where clearRect has nothing to erase.
  private clipTileToSpread(canvas: HTMLCanvasElement, cam: Camera): void {
    const cache = this.cache;
    const ctx = canvas.getContext('2d');
    if (!cache || !ctx) return;
    const k = cam.zoom * this.cameraDpr();
    const x = -cam.originX * k;
    const y = -cam.originY * k;
    const w = cache.viewport.width * k;
    const h = cache.viewport.height * k;
    ctx.clearRect(0, 0, canvas.width, y);
    ctx.clearRect(0, y + h, canvas.width, canvas.height - (y + h));
    ctx.clearRect(0, y, x, h);
    ctx.clearRect(x + w, y, canvas.width - (x + w), h);
  }

  private hideSharpTile(): void {
    // Drop the pan state with the tile: a hidden tile carries stale pixels, so it must never be CSS-panned back in,
    // and a leftover transform would keep updatePageChrome from re-clipping the layer.
    if (this.sharpTileCanvas) {
      this.sharpTileCanvas.style.display = 'none';
      this.sharpTileCanvas.style.transform = '';
    }

    this.sharpTileCamera = undefined;
  }

  // Chrome canvas tracks SCREEN px (resized in reposition()) so its 1px lines stay crisp at any zoom.
  private ensureChromeCanvas(): HTMLCanvasElement {
    let canvas = this.chromeCanvas;
    if (!canvas) {
      canvas = document.createElement('canvas');
      canvas.style.position = 'absolute';
      canvas.style.pointerEvents = 'none';
      canvas.style.zIndex = '11';
      this.cfg.host.appendChild(canvas);
      this.chromeCanvas = canvas;
    }

    return canvas;
  }

  private ensureHoverCanvas(width: number, height: number): HTMLCanvasElement {
    let canvas = this.hoverCanvas;
    if (!canvas) {
      canvas = document.createElement('canvas');
      canvas.style.position = 'absolute';
      canvas.style.pointerEvents = 'none';
      canvas.style.zIndex = '12';
      this.cfg.host.appendChild(canvas);
      this.hoverCanvas = canvas;
    }

    // eslint-disable-next-line no-restricted-syntax -- прозрачная обвязка: очистка не бумажная, контент слой не держит
    if (canvas.width !== width) canvas.width = width;
    // eslint-disable-next-line no-restricted-syntax -- прозрачная обвязка: очистка не бумажная, контент слой не держит
    if (canvas.height !== height) canvas.height = height;
    return canvas;
  }

  // Selection canvas tracks SCREEN px (laid out below, like the chrome and handle layers) so the frame keeps ONE
  // on-screen width and stays sharp at any zoom. A scene-sized buffer cannot: past the render-scale cap the browser
  // stretches it, which both blurs the frame and thins its stroke below a pixel, leaving it translucent.
  private ensureSelectionCanvas(): HTMLCanvasElement {
    let canvas = this.selectionCanvas;
    if (!canvas) {
      canvas = document.createElement('canvas');
      canvas.style.position = 'absolute';
      canvas.style.pointerEvents = 'none';
      canvas.style.zIndex = '13';
      this.cfg.host.appendChild(canvas);
      this.selectionCanvas = canvas;
    }

    this.layoutSelectionCanvas();
    return canvas;
  }

  /**
   * Lay a chrome layer over the host: the full host in camera mode, the sheet in Konva mode, backed at DEVICE
   * resolution. Two failure modes this avoids. Sizing the backing to the SPREAD leaves the browser stretching it
   * as the camera zooms past the resolution cap — at 800% that is an 8× blow-up, which blurs every mark on it.
   * Sizing it in CSS px leaves the browser upscaling by the device ratio, which smears each hairline over two
   * device pixels and blends its colour toward the page: the --acc hairline reached the screen as a pale lilac
   * instead of the token, reading as half-transparent. Host-sized × dpr is immune to both.
   */
  private layoutHostLayer(canvas: HTMLCanvasElement): void {
    const dpr = this.cameraDpr();
    if (this.camera) {
      const host = this.hostSize();
      const hw = Math.min(CHROME_MAX_DIM, Math.max(1, Math.round(host.width * dpr)));
      const hh = Math.min(CHROME_MAX_DIM, Math.max(1, Math.round(host.height * dpr)));
      canvas.style.left = '0px';
      canvas.style.top = '0px';
      canvas.style.width = `${host.width}px`;
      canvas.style.height = `${host.height}px`;
      // eslint-disable-next-line no-restricted-syntax -- прозрачная обвязка: очистка не бумажная, контент слой не держит
      if (canvas.width !== hw) canvas.width = hw;
      // eslint-disable-next-line no-restricted-syntax -- прозрачная обвязка: очистка не бумажная, контент слой не держит
      if (canvas.height !== hh) canvas.height = hh;
      return;
    }

    const sheet = this.sheetLayerBox();
    setLayerBox(canvas, sheet);
    const bw = Math.min(CHROME_MAX_DIM, Math.max(1, Math.round(sheet.width * dpr)));
    const bh = Math.min(CHROME_MAX_DIM, Math.max(1, Math.round(sheet.height * dpr)));
    // eslint-disable-next-line no-restricted-syntax -- прозрачная обвязка: очистка не бумажная, контент слой не держит
    if (canvas.width !== bw) canvas.width = bw;
    // eslint-disable-next-line no-restricted-syntax -- прозрачная обвязка: очистка не бумажная, контент слой не держит
    if (canvas.height !== bh) canvas.height = bh;
  }

  private layoutSelectionCanvas(): void {
    const canvas = this.selectionCanvas;
    if (!canvas) return;
    this.layoutHostLayer(canvas);
    // Обводка живёт по той же поверхности, что и хром: у рамки навылет она иначе обводит часть листа,
    // которую хост не показывает, и читается как выделение за пределами страницы. Где вылет ПОКАЗАН
    // (дизайн-хост без `trimPage`, включённые тех. зоны) — рамку по-прежнему не держит ничто.
    const sheet = this.sheetLayerBox();
    const surface = this.surfaceLayerBox(sheet);
    if (!surface) {
      canvas.style.clipPath = '';
      return;
    }

    const host = this.hostSize();
    this.clipToSurface(canvas, this.camera ? { left: 0, top: 0, width: host.width, height: host.height } : sheet, surface);
  }

  /** A live move/resize/skew owns the on-screen frame (handle canvas); the committed-rect one must stay off screen. */
  private gestureFrameLive(): boolean {
    return !!this.press?.active || !!this.gesture;
  }

  /**
   * Boxes a live lift owns at this instant — the set every full paint must leave OUT of the overlay scene.
   * The inline edit counts once its lift is up (before that the overlay is the only thing showing the box);
   * a TRANSFORM gesture counts for its lifted boxes — a photo/card swap deliberately does not, its repaint
   * restores the lifted photo and covers the vacated slot with the sampled page colour instead.
   */
  private liftExcludedIds(): ReadonlySet<string> {
    const out = new Set<string>();
    if (this.editingBoxId && this.editLiftReady) for (const id of this.editLiftIds(this.editingBoxId)) out.add(id);
    if (this.gesture && this.dragLiftIds) for (const id of this.dragLiftIds) out.add(id);
    return out;
  }

  /** Resizing a canvas wipes it, so the frame is laid out and then repainted in one step. */
  private repositionSelectionCanvas(): void {
    this.layoutSelectionCanvas();
    if (this.selectedIds.length > 0 && !this.gestureFrameLive()) this.drawSelection(this.selectedIds);
  }

  reposition(): void {
    if (this.destroyed) return;
    const sheet = this.sheetLayerBox();
    // CSS size from full book-px (the render buffer is capped lower; the browser scales it up).
    if (this.overlayCanvas) setLayerBox(this.overlayCanvas, fitBoxToBitmap(this.overlayCanvas, sheet));

    // The overflow + hover buffers can start left/above the spread (region origin ≤ 0); their own offset.
    for (const canvas of [this.overflowCanvas, this.hoverCanvas]) {
      if (canvas) this.positionRegionCanvas(canvas);
    }

    // The selection layer is screen-px, not a region buffer — it lays out against the host and repaints itself.
    this.repositionSelectionCanvas();
    this.positionCascadeCanvas();

    // A crop — running, or committed and awaiting its render — owns the drag buffer as a full-host ghost layer;
    // repositioning it as a lift region would both misplace it and hand back the page clip it must not have. It
    // bakes the camera into its PIXELS, so a camera move (pan, zoom, a host resize from a panel opening) has to
    // repaint it: left alone it keeps the pre-move transform while the spread underneath has already slid, which
    // reads as a duplicated, shifted photo.
    if (this.cropBoxId || this.cropSettleGhost) this.drawCropPreview();
    else if (this.dragCanvas) this.positionRegionCanvas(this.dragCanvas, this.dragRegionBook);
    if (this.liftOriginCanvas) this.positionRegionCanvas(this.liftOriginCanvas, this.dragRegionBook);
    if (this.editCanvas && this.editRegionBook) this.positionRegionCanvas(this.editCanvas, this.editRegionBook);

    // The chrome layer carries the technical zones, the guides and the grid — all of it geometry we know exactly,
    // so it rides the host at device resolution instead of a spread buffer the camera would stretch.
    if (this.chromeCanvas) {
      this.layoutHostLayer(this.chromeCanvas);
      this.drawChrome();
    }

    this.repositionHandleCanvas();
    this.updatePageChrome(sheet);
    this.emitFontErrorBadges();
    // The host action bar anchors to this rect — keep it glued to the frame while the camera pans/zooms.
    if (this.selectedIds.length > 0) this.cfg.onSelectionRect?.(this.computeSelectionScreenRect());
    // The caret surface was seated on the box's screen quad at session entry — a camera pan/zoom must re-seat
    // it, or the caret keeps blinking at the old screen point while the spread glides away underneath it.
    // Seated on the ENTRY frame, never the live drawable: mid-session that rect tracks the auto-width growth,
    // and re-seating on it would scale the surface by grown/entry (the growth machinery owns the frame).
    if (this.editingBoxId && this.textEdit?.active) {
      const quad = this.textEditCameraQuad();
      if (quad) this.textEdit.reposition(quad);
    }
  }

  /** Where the spread buffers sit, in host CSS px. */
  private sheetLayerBox(): LayerBox {
    const { scale, viewportOffset: vo, chromeOffset: co } = this.viewState;
    return {
      left: co.x - vo.x * scale,
      top: co.y - vo.y * scale,
      width: this.overlayBook.w * scale,
      height: this.overlayBook.h * scale,
    };
  }

  /** The surface on show, in host CSS px: the sheet while the technical zones are on, the printed page otherwise. */
  private surfaceLayerBox(sheet: LayerBox): LayerBox | null {
    if (!this.cfg.trimPage || sheet.width <= 0 || sheet.height <= 0) return null;
    const zones = this.cache?.zones;
    // No print geometry to trim by: the sheet IS the page, so the plate still carries the paper's shadow.
    if (!zones) return sheet;
    const surface = editorSurfaceBox(zones, this.cfg.getZoneVisibility?.());
    const { scale } = this.viewState;
    return {
      left: sheet.left + surface.x * scale,
      top: sheet.top + surface.y * scale,
      width: surface.width * scale,
      height: surface.height * scale,
    };
  }

  private updatePageChrome(sheet: LayerBox): void {
    const surface = this.surfaceLayerBox(sheet);
    const host = this.hostSize();
    const hostBox: LayerBox = { left: 0, top: 0, width: host.width, height: host.height };
    // The chrome layer is host-anchored now, so the sheet is no longer its own bound the way a sheet-sized buffer
    // was. Clip it before the early return: a host that shows no trimmed surface (the design editor sets no
    // trimPage) would otherwise let an off-sheet text frame paint out onto the pasteboard.
    this.clipToSurface(this.chromeCanvas, this.camera ? hostBox : sheet, surface ?? sheet);
    if (!surface) return;
    setLayerBox(this.ensurePagePlate(), surface);
    this.clipToSurface(this.overlayCanvas, sheet, surface);
    // A CSS-panned tile rides its own transform, clip included: it already carries the clip of the camera its pixels
    // were rendered for, and that one travels with them. Recomputing it against the live camera would shift it twice.
    if (!this.sharpTileCanvas?.style.transform) this.clipToSurface(this.sharpTileCanvas, hostBox, surface);
  }

  // Percent, not px: the clip then survives every later resize of the layer (spread switch, zoom, host resize)
  // on its own, so a frame painted before the next reposition is still trimmed instead of flashing the sheet.
  private clipToSurface(element: HTMLElement | undefined, layer: LayerBox, surface: LayerBox): void {
    if (!element) return;
    const inset = pageClipInsets(layer, surface);
    if (!inset.top && !inset.right && !inset.bottom && !inset.left) {
      element.style.clipPath = '';
      return;
    }

    if (!(layer.width > 0) || !(layer.height > 0)) return;
    const pct = (value: number, span: number): string => `${((value / span) * 100).toFixed(4)}%`;
    const round = this.cfg.spreadRadius ? ` round ${this.cfg.spreadRadius}` : '';
    element.style.clipPath =
      `inset(${pct(inset.top, layer.height)} ${pct(inset.right, layer.width)} ` +
      `${pct(inset.bottom, layer.height)} ${pct(inset.left, layer.width)}${round})`;
  }

  private ensurePagePlate(): HTMLDivElement {
    let plate = this.pagePlateEl;
    if (!plate) {
      plate = document.createElement('div');
      plate.style.position = 'absolute';
      plate.style.pointerEvents = 'none';
      plate.style.zIndex = '9';
      // No paper fill here: the raster carries it, and a white plate under a scaled bitmap shows as a light rim.
      if (this.cfg.spreadShadow) plate.style.boxShadow = this.cfg.spreadShadow;
      if (this.cfg.spreadRadius) plate.style.borderRadius = this.cfg.spreadRadius;
      this.cfg.host.appendChild(plate);
      this.pagePlateEl = plate;
    }

    return plate;
  }

  // Konva: the handle canvas tracks the spread (screen px, so anchors stay constant-size at any zoom). Camera: it spans the
  // full host so handles past the spread edge are visible AND grabbable; drawHandles shifts book origin in via handleDrawOffset.
  // Backed at device resolution through layoutHostLayer — CSS size (and so pointer hit coords) stays unchanged.
  private repositionHandleCanvas(): void {
    const canvas = this.handleCanvas;
    if (!canvas) return;
    this.layoutHostLayer(canvas);
    this.drawHandles();
  }

  destroy(): void {
    this.destroyed = true;
    this.stopRendererLost?.();
    // A teardown mid-gesture must release the host's gesture flag, or the next canvas click is silently ignored.
    this.setGestureActive(false);
    // Разбор посреди жеста: снимок держит три растра разворота, а флаг чернового масштаба живёт на СЕРВИСЕ и
    // пережил бы этот холст — следующий редактор открылся бы с текстурами на ступень крупнее.
    this.draftSnapshot = undefined;
    if (this.draftResolution) {
      this.draftResolution = false;
      this.cfg.wasm.setDraftScaling?.(false);
    }

    this.textEdit?.destroy();
    this.editingBoxId = undefined;
    this.liveEditText = undefined;
    this.liveEditRuns = undefined;
    this.liveEditParagraphRuns = undefined;
    this.liveEditPrevText = undefined;
    this.liveEditRect = undefined;
    this.liveEditScaleX = undefined;
    this.liveEditSpaceAdvance = undefined;
    this.liveEditBareWidth = undefined;
    this.editEntrySceneRect = undefined;
    if (this.renderTimer !== undefined) clearTimeout(this.renderTimer);
    this.renderTimer = undefined;
    if (this.paintTimer !== undefined) clearTimeout(this.paintTimer);
    this.paintTimer = undefined;
    if (this.sharpRearmTimer !== undefined) clearTimeout(this.sharpRearmTimer);
    this.sharpRearmTimer = undefined;
    if (this.renditionUpgradeTimer !== undefined) clearTimeout(this.renditionUpgradeTimer);
    this.renditionUpgradeTimer = undefined;
    this.pendingRenditionUpgrades = undefined;
    if (this.renditionSettleRefs.size > 0) {
      this.renditionSettleRefs.clear();
      this.reportRenditionUpgrades();
    }

    if (this.livePaintFrame !== undefined) cancelAnimationFrame(this.livePaintFrame);
    this.livePaintFrame = undefined;
    this.pendingRender = false;
    this.replanZoomAfterRender = false;
    this.dragSettlePending = false;
    this.editLiftReady = false;
    this.editTickPending = false;
    this.editTickToken++;
    this.editSessionToken++;
    if (this.editTickRaf !== undefined) {
      if (typeof cancelAnimationFrame === 'function') cancelAnimationFrame(this.editTickRaf);
      else clearTimeout(this.editTickRaf as unknown as ReturnType<typeof setTimeout>);
    }

    this.editTickRaf = undefined;
    this.removeEditCanvas();
    this.styleLiftIds = [];
    this.styleLiftReady = false;
    this.styleTickPending = false;
    this.styleTickToken++;
    this.styleSessionToken++;
    if (this.styleTickRaf !== undefined) {
      if (typeof cancelAnimationFrame === 'function') cancelAnimationFrame(this.styleTickRaf);
      else clearTimeout(this.styleTickRaf as unknown as ReturnType<typeof setTimeout>);
      this.styleTickRaf = undefined;
    }

    this.removeStyleCanvas();
    if (this.hoverRaf) cancelAnimationFrame(this.hoverRaf);
    this.hoverRaf = 0;
    if (this.dropHoverRaf) cancelAnimationFrame(this.dropHoverRaf);
    this.dropHoverRaf = 0;
    if (this.swapTargetFadeRaf) cancelAnimationFrame(this.swapTargetFadeRaf);
    this.swapTargetFadeRaf = 0;
    if (this.hoverFadeRaf) cancelAnimationFrame(this.hoverFadeRaf);
    this.hoverFadeRaf = 0;
    this.hoverPaint = null;
    if (this.swapPreview?.raf) cancelAnimationFrame(this.swapPreview.raf);
    this.swapPreview = null;
    this.releaseCascade(this.press?.cascade);
    this.removeCascadeCanvas();
    this.cancelResizePreview();
    this.gesture = undefined;
    this.reflowLive = undefined;
    // A canvas torn down mid-drag never reaches endPress, so the hover chrome would stay painted on the next mount.
    if (this.press?.mode === 'photo-swap') {
      this.setSwapHover(undefined);
    }

    this.hideSwapGhost(); // body-level too: it would outlive the whole editor otherwise

    this.press = undefined;
    this.disarmWindowDrag(this.onPointerMove, this.onPointerUp);
    this.disarmWindowDrag(this.onPressMove, this.onPressUp);
    this.detachInput(); // input listeners live on whichever surface owns them (overlay in Konva, handle layer in camera)
    window.removeEventListener('keydown', this.onMetaKey);
    window.removeEventListener('keyup', this.onMetaKey);
    window.removeEventListener('keydown', this.onKeyNudge);
    this.disarmWindowDrag(this.onCropMove, this.onCropUp);
    window.removeEventListener('wheel', this.onCropWheel);
    window.removeEventListener('keydown', this.onCropKeyDown);
    this.cropGhostBitmap?.close();
    this.cropGhostBitmap = undefined;
    this.releaseCropSettleGhost();
    this.cropBoxId = undefined;
    this.cropState = undefined;
    this.liveCropOverrides.clear();
    this.disarmWindowDrag(this.onTrimMove, this.onTrimUp);
    window.removeEventListener('keydown', this.onTrimKeyDown);
    this.trimGesture = undefined;
    this.trimBoxId = undefined;
    this.trimState = undefined;
    this.liveTrimOverrides.clear();
    if (this.nudgeTimer) clearTimeout(this.nudgeTimer);
    this.overlayCanvas?.remove();
    this.overlayCanvas = undefined; // буферы кадров висят на самих холстах (WeakMap) и уходят вместе с ними
    this.pagePlateEl?.remove();
    this.pagePlateEl = undefined;
    this.handleCanvas?.remove();
    this.handleCanvas = undefined;
    this.chromeCanvas?.remove();
    this.chromeCanvas = undefined;
    this.hoverCanvas?.remove();
    this.hoverCanvas = undefined;
    this.selectionCanvas?.remove();
    this.selectionCanvas = undefined;
    this.liftOriginCanvas?.remove();
    this.liftOriginCanvas = undefined;
    this.dragCanvas?.remove();
    this.dragCanvas = undefined;
    this.dragRegionBook = undefined;
    this.sharpTileCanvas?.remove();
    this.sharpTileCanvas = undefined;
    this.sharpTileCamera = undefined;
    this.overflowCanvas?.remove();
    this.overflowCanvas = undefined;
    this.drawableRects.clear();
    this.groupOffsets.clear();
    this.cache = undefined;
    this.sceneJson = undefined;
  }
}
